import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import MarketingCollateralTab from "./VexosMarketingCollateral";
import {
  MARKETING_DEFAULTS,
  COLLATERAL_DEFAULTS,
  getMarketingConfig,
  getCollateralConfig,
  getCPLConfig,
  resolveCPL,
  resolveCampaignFeeTier,
  fetchSharedAdminState,
  saveSharedAdminState,
  fetchPricingAudienceCount,
  applyAdminToConfigs,
  isVexosPricingAdmin,
  peekLegacyAdminState,
  clearLegacyAdminState,
} from "./vexosClientPricing";
import { apiClient, useAuth } from "../../App";
import { WIRE_INSTRUCTIONS, WIRE_ROWS } from "@/lib/wireInstructions";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import {
  SETUP_FEE_TIERS,
  MONTHLY_SUBSCRIPTION_TIERS,
  CONTRACT_TERM_OPTIONS,
  EXTERIOR_ADDONS,
  // ─ Orbit structure (base orbit + suborbits) ─
  BASE_ORBIT_KEYFRAMES,
  SUBORBIT_INCLUDED_KEYFRAMES,
  dayToNightBreakdown,
  SUBORBIT_MAX_EXTRA_KEYFRAMES,
  suborbitKeyframeSubtext,
  keyframeWord,
  // ─ Master Plan (community variant) ─
  MASTER_PLAN_INCLUDED_SUBORBITS,
  MASTER_PLAN_SUBORBIT_KEYFRAMES,
  getMasterPlanSetupFee,
  masterPlanDefaultSubOrbits,
  masterPlanBillableSubOrbits,
  INTERIOR_RENDERING,
  INTERIOR_VR,
  EXTERIOR_VR,
  AMENITY_RENDERING,
  AMENITY_VR,
  getSetupFee,
  getMonthlyRate,
  recommendInteriorRenders,
  recommendInteriorVR,
  recommendExteriorVR,
  recommendAmenityRenders,
  recommendAmenityVRPoints,
  // ─ Community variant ─
  COMMUNITY_EXTERIOR_ADDONS,
  HOME_MODEL_RENDERING,
  HOME_MODEL_VR,
  COMMUNITY_AMENITY_RENDERING,
  COMMUNITY_AMENITY_VR,
  // ─ Shared (Phase 05 & 06) ─
  VIDEO_ANIMATION,
  STANDALONE_VIDEO,
  STANDALONE_VIDEO_SIZES,
  computeStandaloneVideo,
  standaloneTier,
  standaloneHeroesForSize,
  standaloneSizeUnlocksAddOns,
  standaloneHeroPrice,
  FLOOR_PLANS,
  floorPlanVolumeDiscount,
  floorPlan3DRate,
  // Conceptual Rendering Hybrid Workflow — VEXOS + À La Carte, flat per-image rate.
  CONCEPTUAL_MODELING,
  getCommunitySetupFee,
  getCommunityMonthlyRate,
  recommendHomeModelRenders,
  recommendHomeModelVR,
  recommendCommunityAmenityRenders,
  recommendCommunityAmenityVRPoints,
  isTBD,
  lookupVolumeRate,
  renderingTiers,
  activeVolumeTierIndex,
  evaluateBundle,
  amenityVRPackagePrice,
  DRONE,
  // SFH (Single-Family Home) — fixed pricing resolver pulls from admin
  // config when present, else falls back to canonical defaults.
  getSFHPricing,
  SFH_DEFAULTS,
  // Additional revision rounds + on-site travel (admin-editable).
  REVISIONS_CONFIG,
  TRAVEL_CONFIG,
  revisionRoundCost,
  defaultPostTermMonthly,
  // Internal discount guardrails + complimentary add-ons (Sections 8C / 8D).
  DISCOUNT_GUARDRAILS,
  FREE_ADD_ONS,
} from "./vexosClientPricing";
import VexosPricingAdmin from "./VexosPricingAdmin";
import ProductionTimelineCards from "./ProductionTimelineCards";
import { computeVexosTimeline, fetchTimelineDurations, normalizeTimelineOverride, TIMELINE_FALLBACK_DURATIONS } from "./vexosTimeline";
import { snapshotMoney } from "./snapshotMoney";
import { applyPriceOverrides, overridesPutDiscountsAtStake } from "./priceOverrides";
import {
  NEW_QUOTE_BUNDLE_APPLIED, savedBundleApplied, appliedBundle, bundleOfferSavings, bundleOfferCopy,
} from "./bundleOptIn";
import { PRICE_OVERRIDE_LINES } from "./priceOverrideLines";
import {
  lineGuardrail, additionalDiscountGuardrail, guardrailFlagsSummary,
  GUARDRAIL_APPROVAL, GUARDRAIL_BELOW_FLOOR,
  normalizeFreeAddOns, freeAddOnsStatus, freeAddOnsChangeAllowed, freeAddOnCapMessage,
} from "./guardrails";
import { normalizeVideos, primaryDuration, videosCost, shortVideos, describeVideos, videoCount } from "./videoLineup";
import {
  applyProposalDiscount, normalizeProposalDiscount, proposalDiscountLabel,
  DISCOUNT_AMOUNT, DISCOUNT_PCT,
} from "./proposalDiscount";
import {
  applyConceptualDesignFee, normalizeConceptualDesignFee, conceptualDesignFeeSub,
  conceptualDesignFeeLabel, clampInteriorDesignRate, interiorDesignMath,
  INTERIOR_DESIGN_LABEL, INTERIOR_DESIGN_RATE_DEFAULT, INTERIOR_DESIGN_RATE_MIN,
  INTERIOR_DESIGN_RATE_MAX, INTERIOR_DESIGN_MAX_SPACES, INTERIOR_DESIGN_SCOPE,
  CONCEPTUAL_DESIGN_FEE_LABEL,
} from "./conceptualDesignFee";
import {
  applyModelingFee, normalizeModelingFee, modelingFeeFromInputs, modelingFeeSub,
  modelingFeeTier, MODELING_FEE_TIERS, MODELING_FEE_NONE, MODELING_FEE_LABEL,
  clampModelingAmount, MODELING_FEE_SLIDER_MIN, MODELING_FEE_SLIDER_MAX, MODELING_FEE_SLIDER_STEP,
} from "./modelingFee";
import {
  VR_CONFIGURATOR, VR_CONFIGURATOR_LABEL, VR_CONFIGURATOR_CATEGORY_TYPES,
  normalizeVrConfigurator, vrConfiguratorFields, categoryCost, categoryCostDetail, newCategory, configuratorSub,
} from "./vrConfigurator";

// The Project Intake Guide — the files we need before the first render. Reps
// show it on the sales call so a client sees the level of detail an interior
// design package takes before saying they have it covered. Static file in
// public/docs; page 7 is the interior design package.
const INTAKE_GUIDE_URL = "/docs/trp-project-intake-guide.pdf";
const INTAKE_GUIDE_INTERIOR_PAGE = 7;
import {
  resolvePaymentPlan, planNote, pifOffer, pifCopy, pifPctOf, payInFullTotal, PIF_DISCOUNT_PCT,
  PLAN_TWO_PART, PLAN_FULL, PLAN_75_25, PAY_PLAN_AUTO, ALACARTE_THREE_PART,
  NEW_QUOTE_PAY_PLAN, FINAL_POLICY_NOTICE, quoteFinalPaymentPolicy, finalDueSentence, finalTiming,
  normalizePayPlanChoice, MILESTONE_POLICY_CLAY, quoteMilestonePolicy,
} from "./paymentPlan";
import { Minus, Plus, Check, Sparkles, Building2, Layers, Home, FileText, Printer, Download, AlertTriangle, Settings, Maximize2, X, Video, Square, Presentation, Globe, Image as ImageIcon, Lock } from "lucide-react";

/* ──────────────────────────────────────────────────────────────────────
 *  VEXOS Client-Facing Sales Calculator
 *
 *  Stateless, premium client-facing tool. Designed to be screen-shared
 *  with a prospective client. 4 sequential phases, sticky live quote
 *  summary, smooth reveals. Rates live in `vexosClientPricing.js`.
 *
 *  Build scope: Condo Building variant only. Architecture leaves room
 *  for future tabs (SFH / Community) — tab bar is rendered but disabled.
 * ─────────────────────────────────────────────────────────────────────── */

// Live display-currency context for the presentation view + PDF (USD/CAD).
// fmt()/fmtUSD() read this directly rather than taking it as an argument so
// the dozens of sibling presentational components below (PriceTag,
// SummaryRow, ScopeRow, ExpRow, etc.) don't need currency threaded through
// props. Kept in sync with React state via setDisplayCurrency(), called at
// the top of the main component's render — Stripe checkout and the
// underlying rate card always stay USD; this only affects what's shown.
let _displayFx = { currency: "USD", symbol: "$", rate: 1 };
function setDisplayCurrency(currency, rate) {
  const isCad = currency === "CAD" && rate;
  _displayFx = isCad
    ? { currency: "CAD", symbol: "CA$", rate }
    : { currency: "USD", symbol: "$", rate: 1 };
}

const fmt = (n) => `${_displayFx.symbol}${Math.round((Number(n) || 0) * _displayFx.rate).toLocaleString()}`;
const fmtSqFt = (n) => `${Math.round(Number(n) || 0).toLocaleString()} sq ft`;

const ACCENT = "#8FA79B"; // warm gold

// ── Orbits & keyframes ────────────────────────────────────────────────
// "Master Orbit" is retired wording. The base orbit (building / home /
// community) ships with BASE_ORBIT_KEYFRAMES keyframes; every orbit beyond
// it is a SUBORBIT, sold through the "Additional Orbit" line at a flat
// per-orbit price that bundles SUBORBIT_INCLUDED_KEYFRAMES keyframes.
//
// Suborbits are held as an array of per-orbit EXTRA keyframe counts: `[0, 2]`
// is two suborbits, the first with its bundled 2 keyframes and the second
// with 4. Modelling them this way makes a 1-keyframe suborbit unrepresentable
// — a suborbit only exists as an array entry, and every entry carries the
// bundled two — so there is no state in which the builder can emit one.
//
// Internal field names (masterOrbitsQty / masterOrbitCost) are unchanged for
// back-compat with saved quotes; only the wording and the shape changed.
const ADDITIONAL_ORBIT_LABEL = "Additional orbit";
const MAX_SUBORBITS = EXTERIOR_ADDONS.masterOrbit.maxQuantity ?? 5;

const subOrbitCount = (subOrbits) => (Array.isArray(subOrbits) ? subOrbits.length : 0);
const subOrbitExtraKf = (subOrbits) =>
  (Array.isArray(subOrbits) ? subOrbits : []).reduce((s, n) => s + (Number(n) || 0), 0);

/**
 * Why day-to-night costs what it costs. A rep gets asked this on the call, and
 * the answer has to read off the orbits: 4 included keyframes at the 1:1 rate,
 * a suborbit's 2 at the same rate, anything added at half.
 */
function D2NBreakdown({ calc, addons }) {
  const b = calc?.dayToNightDetail;
  if (!b) return null;
  const rate = addons.dayToNight.pricePerKeyframe;
  const extraRate = addons.dayToNight.pricePerExtraKeyframe;
  const rows = [
    { label: `Building orbit · ${BASE_ORBIT_KEYFRAMES} keyframes included`,
      amount: b.baseOrbitIncludedCost },
    ...(b.subOrbitCount > 0
      ? [{ label: `${b.subOrbitCount} suborbit${b.subOrbitCount > 1 ? "s" : ""} · ${SUBORBIT_INCLUDED_KEYFRAMES} keyframes each`,
           amount: b.subOrbitIncludedCost * b.subOrbitCount }]
      : []),
    ...(b.extraKf > 0
      ? [{ label: `${b.extraKf} added keyframe${b.extraKf > 1 ? "s" : ""} · ${fmtUSD(extraRate)} each`,
           amount: b.extraCost }]
      : []),
  ];
  return (
    <div className="space-y-1.5">
      {rows.map((r) => (
        <div key={r.label} className="flex items-baseline justify-between gap-3 text-[12.5px]">
          <span className="text-white/50">{r.label}</span>
          <span className="text-white/80 tabular-nums">{fmtUSD(r.amount)}</span>
        </div>
      ))}
      <div className="flex items-baseline justify-between gap-3 text-[13px] pt-1.5 border-t border-white/[0.04]">
        <span className="text-white/70">
          Day-to-night · {b.totalKf} keyframe{b.totalKf === 1 ? "" : "s"}
        </span>
        <span className="text-white tabular-nums">{fmtUSD(b.total)}</span>
      </div>
      <p className="text-[11px] text-white/35 pt-1 leading-snug">
        Included keyframes are {fmtUSD(rate)} each, 1:1 with the orbit. Keyframes added
        to an orbit are {fmtUSD(extraRate)}.
      </p>
    </div>
  );
}

/** Every keyframe sold on the exterior — base orbit + suborbits, extras included. */
const totalExteriorKeyframes = (baseExtraKf, subOrbits) =>
  BASE_ORBIT_KEYFRAMES
  + (Number(baseExtraKf) || 0)
  + subOrbitCount(subOrbits) * SUBORBIT_INCLUDED_KEYFRAMES
  + subOrbitExtraKf(subOrbits);

/** Keyframes billed at the additional-keyframe rate — base extras + suborbit extras. */
const billableExtraKeyframes = (baseExtraKf, subOrbits) =>
  (Number(baseExtraKf) || 0) + subOrbitExtraKf(subOrbits);

/**
 * Hydrate the suborbit array from a saved quote. Quotes saved before the
 * array existed only carry `masterOrbitsQty`, so they rebuild as N suborbits
 * with no extra keyframes — exactly the scope they were sold with.
 */
const subOrbitsFromSaved = (savedArray, legacyQty) => {
  if (Array.isArray(savedArray)) {
    return savedArray
      .slice(0, MAX_SUBORBITS)
      .map((n) => Math.min(SUBORBIT_MAX_EXTRA_KEYFRAMES, Math.max(0, Number(n) || 0)));
  }
  const n = Math.max(0, Math.min(MAX_SUBORBITS, Math.floor(Number(legacyQty) || 0)));
  return Array.from({ length: n }, () => 0);
};

// ── 3D floor plan levels ──────────────────────────────────────────────
// A 3D floor plan is scoped per UNIT TYPE, but a multi-level unit needs one
// drawing per level inside that single unit type. Quantity alone can't say
// which of the two a number means — the ambiguity behind Southside's
// "3D floor plans: 3" — so levels are tracked separately and the billable
// plan count is quantity × levels.
const LEVELS_MAX = 6;
const levelsFor = (levels, i) => {
  const n = Math.floor(Number((levels || {})[i]) || 0);
  return Math.min(LEVELS_MAX, Math.max(1, n || 1));
};

/**
 * Billable plans, unit types and levels for a 3D floor plan selection.
 * `billablePlans` is what the volume tier and every downstream production
 * surface count, because each level is a plan's worth of work.
 */
function floorPlanLevelTotals(values, levels, planCount) {
  let billablePlans = 0;
  let unitTypes = 0;
  let totalLevels = 0;
  for (let i = 0; i < planCount; i++) {
    const qty = Math.max(0, Number((values || {})[i]) || 0);
    if (qty <= 0) continue;
    const lv = levelsFor(levels, i);
    billablePlans += qty * lv;
    unitTypes += qty;
    totalLevels += qty * lv;
  }
  return { billablePlans, unitTypes, totalLevels, multiLevel: totalLevels > unitTypes };
}

/**
 * How a 3D floor plan scope reads on a quote: "2 unit types · 4 levels" when
 * any unit is multi-level, plain "N plans" otherwise. Snapshots saved before
 * levels existed carry no unit/level split and fall through to the plain form.
 */
const floorPlan3DScopeLabel = (units, levels, plans) => {
  const u = Number(units) || 0;
  const lv = Number(levels) || 0;
  const p = Number(plans) || 0;
  if (u > 0 && lv > u) return `${u} unit type${u === 1 ? "" : "s"} · ${lv} levels`;
  return `${p} plan${p === 1 ? "" : "s"}`;
};

/** Resize the suborbit list, preserving the extras already set on kept orbits. */
const resizeSubOrbits = (subOrbits, nextCount, maxCount = MAX_SUBORBITS) => {
  const cur = Array.isArray(subOrbits) ? subOrbits : [];
  const n = Math.max(0, Math.min(maxCount, Math.floor(Number(nextCount) || 0)));
  return Array.from({ length: n }, (_, i) => Number(cur[i]) || 0);
};

// Client-facing sales reps shown on the proposal Contact page. Values match
// the backend SALES_REPS directory keys (calculators.py) so a saved
// `inputs.salesRep` resolves to that rep's stored email/phone/headshot.
const REP_OPTIONS = [
  { value: "julian ruiz", label: "Julian Ruiz", email: "julian@therenderpros.com" },
  { value: "jordan evans", label: "Jordan Evans", email: "jordan@therenderpros.com" },
  { value: "bernardo essenfeld", label: "Bernardo Essenfeld", email: "bernard@therenderpros.com" },
  { value: "cole becker", label: "Cole Becker", email: "cole@therenderpros.com" },
];

// Client-facing reps who generate their OWN proposals — for them the proposal
// contact auto-resolves from their login and the rep selector is hidden.
// Everyone else (internal users, incl. Bernardo/Cole) sees the selector and
// picks whichever rep should appear on the PDF's contact page.
const SELF_SERVE_REPS = [
  { value: "julian ruiz", email: "julian@therenderpros.com" },
  { value: "jordan evans", email: "jordan@therenderpros.com" },
];

// CountUp number — smooth transition when values change.
function CountUp({ value, prefix = "$", className = "" }) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef(null);
  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    const from = fromRef.current;
    const to = value;
    if (from === to) return;
    const start = performance.now();
    const duration = 480;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(from + (to - from) * eased);
      setDisplay(next);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
      else fromRef.current = to;
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value]);
  return <span className={className}>{prefix}{Math.max(0, display).toLocaleString()}</span>;
}

function PhaseHeader({ number, title, subtitle, locked }) {
  return (
    <div className="flex items-start gap-5 mb-10">
      <div
        className={`shrink-0 w-12 h-12 rounded-full border flex items-center justify-center transition-all duration-500 ${
          locked
            ? "border-white/15 text-white/30 bg-transparent"
            : "border-[#8FA79B]/60 text-[#8FA79B] bg-[#8FA79B]/5 shadow-[0_0_20px_rgba(143,167,155,0.15)]"
        }`}
      >
        <span className="font-serif text-lg" style={{ fontFamily: 'var(--font-sans)' }}>{number}</span>
      </div>
      <div>
        <h2
          className={`text-[34px] leading-none tracking-tight transition-colors duration-500 ${locked ? "text-white/30" : "text-white"}`}
          style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}
        >
          {title}
        </h2>
        {subtitle && <p className="text-[13px] text-white/45 mt-2 max-w-xl">{subtitle}</p>}
      </div>
    </div>
  );
}

function FormField({ label, hint, children }) {
  return (
    <div className="space-y-2">
      <label className="block text-[10.5px] uppercase tracking-[0.14em] text-white/55 font-medium">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-white/40 italic">{hint}</p>}
    </div>
  );
}

function NumInput({ value, onChange, placeholder, min = 0, max = null, dataTestId }) {
  return (
    <input
      type="number"
      inputMode="numeric"
      min={min}
      max={max ?? undefined}
      value={value === "" ? "" : value}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "") return onChange("");
        const n = Number(v);
        if (!Number.isFinite(n) || n < min) return;
        if (max !== null && n > max) return onChange(max);
        onChange(n);
      }}
      onFocus={(e) => e.target.select()}
      placeholder={placeholder}
      data-testid={dataTestId}
      className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#8FA79B] outline-none text-[20px] text-white pb-2 transition-colors duration-200 tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none placeholder:text-white/15 placeholder:text-[16px]"
    />
  );
}

function StepperRow({ label, badge, value, recommended, onChange, min = 0, max = null, dataTestId }) {
  const atRecommended = recommended != null && value === recommended;
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-white/[0.05] last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-3">
          <span className="text-[14px] text-white/85 truncate">{label}</span>
          {badge && <span className="text-[10px] uppercase tracking-[0.12em] text-white/35">{badge}</span>}
        </div>
        {recommended != null && (
          <div className="mt-1 flex items-center gap-2">
            <span className={`text-[10.5px] ${atRecommended ? "text-[#8FA79B]" : "text-white/35"}`}>
              Recommended: {recommended}
            </span>
            {atRecommended && <Check className="w-3 h-3 text-[#8FA79B]" />}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(Math.max(min, (value || 0) - 1))}
          className="w-7 h-7 rounded-full bg-white/[0.04] hover:bg-white/[0.08] text-white/60 flex items-center justify-center transition-colors duration-150"
          aria-label="Decrease"
          data-testid={dataTestId ? `${dataTestId}-minus` : undefined}
        >
          <Minus className="w-3 h-3" />
        </button>
        <input
          type="number"
          min={min}
          max={max ?? undefined}
          value={value === "" ? "" : value}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") return onChange(0);
            const n = Number(v);
            if (!Number.isFinite(n) || n < min) return;
            if (max !== null && n > max) return onChange(max);
            onChange(n);
          }}
          onFocus={(e) => e.target.select()}
          className="w-14 text-center bg-transparent text-[15px] text-white outline-none tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          data-testid={dataTestId}
        />
        {/* The typed input already clamps to `max`; the + button has to as
            well, or a rep can click a capped row past its ceiling. On rows
            whose price is qty × rate (day-to-night, animated keyframes) that
            showed a count the quote wasn't actually charging for. */}
        <button
          onClick={() => onChange(max !== null && max !== undefined
            ? Math.min(max, (value || 0) + 1)
            : (value || 0) + 1)}
          disabled={max !== null && max !== undefined && (value || 0) >= max}
          className="w-7 h-7 rounded-full bg-white/[0.04] hover:bg-white/[0.08] text-white/60 disabled:opacity-25 disabled:cursor-not-allowed flex items-center justify-center transition-colors duration-150"
          aria-label="Increase"
          data-testid={dataTestId ? `${dataTestId}-plus` : undefined}
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// Compact +/- stepper for the sticky quote sidebar (smaller than StepperRow).
function CompactStepper({ value, onChange, min = 0, max = null, dataTestId }) {
  const dec = () => onChange(Math.max(min, (value || 0) - 1));
  const inc = () => onChange(max !== null ? Math.min(max, (value || 0) + 1) : (value || 0) + 1);
  return (
    <div className="flex items-center gap-1 shrink-0">
      <button
        onClick={dec}
        className="w-6 h-6 rounded-full bg-white/[0.04] hover:bg-white/[0.08] text-white/60 flex items-center justify-center transition-colors duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
        disabled={(value || 0) <= min}
        aria-label="Decrease"
        data-testid={dataTestId ? `${dataTestId}-minus` : undefined}
      >
        <Minus className="w-3 h-3" />
      </button>
      <span className="w-7 text-center text-[15px] text-white tabular-nums" data-testid={dataTestId}>{value || 0}</span>
      <button
        onClick={inc}
        className="w-6 h-6 rounded-full bg-white/[0.04] hover:bg-white/[0.08] text-white/60 flex items-center justify-center transition-colors duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
        disabled={max !== null && (value || 0) >= max}
        aria-label="Increase"
        data-testid={dataTestId ? `${dataTestId}-plus` : undefined}
      >
        <Plus className="w-3 h-3" />
      </button>
    </div>
  );
}

function PointSelectorRow({ label, value, options, onChange, dataTestId, allowCustom = true }) {
  // value: number (0/2/4/…/10, or any custom count) or null (blank — no selection yet).
  const maxOption = options.length ? options[options.length - 1] : 0;
  // A selected value that isn't one of the presets is a custom (> max) count.
  const valueIsCustom = value != null && !options.includes(value);
  const [showCustom, setShowCustom] = React.useState(valueIsCustom);

  // Reveal the input whenever the value lands on a custom number (e.g. via Apply to All).
  React.useEffect(() => {
    if (valueIsCustom) setShowCustom(true);
  }, [valueIsCustom]);

  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-white/[0.05] last:border-b-0">
      <span className="text-[14px] text-white/85 truncate">{label}</span>
      <div className="flex flex-wrap gap-1.5 shrink-0 justify-end items-center">
        {options.map(pt => (
          <button
            key={pt}
            onClick={() => onChange(pt)}
            className={`min-w-[40px] px-3 py-1.5 rounded-full text-[12px] tabular-nums border transition-all duration-200 ${
              value === pt
                ? (pt === 0
                    ? "bg-white/[0.08] text-white border-white/25"
                    : "bg-[#8FA79B]/15 text-[#8FA79B] border-[#8FA79B]/40")
                : "bg-transparent text-white/55 border-white/10 hover:border-white/25 hover:text-white/80"
            }`}
            data-testid={dataTestId ? `${dataTestId}-pt-${pt}` : undefined}
          >
            {pt}
          </button>
        ))}
        {allowCustom && (
          showCustom ? (
            <input
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              value={valueIsCustom ? value : ""}
              placeholder={`${maxOption}+`}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") { onChange(null); setShowCustom(false); return; }
                const n = Math.max(0, Math.round(Number(raw)));
                if (Number.isFinite(n)) onChange(n);
              }}
              className="w-[60px] px-2.5 py-1.5 rounded-full text-[12px] tabular-nums text-center bg-[#8FA79B]/15 text-[#8FA79B] border border-[#8FA79B]/40 placeholder:text-[#8FA79B]/40 focus:outline-none focus:border-[#8FA79B]/70 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              data-testid={dataTestId ? `${dataTestId}-custom` : undefined}
              autoFocus={!valueIsCustom}
              aria-label={`${label} — custom point count`}
            />
          ) : (
            <button
              type="button"
              onClick={() => setShowCustom(true)}
              title="Enter a custom point count above the presets"
              className="min-w-[40px] px-3 py-1.5 rounded-full text-[12px] border border-dashed border-white/15 text-white/45 hover:border-[#8FA79B]/40 hover:text-[#8FA79B] transition-all duration-200"
              data-testid={dataTestId ? `${dataTestId}-more` : undefined}
            >
              +
            </button>
          )
        )}
      </div>
    </div>
  );
}

// Inline "Apply to All" action — appears under the first row of a multi-row
// section and copies row 0's value to every other row in the group.
function ApplyToAllInline({ disabled, onClick, dataTestId }) {
  return (
    <div className="flex justify-end py-2 border-b border-white/[0.05]">
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA79B]/75 hover:text-[#8FA79B] disabled:text-white/20 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-1.5"
        data-testid={dataTestId}
      >
        <span aria-hidden>↓</span> Apply to All Below
      </button>
    </div>
  );
}

function TbdBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.08em] bg-amber-400/10 text-amber-300/90 border border-amber-400/20"
      title="Rate not yet wired — placeholder value. Update in vexosClientPricing.js before going live."
    >
      <AlertTriangle className="w-2.5 h-2.5" /> TBD
    </span>
  );
}

function PriceTag({ amount, prefix = "$", showTbd = true }) {
  // Callers never override `prefix` — always converts via the shared
  // currency context so this respects the USD/CAD toggle.
  const display = prefix === "$" ? fmt(amount) : `${prefix}${Math.round(Number(amount) || 0).toLocaleString()}`;
  if (showTbd && isTBD(amount)) {
    return (
      <span className="inline-flex items-center gap-2">
        <span className="text-white/40 line-through tabular-nums">{display}</span>
        <TbdBadge />
      </span>
    );
  }
  return <span className="text-white tabular-nums">{display}</span>;
}

/* ──────────────────────────────────────────────────────────────────────
 *  VolumeTierBadge — surfaces the currently-active volume tier inline
 *  with the rate label so reps and clients can see at a glance which
 *  tier discount is being applied. Renders nothing when no quantity is
 *  selected yet (tier index = -1) or volumeTiers is unset.
 *
 *  Tier names are derived from index → label mapping (Standard / Volume
 *  / Scale) matching the Pricing Admin nomenclature.
 * ─────────────────────────────────────────────────────────────────────── */
const TIER_LABELS = ["Standard", "Volume", "Scale"];
function VolumeTierBadge({ qty, volumeTiers, testId }) {
  if (!Array.isArray(volumeTiers) || volumeTiers.length === 0) return null;
  if (!Number(qty)) return null;
  const idx = activeVolumeTierIndex(qty, volumeTiers);
  if (idx < 0) return null;
  const label = TIER_LABELS[idx] || `Tier ${idx + 1}`;
  return (
    <span
      className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[9.5px] uppercase tracking-[0.10em] bg-[#8FA79B]/10 text-[#8FA79B]/90 border border-[#8FA79B]/25 tabular-nums"
      title={`Volume tier: ${label} (${qty} units)`}
      data-testid={testId}
    >
      {label}
    </span>
  );
}

/* Normalize a persisted standalone-video selection back into the state shape
 * (tolerates old quotes with no selection saved). */
function hydrateStandaloneSel(saved) {
  const s = saved && typeof saved === "object" ? saved : {};
  return {
    heroKey: s.heroKey ?? null,
    unitInteriorQty: Math.max(0, Math.round(Number(s.unitInteriorQty) || 0)),
    penthouseQty: Math.max(0, Math.round(Number(s.penthouseQty) || 0)),
    socialCutdownQty: Math.max(0, Math.round(Number(s.socialCutdownQty) || 0)),
  };
}

/* ──────────────────────────────────────────────────────────────────────
 *  VideoAnimationCard — Phase 05.  Mutually-exclusive duration selector
 *  inside one product card. Click an active duration again to deselect.
 * ─────────────────────────────────────────────────────────────────────── */
/* One card per video kind. Each video is its own row of duration pills, so a
 * quote can carry several of the same kind at mixed lengths (two 60s films +
 * a 30s cut). The first row is always shown; clicking its active pill again
 * removes it, like the old single-select. `videos` is a list of seconds —
 * see ./videoLineup. */
function VideoAnimationCard({ label, description, durations, videos, onChange, productLabel, testIdRoot, durationNotes, disabled = false, disabledHint = null }) {
  const durationKeys = Object.keys(durations).map(Number).sort((a, b) => a - b);
  const list = normalizeVideos(videos);
  const total = videosCost(list, durations);
  // Always one row to pick from; extra rows only once a first video exists.
  const rows = list.length ? list : [null];
  const setRow = (idx, sec) => {
    const next = list.slice();
    if (sec === null) next.splice(idx, 1);
    else if (idx >= next.length) next.push(sec);
    else next[idx] = sec;
    onChange(next);
  };
  const selectedSet = new Set(list);
  return (
    <div
      className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]"
      style={disabled ? { opacity: 0.55 } : undefined}
      data-testid={`${testIdRoot}-card`}
      aria-disabled={disabled || undefined}
    >
      <div className="flex items-start gap-3 mb-4">
        {disabled
          ? <Lock className="w-4 h-4 text-white/30 shrink-0 mt-1" />
          : <Video className="w-4 h-4 text-[#8FA79B] shrink-0 mt-1" />}
        <div className="min-w-0 flex-1">
          <p className="text-[14.5px] text-white">{label}</p>
          <p className="text-[12px] text-white/45 mt-1 leading-snug">{description}</p>
        </div>
      </div>
      <div className="space-y-2 mb-3">
        {rows.map((selected, idx) => (
          <div key={idx} className="flex items-center gap-2" data-testid={`${testIdRoot}-video-${idx}`}>
            {rows.length > 1 && (
              <span className="text-[10.5px] text-white/35 tabular-nums w-4 shrink-0">{idx + 1}</span>
            )}
            <div className="flex flex-1 gap-2" role="radiogroup" aria-label={`${label} ${idx + 1}`}>
              {durationKeys.map((sec) => {
                const active = selected === sec;
                return (
                  <button
                    key={sec}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={disabled}
                    onClick={() => { if (!disabled) setRow(idx, active ? null : sec); }}
                    className="flex-1 rounded-full text-[12px] tracking-wide transition-colors"
                    style={{
                      padding: "9px 0",
                      background: active ? "rgba(143,167,155,0.18)" : "rgba(255,255,255,0.025)",
                      border: `1px solid ${active ? "rgba(143,167,155,0.55)" : "rgba(255,255,255,0.08)"}`,
                      color: active ? "#8FA79B" : "rgba(255,255,255,0.6)",
                      fontWeight: active ? 500 : 400,
                      cursor: disabled ? "not-allowed" : "pointer",
                    }}
                    // Row 0 keeps the original test ids.
                    data-testid={idx === 0 ? `${testIdRoot}-duration-${sec}` : `${testIdRoot}-${idx}-duration-${sec}`}
                  >
                    {sec} sec
                  </button>
                );
              })}
            </div>
            {rows.length > 1 && (
              <button
                type="button"
                onClick={() => setRow(idx, null)}
                disabled={disabled}
                className="shrink-0 w-7 h-7 rounded-full text-white/40 hover:text-white/85 hover:bg-white/[0.05]"
                aria-label={`Remove video ${idx + 1}`}
                data-testid={`${testIdRoot}-remove-${idx}`}
              >
                <X className="w-3.5 h-3.5 mx-auto" />
              </button>
            )}
          </div>
        ))}
      </div>
      {!disabled && list.length > 0 && (
        <button
          type="button"
          onClick={() => onChange([...list, list[list.length - 1]])}
          className="mb-3 text-[11.5px] text-[#8FA79B] hover:text-[#A9BFB3]"
          data-testid={`${testIdRoot}-add-video`}
        >
          + Add another {productLabel}
        </button>
      )}
      {durationNotes && !disabled && (
        <ul className="mb-3 space-y-1" data-testid={`${testIdRoot}-coverage`}>
          {durationKeys.map((sec) => (
            durationNotes[sec] ? (
              <li
                key={sec}
                className={`text-[11.5px] flex items-start gap-2 leading-snug ${selectedSet.has(sec) ? "text-[#8FA79B]" : "text-white/40"}`}
              >
                <span className="tabular-nums shrink-0 w-8">{sec}s</span>
                <span>{durationNotes[sec]}</span>
              </li>
            ) : null
          ))}
        </ul>
      )}
      {disabled ? (
        <p className="text-[12px] text-white/40 leading-snug" data-testid={`${testIdRoot}-locked-hint`}>
          {disabledHint || "Not available for this configuration."}
        </p>
      ) : (
        <p className="text-[12.5px] text-white/55" data-testid={`${testIdRoot}-total`}>
          {list.length ? (
            <>
              <span className="text-white tabular-nums">{fmt(total)}</span>
              <span className="text-white/40"> · {list.length === 1 ? `${list[0]}-second ${productLabel}` : `${list.length} videos · ${shortVideos(list)}`}</span>
            </>
          ) : (
            <span className="text-white/35">Select a duration to add</span>
          )}
        </p>
      )}
    </div>
  );
}

/* À La Carte only — AI Motion is render-gated, and an à-la-carte quote may
 * carry no renders of ours to animate. This checkbox is the rep's attestation
 * that the client is supplying the source renderings, which unlocks the
 * AI Motion card. */
function AiMotionClientRendersToggle({ checked, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(!checked)}
      className="w-full flex items-center gap-3 rounded-xl border text-left px-4 py-3 transition-all duration-200"
      style={{
        borderColor: checked ? "rgba(143,167,155,0.55)" : "rgba(255,255,255,0.08)",
        background: checked ? "rgba(143,167,155,0.10)" : "rgba(255,255,255,0.02)",
      }}
      data-testid="ai-motion-client-renders-toggle"
      role="checkbox"
      aria-checked={checked}
    >
      <span
        className="shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-colors"
        style={{
          background: checked ? "#8FA79B" : "transparent",
          borderColor: checked ? "#8FA79B" : "rgba(255,255,255,0.25)",
        }}
      >
        {checked && <Check className="w-3 h-3 text-black" />}
      </span>
      <span className="text-[12.5px] leading-snug" style={{ color: checked ? "#8FA79B" : "rgba(255,255,255,0.65)" }}>
        Client is providing a minimum of 5 to 6 renderings.
      </span>
    </button>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 *  Standalone Video flow (size-first) — shown ONLY when a video is sold on
 *  its own (À La Carte, no Vexos package / no other products).
 *    Step 1  pick project size (S/M/L)
 *    Step 2  pick the video format the size offers (hero / master)
 *    Step 3  (Large only) add-on menu, greyed out until a video is in scope
 *  One single total, split into new-animation vs editing sub-totals.
 * ─────────────────────────────────────────────────────────────────────── */
function StandaloneQtyRow({ label, hint, unit, qty, onQty, disabled, editing = false, testId }) {
  const n = Math.max(0, Math.round(Number(qty) || 0));
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-white/[0.05] last:border-b-0" data-testid={testId}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-[13px] text-white/85">{label}</p>
          {editing && (
            <span className="text-[9.5px] uppercase tracking-[0.12em] text-[#8FA79B]/80 border border-[#8FA79B]/30 rounded px-1.5 py-0.5">Editing</span>
          )}
        </div>
        {hint && <p className="text-[11px] text-white/40 mt-0.5 leading-snug">{hint}</p>}
        <p className="text-[11px] text-white/45 mt-0.5 tabular-nums">{fmt(unit)} each</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" disabled={disabled || n === 0} onClick={() => onQty(n - 1)}
          className="w-7 h-7 rounded-md border border-white/15 flex items-center justify-center disabled:opacity-30"
          style={{ cursor: disabled || n === 0 ? "not-allowed" : "pointer" }} data-testid={`${testId}-minus`}>
          <Minus className="w-3.5 h-3.5 text-white/70" />
        </button>
        <span className="w-8 text-center text-[14px] tabular-nums text-white" data-testid={`${testId}-qty`}>{n}</span>
        <button type="button" disabled={disabled} onClick={() => onQty(n + 1)}
          className="w-7 h-7 rounded-md border border-white/15 flex items-center justify-center disabled:opacity-30"
          style={{ cursor: disabled ? "not-allowed" : "pointer" }} data-testid={`${testId}-plus`}>
          <Plus className="w-3.5 h-3.5 text-white/70" />
        </button>
      </div>
    </div>
  );
}

function StandaloneVideoFlow({ size, onSize, sel, onSel, result }) {
  const heroes = standaloneHeroesForSize(size);
  const unlocked = standaloneSizeUnlocksAddOns(size);
  const heroInScope = !!sel.heroKey;
  const included = Math.max(0, Math.round(Number(STANDALONE_VIDEO.socialCutdownsIncluded) || 0));
  const perClip = Number(STANDALONE_VIDEO.addOnPrices.socialCutdownPerClip) || 0;
  const setQty = (k, v) => onSel({ ...sel, [k]: Math.max(0, Math.round(Number(v) || 0)) });
  const stepLabel = "text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-3";

  return (
    <div className="pl-[68px] space-y-7" data-testid="video-standalone-flow">
      {/* Step 1 — project size (required; dictates the formats offered) */}
      <div data-testid="video-size-step">
        <p className={stepLabel}>Step 1 · Project size</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {STANDALONE_VIDEO_SIZES.map((key) => {
            const t = standaloneTier(key); const active = size === key;
            return (
              <button key={key} type="button" onClick={() => onSize(key)}
                className="rounded-2xl border text-left transition-all duration-200 overflow-hidden"
                style={{ borderColor: active ? "rgba(143,167,155,0.55)" : "rgba(255,255,255,0.08)", background: active ? "rgba(143,167,155,0.10)" : "rgba(255,255,255,0.02)" }}
                data-testid={`video-size-${key}`}>
                <div className="w-full flex items-center justify-center" style={{ height: 84, background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <ImageIcon className="w-5 h-5 text-white/25" />
                </div>
                <div className="p-3.5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[15px] font-semibold" style={{ color: active ? "#8FA79B" : "#fff" }}>{key}</span>
                    <span className="text-[12.5px] text-white/70">{t?.label}</span>
                  </div>
                  <p className="text-[11.5px] text-white/45 mt-1 leading-snug">{t?.blurb}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 2 — video format offered by the chosen size */}
      <div data-testid="video-hero-step">
        <p className={stepLabel}>Step 2 · Video format</p>
        <div className={`grid gap-3 ${heroes.length > 1 ? "sm:grid-cols-2" : "sm:grid-cols-1"}`}>
          {heroes.map((h) => {
            const active = sel.heroKey === h.key; const price = standaloneHeroPrice(h.key);
            const kind = h.key === "master120" ? "master cinematic" : "hero";
            return (
              <button key={h.key} type="button" onClick={() => onSel({ ...sel, heroKey: active ? null : h.key })}
                className="rounded-2xl border text-left transition-all duration-200 p-4"
                style={{ borderColor: active ? "rgba(143,167,155,0.55)" : "rgba(255,255,255,0.08)", background: active ? "rgba(143,167,155,0.10)" : "rgba(255,255,255,0.02)" }}
                data-testid={`video-hero-${h.key}`}>
                <div className="text-[14.5px] font-semibold" style={{ color: active ? "#8FA79B" : "#fff" }}>{h.label}</div>
                <p className="text-[11.5px] text-white/45 mt-1 leading-snug">{h.seconds} new animated seconds · {kind}</p>
                <div className="text-[15px] font-semibold tabular-nums mt-2 text-white" data-testid={`video-hero-${h.key}-price`}>{fmt(price)}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 3 — Large add-on menu (locked until a video is in scope) */}
      {unlocked && (
        <div data-testid="video-addon-step" style={{ opacity: heroInScope ? 1 : 0.55 }}>
          <p className={stepLabel}>Step 3 · Large add-ons{heroInScope ? "" : " · locked"}</p>
          {!heroInScope && (
            <p className="text-[12px] text-white/45 mb-2 leading-snug" data-testid="video-addon-locked-hint">
              Select a hero or master video above to unlock add-ons.
            </p>
          )}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4">
            <StandaloneQtyRow
              label={STANDALONE_VIDEO.addOnMeta.unitInterior.label}
              hint="One per typical unit layout"
              unit={Number(STANDALONE_VIDEO.addOnPrices.unitInterior) || 0}
              qty={sel.unitInteriorQty} onQty={(v) => setQty("unitInteriorQty", v)}
              disabled={!heroInScope} testId="video-addon-unit" />
            <StandaloneQtyRow
              label={STANDALONE_VIDEO.addOnMeta.penthouse.label}
              unit={Number(STANDALONE_VIDEO.addOnPrices.penthouse) || 0}
              qty={sel.penthouseQty} onQty={(v) => setQty("penthouseQty", v)}
              disabled={!heroInScope} testId="video-addon-penthouse" />
            <StandaloneQtyRow
              label={STANDALONE_VIDEO.addOnMeta.socialCutdown.label}
              hint={`${included > 0 ? `${included} included with the master, then ` : ""}${fmt(perClip)} per clip · edited from existing footage, not new animation`}
              unit={perClip}
              qty={sel.socialCutdownQty} onQty={(v) => setQty("socialCutdownQty", v)}
              disabled={!heroInScope} editing testId="video-addon-social" />
          </div>
        </div>
      )}

      {/* Single total + animation vs editing split */}
      {result && result.total > 0 && (
        <div className="rounded-xl px-5 py-4" style={{ background: "rgba(143,167,155,0.06)", border: "1px solid rgba(143,167,155,0.22)" }} data-testid="video-standalone-total">
          <div className="flex items-baseline justify-between">
            <span className="text-[13px] text-white/70">{result.sizeLabel} · {result.heroLabel}</span>
            <span className="text-[18px] font-light tabular-nums text-white">{fmt(result.total)}</span>
          </div>
          <div className="text-[11.5px] text-white/45 mt-1 tabular-nums">
            New animation {fmt(result.animationTotal)}
            {result.editingTotal > 0 ? <> · Editing {fmt(result.editingTotal)}</> : null}
          </div>
        </div>
      )}

      <VideoSubtext />
    </div>
  );
}

/* Client-facing subtext shown under the video option (both modes) and
 * mirrored on the generated proposal (see vexos_proposal_pdf.py). */
function VideoSubtext() {
  return (
    <div className="space-y-1.5" data-testid="video-subtext">
      <p className="text-[11.5px] text-white/45 leading-snug">
        Interior spaces are counted individually. Open layouts are not a single space: kitchen, living, and dining count as three separate spaces.
      </p>
      <p className="text-[11.5px] text-white/45 leading-snug">
        Includes exterior shots (approximately one quarter to one third of the video).
      </p>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 *  OrbitKeyframesGroup — Phase 02.  The whole orbit/keyframe decision in
 *  one grouped block with exactly two paths:
 *
 *    1. add keyframes to the BASE orbit, or
 *    2. add a suborbit (which arrives with two keyframes) and, if wanted,
 *       up to two more keyframes on that specific orbit.
 *
 *  Extra keyframes are rendered as a sub-indent under the orbit they attach
 *  to, so there is never a floating "additional keyframes: 3" that could
 *  belong to either orbit — the ambiguity this block exists to remove.
 * ─────────────────────────────────────────────────────────────────────── */
function OrbitKeyframesGroup({
  addons, baseOrbitName, baseExtraKf, onBaseExtraKfChange,
  subOrbits, onSubOrbitsChange, testPrefix = "",
  // Master Plan: the first N suborbits are in the package at
  // MASTER_PLAN_SUBORBIT_KEYFRAMES each. They can't be removed or trimmed;
  // the stepper counts only the paid orbits added after them.
  includedSubOrbits = 0,
}) {
  const kfRate = addons.additionalKeyframes.pricePerKeyframe;
  const orbitRate = addons.masterOrbit.pricePerOrbit;
  const orbits = Array.isArray(subOrbits) ? subOrbits : [];
  const included = Math.min(includedSubOrbits, orbits.length);
  const addedCount = orbits.length - included;
  const baseKfTotal = BASE_ORBIT_KEYFRAMES + (Number(baseExtraKf) || 0);
  const grandTotalKf = totalExteriorKeyframes(baseExtraKf, orbits);

  const setOrbitExtra = (i, n) => {
    const next = [...orbits];
    next[i] = Math.min(SUBORBIT_MAX_EXTRA_KEYFRAMES, Math.max(0, Number(n) || 0));
    onSubOrbitsChange(next);
  };

  return (
    <div data-testid={`${testPrefix}orbit-keyframes-group`}>
      <p className="text-[10.5px] uppercase tracking-[0.16em] text-white/55 mb-2">Orbits &amp; Keyframes</p>
      <p className="text-[11.5px] text-white/40 mb-4 leading-snug">
        Two ways to widen the exterior: add keyframes to the base orbit, or add a suborbit of its own.
      </p>

      <div className="space-y-3">
        {/* PATH 1 — the base orbit and its extra keyframes. */}
        <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]" data-testid={`${testPrefix}base-orbit-card`}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[14.5px] text-white">{baseOrbitName}</p>
              <p className="text-[12px] text-white/45 mt-1">
                Included in the platform · {BASE_ORBIT_KEYFRAMES} keyframes
              </p>
            </div>
            <span className="shrink-0 text-[10.5px] uppercase tracking-[0.14em] text-[#8FA79B]/80 mt-1">Included</span>
          </div>

          {/* Sub-indent: these keyframes belong to the base orbit. */}
          <div className="mt-4 pl-4 border-l border-[#8FA79B]/25">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-[13px] text-white/85">{addons.additionalKeyframes.label}</p>
                <p className="text-[11.5px] text-white/40 mt-0.5">
                  <PriceTag amount={kfRate} /> <span className="text-white/35">/ keyframe on this orbit</span>
                </p>
              </div>
              <StepperRow
                label=""
                value={baseExtraKf}
                onChange={onBaseExtraKfChange}
                min={0}
                dataTestId={`${testPrefix}additional-keyframes`}
              />
            </div>
            <p className="text-[11.5px] text-[#8FA79B]/80 mt-1" data-testid={`${testPrefix}base-orbit-kf-subtext`}>
              {baseOrbitName} includes {keyframeWord(baseKfTotal)} keyframe{baseKfTotal === 1 ? "" : "s"}
            </p>
          </div>
        </div>

        {/* PATH 2 — suborbits, each with its own nested keyframe extras. */}
        <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]" data-testid={`${testPrefix}suborbits-card`}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-[14.5px] text-white">{addons.masterOrbit.label}</p>
              <p className="text-[12px] text-white/45 mt-1">
                {includedSubOrbits > 0
                  ? "Add a suborbit beyond the ones the master plan includes — another neighbourhood, an amenity area, or more site context."
                  : addons.masterOrbit.description}
              </p>
              <div className="mt-2 text-[13px]">
                <PriceTag amount={orbitRate} /> <span className="text-white/40 text-[12px]">/ suborbit</span>
              </div>
            </div>
            <StepperRow
              label=""
              value={addedCount}
              onChange={(n) => onSubOrbitsChange(resizeSubOrbits(orbits, included + n, included + MAX_SUBORBITS))}
              min={0}
              max={MAX_SUBORBITS}
              dataTestId={`${testPrefix}suborbit-qty`}
            />
          </div>
          {included > 0 && (
            <p className="text-[11.5px] text-[#8FA79B]/80 mt-2" data-testid={`${testPrefix}suborbits-included-note`}>
              {keyframeWord(included).replace(/^./, (c) => c.toUpperCase())} suborbits are included in the master plan
              · {keyframeWord(MASTER_PLAN_SUBORBIT_KEYFRAMES)} keyframes each. Add more here.
            </p>
          )}

          {orbits.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/[0.05] space-y-3">
              {orbits.map((extra, i) => i < included ? (
                  <div key={i} className="pl-4 border-l border-[#8FA79B]/25" data-testid={`${testPrefix}suborbit-${i}`}>
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="text-[13.5px] text-white">Suborbit {i + 1}</p>
                      <span className="text-[10.5px] uppercase tracking-[0.14em] text-[#8FA79B]/80">Included</span>
                    </div>
                    <p
                      className="text-[11.5px] text-[#8FA79B]/80 mt-0.5"
                      data-testid={`${testPrefix}suborbit-${i}-subtext`}
                    >
                      {suborbitKeyframeSubtext(extra)}
                    </p>
                  </div>
              ) : (
                  <div key={i} className="pl-4 border-l border-[#8FA79B]/25" data-testid={`${testPrefix}suborbit-${i}`}>
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="text-[13.5px] text-white">Suborbit {i + 1}</p>
                      <span className="text-[12px] text-white/55 tabular-nums">{fmt(orbitRate)}</span>
                    </div>
                    <p
                      className="text-[11.5px] text-[#8FA79B]/80 mt-0.5"
                      data-testid={`${testPrefix}suborbit-${i}-subtext`}
                    >
                      {suborbitKeyframeSubtext(extra)}
                    </p>
                    <div className="mt-1.5 flex items-center justify-between gap-4">
                      <p className="text-[11.5px] text-white/40">
                        Additional keyframes on this suborbit
                        <span className="text-white/25"> · up to {SUBORBIT_MAX_EXTRA_KEYFRAMES}</span>
                      </p>
                      <StepperRow
                        label=""
                        value={Number(extra) || 0}
                        onChange={(n) => setOrbitExtra(i, n)}
                        min={0}
                        max={SUBORBIT_MAX_EXTRA_KEYFRAMES}
                        dataTestId={`${testPrefix}suborbit-${i}-extra-kf`}
                      />
                    </div>
                  </div>
              ))}
              <p className="text-[11px] text-white/35 leading-snug pt-1">
                Every suborbit ships with {keyframeWord(SUBORBIT_INCLUDED_KEYFRAMES)} keyframes — the minimum a
                buildable orbit needs — so a one-keyframe orbit can&apos;t be quoted.
              </p>
            </div>
          )}
        </div>

        {/* Running total — the pool day-to-night and animation both price off. */}
        <div
          className="flex items-center justify-between px-5 py-3 rounded-xl bg-[#8FA79B]/[0.05] border border-[#8FA79B]/15"
          data-testid={`${testPrefix}exterior-keyframe-total`}
        >
          <span className="text-[12px] text-white/55">
            {orbits.length + 1} orbit{orbits.length === 0 ? "" : "s"} · exterior keyframes
          </span>
          <span className="text-[13px] text-white tabular-nums">{grandTotalKf}</span>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 *  FloorPlansSection — Phase 06.  One row per unique floor plan / home
 *  model. Flat per-plan rate. Recommendation = 1 per plan. Apply-to-All
 *  button sets every row to 1.
 *
 *  The 3D variant is sold under two workflows (selector applies to the
 *  whole 3D scope, not per plan). Prices resolve via floorPlan3DRate().
 * ─────────────────────────────────────────────────────────────────────── */
const FP3D_WORKFLOWS = [
  {
    key: "hybrid",
    name: "AI Hybrid",
    blurb: "Built on our traditional 3D workflow and enhanced with AI · 1 revision round included.",
    note: "May not be pixel-perfect to your specs — but it will be very, very close.",
  },
  {
    key: "traditional",
    name: "Traditional 3D Pipeline",
    blurb: "Full 3D from modeling through final render — no AI · 2 revision rounds included.",
    note: "Exactly accurate to your specs.",
  },
];

function FloorPlansSection({
  title, sublabel, pricePerPlan, planCount, planLabel, values, onChange, testIdRoot,
  workflow, onWorkflowChange, workflowOptions, manualPricing,
  // Levels are 3D-only: pass both to enable the per-unit level stepper.
  levels, onLevelsChange,
}) {
  const levelsEnabled = !!levels && typeof onLevelsChange === "function";
  const unitTypeQty = Array.from({ length: planCount }).reduce((s, _, i) => s + (Number(values[i]) || 0), 0);
  // Every level is a plan's worth of work, so the billable count — and the
  // volume tier it lands in — is quantity × levels, not quantity.
  const totalQty = levelsEnabled
    ? floorPlanLevelTotals(values, levels, planCount).billablePlans
    : unitTypeQty;
  const volume = floorPlanVolumeDiscount(totalQty, pricePerPlan);
  // Hand-priced quote: the plans still cost the volume rate, but nothing is
  // presented as a discount, so the line reads as a single price.
  const { pct, savings, gross, net } = manualPricing
    ? { pct: 0, savings: 0, gross: volume.net, net: volume.net }
    : volume;

  const setQty = (i, n) => {
    const next = { ...values };
    next[i] = Math.max(0, Number(n) || 0);
    onChange(next);
  };
  const setLevels = (i, n) => {
    const next = { ...levels };
    next[i] = Math.min(LEVELS_MAX, Math.max(1, Math.floor(Number(n) || 0) || 1));
    onLevelsChange(next);
  };
  const applyAll = () => {
    const next = {};
    for (let i = 0; i < planCount; i++) next[i] = 1;
    onChange(next);
  };

  return (
    <div data-testid={`${testIdRoot}-section`}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-1.5">{title}</p>
          <p className="text-[12px] text-white/50">{sublabel}</p>
        </div>
        <div className="text-[12px] text-white/55 text-right">
          <span className="text-white tabular-nums">{fmt(pricePerPlan)}</span>
          <span className="text-white/40"> / {levelsEnabled ? "level" : "floor plan"}</span>
        </div>
      </div>

      {levelsEnabled && planCount > 0 && (
        <p className="text-[11.5px] text-white/40 mb-3 leading-snug" data-testid={`${testIdRoot}-levels-hint`}>
          One 3D floor plan covers one whole unit type. A multi-level unit — a tri-level townhome, say — is
          still one unit type, but each level is drawn and priced separately.
        </p>
      )}

      {planCount === 0 ? (
        <p className="text-[12.5px] text-white/40 italic py-3 px-4 rounded-lg bg-white/[0.02] border border-white/[0.04]">
          Set Unique {planLabel}s in Phase 1 to enable this section.
        </p>
      ) : (
        <div className="space-y-2">
          {workflowOptions && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-testid={`${testIdRoot}-workflow`}>
              {workflowOptions.map((opt) => {
                const active = workflow === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => onWorkflowChange(opt.key)}
                    className={`text-left px-4 py-3.5 rounded-lg border transition-colors ${
                      active
                        ? "border-[#8FA79B]/70 bg-[#8FA79B]/[0.06]"
                        : "border-white/[0.08] bg-white/[0.02] hover:border-white/20"
                    }`}
                    data-testid={`${testIdRoot}-workflow-${opt.key}`}
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <span className={`text-[13.5px] ${active ? "text-[#8FA79B]" : "text-white"}`}>{opt.name}</span>
                      <span className="text-[12px] text-white/70 tabular-nums whitespace-nowrap">
                        {fmt(opt.price)} <span className="text-white/40">/ plan</span>
                      </span>
                    </div>
                    <p className="text-[11.5px] text-white/50 leading-snug">{opt.blurb}</p>
                    <p className={`text-[11px] italic leading-snug mt-1.5 ${active ? "text-[#8FA79B]/80" : "text-white/40"}`}>
                      {opt.note}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
          {Array.from({ length: planCount }).map((_, i) => {
            const v = Number(values[i]) || 0;
            const isRecommended = v === 1;
            const lv = levelsFor(levels, i);
            return (
              <div
                key={i}
                className="px-4 py-3 rounded-lg bg-white/[0.02] border border-white/[0.04]"
                data-testid={`${testIdRoot}-row-${i}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-[13.5px] text-white">{planLabel} {i + 1}</p>
                    <p className="text-[11px] text-white/45 mt-0.5">
                      Recommended <span className="text-[#8FA79B]">1</span>{isRecommended && <span className="text-[#8FA79B] ml-1">✓</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setQty(i, v - 1)}
                      disabled={v <= 0}
                      className="w-7 h-7 rounded-full border border-white/15 hover:border-[#8FA79B]/60 text-white/70 hover:text-[#8FA79B] disabled:opacity-25 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                      data-testid={`${testIdRoot}-row-${i}-minus`}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <input
                      type="number"
                      min="0"
                      value={v}
                      onChange={(e) => setQty(i, e.target.value)}
                      className="w-12 text-center bg-transparent border-b border-white/10 focus:border-[#8FA79B]/60 outline-none text-[13px] text-white tabular-nums py-1"
                      data-testid={`${testIdRoot}-row-${i}-input`}
                    />
                    <button
                      type="button"
                      onClick={() => setQty(i, v + 1)}
                      className="w-7 h-7 rounded-full border border-white/15 hover:border-[#8FA79B]/60 text-white/70 hover:text-[#8FA79B] flex items-center justify-center transition-colors"
                      data-testid={`${testIdRoot}-row-${i}-plus`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {/* Levels live inside the unit they belong to, so the row can
                    never be read as "3 floor plans" when it means one
                    three-level unit. */}
                {levelsEnabled && v > 0 && (
                  <div className="mt-3 pt-3 pl-4 border-t border-white/[0.04] border-l border-l-[#8FA79B]/25 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-[12px] text-white/70">Levels in this unit</p>
                      <p className="text-[11px] text-white/40 mt-0.5" data-testid={`${testIdRoot}-row-${i}-levels-subtext`}>
                        {lv === 1
                          ? "Single level · 1 plan"
                          : `${lv} levels · ${v * lv} plan${v * lv === 1 ? "" : "s"} of work`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setLevels(i, lv - 1)}
                        disabled={lv <= 1}
                        className="w-7 h-7 rounded-full border border-white/15 hover:border-[#8FA79B]/60 text-white/70 hover:text-[#8FA79B] disabled:opacity-25 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                        data-testid={`${testIdRoot}-row-${i}-levels-minus`}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <input
                        type="number"
                        min="1"
                        max={LEVELS_MAX}
                        value={lv}
                        onChange={(e) => setLevels(i, e.target.value)}
                        className="w-12 text-center bg-transparent border-b border-white/10 focus:border-[#8FA79B]/60 outline-none text-[13px] text-white tabular-nums py-1"
                        data-testid={`${testIdRoot}-row-${i}-levels-input`}
                      />
                      <button
                        type="button"
                        onClick={() => setLevels(i, lv + 1)}
                        disabled={lv >= LEVELS_MAX}
                        className="w-7 h-7 rounded-full border border-white/15 hover:border-[#8FA79B]/60 text-white/70 hover:text-[#8FA79B] disabled:opacity-25 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                        data-testid={`${testIdRoot}-row-${i}-levels-plus`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={applyAll}
              className="text-[11px] uppercase tracking-[0.14em] text-[#8FA79B] hover:text-[#8FA79B] hover:underline transition"
              data-testid={`${testIdRoot}-apply-all`}
            >
              Apply recommended to all
            </button>
            <p className="text-[12px] text-white/55" data-testid={`${testIdRoot}-total`}>
              {levelsEnabled && totalQty !== unitTypeQty
                ? `${unitTypeQty} unit type${unitTypeQty === 1 ? "" : "s"} · ${totalQty} levels`
                : `${totalQty} ${planLabel.toLowerCase()}${totalQty === 1 ? "" : "s"} total`}
              <span className="mx-2 text-white/25">·</span>
              <span className={pct > 0 ? "text-white/45 line-through tabular-nums" : "text-white tabular-nums"}>{fmt(gross)}</span>
            </p>
          </div>
          {pct > 0 && (
            <div className="mt-2 pl-1 space-y-1 text-[12px]" data-testid={`${testIdRoot}-discount-row`}>
              <div className="flex items-center justify-between">
                <span className="text-[#8FA79B]">Volume discount ({pct}%)</span>
                <span className="text-emerald-300/85 tabular-nums">−{fmt(savings)}</span>
              </div>
              <div className="flex items-center justify-between pt-1 border-t border-white/[0.05]">
                <span className="text-white/70">Net {title.replace(" Floor Plans", "")} total</span>
                <span className="text-white tabular-nums" style={{ fontWeight: 400 }}>{fmt(net)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 *  ConceptualModelingSection — Phase 07 body, on the VEXOS and À La Carte
 *  tabs. Design-stage renderings at a flat per-image rate. One workflow
 *  (hybrid 3D + AI), so there is nothing to choose: the two subtexts say
 *  what it is and how it differs from the photoreal renderings above, and
 *  the no-aerials rule and included revision are stated rather than sold.
 * ─────────────────────────────────────────────────────────────────────── */
function ConceptualModelingSection({ qty, onChange, rate, cost, testIdRoot = "conceptual" }) {
  const n = Math.max(0, Number(qty) || 0);
  return (
    <div className="pl-[68px]" data-testid={`${testIdRoot}-section`}>
      <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-[14.5px] text-white">{CONCEPTUAL_MODELING.label}</p>
            <p className="text-[12px] text-white/55 mt-1.5 leading-snug" data-testid={`${testIdRoot}-description`}>
              {CONCEPTUAL_MODELING.description}
            </p>
            <p className="text-[12px] text-[#8FA79B]/80 mt-1.5 leading-snug" data-testid={`${testIdRoot}-workflow-note`}>
              {CONCEPTUAL_MODELING.workflowNote}
            </p>
            <div className="mt-3 text-[13px]">
              <PriceTag amount={rate} /> <span className="text-white/40 text-[12px]">/ image</span>
            </div>
          </div>
          <StepperRow
            label=""
            value={n}
            onChange={onChange}
            min={0}
            dataTestId={`${testIdRoot}-qty`}
          />
        </div>
        {n > 0 && (
          <div className="mt-3 pt-3 border-t border-white/[0.04] flex justify-between text-[12px] text-white/55">
            <span>{n} conceptual image{n === 1 ? "" : "s"}</span>
            <span><PriceTag amount={cost} showTbd={false} /></span>
          </div>
        )}
      </div>
    </div>
  );
}

/* Conceptual interior design — sold as a service, priced per space. The rep
   sets the number of spaces and a per-space rate ($750 default, held to
   $500–$1,000); both print on the proposal with the scope blurb below. A quote
   saved before per-space pricing keeps its flat fee until the rep adds spaces.
   Lives in its own unlocked block under Phase 07 and as a row in the sidebar
   (InteriorDesignRow), so it can be found from every tab. The rate and note
   commit on blur / Enter, so a half-typed number never re-prices the
   milestones mid-keystroke. */
function SpacesStepper({ spaces, onStep, testIdRoot, size = "md" }) {
  const btn = size === "sm"
    ? "w-6 h-6 text-[13px]"
    : "w-8 h-8 text-[15px]";
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <button
        type="button"
        onClick={() => onStep(-1)}
        disabled={spaces <= 0}
        className={`${btn} rounded-md border border-white/12 text-white/70 hover:border-white/30 disabled:opacity-30`}
        aria-label="One fewer space"
        data-testid={`${testIdRoot}-minus`}
      >
        −
      </button>
      <span className={`min-w-[22px] text-center tabular-nums text-white ${size === "sm" ? "text-[12.5px]" : "text-[15px]"}`} data-testid={`${testIdRoot}-spaces`}>
        {spaces}
      </span>
      <button
        type="button"
        onClick={() => onStep(1)}
        disabled={spaces >= INTERIOR_DESIGN_MAX_SPACES}
        className={`${btn} rounded-md border border-white/12 text-white/70 hover:border-white/30 disabled:opacity-30`}
        aria-label="One more space"
        data-testid={`${testIdRoot}-plus`}
      >
        +
      </button>
    </div>
  );
}

// The fee after one step of the spaces stepper. Adding the first space to a
// legacy flat fee replaces it; stepping to zero removes the fee.
function stepInteriorDesign(fee, delta, rate) {
  const perSpace = fee && fee.spaces != null;
  const spaces = (perSpace ? fee.spaces : 0) + delta;
  if (spaces <= 0) return null;
  return { spaces, rate: perSpace ? fee.rate : rate, note: perSpace ? fee.note : "" };
}

function InteriorDesignFeeCard({ fee, onChange }) {
  const perSpace = !!fee && fee.spaces != null;
  const flat = !!fee && !perSpace;
  const [rate, setRate] = React.useState(String(perSpace ? fee.rate : INTERIOR_DESIGN_RATE_DEFAULT));
  const [note, setNote] = React.useState(perSpace ? fee.note : "");

  // Re-sync only when the committed fee changes (a quote loads, a commit
  // lands) — never on focus moves, or a note typed before the rate would be
  // wiped when the rep tabs across.
  React.useEffect(() => {
    if (fee && fee.spaces != null) {
      setRate(String(fee.rate));
      setNote(fee.note || "");
    } else if (!fee) {
      setNote("");
    }
  }, [fee]);

  // Each commit carries BOTH drafts: the commit re-syncs the card from the
  // saved fee, so sending only the field that blurred would wipe the other.
  const committedRate = clampInteriorDesignRate(String(rate).replace(/[^0-9.]/g, ""));
  const commit = () => {
    setRate(String(committedRate));
    if (perSpace && (committedRate !== fee.rate || note.trim() !== (fee.note || ""))) {
      onChange({ ...fee, rate: committedRate, note });
    }
  };
  const keys = (e) => { if (e.key === "Enter") e.currentTarget.blur(); };
  const step = (d) => {
    const next = stepInteriorDesign(fee, d, committedRate);
    onChange(next && !perSpace ? { ...next, note } : next);
  };

  return (
    <div className="pl-[68px] mt-6" data-testid="interior-design-fee">
      <div className={`p-5 rounded-xl border transition-colors ${fee ? "bg-[#8FA79B]/[0.05] border-[#8FA79B]/30" : "bg-white/[0.02] border-white/[0.05]"}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-[14.5px] text-white">{INTERIOR_DESIGN_LABEL}</p>
            <p className="text-[12px] text-white/55 mt-1.5 leading-snug">
              A design service, priced per space, for spaces the client has no interior design package for. Quote ${INTERIOR_DESIGN_RATE_MIN.toLocaleString("en-US")}–${INTERIOR_DESIGN_RATE_MAX.toLocaleString("en-US")} per space on the call.
            </p>
          </div>
          <SpacesStepper spaces={perSpace ? fee.spaces : 0} onStep={step} testIdRoot="interior-design" />
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-3">
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.14em] text-white/40 mb-1">Rate per space</span>
            <span className={`flex items-center rounded-md border px-2 ${perSpace ? "border-[#8FA79B]/45" : "border-white/12 bg-white/[0.02]"}`}>
              <span className="text-[12px] text-white/40 mr-0.5">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                onBlur={commit}
                onKeyDown={keys}
                className="w-full bg-transparent outline-none py-1.5 text-[13px] text-white tabular-nums"
                data-testid="interior-design-rate"
              />
            </span>
          </label>
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.14em] text-white/40 mb-1">Which spaces (optional)</span>
            <input
              type="text"
              value={note}
              maxLength={140}
              placeholder="e.g. Lobby, amenity lounge, model unit"
              onChange={(e) => setNote(e.target.value)}
              onBlur={commit}
              onKeyDown={keys}
              className="w-full rounded-md border border-white/12 bg-white/[0.02] px-2.5 py-1.5 text-[12px] text-white outline-none placeholder:text-white/25"
              data-testid="interior-design-note"
            />
          </label>
        </div>

        {flat && (
          <div className="mt-4 rounded-md border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2.5 text-[12px] text-amber-100/85 leading-snug" data-testid="interior-design-legacy">
            This quote has a flat {CONCEPTUAL_DESIGN_FEE_LABEL.toLowerCase()} fee from before per-space pricing:{" "}
            <span className="tabular-nums">{fmtUSD(fee.amount)}</span> · {conceptualDesignFeeSub(fee.note)}. It still bills as sent. Adding a space replaces it.
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-white/[0.06] text-[11.5px] leading-snug space-y-1.5">
          <p className="text-white/60"><span className="text-[#8FA79B]">Included · </span>{INTERIOR_DESIGN_SCOPE.included}</p>
          <p className="text-white/45"><span className="text-white/60">Not included · </span>{INTERIOR_DESIGN_SCOPE.excluded}</p>
          <a
            href={`${INTAKE_GUIDE_URL}#page=${INTAKE_GUIDE_INTERIOR_PAGE}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block pt-1 text-[#8FA79B] hover:underline"
            data-testid="interior-design-guide-link"
          >
            Client says they have a design package? Show them what one includes →
          </a>
        </div>

        {fee && (
          <div className="mt-3 pt-3 border-t border-white/[0.04] flex justify-between items-center text-[12px] text-white/55">
            <span>{perSpace ? interiorDesignMath(fee.spaces, fee.rate) : conceptualDesignFeeSub(fee.note)}</span>
            <span className="flex items-center gap-3">
              <PriceTag amount={fee.amount} showTbd={false} />
              <button
                type="button"
                onClick={() => onChange(null)}
                className="text-[10.5px] text-white/40 hover:text-white/80"
                data-testid="interior-design-clear"
              >
                Remove
              </button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/* Real-Time Configurator — the VR tour add-on brought back from the legacy
   calculator's Customizer. Sits under a VR section once it has a tour: a
   switch, then one row per category (type, option count, live price). The
   activation fee bills once per section. See ./vrConfigurator. */
function VrConfiguratorCard({ sectionKey, value, onChange, costs, noun }) {
  const cfg = value || { enabled: false, categories: [] };
  const set = (patch) => onChange(sectionKey, { ...cfg, ...patch });
  const setCat = (id, patch) => set({ categories: cfg.categories.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
  const toggle = (on) => set({ enabled: on, categories: on && cfg.categories.length === 0 ? [newCategory()] : cfg.categories });
  const tid = `vr-configurator-${sectionKey}`;
  return (
    <div className="mt-6 p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]" data-testid={tid}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-2">VR add-on</p>
          <p className="text-[14.5px] text-white">{VR_CONFIGURATOR_LABEL}</p>
          <p className="text-[12px] text-white/45 mt-1">
            Buyers switch finishes, materials and furniture inside the {noun} VR tours, in real time.
            {" "}<PriceTag amount={VR_CONFIGURATOR.activationFee} /> <span className="text-white/40">activation covers each category's first option; added options bill on top.</span>
          </p>
        </div>
        <Switch checked={!!cfg.enabled} onCheckedChange={toggle} data-testid={`${tid}-toggle`} />
      </div>

      {cfg.enabled && (
        <div className="mt-4 space-y-2">
          {cfg.categories.map((c) => {
            const cost = categoryCost(c.type, c.optionCount);
            return (
              <div key={c.id} className="rounded-lg border border-white/[0.06] px-3 py-2.5" data-testid={`${tid}-category`}>
                <div className="flex items-center gap-3">
                  <select
                    value={c.type}
                    onChange={(e) => setCat(c.id, { type: e.target.value })}
                    className="flex-1 min-w-0 bg-transparent border-b border-white/10 focus:border-[#8FA79B]/60 outline-none text-[13px] text-white py-1 [&>option]:bg-[#000000] [&>option]:text-white"
                    data-testid={`${tid}-category-type`}
                  >
                    {VR_CONFIGURATOR_CATEGORY_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => setCat(c.id, { optionCount: Math.max(1, c.optionCount - 1) })}
                      className="w-7 h-7 rounded-full bg-white/[0.04] hover:bg-white/[0.08] text-white/60 flex items-center justify-center"
                      aria-label="Fewer options"
                      data-testid={`${tid}-options-minus`}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-14 text-center text-[13px] text-white tabular-nums" data-testid={`${tid}-options`}>
                      {c.optionCount} opt{c.optionCount === 1 ? "" : "s"}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCat(c.id, { optionCount: c.optionCount + 1 })}
                      className="w-7 h-7 rounded-full bg-white/[0.04] hover:bg-white/[0.08] text-white/60 flex items-center justify-center"
                      aria-label="More options"
                      data-testid={`${tid}-options-plus`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => set({ categories: cfg.categories.filter((x) => x.id !== c.id) })}
                    className="shrink-0 text-white/35 hover:text-white/75"
                    aria-label="Remove category"
                    data-testid={`${tid}-category-remove`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                {c.type === "custom" && (
                  <input
                    value={c.customName}
                    onChange={(e) => setCat(c.id, { customName: e.target.value })}
                    placeholder="Category name, e.g. Window treatments"
                    className="mt-2 w-full bg-transparent border-b border-white/10 focus:border-[#8FA79B]/60 outline-none text-[12.5px] text-white py-1"
                    data-testid={`${tid}-category-name`}
                  />
                )}
                <div className="mt-1.5 flex justify-between text-[11px]">
                  <span className="text-white/40">{categoryCostDetail(c.type, c.optionCount)}</span>
                  <span className="text-white/70 tabular-nums">{fmtUSD(cost)}</span>
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => set({ categories: [...cfg.categories, newCategory()] })}
            className="text-[11.5px] text-[#8FA79B] rounded-md px-2 py-1 border border-[#8FA79B]/35 hover:bg-[#8FA79B]/10"
            data-testid={`${tid}-add`}
          >
            + Add category
          </button>
          <div className="pt-3 flex justify-between text-[12px] text-white/55">
            <span>{costs?.total > 0 ? `${fmtUSD(costs.activationFee)} activation + ${fmtUSD(costs.categoriesCost)} options` : "Bills once this section has a VR tour"}</span>
            <span data-testid={`${tid}-total`}><PriceTag amount={costs?.total || 0} showTbd={false} /></span>
          </div>
        </div>
      )}
    </div>
  );
}

/* Sidebar row for the 3D modeling fee. The tier picks itself from the project
   type and size ("Auto"); the rep can pick another tier, or None for a quote
   whose model is already in hand, and slide the price off the tier's. Picking
   a tier resets the slider. The credit promise prints on the proposal.
   With no fee (every quote saved before the fee existed opens this way) the
   row offers "+ Add", like Interior Design, which turns on the auto tier. */
function ModelingFeeRow({ fee, resolvedTier, autoTier, amount, onChange }) {
  const [open, setOpen] = React.useState(false);
  const picked = fee?.tier || null;
  const none = picked === MODELING_FEE_NONE;
  const adjusted = fee?.amount != null;
  const tier = modelingFeeTier(resolvedTier);
  const auto = modelingFeeTier(autoTier);
  const choose = (key) => { onChange({ tier: key, amount: null }); setOpen(false); };
  const slide = (v) => onChange({ tier: picked, amount: clampModelingAmount(v) });
  const chip = (active) => `px-2 py-1 rounded-md border text-[10.5px] transition-colors ${
    active ? "bg-[#8FA79B]/15 text-[#8FA79B] border-[#8FA79B]/40" : "text-white/55 border-white/10 hover:border-white/25"
  }`;
  return (
    <div className="py-2.5 border-b border-white/[0.04] last:border-b-0" data-testid="sidebar-modeling-row">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] text-white/75">3D Modeling</p>
          {none ? (
            <p className="text-[10.5px] text-white/35 mt-0.5" data-testid="sidebar-modeling-tier">
              Not on this quote{auto ? ` · ${auto.label} is ${fmtUSD(auto.amount)}` : ""}
            </p>
          ) : (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="text-[10.5px] text-white/40 hover:text-white/75 mt-0.5 text-left"
              data-testid="sidebar-modeling-tier"
            >
              {tier ? tier.label : "—"}
              {picked === null ? " · auto" : ""}
              {adjusted && tier ? ` · adjusted from ${fmtUSD(tier.amount)}` : ""} · change
            </button>
          )}
        </div>
        {none ? (
          <button
            type="button"
            onClick={() => choose(null)}
            className="shrink-0 text-[11.5px] text-[#8FA79B] rounded-md px-2 py-1 border border-[#8FA79B]/35 hover:bg-[#8FA79B]/10"
            data-testid="sidebar-modeling-add"
          >
            + Add
          </button>
        ) : (
          <span className="text-[13px] text-white tabular-nums shrink-0" data-testid="sidebar-modeling-amount">
            {fmt(amount || 0)}
          </span>
        )}
      </div>
      {open && !none && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="sidebar-modeling-options">
          <button type="button" onClick={() => choose(null)} className={chip(picked === null)} data-testid="modeling-tier-auto">
            Auto{auto ? ` (${auto.label})` : ""}
          </button>
          {MODELING_FEE_TIERS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => choose(t.key)}
              title={t.blurb}
              className={chip(picked === t.key)}
              data-testid={`modeling-tier-${t.key}`}
            >
              {t.label} · {fmtUSD(t.amount)}
            </button>
          ))}
          <button type="button" onClick={() => choose(MODELING_FEE_NONE)} className={chip(picked === MODELING_FEE_NONE)} data-testid="modeling-tier-none">
            None
          </button>
        </div>
      )}
      {open && picked !== MODELING_FEE_NONE && tier && (
        <div className="mt-2.5" data-testid="sidebar-modeling-slider">
          <input
            type="range"
            min={MODELING_FEE_SLIDER_MIN}
            max={MODELING_FEE_SLIDER_MAX}
            step={MODELING_FEE_SLIDER_STEP}
            value={amount || tier.amount}
            onChange={(e) => slide(e.target.value)}
            className="w-full accent-[#8FA79B] cursor-pointer"
            aria-label="3D modeling fee"
            data-testid="modeling-fee-slider"
          />
          <div className="flex justify-between text-[10px] text-white/35 tabular-nums">
            <span>{fmtUSD(MODELING_FEE_SLIDER_MIN)}</span>
            {adjusted && (
              <button
                type="button"
                onClick={() => onChange({ tier: picked, amount: null })}
                className="text-white/50 hover:text-white/80"
                data-testid="modeling-fee-reset"
              >
                Reset to {fmtUSD(tier.amount)}
              </button>
            )}
            <span>{fmtUSD(MODELING_FEE_SLIDER_MAX)}</span>
          </div>
        </div>
      )}
      {(amount || 0) > 0 && (
        <p className="text-[10px] text-white/35 mt-1 leading-snug">Credited back (refunded if paid in full) if the client's model is complete at the planning call</p>
      )}
    </div>
  );
}

/* Sidebar row for the interior design fee — shown on every tab that carries a
   visualization proposal, so the rep can add it without hunting for Phase 07.
   Empty: "+ Add" starts one space at the default rate. Per space: a stepper
   and a click-to-edit rate. A legacy flat fee shows read-only. */
function InteriorDesignRow({ fee, onChange }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const perSpace = !!fee && fee.spaces != null;
  const commitRate = (raw) => {
    setEditing(false);
    const cleaned = String(raw == null ? "" : raw).replace(/[^0-9.]/g, "");
    if (cleaned === "" || !perSpace) return;
    onChange({ ...fee, rate: clampInteriorDesignRate(cleaned) });
  };

  if (fee && !perSpace) {
    return <SummaryRow label="Conceptual Design Fee" value={fee.amount} sublabel={conceptualDesignFeeSub(fee.note)} />;
  }
  return (
    <div className="py-2.5 border-b border-white/[0.04] last:border-b-0" data-testid="sidebar-interior-design-row">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] text-white/75">Interior Design</p>
          {!perSpace && (
            <p className="text-[10.5px] text-white/35 mt-0.5">
              Per space · {fmtUSD(INTERIOR_DESIGN_RATE_MIN)}–{fmtUSD(INTERIOR_DESIGN_RATE_MAX)}
            </p>
          )}
        </div>
        {perSpace ? (
          <span className="text-[13px] text-white tabular-nums shrink-0">{fmt(fee.amount)}</span>
        ) : (
          <button
            type="button"
            onClick={() => onChange({ spaces: 1, rate: INTERIOR_DESIGN_RATE_DEFAULT, note: "" })}
            className="shrink-0 text-[11.5px] text-[#8FA79B] rounded-md px-2 py-1 border border-[#8FA79B]/35 hover:bg-[#8FA79B]/10"
            data-testid="sidebar-interior-design-add"
          >
            + Add
          </button>
        )}
      </div>
      {perSpace && (
        <div className="mt-1.5 flex items-center gap-2 text-[11px] text-white/45">
          <SpacesStepper spaces={fee.spaces} onStep={(d) => onChange(stepInteriorDesign(fee, d, fee.rate))} testIdRoot="sidebar-interior-design" size="sm" />
          <span>space{fee.spaces === 1 ? "" : "s"} ×</span>
          {editing ? (
            <span className="flex items-center rounded-md border border-[#8FA79B]/45 px-1.5">
              <span className="text-white/40 mr-0.5">$</span>
              <input
                type="text"
                inputMode="decimal"
                autoFocus
                value={draft}
                onFocus={(e) => e.currentTarget.select()}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => commitRate(draft)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRate(draft);
                  if (e.key === "Escape") setEditing(false);
                }}
                className="w-[48px] bg-transparent outline-none py-0.5 text-[11.5px] text-white tabular-nums"
                data-testid="sidebar-interior-design-rate-input"
              />
            </span>
          ) : (
            <button
              type="button"
              onClick={() => { setDraft(String(fee.rate)); setEditing(true); }}
              title={`Click to set the rate (${fmtUSD(INTERIOR_DESIGN_RATE_MIN)}–${fmtUSD(INTERIOR_DESIGN_RATE_MAX)})`}
              className="tabular-nums text-white/75 rounded px-1 border border-transparent hover:border-white/20"
              data-testid="sidebar-interior-design-rate"
            >
              {fmtUSD(fee.rate)}
            </button>
          )}
          <span>/ space</span>
        </div>
      )}
    </div>
  );
}

// Section IDs for the Presentation-Mode click-to-expand modals.
// Order drives left/right arrow navigation inside an open modal.
const SECTION_ORDER = [
  "hero",
  "platform",
  "exterior",
  "interior",
  "amenities",
  "video",
  "floorplans",
  "marketing",
  "bundle",
  "payments",
  "monthly",
];

const SECTION_TITLES = {
  hero: "Due Today",
  platform: "Platform",
  exterior: "Exterior",
  interior: "Interior",
  amenities: "Amenities",
  video: "Video Animation",
  floorplans: "Floor Plans",
  marketing: "Marketing",
  bundle: "Bundle Savings",
  payments: "Payment Schedule",
  monthly: "Monthly Platform Installment",
};

// Reusable typography primitives for the Click-to-Expand modals. All values
// are sized for Zoom-screen readability (48px serif amounts, 22px labels).
const expandStyles = {
  eyebrow: { fontSize: "14px", letterSpacing: "0.28em", color: "#8FA79B", textTransform: "uppercase", marginBottom: "28px" },
  row: { display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "16px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" },
  label: { fontSize: "22px", color: "rgba(255,255,255,0.78)" },
  value: { fontFamily: 'var(--font-sans)', fontWeight: 450, fontSize: "48px", color: "#ffffff", fontVariantNumeric: "tabular-nums", lineHeight: 1 },
  small: { fontSize: "16px", color: "rgba(255,255,255,0.5)", marginTop: "6px" },
  muted: { fontSize: "32px", color: "rgba(255,255,255,0.35)", fontFamily: 'var(--font-sans)' },
  badge: { fontSize: "16px", textTransform: "uppercase", letterSpacing: "0.18em", color: "rgba(255,255,255,0.55)" },
};

function ExpandRow({ name, value, sub, dim = false }) {
  return (
    <div style={expandStyles.row}>
      <div style={{ flex: 1 }}>
        <div style={expandStyles.label}>{name}</div>
        {sub && <div style={expandStyles.small}>{sub}</div>}
      </div>
      <div style={dim ? expandStyles.muted : expandStyles.value}>{value}</div>
    </div>
  );
}

const fmtUSD = (n) => {
  const v = Number(n) || 0;
  return `${_displayFx.symbol}${Math.round(v * _displayFx.rate).toLocaleString()}`;
};

/** What the closure flow collects: the Payment 1 deposit, or the whole
 *  one-time scope at the pay-in-full discount.
 *
 *  Only offered when there is a discount to give — a quote with no one-time
 *  money has nothing to settle in full. Switching it re-labels every figure in
 *  the card AND changes what the Stripe button charges; the election is
 *  recorded on the quote when the checkout session is created, and the
 *  project's milestone schedule follows it (one paid-in-full row instead of
 *  three the client no longer owes). */
function CollectModeChoice({ mode, onChange, deposit, pif, testId }) {
  if (!pif?.eligible) return null;
  const options = [
    ["deposit", "Deposit", deposit, "Payment 1 \u00b7 the rest bills on delivery"],
    ["full", `Pay in Full \u2212${pif.pct}%`, pif.total, `One payment \u00b7 saves ${fmtUSD(pif.savings)}`],
  ];
  return (
    <div className="mb-4" data-testid={testId}>
      <p className="text-[10.5px] uppercase tracking-[0.16em] text-white/35 mb-2">Collecting</p>
      <div className="grid grid-cols-2 gap-2">
        {options.map(([val, label, amount, sub]) => (
          <button
            key={val}
            type="button"
            onClick={() => onChange(val)}
            className={`text-left px-3.5 py-2.5 rounded-xl border transition-colors ${
              mode === val
                ? "border-[#8FA79B]/70 bg-[#8FA79B]/[0.10]"
                : "border-white/10 bg-white/[0.02] hover:border-white/25"
            }`}
            data-testid={`${testId}-${val}`}
            aria-pressed={mode === val}
          >
            <span className="block text-[12px] text-white/85">{label}</span>
            <span className="block text-[14px] tabular-nums text-[#8FA79B] mt-0.5">{fmtUSD(amount)}</span>
            <span className="block text-[10.5px] text-white/40 mt-0.5">{sub}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Client-facing description of what Payment 1 actually covers.
 *
 *  `ps` is the PRESENTATION schedule — the viz split plus the marketing
 *  campaign setup (billed whole at signing) and half the collateral — so the
 *  copy has to name every piece that landed on the number above it, or the
 *  client reads a total that the sentence beneath it doesn't explain.
 *  `short` is the payment-tile register, where the columns are narrow enough
 *  that the scope just reads "scope". */
function dueTodayCopy(ps, { short = false } = {}) {
  ps = ps || {};
  const pct = ps.pct?.p1 ?? 50;
  const scopeLabel = short ? "scope" : (ps.scopeLabel || "visualization scope");
  const parts = [
    (ps.isAlacarte || ps.setupInSplit)
      ? `${pct}% of ${scopeLabel}`
      : `${short ? "Setup" : "VEXOS setup"} + ${pct}% of ${scopeLabel}`,
  ];
  if ((ps.travelCost || 0) > 0) parts.push(short ? "travel" : "on-site travel");
  if ((ps.marketingSetup || 0) > 0) parts.push(`marketing setup (${fmtUSD(ps.marketingSetup)})`);
  if ((ps.collateralP1 || 0) > 0) parts.push("50% of collateral");
  return parts.join(" + ");
}

// USD/CAD segmented toggle for the presentation toolbars. Rendering-only —
// the underlying rate card and Stripe checkout always stay USD.
function CurrencyToggle({ displayCurrency, onChange, fxLoading }) {
  return (
    <div
      className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.03] p-0.5 text-[12px]"
      data-testid="currency-toggle"
      title="Display currency — presentation and PDF only"
    >
      {["USD", "CAD"].map((cur) => (
        <button
          key={cur}
          type="button"
          onClick={() => onChange(cur)}
          disabled={fxLoading && cur === "CAD" && displayCurrency !== "CAD"}
          className={`px-3 py-1.5 rounded-full tracking-wide transition-all disabled:opacity-40 ${
            displayCurrency === cur
              ? "bg-[#8FA79B] text-[#000000]"
              : "text-white/55 hover:text-white"
          }`}
          data-testid={`currency-toggle-${cur.toLowerCase()}`}
        >
          {cur === "CAD" && fxLoading && displayCurrency !== "CAD" ? "…" : cur}
        </button>
      ))}
    </div>
  );
}

function renderExpandSection(id, { c, paymentSchedule, marketing, isCondo, contractTerm, crmIncluded, onCrmToggle, monthlyRateOverride, onOverrideChange, postTerm }) {
  c = c || {};
  paymentSchedule = paymentSchedule || {};
  marketing = marketing || {};
  const Title = ({ children }) => <div style={expandStyles.eyebrow}>{children}</div>;

  switch (id) {
    case "hero":
      return (
        <div style={{ textAlign: "center" }}>
          <Title>Due Today to Get Started</Title>
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 400,
              fontSize: "144px",
              lineHeight: 1,
              color: "#ffffff",
              fontVariantNumeric: "tabular-nums",
              margin: "12px 0 32px",
            }}
            data-testid="expand-hero-amount"
          >
            {fmtUSD(paymentSchedule.p1)}
          </div>
          <div style={{ fontSize: "20px", color: "rgba(255,255,255,0.55)", maxWidth: "520px", margin: "0 auto", lineHeight: 1.5 }}>
            {dueTodayCopy(paymentSchedule)}
          </div>
        </div>
      );

    case "platform":
      return (
        <div>
          <Title>Platform</Title>
          <ExpandRow name="Setup fee" value={fmtUSD(c.setupFee)} />
          <ExpandRow
            name="Monthly platform installment"
            value={
              <span>
                {fmtUSD(c.monthlyRate)}
                <span style={{ fontSize: "26px", color: "rgba(255,255,255,0.5)", marginLeft: "8px" }}>/mo</span>
              </span>
            }
            sub={`${contractTerm}-month platform commitment included.`}
          />
        </div>
      );

    case "exterior": {
      const rows = [];
      rows.push({ name: isCondo ? "Building orbit" : c.masterPlan ? "Master orbit" : "Community orbit", value: "INCLUDED", dim: true });
      const incSub = Number(c.masterPlanIncludedSubOrbits || 0);
      if (incSub > 0) rows.push({ name: `${incSub} suborbits · ${MASTER_PLAN_SUBORBIT_KEYFRAMES} keyframes each`, value: "INCLUDED", dim: true });
      if ((c.dayToNightCost || 0) > 0) {
        const d2nKf = Number(c.dayToNightKfQty || 0);
        rows.push({
          name: d2nKf > 0 ? `Day-to-night upgrade · ${d2nKf} keyframes` : "Day-to-night upgrade",
          value: fmtUSD(c.dayToNightCost),
        });
      }
      if ((c.additionalKfCost || 0) > 0) rows.push({ name: "Additional keyframes", value: fmtUSD(c.additionalKfCost) });
      if ((c.animatedKfCost || 0) > 0) rows.push({ name: "Animated keyframes", value: fmtUSD(c.animatedKfCost) });
      if ((c.masterOrbitCost || 0) > 0) {
        const so = Number(c.billableSubOrbits ?? c.masterOrbitsQty ?? 0);
        rows.push({
          name: so > 0 ? `${ADDITIONAL_ORBIT_LABEL} · ${so} suborbit${so === 1 ? "" : "s"}` : ADDITIONAL_ORBIT_LABEL,
          value: fmtUSD(c.masterOrbitCost),
        });
      }
      const er = Number(c.exteriorRendersQty || 0);
      if (er > 0) rows.push({ name: `Exterior renders · ${er}`, value: fmtUSD(c.exteriorRendersCost) });
      const cr = Number(c.conceptualRendersQty || 0);
      if (cr > 0) rows.push({ name: `Conceptual renderings (hybrid) · ${cr}`, value: fmtUSD(c.conceptualRendersCost) });
      if ((c.conceptualDesignFee || 0) > 0) rows.push({ name: conceptualDesignFeeLabel(c.conceptualDesignFeeSpaces), value: fmtUSD(c.conceptualDesignFee) });
      if ((c.modelingFee || 0) > 0) rows.push({ name: `${MODELING_FEE_LABEL} · ${modelingFeeTier(c.modelingFeeTier)?.label || ""}`, value: fmtUSD(c.modelingFee) });
      if (c.exteriorVRActive) rows.push({ name: `Exterior VR tour · ${Number(c.exteriorVRPoints || 0)} points`, value: fmtUSD(c.exteriorVRCost) });
      return (
        <div>
          <Title>Exterior</Title>
          {rows.map((r, i) => (
            <ExpandRow key={i} name={r.name} value={r.dim ? <span style={expandStyles.badge}>{r.value}</span> : r.value} />
          ))}
        </div>
      );
    }

    case "interior": {
      const r = Number(c.totalInteriorRenders || 0);
      const v = Number(c.totalInteriorVR || 0);
      return (
        <div>
          <Title>Interior</Title>
          {r > 0 && <ExpandRow name={`Renderings · ${r} ${isCondo ? "plans" : "models"}`} value={fmtUSD(c.interiorRendersCost)} />}
          {v > 0 && <ExpandRow name={`VR tours · ${v} ${isCondo ? "plans" : "models"}`} value={fmtUSD(c.interiorVRCost)} />}
          {(c.residenceConfiguratorCost || 0) > 0 && <ExpandRow name={VR_CONFIGURATOR_LABEL} value={fmtUSD(c.residenceConfiguratorCost)} />}
          {r === 0 && v === 0 && (
            <div style={{ ...expandStyles.label, padding: "16px 0" }}>No interior scope selected.</div>
          )}
        </div>
      );
    }

    case "amenities": {
      const r = Number(c.totalAmenityRenders || 0);
      const v = Number(c.totalAmenityVR || 0);
      return (
        <div>
          <Title>Amenities</Title>
          {r > 0 && <ExpandRow name={`Renderings · ${r} spaces`} value={fmtUSD(c.amenityRendersCost)} />}
          {v > 0 && <ExpandRow name={`VR tours · ${v} spaces`} value={fmtUSD(c.amenityVRCost)} />}
          {(c.amenityConfiguratorCost || 0) > 0 && <ExpandRow name={VR_CONFIGURATOR_LABEL} value={fmtUSD(c.amenityConfiguratorCost)} />}
          {r === 0 && v === 0 && (
            <div style={{ ...expandStyles.label, padding: "16px 0" }}>No amenity scope selected.</div>
          )}
        </div>
      );
    }

    case "video": {
      const aiDur = c.aiMotionDur;
      const cinDur = c.cinematicDur;
      const aiList = normalizeVideos(c.aiMotionVideos, aiDur);
      const cinList = normalizeVideos(c.cinematicVideos, cinDur);
      // Standalone video → size-first breakdown: hero/master + Large add-ons
      // (animation) and social cut-downs (editing), each as its own line.
      if (c.videoStandalone && c.standaloneVideo) {
        const sv = c.standaloneVideo;
        return (
          <div>
            <Title>Video Animation — {sv.sizeLabel}</Title>
            {(sv.lines || []).map((ln, idx) => (
              <ExpandRow
                key={idx}
                name={ln.qty > 1 ? `${ln.label} · ${ln.qty}` : ln.label}
                value={fmtUSD(ln.amount)}
                sub={ln.type === "editing"
                  ? (ln.includedFree > 0 ? `${ln.includedFree} included · ${ln.billable} billable (editing)` : "editing only")
                  : (ln.isHero ? `${ln.seconds}-second production` : `${ln.seconds}s × ${ln.qty}`)}
              />
            ))}
            {/* AI Motion sold alongside the standalone video (client-provided
                renderings) — not part of the size-first breakdown lines. */}
            {aiDur != null && (c.aiMotionCost || 0) > 0 && (
              <ExpandRow
                name="AI Motion"
                value={fmtUSD(c.aiMotionCost)}
                sub={`${describeVideos(aiList)}${c.aiMotionClientRenders ? " · client-provided renderings" : ""}`}
              />
            )}
          </div>
        );
      }
      return (
        <div>
          <Title>Video Animation</Title>
          <ExpandRow
            name="AI Motion"
            value={aiDur != null ? fmtUSD(c.aiMotionCost) : "—"}
            sub={aiDur != null ? `${describeVideos(aiList)}${c.aiMotionClientRenders ? " · client-provided renderings" : ""}` : null}
            dim={aiDur == null}
          />
          <ExpandRow
            name="Cinematic"
            value={cinDur != null ? fmtUSD(c.cinematicCost) : "—"}
            sub={cinDur != null ? describeVideos(cinList) : null}
            dim={cinDur == null}
          />
          {c.motionPackageActive && (c.motionPackageSavings || 0) > 0 && (
            <div style={{ ...expandStyles.row, color: "#8FA79B" }}>
              <div style={{ ...expandStyles.label, color: "#8FA79B" }}>
                Motion Package — {c.motionPackagePct}% off combined video
              </div>
              <div style={{ ...expandStyles.value, fontSize: "32px", color: "#8FA79B" }}>−{fmtUSD(c.motionPackageSavings)}</div>
            </div>
          )}
        </div>
      );
    }

    case "floorplans": {
      const d2 = Number(c.total2DPlans || 0);
      const d3 = Number(c.total3DPlans || 0);
      return (
        <div>
          <Title>Floor Plans</Title>
          {d2 > 0 && (
            <ExpandRow
              name={`2D plans · ${d2}`}
              value={fmtUSD((c.floorPlans2DPct || 0) > 0 ? c.floorPlans2DGross : c.floorPlans2DCost)}
              sub="CAD vectorized"
            />
          )}
          {(c.floorPlans2DPct || 0) > 0 && (
            <ExpandRow
              name={`Volume discount — ${c.floorPlans2DPct}% off 2D`}
              value={<span style={{ color: "#8FA79B" }}>−{fmtUSD(c.floorPlans2DSavings)}</span>}
            />
          )}
          {d3 > 0 && (
            <ExpandRow
              name={`3D plans · ${floorPlan3DScopeLabel(c.total3DUnits, c.total3DLevels, d3)}`}
              value={fmtUSD((c.floorPlans3DPct || 0) > 0 ? c.floorPlans3DGross : c.floorPlans3DCost)}
              sub={
                c.floorPlans3DWorkflow === "traditional"
                  ? "Traditional 3D pipeline · 2 revision rounds · exact to spec"
                  : c.floorPlans3DWorkflow === "hybrid"
                  ? "AI Hybrid · 1 revision round"
                  : "Dimensioned perspective"
              }
            />
          )}
          {(c.floorPlans3DPct || 0) > 0 && (
            <ExpandRow
              name={`Volume discount — ${c.floorPlans3DPct}% off 3D`}
              value={<span style={{ color: "#8FA79B" }}>−{fmtUSD(c.floorPlans3DSavings)}</span>}
            />
          )}
          {d2 === 0 && d3 === 0 && (
            <div style={{ ...expandStyles.label, padding: "16px 0" }}>No floor plan scope selected.</div>
          )}
        </div>
      );
    }

    // Marketing & Lead Generation. The campaign setup fee is a one-time
    // charge that bills in full at signing (it never joins the viz split),
    // so it reads as its own line here and lands on Payment 1.
    case "marketing": {
      const months = Number(marketing.sellOutMonths) || 0;
      const setup = Number(marketing.setupFee) || 0;
      const mgmt = Number(marketing.monthlyServiceFee) || 0;
      const ad = Math.round(Number(marketing.monthlyAdSpend) || 0);
      if (!marketing.active) {
        return (
          <div>
            <Title>Marketing</Title>
            <div style={{ ...expandStyles.label, padding: "16px 0" }}>No marketing campaign selected.</div>
          </div>
        );
      }
      return (
        <div>
          <Title>Marketing &amp; Lead Generation</Title>
          <ExpandRow
            name="Campaign setup"
            value={fmtUSD(setup)}
            sub="One-time · CRM, landing page, Meta account, pixel & analytics, ad creative, lead system. Billed in full at signing."
          />
          <ExpandRow
            name="Management fee"
            value={
              <span>
                {fmtUSD(mgmt)}
                <span style={{ fontSize: "26px", color: "rgba(255,255,255,0.5)", marginLeft: "8px" }}>/mo</span>
              </span>
            }
            sub={months > 0
              ? `${months}-month campaign · strategy, creative, optimization, weekly check-ins, monthly reporting.`
              : "Strategy, creative, optimization, weekly check-ins, monthly reporting."}
          />
          <ExpandRow
            name="Ad budget"
            value={
              <span style={expandStyles.muted}>
                ~{fmtUSD(ad)}
                <span style={{ fontSize: "22px", marginLeft: "8px" }}>/mo</span>
              </span>
            }
            sub="Client-controlled · deployed directly to Meta & Google. Not a Render Pros fee and not part of the totals."
          />
        </div>
      );
    }

    case "bundle": {
      const b = c.bundle || {};
      return (
        <div>
          <Title>{(b.label || "Bundle").toUpperCase()}</Title>
          {b.pct > 0 && (
            <div style={{ fontSize: "20px", color: "rgba(255,255,255,0.6)", marginBottom: "32px" }}>
              {b.pct}% off visualization scope
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", paddingTop: "8px" }}>
            <div style={expandStyles.label}>You save</div>
            <div style={{ ...expandStyles.value, fontSize: "64px", color: "#8FA79B" }}>
              {fmtUSD(c.bundleSavings)}
            </div>
          </div>
        </div>
      );
    }

    case "payments":
      return (
        <div>
          <Title>Payment Schedule</Title>
          {paymentSchedule.singlePayment ? (
            /* Single-payment terms: one row, and none of the delivery-gated
               rows below it — there is no second payment to gate. */
            <div style={expandStyles.row}>
              <div style={{ flex: 1 }}>
                <div style={{ ...expandStyles.badge, color: "#8FA79B" }}>Due at Signing — 100%</div>
                <div style={{ ...expandStyles.small, marginTop: "8px" }}>
                  {paymentSchedule.singlePaymentSavings > 0
                    ? `The entire one-time scope in a single payment at signing, less the ${paymentSchedule.pifPct}% pay-in-full discount — you save ${fmtUSD(paymentSchedule.singlePaymentSavings)}. Nothing further is collected on delivery.`
                    : "The entire one-time scope in a single payment at signing. Nothing further is collected on delivery."}
                </div>
              </div>
              <div style={expandStyles.value}>{fmtUSD(paymentSchedule.p1)}</div>
            </div>
          ) : (
          <>
          <div style={expandStyles.row}>
            <div style={{ flex: 1 }}>
              <div style={{ ...expandStyles.badge, color: "#8FA79B" }}>Due at Signing — Project Initiation</div>
              <div style={{ ...expandStyles.small, marginTop: "8px" }}>{dueTodayCopy(paymentSchedule)}</div>
            </div>
            <div style={expandStyles.value}>{fmtUSD(paymentSchedule.p1)}</div>
          </div>
          <div style={expandStyles.row}>
            <div style={{ flex: 1 }}>
              <div style={{ ...expandStyles.badge, color: "#8FA79B" }}>{paymentSchedule.twoPart ? "Due on Finalization" : `Due at ${paymentSchedule.v1?.dueLabel || "V1 Clay Delivery"}`}</div>
              <div style={{ ...expandStyles.small, marginTop: "8px" }}>{`${paymentSchedule.twoPart ? `${paymentSchedule.pct?.p2 ?? 50}% of ${paymentSchedule.scopeLabel || "visualization scope"} — the ${paymentSchedule.finalAssets || "remaining visualization assets"}, complete and approved by you; collected prior to final delivery` : `${paymentSchedule.pct?.p2 ?? 25}% of ${paymentSchedule.scopeLabel || "visualization scope"}`}${(paymentSchedule.collateralP2 || 0) > 0 ? " + 50% of collateral" : ""}`}</div>
              {/* A two-part plan's Payment 2 is the finalization payment —
                  nothing is gated on V1, so its definition would only
                  confuse. */}
              {paymentSchedule.twoPart ? null : !paymentSchedule.isAlacarte ? (
                <ul style={{ ...expandStyles.small, marginTop: "10px", paddingLeft: "18px", opacity: 0.8, lineHeight: 1.6 }}>
                  {(paymentSchedule.v1?.bullets || []).map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              ) : (
                paymentSchedule.v1?.note && (
                  <div style={{ ...expandStyles.small, marginTop: "10px", opacity: 0.8 }}>{paymentSchedule.v1.note}</div>
                )
              )}
            </div>
            <div style={expandStyles.value}>{fmtUSD(paymentSchedule.p2)}</div>
          </div>
          {!paymentSchedule.twoPart && (
          <div style={expandStyles.row}>
            <div style={{ flex: 1 }}>
              <div style={{ ...expandStyles.badge, color: "#8FA79B" }}>Due on Finalization</div>
              <div style={{ ...expandStyles.small, marginTop: "8px" }}>
                {`Remaining ${paymentSchedule.pct?.p3 ?? 25}% of ${paymentSchedule.isAlacarte ? "visualization scope" : (paymentSchedule.scopeLabel || "visualization scope")} — the ${paymentSchedule.finalAssets || "remaining visualization assets"}, complete and approved by you; collected prior to final delivery`}
              </div>
            </div>
            <div style={expandStyles.value}>{fmtUSD(paymentSchedule.p3)}</div>
          </div>
          )}
          </>
          )}
          {/* Marketing management is a recurring fee on its own campaign term,
              so it never folds into a milestone above — it gets its own line
              here the same way the platform subscription does. */}
          {(paymentSchedule.marketingMonthly || 0) > 0 && (
          <div style={expandStyles.row}>
            <div style={{ flex: 1 }}>
              <div style={{ ...expandStyles.badge, color: "#8FA79B" }}>Marketing Management</div>
              <div style={{ ...expandStyles.small, marginTop: "8px" }}>
                {`Begins post-launch${(paymentSchedule.marketingMonths || 0) > 0 ? ` · ${paymentSchedule.marketingMonths}-month campaign` : ""}`}
              </div>
            </div>
            <div style={expandStyles.value}>
              {fmtUSD(paymentSchedule.marketingMonthly)}
              <span style={{ fontSize: "26px", color: "rgba(255,255,255,0.5)", marginLeft: "8px" }}>/mo</span>
            </div>
          </div>
          )}
        </div>
      );

    case "monthly":
      return <MonthlyExpandPanel
        c={c}
        contractTerm={contractTerm}
        crmIncluded={crmIncluded}
        onCrmToggle={onCrmToggle}
        monthlyRateOverride={monthlyRateOverride}
        onOverrideChange={onOverrideChange}
        paymentSchedule={paymentSchedule}
        postTerm={postTerm}
      />;

    default:
      return null;
  }
}

/* ══════════════════════════════════════════════════════════════════════
 *  MANUAL PRICE OVERRIDE
 *
 *  Rare-case escape hatch: a rep can replace the calculated amount on any
 *  priced line in the quote. Overrides are keyed by line id and stored as
 *  a flat map ({ interiorRenders: 30000 }). `applyPriceOverrides` swaps the
 *  overridden leaf amounts into the calc object and then recomputes every
 *  rollup above them, so the sidebar, payment milestones, saved snapshot,
 *  and PDF all stay internally consistent — line items always sum to the
 *  totals the client signs.
 *
 *  THE REP DECIDES WHETHER THE DISCOUNTS SURVIVE. When the first override
 *  would cost the quote its bundle / motion package, a popup shows the rep
 *  exactly how much that is and asks: keep the discount, or remove it.
 *  `overrideDiscountChoice` records the answer ("keep" | "remove"; null =
 *  not asked yet, which behaves as keep). The bundle is opt-in (see
 *  ./bundleOptIn), so it is only at stake once the rep has applied it; until
 *  then the popup only ever asks about the Motion Package.
 *
 *  KEEP: the bundle tier and motion-package % stay as the rate card set them
 *  and are recomputed against the overridden lines, so "N% off" is always N%
 *  of the gross the client sees printed. The quote is NOT `manualPricing`.
 *
 *  REMOVE: the quote stops being rate-card priced, so the bundle and the
 *  motion package don't apply at all and nothing anywhere — sidebar, walk-
 *  through, expand modals, web proposal, PDF — says the word discount. A
 *  hand-priced quote reads as a plain list of prices that add up. This is
 *  why `manualPricing` rides on the calc and into `calc_snapshot`: most
 *  discount UI is already gated on `savings > 0` and disappears once the
 *  math zeroes out, but the copy that isn't value-gated reads that flag.
 *
 *  Floor-plan volume pricing is the one thing that survives, because it's a
 *  per-line bulk RATE rather than an incentive: the line keeps the price it
 *  already had, but reports it as its own gross with no volume-savings row,
 *  so it reads as a price and not as a discount.
 * ═════════════════════════════════════════════════════════════════════ */

// PRICE_OVERRIDE_LINES lives in ./priceOverrideLines so the Pricing Admin's
// discount guardrail table keys off the very same line ids.

/*  Marketing campaign fees are overridable too, but they are deliberately NOT
 *  part of PRICE_OVERRIDE_LINES and never enter the `priceOverrides` map.
 *
 *  The marketing campaign is a separate scope from the visualization scope:
 *  it is itemized on its own in the sidebar, the web proposal and the PDF, and
 *  it contributes nothing to `vizGross` / `vizNet` / `oneTimeAllIn`. So a rep
 *  honouring a negotiated campaign fee must NOT flip the quote to manual
 *  pricing — that would silently switch off the visualization bundle and the
 *  motion package and bill the untouched render lines HIGHER than the client
 *  was just quoted. Same reasoning that keeps `monthlyRateOverride` out of the
 *  manual-pricing path.
 *
 *  These two live on the `marketing` state block, which means `marketingMath`
 *  is the single seam: the sidebar, payment milestones, saved snapshot, web
 *  proposal and PDF all read the overridden fee with no extra wiring, and the
 *  campaign rollups above them (monthly budget, total campaign investment)
 *  recompute from it.
 */
const MARKETING_OVERRIDE_LINES = [
  { id: "marketingSetupFee",   label: "Campaign Setup Fee",  section: "Marketing", stateKey: "setupFeeOverride",            autoKey: "setupFeeAuto" },
  { id: "marketingMonthlyFee", label: "Monthly Management",  section: "Marketing", stateKey: "monthlyServiceFeeOverride",   autoKey: "monthlyServiceFeeAuto", monthly: true },
];

const MARKETING_OVERRIDE_IDS = new Set(MARKETING_OVERRIDE_LINES.map(l => l.id));

/*  Collateral lines are overridable for the same reason, on the same terms:
 *  collateral is its own scope (itemized separately, outside vizGross / vizNet),
 *  so a hand-set collateral price must NOT flip the quote to manual pricing and
 *  cost it the visualization bundle.
 *
 *  Overrides live on `collateral.priceOverrides`, keyed by the collateral
 *  config key. `collateralMath` is the single seam: it builds the effective
 *  price table (rate card + overrides) and sums from it, and that table is
 *  what the save freezes onto `collateral.prices` — which the web proposal
 *  and the PDF already itemize from. So the line items, the collateral total,
 *  the payment milestones and both client documents all agree with no extra
 *  wiring downstream.
 */
const COLLATERAL_OVERRIDE_LINES = [
  { key: "landingPage",            label: "Landing Page" },
  { key: "fullWebsite",            label: "Full Website" },
  { key: "brandingPackage",        label: "Branding & Conceptual Package" },
  { key: "investmentPresentation", label: "Investment Presentation" },
  { key: "developmentFlipbook",    label: "Development Flipbook" },
  { key: "billboardStatic",        label: "Billboard Design (Static)" },
  { key: "billboardAnimated",      label: "Billboard Design (Animated)" },
  { key: "unitInventorySheet",     label: "Unit Inventory Breakdown Sheet" },
].map((l) => ({ ...l, id: `collateral:${l.key}`, section: "Collateral" }));

const COLLATERAL_OVERRIDE_BY_ID = new Map(COLLATERAL_OVERRIDE_LINES.map((l) => [l.id, l]));

// Normalize the collateral override map to { configKey: roundedNonNegative },
// dropping unknown keys and blank / unusable values.
function cleanCollateralOverrides(raw) {
  if (!raw || typeof raw !== "object") return {};
  const out = {};
  for (const { key } of COLLATERAL_OVERRIDE_LINES) {
    const v = cleanFeeOverride(raw[key]);
    if (v !== null) out[key] = v;
  }
  return out;
}

// Normalize one campaign-fee override to a rounded non-negative number, or
// null when the rep left the field empty / typed something unusable.
function cleanFeeOverride(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
}

const PRICE_OVERRIDE_SECTIONS = ["VEXOS Platform", "Exterior", "Interior", "Amenity", "Video", "Floor Plans", "Conceptual Design", "Revisions & Travel", "Marketing", "Collateral"];

// Normalize a raw override map to { id: roundedNonNegativeNumber }, dropping
// blank / non-numeric entries. Used on read, save, and hydrate so a malformed
// value can never reach the pricing math.
function cleanPriceOverrides(raw) {
  if (!raw || typeof raw !== "object") return {};
  const validIds = new Set(PRICE_OVERRIDE_LINES.map(l => l.id));
  const out = {};
  for (const [id, v] of Object.entries(raw)) {
    if (!validIds.has(id)) continue;
    const n = Number(v);
    if (!Number.isFinite(n) || n < 0) continue;
    out[id] = Math.round(n);
  }
  return out;
}

// applyPriceOverrides lives in ./priceOverrides (unit-tested there).

export default function VexosClientCalculator() {
  // Tab nav — Tab 1 (vexos): existing Phases 1-6. Tab 2 (marketing):
  // Marketing & Collateral modules. Independent state per tab.
  const [activeTab, setActiveTab] = useState("vexos");
  // Derived tab flags — declared early so useEffects below can reference them.
  const isAlacarte = activeTab === "alacarte";
  const isLotExplorer = activeTab === "lotexplorer";

  // Marketing module state (Tab 2 — Part 2 of spec).
  const [marketing, setMarketing] = useState(() => ({
    active: false,
    campaignType: "pre-sale",       // "pre-sale" | "pre-lease"
    priceTier: null,                // tier id from PRESALE_CPL_TIERS / PRELEASE_CPL_TIERS
    units: MARKETING_DEFAULTS.unitsDefault,
    sellOutMonths: MARKETING_DEFAULTS.timelineDefaultMonths,
    conversionRate: MARKETING_DEFAULTS.conversionDefaultPct,
    contentStrategyActive: false,
    // Manual campaign-fee overrides (null = use the resolved tier's rate).
    // Kept on the marketing block rather than the `priceOverrides` map on
    // purpose — see MARKETING_OVERRIDE_LINES.
    setupFeeOverride: null,
    monthlyServiceFeeOverride: null,
  }));

  // Collateral module state (Tab 2 — Part 3 of spec).
  const [collateral, setCollateral] = useState(() => ({
    landingPage: false,
    fullWebsite: false,
    brandingPackage: false,
    investmentPresentation: false,
    developmentFlipbook: false,
    billboardStatic: false,
    billboardAnimated: false,
    unitInventorySheet: false,
    animatedRendersQty: 0,
    renderEnhancementQty: 0,
    socialMediaRetainerPackage: null, // "6-clips" | "12-clips" | "18-clips" | null
    // Hand-set line prices, keyed by collateral config key — see
    // COLLATERAL_OVERRIDE_LINES.
    priceOverrides: {},
  }));

  // Pull live admin-editable config every render (cheap localStorage read).
  const marketingConfig  = getMarketingConfig();
  const collateralConfig = getCollateralConfig();
  const cplTiers         = getCPLConfig();

  // Marketing math — live derivations. Conversion rate is a percentage (1.5
  // means 1.5%). Spec formula:
  //   monthlyLeads = units / (sellOutMonths × conversionRate)
  //   monthlyAdSpend = monthlyLeads × CPL
  //   monthlyBudget = setupFee/sellOutMonths + monthlyServiceFee + monthlyAdSpend
  //   totalCampaignInvestment = monthlyBudget × sellOutMonths + setupFee
  // Note: marketingMath useMemo is defined further down (after the Phase-1
  // state vars + per-mode calc memos) because it now depends on `mode`,
  // `units`, and `c_lots` to resolve the campaign-fee tier from Phase 1.

  // Collateral math — sum of toggled items + stepped items + retainer.
  // `oneTimeTotal` is what splits 50/50 into Payment 1 + 2. The retainer is
  // monthly post-launch and never enters the payment-1/2/3 schedule.
  const collateralMath = useMemo(() => {
    let oneTimeTotal = 0;
    // Effective price table: the rate card with any hand-set line prices laid
    // over it. Everything below — and the saved `collateral.prices` snapshot
    // the proposal and PDF itemize from — reads this one table.
    const priceOverrides = cleanCollateralOverrides(collateral.priceOverrides);
    const cc = { ...collateralConfig, ...priceOverrides };
    if (collateral.landingPage)           oneTimeTotal += cc.landingPage;
    if (collateral.fullWebsite)           oneTimeTotal += cc.fullWebsite;
    if (collateral.brandingPackage)       oneTimeTotal += cc.brandingPackage;
    if (collateral.investmentPresentation)oneTimeTotal += cc.investmentPresentation;
    if (collateral.developmentFlipbook)   oneTimeTotal += cc.developmentFlipbook;
    if (collateral.billboardStatic)       oneTimeTotal += cc.billboardStatic;
    if (collateral.billboardAnimated)     oneTimeTotal += cc.billboardAnimated;
    if (collateral.unitInventorySheet)    oneTimeTotal += cc.unitInventorySheet;
    oneTimeTotal += (Number(collateral.animatedRendersQty)   || 0) * cc.animatedRendersPer;
    oneTimeTotal += (Number(collateral.renderEnhancementQty) || 0) * cc.renderEnhancementPer;
    let retainerMonthly = 0;
    if (collateral.socialMediaRetainerPackage === "6-clips")  retainerMonthly = cc.retainer6;
    if (collateral.socialMediaRetainerPackage === "12-clips") retainerMonthly = cc.retainer12;
    if (collateral.socialMediaRetainerPackage === "18-clips") retainerMonthly = cc.retainer18;
    const collateralTotal = oneTimeTotal;
    // A selected line counts even when it is priced at $0 — a rep who waives
    // the website still sells it, and a one-time $0 is a real statement on
    // the proposal ("Full Website — $0"). Gating on the total alone saved a
    // $0-only collateral block as inactive and dropped the line entirely.
    const anySelected = COLLATERAL_OVERRIDE_LINES.some((l) => !!collateral[l.key])
      || (Number(collateral.animatedRendersQty) || 0) > 0
      || (Number(collateral.renderEnhancementQty) || 0) > 0;
    const hasAny = oneTimeTotal > 0 || retainerMonthly > 0 || anySelected;
    return { oneTimeTotal, retainerMonthly, collateralTotal, hasAny, prices: cc, priceOverrides };
  }, [collateral, collateralConfig]);

  const collateralActive = collateralMath.hasAny;

  // ── CALCULATOR MODE (Condo Building | Community) ─────────────────────
  // SFH remains scaffolded for a future tab.
  const [mode, setMode] = useState("condo");

  // ── PRICING ADMIN ─────────────────────────────────────────────────────
  // pricingVersion bumps when admin saves → forces calc memos to recompute.
  const [adminOpen, setAdminOpen] = useState(false);
  const [pricingVersion, setPricingVersion] = useState(0);

  // ── SHARED PRICING CONFIG (MongoDB-backed multi-user sync) ───────────
  // `user` from auth context gates the Pricing Admin button + dictates the
  // one-time localStorage → DB migration eligibility.
  const { user: currentUser } = useAuth();
  const canEditPricing = isVexosPricingAdmin(currentUser);
  const [pricingMeta, setPricingMeta] = useState({ exists: false, updatedAt: null, updatedByName: null });
  const [pricingAudience, setPricingAudience] = useState(0);

  // On mount: hydrate from backend, migrate any legacy localStorage admin
  // config (admins push to DB, non-admins simply discard local cache so
  // the shared values are the single source of truth).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1. Pull shared config + audience count in parallel.
        const [shared, count] = await Promise.all([
          fetchSharedAdminState(),
          fetchPricingAudienceCount(),
        ]);
        if (cancelled) return;

        // 2. One-time legacy localStorage migration.
        const legacy = peekLegacyAdminState();
        if (legacy) {
          if (!shared.meta.exists && isVexosPricingAdmin(currentUser)) {
            // No shared config in DB yet → push local cache up so the
            // team inherits this admin's last-known values.
            try {
              const newMeta = await saveSharedAdminState(legacy);
              if (!cancelled) {
                setPricingMeta(newMeta);
                clearLegacyAdminState();
                setPricingVersion((v) => v + 1);
                return;
              }
            } catch {
              // Migration push failed (likely 403) — fall through to
              // apply remote/defaults and leave legacy cache alone so a
              // proper admin can migrate later.
            }
          } else {
            // Either shared config already exists, or current user is not
            // an admin — local cache is stale, wipe it.
            clearLegacyAdminState();
          }
        }

        // 3. Apply remote state to in-memory configs + bump memo version.
        applyAdminToConfigs(shared.state);
        setPricingMeta(shared.meta);
        setPricingAudience(count);
        setPricingVersion((v) => v + 1);
      } catch {
        // Silent fallback — defaults remain in effect.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  // ── PRODUCTION TIMELINE DURATIONS (Timeline Engine DB) ──────────────
  // Fetched on mount AND whenever the Walkthrough/Presentation modal
  // opens, so the in-app Production Timeline card always reflects the
  // same DB-backed values the backend uses to render the PDF. Without
  // this re-fetch on open, durations edited in the Timeline Engine after
  // the calculator mounted would produce a stale display while the
  // server-rendered PDF correctly showed the up-to-date number.
  const [timelineDurations, setTimelineDurations] = useState(TIMELINE_FALLBACK_DURATIONS);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const d = await fetchTimelineDurations();
      if (!cancelled) setTimelineDurations(d);
    })();
    return () => { cancelled = true; };
  }, []);

  // ── DISPLAY CURRENCY (USD / CAD) ──────────────────────────────────────
  // Presentation view + PDF only — Stripe checkout and the underlying rate
  // card always stay USD. Rate is fetched live from the backend (cached
  // ~1h server-side) the first time CAD is selected.
  const [displayCurrency, setDisplayCurrencyState] = useState("USD");
  const [fxRate, setFxRate] = useState(null);
  const [fxFetchedAt, setFxFetchedAt] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  // Guards the effect below without being a dependency — fxLoading (state)
  // can't do this job: setFxLoading(true) inside the effect would itself
  // change a value in the effect's own dependency array, re-triggering the
  // effect's cleanup (cancelled = true) before the in-flight request
  // resolves, so the response always arrived "cancelled" and fxRate/fxLoading
  // got stuck at null/true forever.
  const fxFetchingRef = useRef(false);

  useEffect(() => {
    if (displayCurrency !== "CAD" || fxRate || fxFetchingRef.current) return;
    let cancelled = false;
    fxFetchingRef.current = true;
    setFxLoading(true);
    apiClient.get("/vexos-quotes/fx-rate", { params: { to: "CAD" } })
      .then(({ data }) => {
        if (cancelled) return;
        setFxRate(data.rate);
        setFxFetchedAt(data.fetched_at);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Couldn't fetch a live exchange rate — staying in USD.");
        setDisplayCurrencyState("USD");
      })
      .finally(() => {
        fxFetchingRef.current = false;
        if (!cancelled) setFxLoading(false);
      });
    return () => { cancelled = true; };
  }, [displayCurrency, fxRate]);

  // Keep the shared fmt()/fmtUSD() currency context in sync for this render
  // pass so every descendant reads the current value, including sibling
  // components rendered below that aren't closures over this state.
  setDisplayCurrency(displayCurrency, fxRate);

  // ── EXPANDED PROJECT SUMMARY (presentation mode) ─────────────────────
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  // ── LOT EXPLORER PRESENTATION OVERLAY ────────────────────────────────
  const [lotExplorerExpanded, setLotExplorerExpanded] = useState(false);
  const [lotExplorerPresentMode, setLotExplorerPresentMode] = useState(false);

  // Re-fetch durations whenever the walkthrough opens so the live
  // Production Timeline card matches the PDF that was just generated.
  useEffect(() => {
    if (!summaryExpanded) return;
    let cancelled = false;
    (async () => {
      const d = await fetchTimelineDurations();
      if (!cancelled) setTimelineDurations(d);
    })();
    return () => { cancelled = true; };
  }, [summaryExpanded]);

  // ── PRESENTATION MODE TOOLKIT (additive — zero impact when OFF) ──────
  //   `presentationMode`  enables zoom-optimized layout + click-spotlight
  //                       + click-to-expand section modals
  //   `activeSection`     id of the currently-open expand modal (or null)
  const [presentationMode, setPresentationMode] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  // Toggle body class so CSS can scale typography / padding cleanly.
  useEffect(() => {
    if (presentationMode) {
      document.body.classList.add("vexos-presentation-mode");
    } else {
      document.body.classList.remove("vexos-presentation-mode");
      setActiveSection(null);
    }
    return () => document.body.classList.remove("vexos-presentation-mode");
  }, [presentationMode]);

  // Keyboard shortcuts (only when the walkthrough modal is open).
  //   P     → toggle Presentation Mode
  //   Esc   → close active expand modal, then exit Presentation Mode
  //   ← / → → step through expand modals when one is open
  useEffect(() => {
    if (!summaryExpanded) return;
    const onKey = (e) => {
      if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
      const k = e.key.toLowerCase();
      if (k === "p") { e.preventDefault(); setPresentationMode((v) => !v); return; }
      // ESC always closes the active expand modal first (works in
      // walkthrough view, not just Presentation Mode), then exits
      // Presentation Mode if it's on.
      if (k === "escape") {
        if (activeSection) { e.preventDefault(); setActiveSection(null); return; }
        if (presentationMode) { e.preventDefault(); setPresentationMode(false); return; }
        return;
      }
      if (!presentationMode) return;
      const order = sectionOrderRef.current;
      if (k === "arrowleft" && activeSection) {
        e.preventDefault();
        const i = order.indexOf(activeSection);
        if (i > 0) setActiveSection(order[i - 1]);
      } else if (k === "arrowright" && activeSection) {
        e.preventDefault();
        const i = order.indexOf(activeSection);
        if (i < order.length - 1 && i >= 0) setActiveSection(order[i + 1]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [summaryExpanded, presentationMode, activeSection]);

  // Auto-cleanup Presentation Mode when the walkthrough closes.
  useEffect(() => {
    if (!summaryExpanded && presentationMode) setPresentationMode(false);
  }, [summaryExpanded, presentationMode]);

  // Auto-cleanup Lot Explorer present mode when overlay closes.
  useEffect(() => {
    if (!lotExplorerExpanded) setLotExplorerPresentMode(false);
  }, [lotExplorerExpanded]);

  // Close the main VEXOS walkthrough if the user switches to Lot Explorer tab.
  useEffect(() => {
    if (isLotExplorer && summaryExpanded) setSummaryExpanded(false);
  }, [isLotExplorer, summaryExpanded]);

  // Toggle body class for Lot Explorer Presentation Mode typography scaling.
  useEffect(() => {
    if (lotExplorerPresentMode) {
      document.body.classList.add("lot-explorer-presentation-mode");
    } else {
      document.body.classList.remove("lot-explorer-presentation-mode");
    }
    return () => document.body.classList.remove("lot-explorer-presentation-mode");
  }, [lotExplorerPresentMode]);

  // Click handler for the overlay — Presentation Mode only.
  //   1. If a [data-vexos-section] ancestor is found → open its expand modal.
  //   2. Otherwise fall back to the click-to-spotlight pulse on the nearest
  //      semantic block. Toolkit chrome is excluded.
  const handlePresentationClick = useCallback((e) => {
    if (!presentationMode) return;
    const t = e.target;
    if (!t || !t.closest) return;
    if (t.closest("[data-vexos-toolkit]")) return;
    if (t.closest("button")) return;
    const sectionEl = t.closest("[data-vexos-section]");
    if (sectionEl) {
      const id = sectionEl.getAttribute("data-vexos-section");
      if (sectionOrderRef.current.includes(id)) {
        setActiveSection(id);
        return;
      }
    }
    let target = t.closest("section, header, aside");
    if (!target) return;
    target.classList.remove("vexos-spotlight-pulse");
    void target.offsetWidth;
    target.classList.add("vexos-spotlight-pulse");
    clearTimeout(handlePresentationClick._t);
    handlePresentationClick._t = setTimeout(() => {
      target.classList.remove("vexos-spotlight-pulse");
    }, 2800);
  }, [presentationMode]);

  // ── PRINT-TIME VIEWPORT SCALING ───────────────────────────────────────
  // The walkthrough modal uses a wide screen-mode layout (Tailwind utilities
  // sized against the user's actual viewport, e.g. 1920px). Chrome's print
  // engine doesn't reliably re-layout against @page width — it scales the
  // screen layout to fit, which clips wide content on the left/right edges
  // of an A4 page. Fix: on `beforeprint` we compute a `zoom` factor so the
  // overlay fits the A4 printable area (~756 CSS px for A4 portrait at 16mm
  // side margins), and reset on `afterprint`. Scoped to the overlay only.
  useEffect(() => {
    if (!summaryExpanded) return;
    const A4_PRINTABLE_PX = 756; // 210mm − 32mm margins, at 96dpi
    const apply = () => {
      const w = window.innerWidth || 1024;
      const scale = Math.min(1, A4_PRINTABLE_PX / w);
      document.documentElement.style.setProperty("--vexos-print-scale", String(scale));
    };
    const reset = () => {
      document.documentElement.style.removeProperty("--vexos-print-scale");
    };
    apply(); // initial value (some browsers emulate print without firing beforeprint)
    window.addEventListener("beforeprint", apply);
    window.addEventListener("afterprint", reset);
    return () => {
      window.removeEventListener("beforeprint", apply);
      window.removeEventListener("afterprint", reset);
      reset();
    };
  }, [summaryExpanded]);

  // ── CLIENT IDENTITY (drives Save Quote + Proposal generation) ────────
  const [clientName, setClientName] = useState("");
  const [clientEntity, setClientEntity] = useState("");
  const [clientEmail, setClientEmail] = useState("");

  // ── PROPOSAL SALES REP ───────────────────────────────────────────────
  // The proposal's Contact page is normally auto-resolved from the account
  // generating it (Julian/Jordan log in as themselves). But internal users
  // (Bernard/Cole) generate on a rep's behalf — for them we surface a
  // selector so the proposal is attributed to the right client-facing rep.
  // Persisted inside the quote `inputs` bag; read back on the backend.
  const [salesRep, setSalesRep] = useState("");
  const currentUserIsKnownRep = useMemo(() => {
    const email = (currentUser?.email || "").trim().toLowerCase();
    const name = (currentUser?.name || "").trim().toLowerCase();
    const first = name.split(" ")[0];
    // Only Julian/Jordan auto-resolve (selector hidden). Bernardo/Cole are
    // selectable reps but still see the selector so they can attribute a
    // proposal to any rep (including themselves).
    return SELF_SERVE_REPS.some(
      (r) => r.email === email || r.value === name || (first && r.value.split(" ")[0] === first)
    );
  }, [currentUser?.email, currentUser?.name]);
  const [saving, setSaving] = useState(false);
  const [savedQuoteId, setSavedQuoteId] = useState(null);
  // Which V1 milestone definition this quote states. A quote the rep is
  // building now is written under the current one; a quote loaded from before
  // the V1 Clay rename keeps what it was sold under, so reopening and re-saving
  // it can never re-word the proposal the client already has. The server is the
  // authority — it stamps on create and never on update — and this mirrors the
  // stamp so the live sidebar shows the rep the terms their quote states.
  const [milestonePolicy, setMilestonePolicy] = useState(MILESTONE_POLICY_CLAY);
  // Same idea for the final payment: a new quote states the three-week terms,
  // a loaded one keeps what it was sold under (server stamps on create).
  const [finalPaymentPolicy, setFinalPaymentPolicy] = useState(FINAL_POLICY_NOTICE);
  const [hydrating, setHydrating] = useState(false);

  // ── Deal closure (Stripe + DocuSign) state ────────────────────────────
  // Tracks the latest envelope + Stripe session metadata so the
  // Presentation-Mode CTAs can show status badges and avoid double-sends.
  const [closureState, setClosureState] = useState({
    docusignEnvelopeId: null,
    docusignStatus: null,
    docusignSentAt: null,
    stripeSessionId: null,
    stripePaymentLink: null,
    stripePaymentStatus: null,
    stripeAmount: null,
  });
  // What the closure flow collects: the Payment 1 deposit, or the whole
  // one-time scope at the pay-in-full discount. The rep sets it before
  // charging; the backend records the election on the quote, and the
  // project's milestone schedule follows it.
  const [collectMode, setCollectMode] = useState("deposit");
  const [closureBusy, setClosureBusy] = useState({ envelope: false, stripe: false });
  const [closureError, setClosureError] = useState("");
  const [showWire, setShowWire] = useState(false);

  // Snapshot of the timeline as it was saved to MongoDB the last time
  // this quote was persisted. Used as the source of truth for the live
  // Production Timeline card on initial hydration so the in-app card
  // matches the server-rendered PDF byte-for-byte. Cleared the moment
  // any Phase-1/Phase-2 selection changes so the card resumes live
  // recompute.
  const [savedTimelineSnapshot, setSavedTimelineSnapshot] = useState(null);

  // Sales-rep override for the auto-calculated VEXOS monthly subscription.
  // `null` ⇒ use the volume-tiered value from `calc.monthlyRate`. Set to
  // a non-negative number ⇒ that exact value flows through the payment
  // schedule, walkthrough sticky pill, recurring contract math, save
  // payload, and PDF. Reps can use this on live calls to honour a
  // negotiated rate without the calculator snapping it back.
  const [monthlyRateOverride, setMonthlyRateOverride] = useState(null);
  // Post-12-month maintenance fee. null = the default for the project type
  // ($750, or $250 for SFH); a number = the rep's negotiated figure.
  const [postTermOverride, setPostTermOverride] = useState(null);
  const postTermMonthly = postTermOverride !== null && Number.isFinite(postTermOverride)
    ? Math.max(0, Math.round(postTermOverride))
    : defaultPostTermMonthly(mode);

  // Bundle incentive is the rep's call: the calc reports what the quote
  // qualifies for (`bundleOffer`) and only takes it off the visualization
  // subtotal once the rep applies it. A new quote starts NOT applied; a saved
  // one hydrates its flag (absent = applied, as it was priced). See ./bundleOptIn.
  const [bundleApplied, setBundleApplied] = useState(NEW_QUOTE_BUNDLE_APPLIED);
  // Manual per-line price overrides ({ lineId: amount }). Empty = fully
  // calculated pricing. See PRICE_OVERRIDE_LINES / applyPriceOverrides.
  const [priceOverrides, setPriceOverrides] = useState({});
  // Whether hand-set prices keep the bundle / motion-package discounts:
  // "keep" | "remove", or null until the rep has been asked (behaves as keep).
  // Asked once per quote by the popup below; changeable in the override panel.
  const [overrideDiscountChoice, setOverrideDiscountChoice] = useState(null);
  // Open popup: { amount } = what removing the discounts adds back.
  const [discountPrompt, setDiscountPrompt] = useState(null);
  const [overridePanelOpen, setOverridePanelOpen] = useState(false);

  // VEXOS CRM monthly inclusion toggle. When the client opts out of the
  // CRM, $1,500 is deducted from the monthly rate. Default = included.
  // A hard $1,000 / mo floor applies after deduction so the displayed
  // rate never reads below the minimum.
  const [crmIncluded, setCrmIncluded] = useState(true);
  // À La Carte payment split, chosen before presenting: three payments
  // (default — signing / V1 renders / finalization, split by the size rule) or
  // "50-50" (signing / finalization). Only affects à la carte quotes.
  const [alacartePayPlan, setAlacartePayPlan] = useState(ALACARTE_THREE_PART);
  // The rep's explicit "one payment at signing" election. Available on every
  // quote, not just à la carte, so it lives in its own state rather than as a
  // third value of the à-la-carte choice above.
  // A brand-new quote opens on 75 / 25 (Cole, 2026-11-02). A saved quote
  // hydrates whatever it stored, so an old quote with no choice stays auto.
  const [payPlanChoice, setPayPlanChoice] = useState(NEW_QUOTE_PAY_PLAN);
  // Pay-in-full DISCOUNT switch, per quote. The pay-in-full option itself is
  // always offered; this only decides whether it carries the 5% off or is
  // priced at the full one-time scope. Default ON.
  const [pifDiscount, setPifDiscount] = useState(true);
  // Additional (manual) discount on the whole proposal — { kind: "amount" |
  // "pct", value } or null. Its own printed line; see ./proposalDiscount.
  const [proposalDiscount, setProposalDiscount] = useState(null);
  // Complimentary add-ons the rep is giving at no charge — [{ id, label,
  // value, qty }], picked from the Pricing Admin list (Section 8D) and capped
  // at a % of the one-time contract value. Printed on the proposal as
  // "Included" lines; they move no money. See ./guardrails.
  const [freeAddOns, setFreeAddOns] = useState([]);
  // Conceptual interior design fee — { spaces, rate, amount, note }, a legacy
  // flat { amount, note }, or null. Its own printed line, see ./conceptualDesignFee.
  const [conceptualDesignFee, setConceptualDesignFee] = useState(null);
  // 3D modeling fee — { tier }: null = automatic from project type/size, a
  // tier key = the rep's pick, "none" = no fee. See ./modelingFee.
  const [modelingFee, setModelingFee] = useState({ tier: null });
  // Real-Time Configurator on the VR tours — { residence, amenity }, each
  // { enabled, categories }. Shared by every mode; a section only bills while
  // it has a VR tour. See ./vrConfigurator.
  const [vrConfigurator, setVrConfigurator] = useState(() => normalizeVrConfigurator(null));
  // Rep's committed production timeline — { viz, vexos } week ranges or null.
  // Applied server-side on top of the calculated estimate, so the walkthrough
  // card, the saved quote, the PDF and the intake hand-off all show it.
  const [timelineOverride, setTimelineOverride] = useState(null);
  // À La Carte only — AI Motion is normally render-gated (it animates renders
  // that must exist in the quote). When the client supplies their own
  // renderings, the rep checks this box to unlock AI Motion without renders
  // in scope. Ignored on the VEXOS/platform tabs.
  const [aiMotionClientRenders, setAiMotionClientRenders] = useState(false);

  // Additional revision rounds beyond the standard package. Each round
  // costs REVISIONS_CONFIG.additionalPct% of the net visualization scope.
  // Mode-independent (applies to whichever project type is active).
  const [additionalRevisions, setAdditionalRevisions] = useState(0);

  // On-site travel — number of days our team travels to the project.
  // Flat TRAVEL_CONFIG.perDayRate per day, capped at TRAVEL_CONFIG.maxDays.
  const [travelDays, setTravelDays] = useState(0);

  // VEXOS master toggle. When OFF, the entire VEXOS scope (setup fee,
  // monthly subscription, visualization scope, platform language, payment
  // schedule rows 01-04, Production Timeline) is excluded from the
  // proposal — same independent-module pattern as the Marketing toggle.
  // Default = ON for backward compatibility with existing saved quotes.
  // `vexosEnabled` is the rep's explicit master toggle; the derived
  // `vexosActive` below also factors in whether there's any VEXOS content.
  const [vexosEnabled, setVexosEnabled] = useState(true);

  // ── VEXOS LOT EXPORER™ STATE ──────────────────────────────────────
  // Standalone tab-mode product. Per-lot pricing × lot count, with
  // 50/25/25 payment plan handled internally. Persisted under
  // `inputs.lotExplorer` for clean save/restore. Mirrors the original
  // CombinedCalculator implementation (`lot_explorer_*` fields).
  const [lotExplorerLotCount, setLotExplorerLotCount] = useState(0);
  const [lotExplorerRatePerLot, setLotExplorerRatePerLot] = useState(250);
  const [lotExplorerIncludePoi, setLotExplorerIncludePoi] = useState(true);
  const [lotExplorerIncludeCrm, setLotExplorerIncludeCrm] = useState(true);
  const [lotExplorerIncludeMedia, setLotExplorerIncludeMedia] = useState(true);
  const [lotExplorerIncludeBackend, setLotExplorerIncludeBackend] = useState(true);
  const [lotExplorerTimelineWeeks, setLotExplorerTimelineWeeks] = useState(4);
  const [lotExplorerHostingMonthly, setLotExplorerHostingMonthly] = useState(100);

  // When A La Carte is active, behave as if vexosEnabled is true for
  // gating phases 2-6 (so viz UI renders) but zero-out platform fees
  // downstream (see calc / calcSFH overrides further below).
  const vexosEffectiveEnabled = vexosEnabled || isAlacarte;
  // Conceptual design (the per-image renders and the interior design fee)
  // bills on every tab that carries a visualization proposal: VEXOS, À La
  // Carte, and Marketing while VEXOS is on (the combined proposal). It used to
  // be the two visualization tabs only, which silently dropped both from a
  // combined quote saved off the Marketing tab. Never on Lot Explorer or a
  // marketing-only quote, so a stale value can't bill there.
  const conceptualAvailable = !isLotExplorer && (activeTab !== "marketing" || vexosEnabled);

  // Which rendering rate card this quote bills off. Renderings sold INSIDE a
  // VEXOS engagement run at half the standalone rates (see `renderingTiers`
  // in ./vexosClientPricing) — À La Carte is a standalone visualization
  // proposal and keeps the full rate card. Renderings are the only line items
  // that differ; VR, floor plans, video and exterior add-ons are one-card.
  const vexosRenderPricing = vexosEnabled && !isAlacarte;
  // The four rendering tables resolved for that context. The per-render rate
  // hints and volume-tier badges in the build column read these so they always
  // show the same card the calc bills off. Recomputed on every render, which
  // covers a Pricing Admin save (it bumps `pricingVersion` state).
  const interiorRenderTiers  = renderingTiers(INTERIOR_RENDERING, vexosRenderPricing);
  const amenityRenderTiers   = renderingTiers(AMENITY_RENDERING, vexosRenderPricing);
  const homeRenderTiers      = renderingTiers(HOME_MODEL_RENDERING, vexosRenderPricing);
  const cAmenityRenderTiers  = renderingTiers(COMMUNITY_AMENITY_RENDERING, vexosRenderPricing);

  // Quick-save microstate so the sidebar Save button can flash "Saved".
  const [justSaved, setJustSaved] = useState(false);

  // Router awareness — `/sales/client-calculator/:quoteId` enters Edit
  // mode and hydrates state from the backend. `/new` starts blank.
  const routeParams = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const routeQuoteId = routeParams.quoteId || null;

  // Mark dirty whenever any tracked input changes after first hydration.
  // Used by the back-link guard. Conservative approach: any keystroke
  // anywhere on the calculator surfaces flips this on; a successful save
  // flips it off.
  const [dirty, setDirty] = useState(false);
  const markDirty = useCallback(() => setDirty(true), []);
  // Shared by the Phase 07 card and the sidebar row.
  const updateConceptualDesignFee = useCallback((next) => {
    setConceptualDesignFee(normalizeConceptualDesignFee(next));
    markDirty();
  }, [markDirty]);
  const updateModelingFee = useCallback((next) => {
    setModelingFee(normalizeModelingFee(next));
    markDirty();
  }, [markDirty]);
  const updateVrConfigurator = useCallback((sectionKey, nextSection) => {
    setVrConfigurator((prev) => normalizeVrConfigurator({ ...prev, [sectionKey]: nextSection }));
    markDirty();
  }, [markDirty]);

  // ── BUNDLE TOAST ─────────────────────────────────────────────────────
  // Fires a non-blocking, auto-fading banner when the tier the quote
  // qualifies for increases ("available" until the rep applies it).
  const [bundleToast, setBundleToast] = useState(null);
  const lastBundleTierRef = useRef(0);

  // Presentation-mode modal order for the current quote. The bundle panel
  // drops out whenever there's no bundle savings to show — every hand-priced
  // quote included — so arrows can't land on an empty "you save $0" screen.
  // Held in a ref because the keyboard handler below is wired up long before
  // the active calc is resolved further down the render.
  const sectionOrderRef = useRef(SECTION_ORDER);

  // ── PHASE 1 STATE ────────────────────────────────────────────────────
  const [floors, setFloors] = useState("");
  const [units, setUnits] = useState("");
  const [uniqueFloorPlans, setUniqueFloorPlans] = useState("");
  const [avgFloorPlanSqFt, setAvgFloorPlanSqFt] = useState("");
  const [amenitySpaces, setAmenitySpaces] = useState("");
  const [totalAmenitySqFt, setTotalAmenitySqFt] = useState("");
  const [contractTerm, setContractTerm] = useState(12);

  // ── VEXOS LOT EXPLORER™ CALC ──────────────────────────────────────
  // Per-lot pricing × lot count. Subtotal flows through 50 / 25 / 25
  // payment plan — same math as the original CombinedCalculator.
  const lotExplorerCalc = useMemo(() => {
    const BASE_FEE = 7500;
    const lots = Math.max(0, parseInt(lotExplorerLotCount) || 0);
    const rate = Math.max(0, parseFloat(lotExplorerRatePerLot) || 0);
    const lotSubtotal = lots * rate;
    const grandTotal = BASE_FEE + lotSubtotal;
    const features = [];
    if (lotExplorerIncludePoi) features.push("POI Widget");
    if (lotExplorerIncludeCrm) features.push("CRM Integration");
    if (lotExplorerIncludeMedia) features.push("Media Pop-up Support");
    if (lotExplorerIncludeBackend) features.push("Backend Management");
    const deposit = Math.round(grandTotal * 0.50);
    const v1 = Math.round(grandTotal * 0.25);
    const finalDelivery = Math.max(0, grandTotal - deposit - v1);
    return {
      baseFee: BASE_FEE,
      lotSubtotal,
      lotCount: lots,
      ratePerLot: rate,
      grandTotal,
      features,
      timelineWeeks: Math.max(1, parseInt(lotExplorerTimelineWeeks) || 4),
      hostingMonthly: Math.max(0, parseFloat(lotExplorerHostingMonthly) || 0),
      milestones: [
        { key: "deposit", percent: 50, label: "Deposit (Due at Signing)", amount: deposit, note: "Initiates project setup, sitemap preparation, backend configuration." },
        { key: "v1_delivery", percent: 25, label: "V1 Delivery", amount: v1, note: "Interactive sitemap deployed · zoom/pan operational · initial lot interaction · initial media pop-up." },
        { key: "final_delivery", percent: 25, label: "Final Delivery", amount: finalDelivery, note: "Finalized lot data · availability logic · CRM connection · POI implementation · final QA." },
      ],
    };
  }, [lotExplorerLotCount, lotExplorerRatePerLot, lotExplorerIncludePoi, lotExplorerIncludeCrm, lotExplorerIncludeMedia, lotExplorerIncludeBackend, lotExplorerTimelineWeeks, lotExplorerHostingMonthly]);

  // ── SFH-SPECIFIC PHASE 1 STATE ──────────────────────────────────────
  // SFH engagements use a completely different project-detail set —
  // the multifamily inputs (units, unique plans, avg sqft per plan,
  // amenity space count) don't apply to a single home. Instead we
  // collect: home sq ft, # of floors (reuses `floors` state), and total
  // outdoor/amenity sq ft. These are informational only — they don't
  // drive pricing tiers (SFH is flat-priced per Pricing Admin Section 1S).
  const [sfh_homeSqFt, setSfhHomeSqFt] = useState("");
  const [sfh_amenitySqFt, setSfhAmenitySqFt] = useState("");

  const phase1Complete = [floors, units, uniqueFloorPlans, avgFloorPlanSqFt, amenitySpaces, totalAmenitySqFt]
    .every(v => v !== "" && Number(v) >= 0 && (v === 0 || Number(v) > 0));
  // Allow amenity counts of 0 (no amenities). All others must be ≥ 1.
  const phase1Ready = floors !== "" && Number(floors) >= 1
    && units !== "" && Number(units) >= 1
    && uniqueFloorPlans !== "" && Number(uniqueFloorPlans) >= 1
    && avgFloorPlanSqFt !== "" && Number(avgFloorPlanSqFt) >= 1
    && amenitySpaces !== "" && Number(amenitySpaces) >= 0
    && totalAmenitySqFt !== "" && Number(totalAmenitySqFt) >= 0;

  // ── PHASE 2 STATE (Exterior) ─────────────────────────────────────────
  const [dayToNight, setDayToNight] = useState(false);
  // Keyframes receiving the dusk treatment. Tracks the full exterior
  // keyframe count by default (day-to-night is built into every orbit) but
  // the rep can dial it down for a partial pass.
  // Extra keyframes on the BASE orbit only — suborbit extras live in subOrbits.
  const [additionalKeyframesQty, setAdditionalKeyframesQty] = useState(0);
  const [animatedKeyframesEnabled, setAnimatedKeyframesEnabled] = useState(false);
  const [animatedKeyframesQty, setAnimatedKeyframesQty] = useState(BASE_ORBIT_KEYFRAMES);
  // One entry per suborbit, holding that orbit's EXTRA keyframes (0–2).
  const [subOrbits, setSubOrbits] = useState([]);
  const masterOrbitsQty = subOrbits.length;
  // Flat exterior renders quantity (recommendation = 2, starts at 0).
  const [exteriorRendersQty, setExteriorRendersQty] = useState(0);
  // Conceptual Rendering Hybrid Workflow — flat per-image quantity. VEXOS + À La
  // Carte; the math below zeroes it on every other tab.
  const [conceptualRendersQty, setConceptualRendersQty] = useState(0);
  // Exterior VR tour — single per-project point selection (null = none).
  const [exteriorVR, setExteriorVR] = useState(null);

  // VEXOS Cover Landing Page — flat $2,500 add-on (Condo).
  const [vexosCoverLandingPage, setVexosCoverLandingPage] = useState(false);

  // ── PHASE 05 STATE (Video Animation) — Condo ─────────────────────────
  // Add-on (Vexos bundle) video selection — 30 | 60 | 90 seconds, or null.
  // Each video kind is a LIST of durations (several videos, mixed lengths) —
  // see ./videoLineup. `aiMotionDur` / `cinematicDur` are the longest video,
  // kept for everything that only asks "is there one / how long".
  const [aiMotionVideos, setAiMotionVideos] = useState([]);
  const [cinematicVideos, setCinematicVideos] = useState([]);
  const aiMotionDur = primaryDuration(aiMotionVideos);
  const cinematicDur = primaryDuration(cinematicVideos);
  const setAiMotionDur = (d) => setAiMotionVideos(normalizeVideos(null, d));
  // STANDALONE video (À La Carte) is size-first: the project size dictates
  // which hero/master formats are offered, and Large unlocks the add-on menu.
  const [videoProjectSize, setVideoProjectSize] = useState("S");
  const [videoStandaloneSel, setVideoStandaloneSel] = useState({
    heroKey: null, unitInteriorQty: 0, penthouseQty: 0, socialCutdownQty: 0,
  });

  // ── PHASE 06 STATE (Floor Plans) — Condo ─────────────────────────────
  // One row per unique floor plan. Default 0; manual & "Apply to All" supported.
  const [floorPlans2D, setFloorPlans2D] = useState({});
  const [floorPlans3D, setFloorPlans3D] = useState({});
  // Levels per unit type, keyed by the same row index as floorPlans3D. One 3D
  // floor plan covers ONE unit type; a tri-level unit is still one unit type
  // but three levels of drawing work, so levels multiply the billable plans.
  // Missing/0 reads as 1 — every unit has at least one level.
  const [floorPlans3DLevels, setFloorPlans3DLevels] = useState({});
  // 3D workflow — "hybrid" (AI Hybrid, base rate) | "traditional" (+upcharge).
  const [floorPlans3DWorkflow, setFloorPlans3DWorkflow] = useState("hybrid");

  // Total keyframes across the base orbit and every suborbit — the pool that
  // day-to-night and animated keyframes are both counted against.
  const exteriorKfTotal = totalExteriorKeyframes(additionalKeyframesQty, subOrbits);

  // When the keyframe pool changes, follow it with the animated count IF the rep
  // hasn't manually overridden it (approximated by the current value still
  // matching the previous total). A manual reduction survives, and it is clamped
  // so it can never exceed the pool. Day-to-night is NOT here any more: it
  // derives from the orbit structure, so there is nothing to keep in step.
  const prevTotalKfRef = useRef(BASE_ORBIT_KEYFRAMES);
  useEffect(() => {
    const newTotalKf = exteriorKfTotal;
    const prev = prevTotalKfRef.current;
    setAnimatedKeyframesQty((q) => (q === prev ? newTotalKf : Math.min(q, newTotalKf)));
    prevTotalKfRef.current = newTotalKf;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exteriorKfTotal]);

  // ── PHASE 3 STATE (Interior) ─────────────────────────────────────────
  // Per-floor-plan render & VR counts. Keyed by index.
  const [interiorRenders, setInteriorRenders] = useState({});
  const [interiorVR, setInteriorVR] = useState({});

  const floorPlanCount = Number(uniqueFloorPlans) || 0;
  const recRender = recommendInteriorRenders(avgFloorPlanSqFt);
  const recVR = recommendInteriorVR(avgFloorPlanSqFt);
  // Exterior VR recommendation is a flat hint (shared across all modes).
  const recExtVR = recommendExteriorVR();

  // Initialize each floor plan row to 0 on first render. Recommendations are
  // shown as hints only — the rep must explicitly accept them via the per-row
  // input or the "Apply Recommended to All" button. Nothing counts toward the
  // running total until the rep actively makes a selection.
  useEffect(() => {
    if (floorPlanCount > 0) {
      const nextR = {}, nextV = {};
      for (let i = 0; i < floorPlanCount; i++) {
        nextR[i] = interiorRenders[i] != null ? interiorRenders[i] : 0;
        // VR is now a Yes/No toggle per plan — blank by default (no selection).
        nextV[i] = interiorVR[i] === undefined ? null : interiorVR[i];
      }
      setInteriorRenders(nextR);
      setInteriorVR(nextV);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorPlanCount]);

  // ── PHASE 4 STATE (Amenity) ─────────────────────────────────────────
  const [amenityRenders, setAmenityRenders] = useState({});
  const amenityCount = Number(amenitySpaces) || 0;
  const recAmenityRender = recommendAmenityRenders(totalAmenitySqFt, amenitySpaces);
  const recAmenityVRPoints = recommendAmenityVRPoints(totalAmenitySqFt, amenitySpaces);
  // Per-amenity VR selections (dict keyed by index). Each value: null | 0..10.
  const [amenityVR, setAmenityVR] = useState({});

  // Drone shot view add-on toggles (per VR section). Independent of the
  // auto-applied drone capture fee.
  // Drone & Reality Capture — single $5,000 add-on. Auto-appears in the
  // sidebar when ANY VR (interior or amenity) is selected. The two visible
  // toggles (one in the Interior VR section, one in the Amenity VR section)
  // are wired to the SAME state so toggling either updates both. Defaults
  // to ON so reps don't have to opt-in every time, but is fully toggleable
  // off to remove the $5,000 line entirely.
  const [interiorDroneShots, setInteriorDroneShots] = useState(true);
  const [amenityDroneShots, setAmenityDroneShots] = useState(true);
  // Helper — flips BOTH state vars together so the user sees a single
  // unified add-on regardless of which section they click the toggle in.
  const setDroneCaptureEnabled = (v) => {
    setInteriorDroneShots(!!v);
    setAmenityDroneShots(!!v);
  };

  useEffect(() => {
    if (amenityCount > 0) {
      const nextR = {}, nextV = {};
      for (let i = 0; i < amenityCount; i++) {
        // Initialize each amenity row to 0 — recommendation is a hint only.
        nextR[i] = amenityRenders[i] != null ? amenityRenders[i] : 0;
        // VR row: null = blank (no selection yet).
        nextV[i] = amenityVR[i] === undefined ? null : amenityVR[i];
      }
      setAmenityRenders(nextR);
      setAmenityVR(nextV);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amenityCount]);

  // ── DERIVED CALCULATIONS ────────────────────────────────────────────
  const calc = useMemo(() => {
    const unitN = Number(units) || 0;
    const setupFee = unitN > 0 ? getSetupFee(unitN) : 0;
    const monthlyRate = unitN > 0 ? getMonthlyRate(unitN, contractTerm) : 0;
    const recurringContractValue = monthlyRate * contractTerm;

    // Exterior addons
    // Day-to-night reads 1:1 off the keyframes each orbit INCLUDES — base orbit
    // 4 → $4,000, a suborbit 2 → $2,000 — with keyframes added on top at half
    // rate. Priced through the shared seam so the proposal and PDF agree.
    const d2n = dayToNight
      ? dayToNightBreakdown({
          baseExtraKf: additionalKeyframesQty,
          subOrbits,
          includedPerKf: EXTERIOR_ADDONS.dayToNight.pricePerKeyframe,
          extraPerKf: EXTERIOR_ADDONS.dayToNight.pricePerExtraKeyframe,
        })
      : null;
    const dayToNightCost = d2n ? d2n.total : 0;
    // Still saved on the quote: the backend proposal PDF and the ArchViz
    // quote-scope both read `dayToNightKfQty`. Derived now, not rep-editable.
    const dayToNightKf = d2n ? d2n.totalKf : 0;
    // Billed extras = base-orbit extras + every keyframe added to a suborbit.
    const additionalKfQty = billableExtraKeyframes(additionalKeyframesQty, subOrbits);
    const additionalKfCost = additionalKfQty * EXTERIOR_ADDONS.additionalKeyframes.pricePerKeyframe;
    const animatedKfCost = animatedKeyframesEnabled
      ? Math.min(Number(animatedKeyframesQty) || 0, exteriorKfTotal) * EXTERIOR_ADDONS.animatedKeyframes.pricePerKeyframe
      : 0;
    const masterOrbitCost = subOrbitCount(subOrbits) * EXTERIOR_ADDONS.masterOrbit.pricePerOrbit;
    const vexosCoverLandingPageCost = vexosCoverLandingPage ? (Number(EXTERIOR_ADDONS.vexosCoverLandingPage?.price) || 0) : 0;
    const exteriorAddonsSubtotal = dayToNightCost + additionalKfCost + animatedKfCost + masterOrbitCost + vexosCoverLandingPageCost;

    // Interior renders
    const totalInteriorRenders = Object.values(interiorRenders).reduce((s, n) => s + (Number(n) || 0), 0);

    // Exterior renders — flat quantity, priced from the SAME volume tier as
    // interior renders. Combined render pool determines the tier rate that
    // both pools pay (so 8 interior + 2 exterior = 10 total, all at the
    // tier-for-10 rate).
    const exteriorRendersN = Number(exteriorRendersQty) || 0;
    const combinedRenderCount = totalInteriorRenders + exteriorRendersN;
    const combinedRenderRate = lookupVolumeRate(combinedRenderCount, renderingTiers(INTERIOR_RENDERING, vexosRenderPricing));
    const interiorRendersCost = totalInteriorRenders * combinedRenderRate;
    const exteriorRendersCost = exteriorRendersN * combinedRenderRate;

    // Conceptual Rendering Hybrid Workflow — flat per-image rate, its own line on
    // the VEXOS and À La Carte tabs. Gated here, not only in the UI, so a
    // quantity that survived a switch to another tab can never bill there.
    const conceptualRate = Number(CONCEPTUAL_MODELING.pricePerRender) || 0;
    const conceptualRendersN = conceptualAvailable ? Math.max(0, Number(conceptualRendersQty) || 0) : 0;
    const conceptualRendersCost = conceptualRendersN * conceptualRate;

    // Exterior VR — single per-project point tour. Cost = points × tiered
    // per-point rate (own rate table, priced like Interior VR).
    const exteriorVRPoints = Number(exteriorVR) || 0;
    const exteriorVRActive = exteriorVRPoints > 0;
    const exteriorVRCost = exteriorVRPoints * lookupVolumeRate(exteriorVRPoints, EXTERIOR_VR.volumeTiers);

    // Interior VR — point selector per plan (0/2/4/6/8/10).
    // Cost = sum of selected points × pricePerPoint. A row is a "tour" if points > 0.
    const interiorVRPointsSum = Object.values(interiorVR).reduce((s, v) => s + (Number(v) || 0), 0);
    const totalInteriorVR = Object.values(interiorVR).filter(v => Number(v) > 0).length;
    const interiorVRCost = interiorVRPointsSum * lookupVolumeRate(interiorVRPointsSum, INTERIOR_VR.volumeTiers);

    // Amenity renders
    const totalAmenityRenders = Object.values(amenityRenders).reduce((s, n) => s + (Number(n) || 0), 0);
    const amenityRendersCost = totalAmenityRenders * lookupVolumeRate(totalAmenityRenders, renderingTiers(AMENITY_RENDERING, vexosRenderPricing));

    // Amenity VR — per-space point selector. Sum across all amenity spaces.
    const amenityVRPointsSum = Object.values(amenityVR).reduce((s, v) => s + (Number(v) || 0), 0);
    const totalAmenityVR = Object.values(amenityVR).filter(v => Number(v) > 0).length;
    const amenityVRCost = Object.values(amenityVR).reduce((s, v) => s + amenityVRPackagePrice(v), 0);

    const amenitySubtotal = amenityRendersCost + amenityVRCost;

    // Real-Time Configurator — its own line per VR section, only while that
    // section has a tour. See ./vrConfigurator.
    const configurator = vrConfiguratorFields(vrConfigurator, {
      residenceActive: totalInteriorVR > 0,
      amenityActive: totalAmenityVR > 0,
      pricing: VR_CONFIGURATOR,
    });
    const vrConfiguratorCost = configurator.vrConfiguratorCost;

    // ── Drone (project-wide) ──────────────────────────────────────────
    // Capture fee auto-applies when any VR tour (interior or amenity) is
    // active. Single $5,000 line; does not stack. Two optional add-on
    // toggles for "Drone Shot Views" — one per VR section.
    const anyInteriorVRActive = totalInteriorVR > 0;
    const anyAmenityVRActive  = totalAmenityVR  > 0;
    const anyExteriorVRActive = exteriorVRActive;
    // Drone & Reality Capture — single $5,000 add-on. Active when ANY
    // VR is selected AND the user-controlled toggle is ON. Toggle defaults
    // to true so the fee auto-appears the moment VR is enabled, but reps
    // can flip it off to zero out the line.
    const droneCaptureEnabled = interiorDroneShots || amenityDroneShots;
    const droneCaptureActive  = (anyInteriorVRActive || anyAmenityVRActive || anyExteriorVRActive) && droneCaptureEnabled;
    const droneCaptureFee     = droneCaptureActive ? (Number(DRONE.captureFee) || 0) : 0;
    // Cinematic shot add-ons were folded into the single capture fee above
    // — keep these zero so the math still balances downstream.
    const interiorDroneShotsCost = 0;
    const amenityDroneShotsCost  = 0;
    const droneSubtotal = droneCaptureFee;

    // (Video Animation is computed after Floor Plans below — it needs to
    //  know whether any OTHER product/service is in the quote to decide
    //  between standalone and add-on pricing.)

    // ── Floor Plans (Phase 06) — independent 2D & 3D volume tiers ──────
    const total2DPlans = Object.values(floorPlans2D).reduce((s, n) => s + (Number(n) || 0), 0);
    // 3D bills per LEVEL: one unit type with three levels is three plans of
    // work, so levels drive both the price and the volume tier.
    const fp3DTotals = floorPlanLevelTotals(floorPlans3D, floorPlans3DLevels, floorPlanCount);
    const total3DPlans = fp3DTotals.billablePlans;
    const total3DUnits = fp3DTotals.unitTypes;
    const total3DLevels = fp3DTotals.totalLevels;
    const fp2D = floorPlanVolumeDiscount(total2DPlans, FLOOR_PLANS.price2D);
    const fp3D = floorPlanVolumeDiscount(total3DPlans, floorPlan3DRate(floorPlans3DWorkflow));
    const floorPlans2DGross = fp2D.gross;
    const floorPlans2DPct = fp2D.pct;
    const floorPlans2DSavings = fp2D.savings;
    const floorPlans2DCost = fp2D.net;
    const floorPlans3DGross = fp3D.gross;
    const floorPlans3DPct = fp3D.pct;
    const floorPlans3DSavings = fp3D.savings;
    const floorPlans3DCost = fp3D.net;
    const floorPlansCost = floorPlans2DCost + floorPlans3DCost;

    // ── Video Animation (Phase 05) ─────────────────────────────────────
    // AI Motion is render-gated: it only becomes selectable when the
    // proposal carries at least 1 exterior render AND 3 interior renders.
    // À La Carte can bypass the gate when the client supplies their own
    // renderings (rep attests via the Phase-05 checkbox).
    const aiMotionRenderGateMet = exteriorRendersN >= 1 && totalInteriorRenders >= 3;
    const aiMotionEligible = aiMotionRenderGateMet || (isAlacarte && aiMotionClientRenders);
    const aiMotionCost = aiMotionEligible
      ? videosCost(aiMotionVideos, VIDEO_ANIMATION.aiMotion.durations)
      : 0;

    // Standalone vs add-on. A video sold on its own — À La Carte, no Vexos
    // platform and no other visualization products in the quote — uses the
    // SIZE-FIRST standalone model (size dictates format; Large unlocks the
    // add-on menu). Sold alongside anything else it keeps the add-on price.
    // Standalone only exists in the À La Carte tab (never a VEXOS engagement),
    // so the VEXOS and SFH platform tabs always price the video as an add-on.
    const otherVizScope =
      exteriorAddonsSubtotal + exteriorRendersCost + conceptualRendersCost + exteriorVRCost +
      interiorRendersCost + interiorVRCost + amenitySubtotal + vrConfiguratorCost + droneSubtotal +
      floorPlansCost;
    const hasOtherScope = otherVizScope > 0;
    const standaloneContext = isAlacarte && !hasOtherScope;

    // Standalone breakdown (typed line items: animation vs editing).
    const standaloneVideo = standaloneContext
      ? computeStandaloneVideo({ size: videoProjectSize, ...videoStandaloneSel })
      : null;
    const videoStandalone = !!standaloneVideo && standaloneVideo.total > 0;

    const cinematicCost = standaloneContext
      ? (standaloneVideo ? standaloneVideo.total : 0)
      : videosCost(cinematicVideos, VIDEO_ANIMATION.cinematic.durations);
    const videoAnimationGross = aiMotionCost + cinematicCost;
    // Motion Package: both products selected → discount on combined video.
    // Never applies in standalone mode (AI Motion needs renders, which would
    // themselves count as other scope and disable standalone pricing).
    const motionPackageActive = !!(
      VIDEO_ANIMATION.motionPackage.enabled &&
      aiMotionCost > 0 &&
      cinematicDur != null &&
      !videoStandalone
    );
    const motionPackagePct = motionPackageActive ? (Number(VIDEO_ANIMATION.motionPackage.pct) || 0) : 0;
    const motionPackageSavings = Math.round(videoAnimationGross * (motionPackagePct / 100));
    const videoAnimationCost = videoAnimationGross - motionPackageSavings;

    // ── Visualization gross + Bundle eval ─────────────────────────────
    const vizGross = exteriorAddonsSubtotal + exteriorRendersCost + conceptualRendersCost + exteriorVRCost + interiorRendersCost + interiorVRCost + amenitySubtotal + vrConfiguratorCost + droneSubtotal + videoAnimationCost + floorPlansCost;
    const moduleActive = {
      // Conceptual modeling is exterior scope, not a seventh module — it
      // rides the exterior flag so it can never inflate the bundle tier.
      exterior: exteriorAddonsSubtotal > 0 || exteriorRendersN > 0 || conceptualRendersN > 0 || exteriorVRActive,
      interiorRender: totalInteriorRenders > 0,
      interiorVR: totalInteriorVR > 0,
      amenity: totalAmenityRenders > 0 || totalAmenityVR > 0,
      video: videoAnimationCost > 0,
      floorPlans: floorPlansCost > 0,
    };
    const activeCount = Object.values(moduleActive).filter(Boolean).length;
    // What the module count qualifies for vs. what the rep applied.
    const bundleOffer = evaluateBundle(activeCount);
    const bundle = appliedBundle(bundleOffer, bundleApplied);
    const bundleSavings = Math.round(vizGross * (bundle.pct / 100));
    const vizNet = vizGross - bundleSavings;

    // ── Additional revision rounds (% of net viz scope, per round) ──────
    const revisionRounds = Math.max(0, Math.round(Number(additionalRevisions) || 0));
    const revisionUnitCost = revisionRoundCost(vizNet);
    const revisionsCost = revisionUnitCost * revisionRounds;

    // ── On-site travel (flat per-day rate, capped) ─────────────────────
    const travelDaysN = Math.min(Number(TRAVEL_CONFIG.maxDays) || 0, Math.max(0, Math.round(Number(travelDays) || 0)));
    const travelDayRate = Number(TRAVEL_CONFIG.perDayRate) || 0;
    const travelCost = travelDaysN * travelDayRate;

    const oneTimeAllIn = setupFee + vizNet + revisionsCost + travelCost;
    const grandTotal = oneTimeAllIn + recurringContractValue;

    return {
      setupFee,
      monthlyRate,
      recurringContractValue,
      // Additional revisions + travel
      revisionRounds,
      revisionUnitCost,
      revisionsCost,
      travelDays: travelDaysN,
      travelDayRate,
      travelCost,
      dayToNightCost,
      dayToNightKfQty: dayToNightKf,
      dayToNightDetail: d2n,
      additionalKfCost,
      additionalKfQty,
      animatedKfCost,
      masterOrbitCost,
      // Orbit/keyframe structure — read by the scope surfaces and the PDF.
      masterOrbitsQty: subOrbitCount(subOrbits),
      subOrbitExtraKfQty: subOrbitExtraKf(subOrbits),
      baseOrbitKeyframes: BASE_ORBIT_KEYFRAMES + (Number(additionalKeyframesQty) || 0),
      totalExteriorKeyframes: exteriorKfTotal,
      vexosCoverLandingPageCost,
      vexosCoverLandingPageActive: vexosCoverLandingPage,
      exteriorAddonsSubtotal,
      exteriorRendersQty: exteriorRendersN,
      exteriorRendersCost,
      // Conceptual Rendering Hybrid Workflow (VEXOS + À La Carte)
      conceptualRendersQty: conceptualRendersN,
      conceptualRenderRate: conceptualRate,
      conceptualRendersCost,
      // Exterior VR tour (single per-project)
      exteriorVRPoints,
      exteriorVRActive,
      exteriorVRCost,
      // Video Animation (Phase 05)
      aiMotionDur,
      aiMotionVideos: aiMotionEligible ? aiMotionVideos : [],
      aiMotionCost,
      aiMotionEligible,
      aiMotionRenderGateMet,
      // Effective "client supplies the renderings" flag (à la carte bypass).
      aiMotionClientRenders: isAlacarte && aiMotionClientRenders && !aiMotionRenderGateMet,
      cinematicDur,
      cinematicVideos: standaloneContext ? [] : cinematicVideos,
      cinematicCost,
      videoStandalone,
      standaloneContext,
      standaloneVideo,
      videoProjectSize,
      videoAnimationGross,
      videoAnimationCost,
      motionPackageActive,
      motionPackagePct,
      motionPackageSavings,
      hasOtherScope,
      // Floor Plans (Phase 06)
      total2DPlans,
      floorPlans2DGross,
      floorPlans2DPct,
      floorPlans2DSavings,
      floorPlans2DCost,
      total3DPlans,
      total3DUnits,
      total3DLevels,
      floorPlans3DWorkflow,
      floorPlans3DGross,
      floorPlans3DPct,
      floorPlans3DSavings,
      floorPlans3DCost,
      floorPlansCost,
      totalInteriorRenders,
      interiorRendersCost,
      totalInteriorVR,
      interiorVRPointsSum,
      interiorVRCost,
      totalAmenityRenders,
      amenityRendersCost,
      totalAmenityVR,
      amenityVRPointsSum,
      amenityVRCost,
      amenitySubtotal,
      ...configurator,
      // Drone (project-wide)
      droneCaptureActive,
      droneCaptureFee,
      anyInteriorVRActive,
      anyAmenityVRActive,
      anyExteriorVRActive,
      interiorDroneShotsActive: anyInteriorVRActive && interiorDroneShots,
      interiorDroneShotsCost,
      amenityDroneShotsActive: anyAmenityVRActive && amenityDroneShots,
      amenityDroneShotsCost,
      droneSubtotal,
      vizGross,
      vizNet,
      bundle,
      bundleOffer,
      bundleApplied,
      bundleSavings,
      moduleActive,
      activeCount,
      oneTimeAllIn,
      grandTotal,
    };
  }, [units, contractTerm, dayToNight, additionalKeyframesQty, animatedKeyframesEnabled, animatedKeyframesQty,
      subOrbits, exteriorKfTotal, exteriorRendersQty, conceptualRendersQty, exteriorVR, interiorRenders, interiorVR, amenityRenders, amenityVR, vrConfigurator,
      aiMotionDur, aiMotionVideos, aiMotionClientRenders, cinematicDur, cinematicVideos, videoProjectSize, videoStandaloneSel, isAlacarte, conceptualAvailable, floorPlans2D, floorPlans3D, floorPlans3DLevels, floorPlans3DWorkflow, floorPlanCount,
      interiorDroneShots, amenityDroneShots, vexosCoverLandingPage,
      additionalRevisions, travelDays, bundleApplied, vexosRenderPricing, pricingVersion]);

  /* ════════════════════════════════════════════════════════════════════
   *  COMMUNITY VARIANT — fully isolated state + derived calculations.
   *  Condo state above is untouched. Sidebar + export bar branch on `mode`.
   * ═══════════════════════════════════════════════════════════════════ */

  // ── COMMUNITY · PHASE 1 STATE ────────────────────────────────────────
  const [c_lots, setCLots] = useState("");
  const [c_homeModels, setCHomeModels] = useState("");
  const [c_avgHomeSqFt, setCAvgHomeSqFt] = useState("");
  const [c_amenitySpaces, setCAmenitySpaces] = useState("");
  const [c_totalAmenitySqFt, setCTotalAmenitySqFt] = useState("");
  const [c_contractTerm, setCContractTerm] = useState(12);
  // Master Plan — a community sold as the expanded package (see
  // MASTER_PLAN_* in vexosClientPricing). Rides on community mode and state.
  const [c_masterPlan, setCMasterPlan] = useState(false);

  // Home models are optional — a lot-sales community may have none (0 or
  // blank). The average sq ft only matters once there's a model to size.
  const c_phase1Ready = c_lots !== "" && Number(c_lots) >= 1
    && (!(Number(c_homeModels) > 0) || (c_avgHomeSqFt !== "" && Number(c_avgHomeSqFt) >= 1))
    && c_amenitySpaces !== "" && Number(c_amenitySpaces) >= 0
    && c_totalAmenitySqFt !== "" && Number(c_totalAmenitySqFt) >= 0;

  // ── COMMUNITY · PHASE 2 STATE (Exterior) ─────────────────────────────
  const [c_dayToNight, setCDayToNight] = useState(false);
  const [c_additionalKfQty, setCAdditionalKfQty] = useState(0);
  const [c_animatedKfEnabled, setCAnimatedKfEnabled] = useState(false);
  const [c_animatedKfQty, setCAnimatedKfQty] = useState(BASE_ORBIT_KEYFRAMES);
  // One entry per suborbit, holding that orbit's EXTRA keyframes (0–2).
  const [c_subOrbits, setCSubOrbits] = useState([]);
  const c_masterOrbitsQty = c_subOrbits.length;
  // Community ⇄ Master Plan. Turning the master plan on puts its included
  // suborbits in front of any the rep already added; turning it off takes
  // them back out, so the rep's own Additional Orbits survive either way.
  const selectCommunityType = (masterPlan) => {
    setMode("community");
    if (masterPlan === c_masterPlan) return;
    setCMasterPlan(masterPlan);
    setCSubOrbits((cur) => (masterPlan
      ? [...masterPlanDefaultSubOrbits(), ...cur].slice(0, MASTER_PLAN_INCLUDED_SUBORBITS + MAX_SUBORBITS)
      : cur.slice(MASTER_PLAN_INCLUDED_SUBORBITS)));
  };
  // Flat exterior renders quantity (recommendation = 2, starts at 0).
  const [c_exteriorRendersQty, setCExteriorRendersQty] = useState(0);
  // Conceptual Rendering Hybrid Workflow — see the condo note.
  const [c_conceptualRendersQty, setCConceptualRendersQty] = useState(0);
  // Exterior VR tour — single per-project point selection (null = none).
  const [c_exteriorVR, setCExteriorVR] = useState(null);

  // VEXOS Cover Landing Page — flat $2,500 add-on (Community).
  const [c_vexosCoverLandingPage, setCVexosCoverLandingPage] = useState(false);

  // ── COMMUNITY · PHASE 05 STATE (Video Animation) ────────────────────
  const [c_aiMotionVideos, setCAiMotionVideos] = useState([]);
  const [c_cinematicVideos, setCCinematicVideos] = useState([]);
  const c_aiMotionDur = primaryDuration(c_aiMotionVideos);
  const c_cinematicDur = primaryDuration(c_cinematicVideos);
  const setCAiMotionDur = (d) => setCAiMotionVideos(normalizeVideos(null, d));
  const [c_videoProjectSize, setCVideoProjectSize] = useState("S");
  const [c_videoStandaloneSel, setCVideoStandaloneSel] = useState({
    heroKey: null, unitInteriorQty: 0, penthouseQty: 0, socialCutdownQty: 0,
  });

  // ── COMMUNITY · PHASE 06 STATE (Floor Plans) ────────────────────────
  const [c_floorPlans2D, setCFloorPlans2D] = useState({});
  const [c_floorPlans3D, setCFloorPlans3D] = useState({});
  // Levels per home model — see the Condo note on floorPlans3DLevels.
  const [c_floorPlans3DLevels, setCFloorPlans3DLevels] = useState({});
  const [c_floorPlans3DWorkflow, setCFloorPlans3DWorkflow] = useState("hybrid");
  const c_exteriorKfTotal = totalExteriorKeyframes(c_additionalKfQty, c_subOrbits);
  const c_prevTotalKfRef = useRef(BASE_ORBIT_KEYFRAMES);
  useEffect(() => {
    const newTotalKf = c_exteriorKfTotal;
    const prev = c_prevTotalKfRef.current;
    setCAnimatedKfQty((q) => (q === prev ? newTotalKf : Math.min(q, newTotalKf)));
    c_prevTotalKfRef.current = newTotalKf;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c_exteriorKfTotal]);

  // ── COMMUNITY · PHASE 3 STATE (Home Models) ──────────────────────────
  const [c_homeModelRenders, setCHomeModelRenders] = useState({});
  const [c_homeModelVR, setCHomeModelVR] = useState({});
  const c_homeModelCount = Number(c_homeModels) || 0;
  const c_recHomeRender = recommendHomeModelRenders(c_avgHomeSqFt);
  const c_recHomeVR = recommendHomeModelVR(c_avgHomeSqFt);
  useEffect(() => {
    if (c_homeModelCount > 0) {
      const nextR = {}, nextV = {};
      for (let i = 0; i < c_homeModelCount; i++) {
        nextR[i] = c_homeModelRenders[i] != null ? c_homeModelRenders[i] : 0;
        // VR is now a Yes/No toggle per home model — blank by default.
        nextV[i] = c_homeModelVR[i] === undefined ? null : c_homeModelVR[i];
      }
      setCHomeModelRenders(nextR);
      setCHomeModelVR(nextV);
    } else {
      // Dropped to 0 home models — clear any renders/VR picked earlier so
      // they stop pricing into the quote.
      setCHomeModelRenders({});
      setCHomeModelVR({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c_homeModelCount]);

  // ── COMMUNITY · PHASE 4 STATE (Amenity Buildings/Spaces) ─────────────
  const [c_amenityRenders, setCAmenityRenders] = useState({});
  const c_amenityCount = Number(c_amenitySpaces) || 0;
  const c_recAmenityRender = recommendCommunityAmenityRenders(c_totalAmenitySqFt, c_amenitySpaces);
  const c_recAmenityVRPoints = recommendCommunityAmenityVRPoints(c_totalAmenitySqFt, c_amenitySpaces);
  // Per-amenity VR selections (dict keyed by index). Each value: null | 0..10.
  const [c_amenityVR, setCAmenityVR] = useState({});

  // Community drone shot view toggles — same semantics as Condo.
  // Same single-add-on model on the community side (see condo notes above).
  const [c_interiorDroneShots, setCInteriorDroneShots] = useState(true);
  const [c_amenityDroneShots, setCAmenityDroneShots] = useState(true);
  const setCDroneCaptureEnabled = (v) => {
    setCInteriorDroneShots(!!v);
    setCAmenityDroneShots(!!v);
  };
  useEffect(() => {
    if (c_amenityCount > 0) {
      const nextR = {}, nextV = {};
      for (let i = 0; i < c_amenityCount; i++) {
        nextR[i] = c_amenityRenders[i] != null ? c_amenityRenders[i] : 0;
        nextV[i] = c_amenityVR[i] === undefined ? null : c_amenityVR[i];
      }
      setCAmenityRenders(nextR);
      setCAmenityVR(nextV);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c_amenityCount]);

  // ── COMMUNITY · DERIVED CALCULATIONS ─────────────────────────────────
  const calcCommunity = useMemo(() => {
    const lotN = Number(c_lots) || 0;
    // Master plan: flat setup fee; the monthly still follows the unit tiers.
    const setupFee = lotN > 0 ? (c_masterPlan ? getMasterPlanSetupFee() : getCommunitySetupFee(lotN)) : 0;
    const monthlyRate = lotN > 0 ? getCommunityMonthlyRate(lotN, c_contractTerm) : 0;
    const recurringContractValue = monthlyRate * c_contractTerm;

    // Day-to-night — same two-tier rule as the Condo calc above.
    const d2n = c_dayToNight
      ? dayToNightBreakdown({
          baseExtraKf: c_additionalKfQty,
          subOrbits: c_subOrbits,
          includedPerKf: COMMUNITY_EXTERIOR_ADDONS.dayToNight.pricePerKeyframe,
          extraPerKf: COMMUNITY_EXTERIOR_ADDONS.dayToNight.pricePerExtraKeyframe,
        })
      : null;
    const dayToNightCost = d2n ? d2n.total : 0;
    const dayToNightKf = d2n ? d2n.totalKf : 0;
    // A master plan's included suborbits (and their keyframes) are in the package.
    const billableSubOrbits = c_masterPlan
      ? masterPlanBillableSubOrbits(c_subOrbits)
      : { orbits: subOrbitCount(c_subOrbits), extraKf: subOrbitExtraKf(c_subOrbits) };
    const additionalKfQty = (Number(c_additionalKfQty) || 0) + billableSubOrbits.extraKf;
    const additionalKfCost = additionalKfQty * COMMUNITY_EXTERIOR_ADDONS.additionalKeyframes.pricePerKeyframe;
    const animatedKfCost = c_animatedKfEnabled
      ? Math.min(Number(c_animatedKfQty) || 0, c_exteriorKfTotal) * COMMUNITY_EXTERIOR_ADDONS.animatedKeyframes.pricePerKeyframe
      : 0;
    const masterOrbitCost = billableSubOrbits.orbits * COMMUNITY_EXTERIOR_ADDONS.masterOrbit.pricePerOrbit;
    const vexosCoverLandingPageCost = c_vexosCoverLandingPage ? (Number(COMMUNITY_EXTERIOR_ADDONS.vexosCoverLandingPage?.price) || 0) : 0;
    const exteriorAddonsSubtotal = dayToNightCost + additionalKfCost + animatedKfCost + masterOrbitCost + vexosCoverLandingPageCost;

    const totalHomeRenders = Object.values(c_homeModelRenders).reduce((s, n) => s + (Number(n) || 0), 0);

    // Exterior renders — flat quantity, priced from the SAME volume tier as
    // home-model renders. Combined render pool determines the tier rate.
    const c_exteriorRendersN = Number(c_exteriorRendersQty) || 0;
    const c_combinedRenderCount = totalHomeRenders + c_exteriorRendersN;
    const c_combinedRenderRate = lookupVolumeRate(c_combinedRenderCount, renderingTiers(HOME_MODEL_RENDERING, vexosRenderPricing));
    const homeRendersCost = totalHomeRenders * c_combinedRenderRate;
    const exteriorRendersCost = c_exteriorRendersN * c_combinedRenderRate;

    // Conceptual Rendering Hybrid Workflow — see the condo memo.
    const conceptualRate = Number(CONCEPTUAL_MODELING.pricePerRender) || 0;
    const conceptualRendersN = conceptualAvailable ? Math.max(0, Number(c_conceptualRendersQty) || 0) : 0;
    const conceptualRendersCost = conceptualRendersN * conceptualRate;

    // Exterior VR — single per-project point tour (community exterior).
    const exteriorVRPoints = Number(c_exteriorVR) || 0;
    const exteriorVRActive = exteriorVRPoints > 0;
    const exteriorVRCost = exteriorVRPoints * lookupVolumeRate(exteriorVRPoints, EXTERIOR_VR.volumeTiers);

    // Home Model VR — point selector per model (0/2/4/6/8/10).
    const homeVRPointsSum = Object.values(c_homeModelVR).reduce((s, v) => s + (Number(v) || 0), 0);
    const totalHomeVR = Object.values(c_homeModelVR).filter(v => Number(v) > 0).length;
    const homeVRCost = homeVRPointsSum * lookupVolumeRate(homeVRPointsSum, HOME_MODEL_VR.volumeTiers);

    const totalAmenityRenders = Object.values(c_amenityRenders).reduce((s, n) => s + (Number(n) || 0), 0);
    const amenityRendersCost = totalAmenityRenders * lookupVolumeRate(totalAmenityRenders, renderingTiers(COMMUNITY_AMENITY_RENDERING, vexosRenderPricing));

    const c_amenityVRPointsSum = Object.values(c_amenityVR).reduce((s, v) => s + (Number(v) || 0), 0);
    const totalAmenityVR = Object.values(c_amenityVR).filter(v => Number(v) > 0).length;
    const amenityVRCost = Object.values(c_amenityVR).reduce((s, v) => s + amenityVRPackagePrice(v), 0);

    const amenitySubtotal = amenityRendersCost + amenityVRCost;

    // Real-Time Configurator — residence = the home-model VR. See condo memo.
    const configurator = vrConfiguratorFields(vrConfigurator, {
      residenceActive: totalHomeVR > 0,
      amenityActive: totalAmenityVR > 0,
      pricing: VR_CONFIGURATOR,
    });
    const vrConfiguratorCost = configurator.vrConfiguratorCost;

    // ── Drone (project-wide) — Community variant ─────────────────────
    const anyInteriorVRActive = totalHomeVR > 0;
    const anyAmenityVRActive  = totalAmenityVR > 0;
    const anyExteriorVRActive = exteriorVRActive;
    // See condo droneCapture notes above — same single-add-on model.
    const droneCaptureEnabled = c_interiorDroneShots || c_amenityDroneShots;
    const droneCaptureActive  = (anyInteriorVRActive || anyAmenityVRActive || anyExteriorVRActive) && droneCaptureEnabled;
    const droneCaptureFee     = droneCaptureActive ? (Number(DRONE.captureFee) || 0) : 0;
    const interiorDroneShotsCost = 0;
    const amenityDroneShotsCost  = 0;
    const droneSubtotal = droneCaptureFee;

    // (Video Animation is computed after Floor Plans below — see condo memo
    //  notes; it needs to know whether any OTHER product/service is present.)

    // ── Floor Plans (Phase 06) — independent 2D & 3D volume tiers ──────
    const total2DPlans = Object.values(c_floorPlans2D).reduce((s, n) => s + (Number(n) || 0), 0);
    // Levels bill as plans — see the Condo calc above.
    const fp3DTotals = floorPlanLevelTotals(c_floorPlans3D, c_floorPlans3DLevels, c_homeModelCount);
    const total3DPlans = fp3DTotals.billablePlans;
    const total3DUnits = fp3DTotals.unitTypes;
    const total3DLevels = fp3DTotals.totalLevels;
    const fp2D = floorPlanVolumeDiscount(total2DPlans, FLOOR_PLANS.price2D);
    const fp3D = floorPlanVolumeDiscount(total3DPlans, floorPlan3DRate(c_floorPlans3DWorkflow));
    const floorPlans2DGross = fp2D.gross;
    const floorPlans2DPct = fp2D.pct;
    const floorPlans2DSavings = fp2D.savings;
    const floorPlans2DCost = fp2D.net;
    const floorPlans3DGross = fp3D.gross;
    const floorPlans3DPct = fp3D.pct;
    const floorPlans3DSavings = fp3D.savings;
    const floorPlans3DCost = fp3D.net;
    const floorPlansCost = floorPlans2DCost + floorPlans3DCost;

    // ── Video Animation (Phase 05) ─────────────────────────────────────
    // AI Motion is render-gated: at least 1 exterior render AND 3 interior
    // (home model) renders must be present for it to become selectable.
    // À La Carte can bypass the gate when the client supplies their own
    // renderings (rep attests via the Phase-05 checkbox).
    const aiMotionRenderGateMet = c_exteriorRendersN >= 1 && totalHomeRenders >= 3;
    const aiMotionEligible = aiMotionRenderGateMet || (isAlacarte && aiMotionClientRenders);
    const aiMotionCost = aiMotionEligible
      ? videosCost(c_aiMotionVideos, VIDEO_ANIMATION.aiMotion.durations)
      : 0;

    // Standalone vs add-on — see condo memo. À La Carte with no other viz
    // scope uses the SIZE-FIRST standalone model; everywhere else the video
    // stays a Vexos add-on.
    const otherVizScope =
      exteriorAddonsSubtotal + exteriorRendersCost + conceptualRendersCost + exteriorVRCost +
      homeRendersCost + homeVRCost + amenitySubtotal + vrConfiguratorCost + droneSubtotal +
      floorPlansCost;
    const hasOtherScope = otherVizScope > 0;
    const standaloneContext = isAlacarte && !hasOtherScope;

    const standaloneVideo = standaloneContext
      ? computeStandaloneVideo({ size: c_videoProjectSize, ...c_videoStandaloneSel })
      : null;
    const videoStandalone = !!standaloneVideo && standaloneVideo.total > 0;

    const cinematicCost = standaloneContext
      ? (standaloneVideo ? standaloneVideo.total : 0)
      : videosCost(c_cinematicVideos, VIDEO_ANIMATION.cinematic.durations);
    const videoAnimationGross = aiMotionCost + cinematicCost;
    const motionPackageActive = !!(
      VIDEO_ANIMATION.motionPackage.enabled &&
      aiMotionCost > 0 &&
      c_cinematicDur != null &&
      !videoStandalone
    );
    const motionPackagePct = motionPackageActive ? (Number(VIDEO_ANIMATION.motionPackage.pct) || 0) : 0;
    const motionPackageSavings = Math.round(videoAnimationGross * (motionPackagePct / 100));
    const videoAnimationCost = videoAnimationGross - motionPackageSavings;

    // ── Visualization gross + Bundle eval ─────────────────────────────
    const vizGross = exteriorAddonsSubtotal + exteriorRendersCost + conceptualRendersCost + exteriorVRCost + homeRendersCost + homeVRCost + amenitySubtotal + vrConfiguratorCost + droneSubtotal + videoAnimationCost + floorPlansCost;
    const moduleActive = {
      exterior: exteriorAddonsSubtotal > 0 || c_exteriorRendersN > 0 || conceptualRendersN > 0 || exteriorVRActive,
      interiorRender: totalHomeRenders > 0,
      interiorVR: totalHomeVR > 0,
      amenity: totalAmenityRenders > 0 || totalAmenityVR > 0,
      video: videoAnimationCost > 0,
      floorPlans: floorPlansCost > 0,
    };
    const activeCount = Object.values(moduleActive).filter(Boolean).length;
    // What the module count qualifies for vs. what the rep applied.
    const bundleOffer = evaluateBundle(activeCount);
    const bundle = appliedBundle(bundleOffer, bundleApplied);
    const bundleSavings = Math.round(vizGross * (bundle.pct / 100));
    const vizNet = vizGross - bundleSavings;

    // ── Additional revision rounds (% of net viz scope, per round) ──────
    const revisionRounds = Math.max(0, Math.round(Number(additionalRevisions) || 0));
    const revisionUnitCost = revisionRoundCost(vizNet);
    const revisionsCost = revisionUnitCost * revisionRounds;

    // ── On-site travel (flat per-day rate, capped) ─────────────────────
    const travelDaysN = Math.min(Number(TRAVEL_CONFIG.maxDays) || 0, Math.max(0, Math.round(Number(travelDays) || 0)));
    const travelDayRate = Number(TRAVEL_CONFIG.perDayRate) || 0;
    const travelCost = travelDaysN * travelDayRate;

    const oneTimeAllIn = setupFee + vizNet + revisionsCost + travelCost;
    const grandTotal = oneTimeAllIn + recurringContractValue;

    return {
      setupFee,
      monthlyRate,
      recurringContractValue,
      // Additional revisions + travel
      revisionRounds,
      revisionUnitCost,
      revisionsCost,
      travelDays: travelDaysN,
      travelDayRate,
      travelCost,
      dayToNightCost,
      dayToNightKfQty: dayToNightKf,
      dayToNightDetail: d2n,
      additionalKfCost,
      additionalKfQty,
      animatedKfCost,
      masterOrbitCost,
      masterOrbitsQty: subOrbitCount(c_subOrbits),
      subOrbitExtraKfQty: subOrbitExtraKf(c_subOrbits),
      // Every extra keyframe built (base + all suborbits) — what the timeline
      // sizes off. additionalKfQty above is only the ones the client pays for.
      productionExtraKfQty: billableExtraKeyframes(c_additionalKfQty, c_subOrbits),
      masterPlan: c_masterPlan,
      masterPlanIncludedSubOrbits: c_masterPlan ? Math.min(MASTER_PLAN_INCLUDED_SUBORBITS, subOrbitCount(c_subOrbits)) : 0,
      billableSubOrbits: billableSubOrbits.orbits,
      baseOrbitKeyframes: BASE_ORBIT_KEYFRAMES + (Number(c_additionalKfQty) || 0),
      totalExteriorKeyframes: c_exteriorKfTotal,
      vexosCoverLandingPageCost,
      vexosCoverLandingPageActive: c_vexosCoverLandingPage,
      exteriorAddonsSubtotal,
      exteriorRendersQty: c_exteriorRendersN,
      exteriorRendersCost,
      // Conceptual Rendering Hybrid Workflow (VEXOS + À La Carte)
      conceptualRendersQty: conceptualRendersN,
      conceptualRenderRate: conceptualRate,
      conceptualRendersCost,
      // Exterior VR tour (single per-project)
      exteriorVRPoints,
      exteriorVRActive,
      exteriorVRCost,
      // Video Animation (Phase 05)
      aiMotionDur: c_aiMotionDur,
      aiMotionVideos: aiMotionEligible ? c_aiMotionVideos : [],
      aiMotionCost,
      aiMotionEligible,
      aiMotionRenderGateMet,
      // Effective "client supplies the renderings" flag (à la carte bypass).
      aiMotionClientRenders: isAlacarte && aiMotionClientRenders && !aiMotionRenderGateMet,
      cinematicDur: c_cinematicDur,
      cinematicVideos: standaloneContext ? [] : c_cinematicVideos,
      cinematicCost,
      videoStandalone,
      standaloneContext,
      standaloneVideo,
      videoProjectSize: c_videoProjectSize,
      videoAnimationGross,
      videoAnimationCost,
      motionPackageActive,
      motionPackagePct,
      motionPackageSavings,
      hasOtherScope,
      // Floor Plans (Phase 06)
      total2DPlans,
      floorPlans2DGross,
      floorPlans2DPct,
      floorPlans2DSavings,
      floorPlans2DCost,
      total3DPlans,
      total3DUnits,
      total3DLevels,
      floorPlans3DWorkflow: c_floorPlans3DWorkflow,
      floorPlans3DGross,
      floorPlans3DPct,
      floorPlans3DSavings,
      floorPlans3DCost,
      floorPlansCost,
      totalHomeRenders,
      homeRendersCost,
      totalHomeVR,
      homeVRPointsSum,
      homeVRCost,
      totalAmenityRenders,
      amenityRendersCost,
      totalAmenityVR,
      amenityVRPointsSum: c_amenityVRPointsSum,
      amenityVRCost,
      amenitySubtotal,
      ...configurator,
      // Drone (project-wide) — Community
      droneCaptureActive,
      droneCaptureFee,
      anyInteriorVRActive,
      anyAmenityVRActive,
      anyExteriorVRActive,
      interiorDroneShotsActive: anyInteriorVRActive && c_interiorDroneShots,
      interiorDroneShotsCost,
      amenityDroneShotsActive: anyAmenityVRActive && c_amenityDroneShots,
      amenityDroneShotsCost,
      droneSubtotal,
      vizGross,
      vizNet,
      bundle,
      bundleOffer,
      bundleApplied,
      bundleSavings,
      moduleActive,
      activeCount,
      oneTimeAllIn,
      grandTotal,
    };
  }, [c_lots, c_masterPlan, c_contractTerm, c_dayToNight, c_additionalKfQty, c_animatedKfEnabled, c_animatedKfQty,
      c_subOrbits, c_exteriorKfTotal, c_exteriorRendersQty, c_conceptualRendersQty, c_exteriorVR, c_homeModelRenders, c_homeModelVR, c_amenityRenders, c_amenityVR, vrConfigurator,
      c_aiMotionDur, c_aiMotionVideos, aiMotionClientRenders, c_cinematicDur, c_cinematicVideos, c_videoProjectSize, c_videoStandaloneSel, isAlacarte, conceptualAvailable, c_floorPlans2D, c_floorPlans3D, c_floorPlans3DLevels, c_floorPlans3DWorkflow, c_homeModelCount,
      c_interiorDroneShots, c_amenityDroneShots, c_vexosCoverLandingPage,
      additionalRevisions, travelDays, bundleApplied, vexosRenderPricing, pricingVersion]);

  // ── AI Motion auto-deactivation ─────────────────────────────────────
  // AI Motion is only selectable once the proposal carries ≥1 exterior and
  // ≥3 interior renders. If renders are removed and the selection drops
  // below either minimum, drop the AI Motion selection so it also clears
  // from the UI (its cost is already gated to $0 in the calc memos).
  useEffect(() => {
    if (aiMotionDur != null && !calc.aiMotionEligible) setAiMotionDur(null);
  }, [calc.aiMotionEligible, aiMotionDur]);
  useEffect(() => {
    if (c_aiMotionDur != null && !calcCommunity.aiMotionEligible) setCAiMotionDur(null);
  }, [calcCommunity.aiMotionEligible, c_aiMotionDur]);

  // ── Standalone video selection normalization (guardrails) ───────────
  // Keep the size-first selection valid: (1) drop a hero that isn't offered
  // by the current size, and (2) clear the Large add-on quantities whenever
  // add-ons are locked (size ≠ Large) or no hero is in scope. Mirrors the
  // AI-motion auto-deactivation pattern.
  const normalizeStandaloneSel = (size, sel) => {
    const heroValid = standaloneHeroesForSize(size).some((h) => h.key === sel.heroKey);
    const nextHero = heroValid ? sel.heroKey : null;
    const addOnsOk = standaloneSizeUnlocksAddOns(size) && nextHero != null;
    const next = {
      heroKey: nextHero,
      unitInteriorQty: addOnsOk ? sel.unitInteriorQty : 0,
      penthouseQty: addOnsOk ? sel.penthouseQty : 0,
      socialCutdownQty: addOnsOk ? sel.socialCutdownQty : 0,
    };
    const changed =
      next.heroKey !== sel.heroKey ||
      next.unitInteriorQty !== sel.unitInteriorQty ||
      next.penthouseQty !== sel.penthouseQty ||
      next.socialCutdownQty !== sel.socialCutdownQty;
    return changed ? next : sel;
  };
  useEffect(() => {
    setVideoStandaloneSel((sel) => normalizeStandaloneSel(videoProjectSize, sel));
  }, [videoProjectSize, videoStandaloneSel.heroKey]);
  useEffect(() => {
    setCVideoStandaloneSel((sel) => normalizeStandaloneSel(c_videoProjectSize, sel));
  }, [c_videoProjectSize, c_videoStandaloneSel.heroKey]);

  // ── SFH (SINGLE-FAMILY HOME) FIXED + VISUALIZATION CALC ────────────
  // SFH = flat platform pricing (admin Section 1S) + the SAME tier-free
  // visualization pricing as Condo (per-render, per-VR, per-floor-plan).
  // The rep configures viz scope using the shared Phase 2 (Exterior),
  // Phase 3 (Interior), Phase 5 (Video), Phase 6 (Floor Plans) UIs which
  // are wired through condo state (`uniqueFloorPlans`, `interiorRenders`,
  // `aiMotionDur`, etc.). Amenity scope (Phase 4) is intentionally
  // skipped for SFH engagements.
  //
  // The cleanest implementation: inherit the entire `calc` (condo) memo
  // and surgically override the platform setup + monthly so the SFH
  // flat-rate platform fee replaces Condo's tier-based one. All viz
  // costs, bundle discounts, motion package logic stay identical.
  const calcSFH = useMemo(() => {
    const sfh = getSFHPricing();
    const setupFee = Number(sfh.setupFee) || 0;
    const monthlyRate = Number(sfh.monthlySubscription) || 0;
    const recurringContractValue = monthlyRate * contractTerm;
    const vizNet = Number(calc.vizNet) || 0;
    // Additional revisions + travel are inherited from `calc` (they key off
    // the same viz scope + global state); fold them into the SFH totals.
    const revisionsCost = Number(calc.revisionsCost) || 0;
    const travelCost = Number(calc.travelCost) || 0;
    return {
      ...calc,
      setupFee,
      monthlyRate,
      contractTerm,
      recurringContractValue,
      // Recompute headline totals against the SFH flat platform fee
      // (calc.* values were computed against the condo platform tier).
      oneTimeAllIn: setupFee + vizNet + revisionsCost + travelCost,
      grandTotal: setupFee + vizNet + revisionsCost + travelCost + recurringContractValue,
      // Alias condo-named interior fields to community/SFH naming so sidebar,
      // calcSnapshot, and PDF code that branches on !isCondo resolves correctly.
      homeRendersCost: calc.interiorRendersCost,
      totalHomeRenders: calc.totalInteriorRenders,
      homeVRCost: calc.interiorVRCost,
      totalHomeVR: calc.totalInteriorVR,
      homeVRPointsSum: calc.interiorVRPointsSum,
    };
  }, [calc, contractTerm, pricingVersion]);

  // ── ACTIVE CALC (mode-resolved, price-overrides applied) ────────────
  // Single source of truth for every surface that shows or persists money:
  // the sidebar summary, expand modal, scope grid, payment schedule, saved
  // snapshot, and PDF. The per-mode memos above stay override-free so the
  // build column keeps showing the rate-card values next to each input.
  const activeCalcUnpriced = useMemo(
    () => (mode === "sfh" ? calcSFH : (mode === "condo" ? calc : calcCommunity)),
    [mode, calc, calcCommunity, calcSFH],
  );
  // The additional proposal discount is applied over the overridden prices,
  // so it is always the rep's number off whatever the lines add to. The flat
  // conceptual design fee goes on after it, so a % discount never touches the
  // fee. Like the conceptual renderings, the fee only bills on the tabs that
  // show Phase 07 — a stale value can't bill anywhere else.
  const activeCalcResolved = useMemo(
    () => applyModelingFee(applyConceptualDesignFee(
      applyProposalDiscount(
        applyPriceOverrides(activeCalcUnpriced, priceOverrides, {
          isCondoLike: mode === "condo" || mode === "sfh",
          keepDiscounts: overrideDiscountChoice !== "remove",
        }),
        proposalDiscount,
        { isAlacarte },
      ),
      conceptualAvailable ? conceptualDesignFee : null,
    ), modelingFee, { mode, lots: c_lots, masterPlan: c_masterPlan, available: conceptualAvailable }),
    [activeCalcUnpriced, priceOverrides, mode, overrideDiscountChoice, proposalDiscount, isAlacarte, conceptualAvailable, conceptualDesignFee, modelingFee, c_lots, c_masterPlan],
  );

  // True when a line carries a hand-set price AND the rep chose to remove the
  // discounts. Every discount is then off for the whole quote, and no surface
  // may mention one.
  const manualPricingActive = !!activeCalcResolved.manualPricing;

  // What the bundle + motion package are worth on this quote with a given set
  // of overrides applied: the amount removing them adds to the client's total.
  const overrideDiscountValue = useCallback((overrides) => {
    const opts = { isCondoLike: mode === "condo" || mode === "sfh" };
    const kept = applyPriceOverrides(activeCalcUnpriced, overrides, { ...opts, keepDiscounts: true });
    const removed = applyPriceOverrides(activeCalcUnpriced, overrides, { ...opts, keepDiscounts: false });
    return Math.max(0, Math.round((removed.oneTimeAllIn || 0) - (kept.oneTimeAllIn || 0)));
  }, [activeCalcUnpriced, mode]);

  const overrideDiscountAmount = useMemo(
    () => overrideDiscountValue(cleanPriceOverrides(priceOverrides)),
    [overrideDiscountValue, priceOverrides],
  );

  const activeOverrideCount = useMemo(
    () => Object.keys(cleanPriceOverrides(priceOverrides)).length + (monthlyRateOverride !== null ? 1 : 0),
    [priceOverrides, monthlyRateOverride],
  );

  const setPriceOverride = (id, value) => {
    // Monthly delegates to the pre-existing override so the CRM deduction
    // and $1,000 / mo floor keep applying to it.
    if (id === "monthlyRate") {
      setMonthlyRateOverride(value === null ? null : Math.max(0, Math.round(value)));
      markDirty();
      return;
    }
    // Campaign fees live on the marketing block so they don't take the
    // visualization scope off the rate card.
    if (MARKETING_OVERRIDE_IDS.has(id)) {
      const line = MARKETING_OVERRIDE_LINES.find((l) => l.id === id);
      setMarketing((prev) => ({
        ...prev,
        [line.stateKey]: value === null ? null : Math.max(0, Math.round(value)),
      }));
      markDirty();
      return;
    }
    // Collateral lines live on the collateral block — same reasoning.
    if (COLLATERAL_OVERRIDE_BY_ID.has(id)) {
      const { key } = COLLATERAL_OVERRIDE_BY_ID.get(id);
      setCollateral((prev) => {
        const nextCol = { ...(prev.priceOverrides || {}) };
        if (value === null) delete nextCol[key];
        else nextCol[key] = Math.max(0, Math.round(value));
        return { ...prev, priceOverrides: nextCol };
      });
      markDirty();
      return;
    }
    const next = { ...priceOverrides };
    if (value === null) delete next[id];
    else next[id] = Math.max(0, Math.round(value));
    setPriceOverrides(next);
    if (!overridesPutDiscountsAtStake(cleanPriceOverrides(next))) {
      // Back on the rate card (a setup fee alone doesn't count) — the next
      // override asks again.
      setOverrideDiscountChoice(null);
    } else if (overrideDiscountChoice === null && value !== null) {
      // First override that puts discounts at stake: ask the rep.
      const amount = overrideDiscountValue(cleanPriceOverrides(next));
      if (amount > 0) setDiscountPrompt({ amount });
    }
    markDirty();
  };

  const clearAllPriceOverrides = () => {
    setPriceOverrides({});
    setOverrideDiscountChoice(null);
    setMonthlyRateOverride(null);
    setPostTermOverride(null);
    setMarketing((prev) => ({ ...prev, setupFeeOverride: null, monthlyServiceFeeOverride: null }));
    setCollateral((prev) => ({ ...prev, priceOverrides: {} }));
    markDirty();
  };

  // One row per priced line currently in the quote. `auto` is what the rate
  // card computes; `override` is the manual replacement (null when none).
  const priceOverrideRows = useMemo(() => {
    const isCondoLike = mode === "condo" || mode === "sfh";
    const cleaned = cleanPriceOverrides(priceOverrides);
    return PRICE_OVERRIDE_LINES
      .map((line) => {
        const key = (!isCondoLike && line.calcKeys[1]) ? line.calcKeys[1] : line.calcKeys[0];
        const override = line.id === "monthlyRate"
          ? (monthlyRateOverride !== null && Number.isFinite(monthlyRateOverride) ? Math.round(monthlyRateOverride) : null)
          : (cleaned[line.id] !== undefined ? cleaned[line.id] : null);
        return { ...line, auto: Number(activeCalcUnpriced[key]) || 0, override };
      })
      .filter((r) => {
        // À La Carte and VEXOS-off quotes carry no platform setup or subscription.
        if ((isAlacarte || !vexosEnabled) && (r.id === "setupFee" || r.id === "monthlyRate")) return false;
        return r.auto > 0 || r.override !== null;
      });
  }, [activeCalcUnpriced, priceOverrides, monthlyRateOverride, mode, isAlacarte, vexosEnabled]);

  // ── MARKETING / LEAD GEN MATH ──────────────────────────────────────
  // Lead-Gen tier resolution is now driven EXCLUSIVELY by the Lead Gen
  // unit count entered on the Marketing & Lead Gen page (`marketing.units`).
  // Previously this fell back to the Phase-1 VEXOS unit count which
  // overpriced engagements where only a subset of the project was being
  // marketed (e.g. 300-unit tower selling its initial 40-unit release —
  // VEXOS pricing stays on Tier 3, Lead Gen drops to Tier 1).
  const marketingMath = useMemo(() => {
    const cpl = resolveCPL(marketing.campaignType, marketing.priceTier);
    const denom = (marketing.sellOutMonths || 1) * ((marketing.conversionRate || 0.01) / 100);
    const monthlyLeads = denom > 0 ? (marketing.units || 0) / denom : 0;
    const monthlyAdSpend = monthlyLeads * cpl;
    // Lead Gen tier is now resolved purely from the marketing slider's
    // unit count — independent of VEXOS project size. Defaults to 0
    // when the slider hasn't been set so the resolver picks the
    // smallest tier (no hidden Phase-1 inheritance).
    const leadGenUnits = Number(marketing.units) || 0;
    const tier = resolveCampaignFeeTier(leadGenUnits, marketingConfig.campaignFeeTiers);
    const setupFeeAuto = Number(tier.setupFee) || 0;
    const monthlyServiceFeeAuto = Number(tier.monthlyServiceFee) || 0;
    // Rep-set campaign fees win over the resolved tier. Applied here, before
    // the campaign rollups below, so monthly budget / total campaign
    // investment / payment milestones / PDF all follow from one place.
    const setupFeeOverride = cleanFeeOverride(marketing.setupFeeOverride);
    const monthlyServiceFeeOverride = cleanFeeOverride(marketing.monthlyServiceFeeOverride);
    const setupFee = setupFeeOverride !== null ? setupFeeOverride : setupFeeAuto;
    const monthlyServiceFee = monthlyServiceFeeOverride !== null ? monthlyServiceFeeOverride : monthlyServiceFeeAuto;
    const monthlyBudget = monthlyAdSpend + monthlyServiceFee + (marketing.sellOutMonths > 0 ? setupFee / marketing.sellOutMonths : 0);
    const totalCampaignInvestment = (monthlyAdSpend + monthlyServiceFee) * marketing.sellOutMonths + setupFee;
    const totalLeads = monthlyLeads * marketing.sellOutMonths;
    return {
      cpl, monthlyLeads, monthlyAdSpend, monthlyBudget, totalCampaignInvestment, totalLeads,
      setupFee, monthlyServiceFee,
      // Rate-card values + which fees are hand-set. Internal only: drives the
      // amber badge and the struck-through calculated price in the override
      // panel and on the Marketing tab. Never shown to the client.
      setupFeeAuto, monthlyServiceFeeAuto,
      setupFeeOverride, monthlyServiceFeeOverride,
      feesOverridden: setupFeeOverride !== null || monthlyServiceFeeOverride !== null,
      campaignFeeTier: tier.tierNumber,
      campaignFeeTierLabel: tier.label,
      campaignFeeTierUnits: leadGenUnits,
    };
  }, [marketing, marketingConfig]);

  const marketingActive = !!(marketing.active && marketing.priceTier && marketingMath.cpl > 0);

  // The rep flipped Marketing ON but has not picked an average price point, so
  // there is no cost-per-lead and the campaign carries no ad spend or lead
  // projection. `marketingActive` is therefore false and the campaign is saved
  // as `{active: false}` — it reaches neither the sidebar nor the proposal.
  // That used to happen in total silence: the campaign card still showed a
  // real setup fee and management fee (both are unit-tier driven, not CPL
  // driven), so the quote looked complete and the proposal came back with no
  // marketing in it at all. Surface the gap wherever the rep can see it.
  const marketingNeedsPriceTier = !!marketing.active && !marketingActive;

  // Campaign-fee rows for the override panel. Only offered once the campaign
  // is actually part of the quote — an inactive marketing block bills nothing,
  // so there is no fee to negotiate.
  const marketingOverrideRows = useMemo(() => {
    if (!marketingActive) return [];
    return MARKETING_OVERRIDE_LINES.map((line) => ({
      ...line,
      auto: Number(marketingMath[line.autoKey]) || 0,
      override: cleanFeeOverride(marketing[line.stateKey]),
    }));
  }, [marketingActive, marketingMath, marketing]);

  // Collateral rows: one per collateral line currently in the quote. `auto`
  // is the rate card; an override on a line that was since switched off is
  // dormant (it bills nothing), so the row goes with the line.
  const collateralOverrideRows = useMemo(() => {
    const overrides = collateralMath.priceOverrides || {};
    return COLLATERAL_OVERRIDE_LINES
      .filter((line) => !!collateral[line.key])
      .map((line) => ({
        ...line,
        auto: Number(collateralConfig[line.key]) || 0,
        override: overrides[line.key] !== undefined ? overrides[line.key] : null,
      }));
  }, [collateral, collateralConfig, collateralMath]);

  // What the panel renders: visualization / platform lines plus the campaign
  // fees and collateral. One place for the rep to look; the viz lines, the
  // campaign fees and collateral are three separate money seams behind it.
  const overridePanelRows = useMemo(
    () => [...priceOverrideRows, ...marketingOverrideRows, ...collateralOverrideRows],
    [priceOverrideRows, marketingOverrideRows, collateralOverrideRows],
  );

  const overridePanelCount = useMemo(
    () => activeOverrideCount
      + marketingOverrideRows.filter((r) => r.override !== null).length
      + collateralOverrideRows.filter((r) => r.override !== null).length,
    [activeOverrideCount, marketingOverrideRows, collateralOverrideRows],
  );

  // ── DISCOUNT GUARDRAILS (internal, advisory) ────────────────────────
  // How far each hand-set price sits below its rate-card price, banded by
  // the Pricing Admin's Section 8C. Warns, never blocks, never reaches the
  // client. `pricingVersion` re-reads the singleton after an admin save.
  const guardrailByLine = useMemo(() => {
    const out = {};
    priceOverrideRows.forEach((r) => {
      const g = lineGuardrail(DISCOUNT_GUARDRAILS, r);
      if (g) out[r.id] = g;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceOverrideRows, pricingVersion]);

  // The additional proposal discount, measured against the same base its %
  // applies to (setup fee unless à la carte + net viz + revisions). Net viz is
  // post-bundle: the bundle is the rep's pre-approved first lever and is never
  // itself banded, so only what is given beyond it can need approval.
  const proposalDiscountGuardrail = useMemo(() => {
    const c = activeCalcResolved;
    const base = (isAlacarte ? 0 : (Number(c.setupFee) || 0)) + (Number(c.vizNet) || 0) + (Number(c.revisionsCost) || 0);
    return additionalDiscountGuardrail(DISCOUNT_GUARDRAILS, { amount: Number(c.proposalDiscount) || 0, base });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCalcResolved, isAlacarte, pricingVersion]);

  // Builds the flat `inputs` bundle the backend expects, then runs the
  // pure compute function from vexosTimeline.js. Display-only — no impact
  // on pricing or payment math. Re-runs whenever any selection or the
  // active mode changes.
  // Build the timeline request payload (cheap, client-side) AND an instant
  // client-side estimate. The authoritative numbers come from the server —
  // the Studio Visualization track is computed by the production Timeline
  // Engine so render / VR / video / floor-plan VOLUME drives the estimate
  // (see backend `compute_timeline_async`). `clientLiveTimeline` is only a
  // first-paint fallback so the card is never blank while the (debounced)
  // server compute is in flight.
  const { timelineRequest, clientLiveTimeline } = useMemo(() => {
    // SFH shares state variable names with Condo (interiorRenders, interiorVR,
    // etc.), so we pass "condo" keys and projectType for both modes. Community
    // uses its own c_* prefixed state variables.
    const useCondoKeys = mode === "condo" || mode === "sfh";
    const inputs = useCondoKeys
      ? {
          dayToNight, dayToNightKfQty: calc.dayToNightKfQty,
          // Timeline sizes off every extra keyframe sold, base orbit or suborbit.
          additionalKeyframesQty: calc.additionalKfQty,
          animatedKeyframesEnabled, animatedKeyframesQty,
          // Conceptual renders ride the exterior lane — the save sends the
          // same raw count, so the card times what the PDF times.
          masterOrbitsQty, subOrbits, exteriorRendersQty, conceptualRendersQty,
          interiorRenders, interiorVR, amenityRenders, amenityVR,
          aiMotionDur, cinematicDur, aiMotionVideos, cinematicVideos, floorPlans2D, floorPlans3D,
          floorPlans3DLevels, total3DPlans: calc.total3DPlans,
          vexosCoverLandingPage,
          // Drone capture toggle — required to suppress the "Drone &
          // Reality Capture" timeline row when the rep has it disabled.
          droneCaptureEnabled: interiorDroneShots || amenityDroneShots,
          // À la carte video-only quotes time the size-first standalone film.
          isAlacarte, videoStandaloneSel,
        }
      : {
          c_dayToNight, c_dayToNightKfQty: calcCommunity.dayToNightKfQty,
          c_additionalKfQty: calcCommunity.productionExtraKfQty,
          c_animatedKfEnabled, c_animatedKfQty,
          c_masterOrbitsQty, c_subOrbits, c_exteriorRendersQty, c_conceptualRendersQty,
          c_homeModelRenders, c_homeModelVR, c_amenityRenders, c_amenityVR,
          c_aiMotionDur, c_cinematicDur, c_aiMotionVideos, c_cinematicVideos, c_floorPlans2D, c_floorPlans3D,
          c_floorPlans3DLevels, total3DPlans: calcCommunity.total3DPlans,
          c_vexosCoverLandingPage,
          c_droneCaptureEnabled: c_interiorDroneShots || c_amenityDroneShots,
          isAlacarte, c_videoStandaloneSel,
        };
    const projectType = useCondoKeys ? "condo" : "community";
    // The total the save stores, which is what the PDF staffs the job off.
    const proposalTotal = snapshotMoney(activeCalcResolved, {
      isAlacarte, monthlyRateOverride, crmIncluded,
      term: mode === "community" ? c_contractTerm : contractTerm,
    }).grandTotal || 0;
    inputs.timelineOverride = normalizeTimelineOverride(timelineOverride);
    return {
      timelineRequest: { inputs, project_type: projectType, proposal_total: proposalTotal },
      clientLiveTimeline: computeVexosTimeline(inputs, projectType, timelineDurations, proposalTotal),
    };
  }, [
    mode, timelineDurations, timelineOverride,
    activeCalcResolved, monthlyRateOverride, crmIncluded, contractTerm, c_contractTerm,
    // Condo state
    dayToNight, calc.dayToNightKfQty, calc.additionalKfQty, calc.total3DPlans,
    animatedKeyframesEnabled, animatedKeyframesQty,
    masterOrbitsQty, subOrbits, exteriorRendersQty, conceptualRendersQty,
    interiorRenders, interiorVR, amenityRenders, amenityVR,
    aiMotionDur, cinematicDur, aiMotionVideos, cinematicVideos, floorPlans2D, floorPlans3D, floorPlans3DLevels,
    vexosCoverLandingPage,
    interiorDroneShots, amenityDroneShots,
    // Community state
    c_dayToNight, calcCommunity.dayToNightKfQty, calcCommunity.productionExtraKfQty, calcCommunity.total3DPlans,
    c_animatedKfEnabled, c_animatedKfQty,
    c_masterOrbitsQty, c_subOrbits, c_exteriorRendersQty, c_conceptualRendersQty,
    c_homeModelRenders, c_homeModelVR, c_amenityRenders, c_amenityVR,
    c_aiMotionDur, c_cinematicDur, c_aiMotionVideos, c_cinematicVideos, c_floorPlans2D, c_floorPlans3D, c_floorPlans3DLevels,
    c_vexosCoverLandingPage,
    c_interiorDroneShots, c_amenityDroneShots,
    isAlacarte, videoStandaloneSel, c_videoStandaloneSel,
  ]);

  // Server-computed (engine-backed) timeline — the authoritative live value.
  // Debounced so rapid edits don't hammer the endpoint; the last good value
  // stays on screen while a new compute is in flight.
  const [serverLiveTimeline, setServerLiveTimeline] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await apiClient.post("/vexos-timeline/compute", timelineRequest);
        if (!cancelled && res?.data) setServerLiveTimeline(res.data);
      } catch {
        // Network/engine hiccup — keep the last good value; the client-side
        // fallback still backs the very first paint.
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [timelineRequest]);

  // Prefer the engine value once we have it; fall back to the instant
  // client estimate only until the first server response lands.
  const liveTimeline = serverLiveTimeline || clientLiveTimeline;

  // Effective timeline shown in the Production Timeline cards. When a
  // saved quote was just loaded and no edits have been made, prefer the
  // snapshot persisted alongside the quote so the card matches the PDF.
  // The moment the user edits anything, the snapshot is cleared (see the
  // "drift" effect below) and `liveTimeline` takes over.
  const displayTimeline = savedTimelineSnapshot || liveTimeline;

  // Invalidate the saved-timeline snapshot whenever the user makes an edit
  // that changes the engine-computed timeline, so the cards resume live
  // recompute. Compare against the SERVER value only (both the snapshot and
  // `serverLiveTimeline` come from the same engine, so an unedited quote
  // matches exactly and the snapshot is preserved).
  useEffect(() => {
    if (!savedTimelineSnapshot || !serverLiveTimeline) return;
    const sameDays =
      Number(savedTimelineSnapshot.vexosBusinessDays || 0) === Number(serverLiveTimeline.vexosBusinessDays || 0) &&
      Number(savedTimelineSnapshot.vizBusinessDays || 0) === Number(serverLiveTimeline.vizBusinessDays || 0);
    if (!sameDays) setSavedTimelineSnapshot(null);
  }, [serverLiveTimeline, savedTimelineSnapshot]);

  // ── PROPOSAL MODE (derived) ─────────────────────────────────────────
  // Mirrors backend `detect_proposal_mode`. Combined when both VEXOS and
  // marketing/collateral are active; marketing-only when only marketing
  // scope; else vexos. Drives download-button label switching.
  // The derived `vexosActive` honors the master toggle (`vexosEnabled`)
  // AND whether the rep has actually built any VEXOS content. Flipping
  // the master toggle OFF forces a clean marketing-only proposal even
  // when calc state still contains stale values.
  const vexosHasContent = (
    (calc.setupFee || 0) > 0
    || (calc.vizGross || 0) > 0
    || (calc.monthlyRate || 0) > 0
    || (calcCommunity.setupFee || 0) > 0
    || (calcCommunity.vizGross || 0) > 0
    || (calcCommunity.monthlyRate || 0) > 0
    // SFH mode contributes setupFee + monthlyRate (flat platform offer).
    || (mode === "sfh" && ((calcSFH.setupFee || 0) > 0 || (calcSFH.monthlyRate || 0) > 0))
  );
  const vexosActive = vexosEnabled && vexosHasContent;
  // À La Carte is a standalone visualization-only proposal — never VEXOS.
  const proposalMode = isAlacarte
    ? "alacarte"
    : (vexosActive && (marketingActive || collateralActive))
      ? "combined"
      : ((marketingActive || collateralActive) && !vexosActive)
        ? "marketing"
        : "vexos";
  // Not "Full Proposal" any more — this button downloads the condensed
  // client deck. The long document is one click to its right.
  const proposalButtonLabel = proposalMode === "combined"
    ? "Download Proposal"
    : proposalMode === "marketing"
      ? "Download Marketing Proposal"
      : proposalMode === "alacarte"
        ? "Download Visualization Proposal"
        : "Download VEXOS Proposal";

  // ── PAYMENT SCHEDULE (live, mode-aware) ───────────────────────────────
  // The plan, the milestone amounts, and the Payment 2 trigger all come from
  // the shared resolver in ./paymentPlan — the single seam every surface
  // (sidebar, client view, web proposal, PDF, Stripe deposit) reads:
  //   • 50 / 25 / 25 — default, one-time scope $100k or under; setup fee
  //                    joins the split
  //   • 25 / 50 / 25 — one-time scope OVER $100k; setup fee bills whole at
  //                    signing
  //   • 50 / 50      — à la carte, rep's choice
  // Visualization scope excludes the Monthly Subscription. Travel always
  // bills at signing; rounding lands on Payment 1 so the milestones sum
  // exactly to the one-time scope.
  const paymentSchedule = useMemo(() => {
    const sfhMode = mode === "sfh";
    const condoMode = mode === "condo";
    const activeC = activeCalcResolved;
    // Bundle discount has already been applied to vizNet by the calc memo.
    const vizPure = activeC.vizNet || 0;
    // Additional revision rounds are visualization work — they ride the
    // split alongside the base viz scope.
    const revisionsCost = activeC.revisionsCost || 0;
    const travelCost = activeC.travelCost || 0;
    // The additional proposal discount comes out of the visualization split,
    // so the milestones sum to the discounted one-time total.
    const proposalDiscountAmt = activeC.proposalDiscount || 0;
    // The conceptual design fee is design work billed like the viz scope — it
    // rides the same split, so the milestones sum to the total with it in.
    const conceptualDesignFeeAmt = activeC.conceptualDesignFee || 0;
    // The 3D modeling fee rides the same split, for the same reason.
    const modelingFeeAmt = activeC.modelingFee || 0;
    const vizSubtotal = vizPure + revisionsCost - proposalDiscountAmt + conceptualDesignFeeAmt + modelingFeeAmt;
    // À La Carte is visualization-only: NO platform setup fee.
    const setupFee = isAlacarte ? 0 : activeC.setupFee;
    // Collateral built on Tab 2 rides the plan percentages alongside the viz
    // scope — one base, so the finalization payment carries its share of the
    // collateral instead of a quarter of the visualization alone.
    const collateralTotal = collateralActive ? (collateralMath?.collateralTotal || 0) : 0;
    const plan = resolvePaymentPlan({
      setupFee,
      vizSubtotal,
      travelCost,
      collateralTotal,
      isAlacarte,
      alacartePayPlan,
      payPlanChoice,
      scope: activeC,
      pifDiscount,
      milestonePolicy,
      finalPaymentPolicy,
      modelingFee: modelingFeeAmt,
    });
    const { p1, p2, p3 } = plan;

    // Apply the rep's manual override to the monthly subscription if set.
    // À La Carte has no platform subscription, so monthly is always 0.
    const autoMonthly = activeC.monthlyRate || 0;
    const baseMonthly = monthlyRateOverride !== null && Number.isFinite(monthlyRateOverride)
      ? Math.max(0, Math.round(monthlyRateOverride))
      : autoMonthly;
    // CRM opt-out deducts a flat $1,500 / mo. Floor at $1,000 / mo so
    // the deduction can never drop the displayed rate below the minimum.
    const crmDeduction = crmIncluded ? 0 : 1500;
    const MIN_MONTHLY_FLOOR = 1000;
    const effectiveMonthly = isAlacarte
      ? 0
      : (crmDeduction > 0
          ? Math.max(baseMonthly - crmDeduction, MIN_MONTHLY_FLOOR)
          : baseMonthly);

    return {
      vizSubtotal,
      vizPure,
      revisionsCost,
      revisionRounds: activeC.revisionRounds || 0,
      revisionUnitCost: activeC.revisionUnitCost || 0,
      travelCost,
      travelDays: activeC.travelDays || 0,
      travelDayRate: activeC.travelDayRate || 0,
      vizGross: activeC.vizGross || 0,
      bundleSavings: activeC.bundleSavings || 0,
      bundleLabel: activeC.bundle?.label || null,
      bundlePct: activeC.bundle?.pct || 0,
      proposalDiscount: proposalDiscountAmt,
      proposalDiscountPct: activeC.proposalDiscountPct ?? null,
      conceptualDesignFee: conceptualDesignFeeAmt,
      modelingFee: modelingFeeAmt,
      p1, p2, p3,
      isAlacarte,
      // Resolved plan + Payment 2 trigger, carried onto the saved quote so
      // downstream surfaces can label the milestones without re-deriving.
      payPlan: plan.planKey,
      pct: plan.pct,
      frontLoaded: plan.frontLoaded,
      largeQuote: plan.largeQuote,
      setupInSplit: plan.setupInSplit,
      setupAtSigning: plan.setupAtSigning,
      // FALSE only when there is no viz/platform scope for collateral to ride
      // — a marketing & collateral quote, where it keeps its own 50 / 50.
      collateralInSplit: plan.collateralInSplit,
      scopeLabel: plan.scopeLabel,
      scopeLabelShort: plan.scopeLabelShort,
      v1: plan.v1,
      finalAssets: plan.finalAssets,
      finalNote: plan.finalNote,
      finalPaymentPolicy: plan.finalPaymentPolicy,
      twoPart: plan.twoPart,
      // The one-time contract value the milestones divide — the base the
      // complimentary add-on cap is a % of.
      oneTimeScope: plan.oneTimeScope,
      // One payment at signing — the schedule IS the pay-in-full, so surfaces
      // render a single row and the pay-in-full block is suppressed.
      singlePayment: plan.singlePayment,
      singlePaymentSavings: plan.singlePaymentSavings,
      singlePaymentGross: plan.singlePaymentGross,
      pifPct: plan.pifPct,
      monthly: effectiveMonthly,
      monthlyAuto: autoMonthly,
      monthlyBase: baseMonthly,
      monthlyOverridden: monthlyRateOverride !== null,
      crmIncluded,
      crmDeduction,
      term: sfhMode ? contractTerm : (condoMode ? contractTerm : c_contractTerm),
    };
  }, [mode, activeCalcResolved, contractTerm, c_contractTerm, monthlyRateOverride, crmIncluded, isAlacarte, alacartePayPlan, payPlanChoice, pifDiscount, collateralActive, collateralMath, milestonePolicy, finalPaymentPolicy]);

  // Complimentary add-ons against their cap (Section 8D): capPct % of the
  // one-time contract value. The add-ons themselves never enter any total.
  const freeAddOnCapPct = Number(FREE_ADD_ONS.capPctOfContract) || 0;
  const freeAddOnStatus = useMemo(
    () => freeAddOnsStatus(freeAddOns, { oneTimeScope: paymentSchedule.oneTimeScope || 0, capPct: freeAddOnCapPct }),
    [freeAddOns, paymentSchedule.oneTimeScope, freeAddOnCapPct],
  );

  // Rep picks / un-picks / re-counts. Shrinking is always allowed; growing
  // past the cap is refused for reps and needs a confirm for COO / Admin.
  // Returns the refusal message (or null) so the panel can show it.
  const changeFreeAddOns = (next) => {
    const cleaned = normalizeFreeAddOns(next);
    const opts = { oneTimeScope: paymentSchedule.oneTimeScope || 0, capPct: freeAddOnCapPct };
    if (!freeAddOnsChangeAllowed(freeAddOns, cleaned, opts)) {
      const msg = freeAddOnCapMessage(freeAddOnCapPct, freeAddOnStatus.cap);
      if (!canEditPricing) return msg;
      if (!window.confirm(`${msg}.\n\nYou are an admin — add it anyway?`)) return msg;
    }
    setFreeAddOns(cleaned);
    markDirty();
    return null;
  };

  // ── SAVE QUOTE + OPEN PROPOSAL ───────────────────────────────────────
  // `openProposalAfterSave` flag controls whether to navigate to the web
  // proposal view after a successful save. The sidebar Save button passes
  // false (quiet save), the walkthrough button passes true.
  const persistQuote = async ({ openProposalAfterSave }) => {
    if (!clientName.trim()) {
      toast.error("Client name is required to save the quote.");
      return null;
    }
    setSaving(true);
    try {
      const isCondoActive = mode === "condo";
      const isSFHActive = mode === "sfh";
      const baseInputs = isSFHActive
        ? {
            // SFH reuses condo state for viz scope (Phase 2 Exterior,
            // Phase 3 Interior, Phase 5 Video, Phase 6 Floor Plans). The
            // flag `sfh: true` lets the backend differentiate at PDF time.
            // SFH-specific Project Details (home sq ft, floors, outdoor
            // amenity sq ft) are persisted alongside the viz scope.
            sfh: true,
            sfh_homeSqFt,
            sfh_floors: floors,
            sfh_amenitySqFt,
            uniqueFloorPlans,
            dayToNight, dayToNightKfQty: calc.dayToNightKfQty, additionalKeyframesQty,
            animatedKeyframesEnabled, animatedKeyframesQty,
            masterOrbitsQty, subOrbits, exteriorRendersQty, conceptualRendersQty, exteriorVR,
            interiorRenders, interiorVR,
            aiMotionDur, cinematicDur, aiMotionVideos, cinematicVideos, videoProjectSize, videoStandaloneSel, floorPlans2D, floorPlans3D, floorPlans3DLevels, floorPlans3DWorkflow,
            interiorDroneShots,
            vexosCoverLandingPage,
          }
        : isCondoActive
        ? {
            floors, units, uniqueFloorPlans, avgFloorPlanSqFt, amenitySpaces, totalAmenitySqFt,
            dayToNight, dayToNightKfQty: calc.dayToNightKfQty, additionalKeyframesQty,
            animatedKeyframesEnabled, animatedKeyframesQty,
            masterOrbitsQty, subOrbits, exteriorRendersQty, conceptualRendersQty, exteriorVR,
            interiorRenders, interiorVR, amenityRenders, amenityVR,
            aiMotionDur, cinematicDur, aiMotionVideos, cinematicVideos, videoProjectSize, videoStandaloneSel, floorPlans2D, floorPlans3D, floorPlans3DLevels, floorPlans3DWorkflow,
            interiorDroneShots, amenityDroneShots,
            vexosCoverLandingPage,
          }
        : {
            c_lots, c_homeModels, c_avgHomeSqFt, c_amenitySpaces, c_totalAmenitySqFt,
            c_masterPlan: !!c_masterPlan,
            c_dayToNight, c_dayToNightKfQty: calcCommunity.dayToNightKfQty, c_additionalKfQty,
            c_animatedKfEnabled, c_animatedKfQty,
            c_masterOrbitsQty, c_subOrbits, c_exteriorRendersQty, c_conceptualRendersQty, c_exteriorVR,
            c_homeModelRenders, c_homeModelVR, c_amenityRenders, c_amenityVR,
            c_aiMotionDur, c_cinematicDur, c_aiMotionVideos, c_cinematicVideos, c_videoProjectSize, c_videoStandaloneSel, c_floorPlans2D, c_floorPlans3D, c_floorPlans3DLevels, c_floorPlans3DWorkflow,
            c_interiorDroneShots, c_amenityDroneShots,
            c_vexosCoverLandingPage,
          };
      // Carry the manual monthly-subscription override on the inputs
      // bundle so it survives reload/hydration.
      const inputs = {
        ...baseInputs,
        monthlyRateOverride: monthlyRateOverride !== null && Number.isFinite(monthlyRateOverride)
          ? Math.max(0, Math.round(monthlyRateOverride))
          : null,
        postTermOverride: postTermOverride !== null && Number.isFinite(postTermOverride)
          ? Math.max(0, Math.round(postTermOverride))
          : null,
        // Manual per-line price overrides, so reopening the quote restores
        // exactly the pricing the client was shown.
        priceOverrides: cleanPriceOverrides(priceOverrides),
        overrideDiscountChoice,
        // The rep applied the bundle incentive (always written, so a quote
        // saved from here on never falls back to the legacy "absent = on").
        bundleApplied: !!bundleApplied,
        crmIncluded: !!crmIncluded,
        alacartePayPlan: alacartePayPlan === PLAN_TWO_PART ? PLAN_TWO_PART : ALACARTE_THREE_PART,
        // Single-payment terms, when the rep wrote them. Empty means "let the
        // size rule decide", which is every quote that predates this.
        payPlanChoice: normalizePayPlanChoice(payPlanChoice),
        // Pay-in-full discount switch. Persisted so the proposal and the PDF
        // price the single-payment option the way the rep presented it.
        pifDiscount: !!pifDiscount,
        // Additional proposal discount ({ kind, value } or null).
        proposalDiscount: normalizeProposalDiscount(proposalDiscount),
        // Complimentary add-ons ([{ id, label, value, qty }]). Printed as
        // "Included" scope lines; never part of any total.
        freeAddOns: normalizeFreeAddOns(freeAddOns),
        // Internal: which prices sat past their guardrail when saved (null =
        // all inside the first step). Advisory only — nothing reads it for money.
        guardrailFlags: guardrailFlagsSummary([...Object.values(guardrailByLine), proposalDiscountGuardrail]),
        // Conceptual interior design fee ({ spaces, rate, amount, note }, a
        // legacy flat { amount, note }, or null).
        conceptualDesignFee: normalizeConceptualDesignFee(conceptualDesignFee),
        // 3D modeling fee choice ({ tier }: null = automatic, "none" = off).
        modelingFee: normalizeModelingFee(modelingFee),
        // Real-Time Configurator on the VR tours ({ residence, amenity }).
        vrConfigurator: normalizeVrConfigurator(vrConfigurator),
        // Rep's committed timeline ({ viz, vexos } week ranges or null). The
        // server applies it when it computes quote.timeline on save.
        timelineOverride: normalizeTimelineOverride(timelineOverride),
        // À La Carte AI Motion unlock — client supplies the source renderings.
        aiMotionClientRenders: !!aiMotionClientRenders,
        // À La Carte is never a VEXOS engagement — force the platform flags
        // off so a saved à-la-carte quote can't record both.
        vexosEnabled: isAlacarte ? false : !!vexosEnabled,
        vexosActive: isAlacarte ? false : !!vexosActive,
        activeTab,
        isAlacarte: !!isAlacarte,
        // Proposal Contact rep. Only set when an internal user explicitly
        // picks one; Julian/Jordan generating their own proposals resolve
        // via their account, so this stays empty for them.
        salesRep: currentUserIsKnownRep ? "" : (salesRep || ""),
        // Additional revision rounds + on-site travel days (mode-independent).
        additionalRevisions: Math.max(0, Math.round(Number(additionalRevisions) || 0)),
        travelDays: Math.max(0, Math.round(Number(travelDays) || 0)),
        // Lot Explorer™ — persisted as a scoped block. Restored on
        // load via the matching hydration branch below.
        lotExplorer: {
          enabled: isLotExplorer,
          lotCount: lotExplorerLotCount,
          ratePerLot: lotExplorerRatePerLot,
          baseFee: lotExplorerCalc.baseFee,
          includePoi: lotExplorerIncludePoi,
          includeCrm: lotExplorerIncludeCrm,
          includeMedia: lotExplorerIncludeMedia,
          includeBackend: lotExplorerIncludeBackend,
          timelineWeeks: lotExplorerTimelineWeeks,
          hostingMonthly: lotExplorerHostingMonthly,
        },
      };
      const activeC = activeCalcResolved;
      // Apply manual monthly-rate override (if any) AND the VEXOS CRM
      // opt-out deduction ($1,500) AND the $1,000 / mo floor. The saved
      // snapshot reflects exactly what the rep showed the client. The
      // PDF reads `calc_snapshot.monthlyRate` directly.
      // À La Carte is visualization-only — NO VEXOS platform setup or monthly
      // subscription. Persist a stripped snapshot so every downstream surface
      // (web proposal, PDF, reports) reflects viz + revisions + travel only.
      const activeTerm = isSFHActive ? contractTerm : (isCondoActive ? contractTerm : c_contractTerm);
      const {
        autoMonthlyRate, overriddenMonthly, effectiveMonthlyRate,
        acVizSplit, acTravel, acOneTime,
        oneTime: snapOneTime, recurring: snapRecurring, grandTotal: snapGrandTotal,
      } = snapshotMoney(activeC, { isAlacarte, monthlyRateOverride, crmIncluded, term: activeTerm });
      // À La Carte milestones come from the same resolver the live schedule
      // uses, so the saved amounts can never drift from what the rep showed:
      // 50/25/25, 25/50/25 (over $100k), or the rep's 50/50. Travel bills at
      // signing in every plan.
      const acPlan = resolvePaymentPlan({
        setupFee: 0,
        vizSubtotal: acVizSplit,
        travelCost: acTravel,
        collateralTotal: collateralActive ? (collateralMath?.collateralTotal || 0) : 0,
        isAlacarte: true,
        alacartePayPlan,
        payPlanChoice,
        scope: activeC,
        pifDiscount,
        milestonePolicy,
        finalPaymentPolicy,
        modelingFee: Number(activeC.modelingFee) || 0,
      });
      const { p1: acP1, p2: acP2, p3: acP3 } = acPlan;
      const snapSetupFee   = isAlacarte ? 0 : activeC.setupFee;
      const snapMonthly    = isAlacarte ? 0 : effectiveMonthlyRate;
      const snapMonthlyAuto= isAlacarte ? 0 : autoMonthlyRate;
      // snapGrandTotal is the AGREED monthly × term, not activeC.grandTotal:
      // that one is built off the rate-card monthly, before the rep's
      // override and the CRM deduction, so it over-stated the deal (Shopped
      // at Wilton Manors saved $77,860 against $65,860 on the signed agreement).
      const calcSnapshot = {
        setupFee: snapSetupFee,
        monthlyRate: snapMonthly,
        monthlyRateAuto: snapMonthlyAuto,
        monthlyRateOverride: overriddenMonthly,
        // What the proposal and contracts state for after month 12. Resolved
        // here so the PDF never has to re-derive a rep's figure.
        postTermMonthly: isAlacarte ? 0 : (postTermOverride !== null && Number.isFinite(postTermOverride)
          ? Math.max(0, Math.round(postTermOverride))
          : defaultPostTermMonthly(mode)),
        // Internal audit trail — which lines carried a manual price and what
        // the calculator would have charged. Never surfaced in the client PDF.
        priceOverrides: cleanPriceOverrides(priceOverrides),
        overrideDiscountChoice,
        bundleApplied: !!bundleApplied,
        crmIncluded: !!crmIncluded,
        crmDeduction: crmIncluded ? 0 : 1500,
        // À La Carte is never a VEXOS engagement — force the platform flags
        // off so a saved à-la-carte quote can't record both.
        vexosEnabled: isAlacarte ? false : !!vexosEnabled,
        vexosActive: isAlacarte ? false : !!vexosActive,
        activeTab,
        isAlacarte: !!isAlacarte,
        lotExplorer: {
          enabled: isLotExplorer,
          lotCount: lotExplorerLotCount,
          ratePerLot: lotExplorerRatePerLot,
          baseFee: lotExplorerCalc.baseFee,
          includePoi: lotExplorerIncludePoi,
          includeCrm: lotExplorerIncludeCrm,
          includeMedia: lotExplorerIncludeMedia,
          includeBackend: lotExplorerIncludeBackend,
          timelineWeeks: lotExplorerTimelineWeeks,
          hostingMonthly: lotExplorerHostingMonthly,
        },
        recurringContractValue: snapRecurring,
        exteriorAddonsSubtotal: activeC.exteriorAddonsSubtotal,
        dayToNightCost: activeC.dayToNightCost,
        additionalKfCost: activeC.additionalKfCost,
        animatedKfCost: activeC.animatedKfCost,
        masterOrbitCost: activeC.masterOrbitCost,
        // Quantities for PDF sub-labels. SFH reuses the condo state
        // variables, so it must read the condo side (not community).
        // `additionalKfQty` counts every billed extra keyframe — base orbit
        // AND suborbit — so it stays the single number the money lines back.
        additionalKfQty: Number(activeC.additionalKfQty) || 0,
        // Split of that total, so the scope can nest suborbit extras under
        // the orbit they belong to instead of listing one flat count.
        baseOrbitExtraKfQty: (isCondoActive || isSFHActive) ? (Number(additionalKeyframesQty) || 0) : (Number(c_additionalKfQty) || 0),
        subOrbitExtraKfQty: Number(activeC.subOrbitExtraKfQty) || 0,
        baseOrbitKeyframes: Number(activeC.baseOrbitKeyframes) || BASE_ORBIT_KEYFRAMES,
        totalExteriorKeyframes: Number(activeC.totalExteriorKeyframes) || 0,
        // Per-suborbit extra keyframes, in order — [0, 2] is two suborbits,
        // the second carrying two extras on top of its bundled pair.
        subOrbits: [...((isCondoActive || isSFHActive) ? subOrbits : c_subOrbits)],
        animatedKfEnabled: (isCondoActive || isSFHActive) ? !!animatedKeyframesEnabled : !!c_animatedKfEnabled,
        animatedKfQty: (isCondoActive || isSFHActive) ? (Number(animatedKeyframesQty) || 0) : (Number(c_animatedKfQty) || 0),
        masterOrbitsQty: Number(activeC.masterOrbitsQty) || 0,
        // Master plan: how many of those suborbits are in the package (the
        // first N of `subOrbits`), and the keyframes each was sold with.
        // Readers print the rest as Additional Orbits.
        masterPlan: !isCondoActive && !isSFHActive && !!activeC.masterPlan,
        masterPlanIncludedSubOrbits: (!isCondoActive && !isSFHActive && Number(activeC.masterPlanIncludedSubOrbits)) || 0,
        masterPlanSubOrbitKeyframes: MASTER_PLAN_SUBORBIT_KEYFRAMES,
        // Keyframes actually receiving the dusk pass — drives the auto-priced
        // day-to-night line.
        dayToNightKfQty: Number(activeC.dayToNightKfQty) || 0,
        // VEXOS Cover Landing Page (new add-on)
        vexosCoverLandingPageActive: !!activeC.vexosCoverLandingPageActive,
        vexosCoverLandingPageCost:    Number(activeC.vexosCoverLandingPageCost) || 0,
        // Every VR tour, in row order, with its own point count — the
        // proposal itemizes them ("Floor plan 1 · 8 points") because a
        // "6–10 points per tour" range was unclear to clients. `n` is the
        // row number the rep saw. SFH reuses the condo rows; community has
        // its own (it used to read the community rows for SFH as well).
        interiorVRPointsSum: isCondoActive ? activeC.interiorVRPointsSum : activeC.homeVRPointsSum,
        interiorVRTours: vrTourList((isCondoActive || isSFHActive) ? interiorVR : c_homeModelVR),
        amenityVRTours: vrTourList((isCondoActive || isSFHActive) ? amenityVR : c_amenityVR),
        // Min/max kept for older readers of the snapshot.
        interiorVRPointsMin: vrPointsBound((isCondoActive || isSFHActive) ? interiorVR : c_homeModelVR, Math.min),
        interiorVRPointsMax: vrPointsBound((isCondoActive || isSFHActive) ? interiorVR : c_homeModelVR, Math.max),
        amenityVRPointsMin: vrPointsBound((isCondoActive || isSFHActive) ? amenityVR : c_amenityVR, Math.min),
        amenityVRPointsMax: vrPointsBound((isCondoActive || isSFHActive) ? amenityVR : c_amenityVR, Math.max),
        exteriorRendersQty: activeC.exteriorRendersQty,
        exteriorRendersCost: activeC.exteriorRendersCost,
        // Conceptual Rendering Hybrid Workflow (VEXOS + À La Carte)
        conceptualRendersQty: activeC.conceptualRendersQty,
        conceptualRenderRate: activeC.conceptualRenderRate,
        conceptualRendersCost: activeC.conceptualRendersCost,
        // Exterior VR tour (single per-project)
        exteriorVRActive: !!activeC.exteriorVRActive,
        exteriorVRPoints: Number(activeC.exteriorVRPoints) || 0,
        exteriorVRCost: Number(activeC.exteriorVRCost) || 0,
        // Phase 05 — Video Animation
        aiMotionDur: activeC.aiMotionDur,
        // Every video of each kind, in seconds — see ./videoLineup.
        aiMotionVideos: activeC.aiMotionDur != null ? (activeC.aiMotionVideos || []) : [],
        aiMotionCost: activeC.aiMotionCost,
        // True when the AI Motion sold rides on client-provided renderings
        // (À La Carte gate bypass) — drives the proposal/PDF sub-label.
        aiMotionClientRenders: !!(activeC.aiMotionClientRenders && activeC.aiMotionDur != null),
        cinematicDur: activeC.cinematicDur,
        cinematicVideos: activeC.cinematicVideos || [],
        cinematicCost: activeC.cinematicCost,
        videoAnimationGross: activeC.videoAnimationGross,
        videoAnimationCost: activeC.videoAnimationCost,
        motionPackageActive: activeC.motionPackageActive,
        motionPackagePct: activeC.motionPackagePct,
        motionPackageSavings: activeC.motionPackageSavings,
        videoStandalone: !!activeC.videoStandalone,
        videoProjectSize: activeC.videoProjectSize,
        // Standalone video breakdown (size tier + typed line items) for the
        // proposal. Null when the video is a Vexos add-on.
        standaloneVideo: activeC.standaloneVideo
          ? {
              size: activeC.videoProjectSize,
              sizeLabel: activeC.standaloneVideo.sizeLabel,
              heroLabel: activeC.standaloneVideo.heroLabel,
              heroSeconds: activeC.standaloneVideo.heroSeconds,
              animationTotal: activeC.standaloneVideo.animationTotal,
              editingTotal: activeC.standaloneVideo.editingTotal,
              total: activeC.standaloneVideo.total,
              lines: activeC.standaloneVideo.lines,
            }
          : null,
        // Phase 06 — Floor Plans
        total2DPlans: activeC.total2DPlans,
        floorPlans2DGross: activeC.floorPlans2DGross,
        floorPlans2DPct: activeC.floorPlans2DPct,
        floorPlans2DSavings: activeC.floorPlans2DSavings,
        floorPlans2DCost: activeC.floorPlans2DCost,
        // `total3DPlans` is the BILLABLE count (unit types × levels) — the
        // number production and the volume tier both work off. The unit /
        // level split rides alongside so the scope can say "2 unit types ·
        // 4 levels" instead of a bare "4" nobody can interpret.
        total3DPlans: activeC.total3DPlans,
        total3DUnits: activeC.total3DUnits,
        total3DLevels: activeC.total3DLevels,
        floorPlans3DWorkflow: activeC.floorPlans3DWorkflow || "hybrid",
        floorPlans3DGross: activeC.floorPlans3DGross,
        floorPlans3DPct: activeC.floorPlans3DPct,
        floorPlans3DSavings: activeC.floorPlans3DSavings,
        floorPlans3DCost: activeC.floorPlans3DCost,
        floorPlansCost: activeC.floorPlansCost,
        interiorRendersCost: isCondoActive ? activeC.interiorRendersCost : activeC.homeRendersCost,
        interiorVRCost: isCondoActive ? activeC.interiorVRCost : activeC.homeVRCost,
        totalInteriorRenders: isCondoActive ? activeC.totalInteriorRenders : activeC.totalHomeRenders,
        totalInteriorVR: isCondoActive ? activeC.totalInteriorVR : activeC.totalHomeVR,
        amenityRendersCost: activeC.amenityRendersCost,
        amenityVRCost: activeC.amenityVRCost,
        totalAmenityRenders: activeC.totalAmenityRenders,
        totalAmenityVR: activeC.totalAmenityVR,
        // Real-Time Configurator — one printed line per VR section, already
        // inside vizGross. The sub-line text rides along so the PDF prints
        // exactly what the calculator shows without mirroring the labels.
        residenceConfiguratorCost: Math.round(activeC.residenceConfiguratorCost || 0),
        residenceConfiguratorSub: activeC.residenceConfiguratorCost > 0 ? configuratorSub(activeC.residenceConfigurator) : "",
        amenityConfiguratorCost: Math.round(activeC.amenityConfiguratorCost || 0),
        amenityConfiguratorSub: activeC.amenityConfiguratorCost > 0 ? configuratorSub(activeC.amenityConfigurator) : "",
        vrConfiguratorCost: Math.round(activeC.vrConfiguratorCost || 0),
        vizGross: activeC.vizGross,
        vizNet: activeC.vizNet,
        bundle: activeC.bundle,
        bundleSavings: activeC.bundleSavings,
        // Hand-priced quote with discounts removed: the proposal must not
        // mention one. Most PDF blocks fall away on their own once the savings
        // are zero; this drives the copy that isn't value-gated. Always
        // written (true or false) so readers never fall back to guessing from
        // `priceOverrides`, which a kept-discount quote also carries.
        manualPricing: !!activeC.manualPricing,
        // Additional proposal discount — its own printed line, already taken
        // off oneTimeAllIn / grandTotal below. The backend payment plan
        // subtracts it from the visualization split, as the calculator does.
        proposalDiscount: Math.round(activeC.proposalDiscount || 0),
        proposalDiscountPct: activeC.proposalDiscount > 0 ? (activeC.proposalDiscountPct ?? null) : null,
        // Flat conceptual design fee — its own printed line, already added to
        // oneTimeAllIn / grandTotal below; the backend payment plan adds it to
        // the visualization split, as the calculator does.
        conceptualDesignFee: Math.round(activeC.conceptualDesignFee || 0),
        conceptualDesignFeeNote: activeC.conceptualDesignFee > 0 ? (activeC.conceptualDesignFeeNote || "") : "",
        // Per-space interior design (0 / 0 for a legacy flat fee) — the
        // proposal prints "N spaces × $rate per space" and the scope blurb.
        conceptualDesignFeeSpaces: activeC.conceptualDesignFee > 0 ? (activeC.conceptualDesignFeeSpaces || 0) : 0,
        conceptualDesignFeeRate: activeC.conceptualDesignFee > 0 ? (activeC.conceptualDesignFeeRate || 0) : 0,
        // 3D modeling fee — its own printed line with the credit promise,
        // already in oneTimeAllIn; the backend plan adds it to the viz split.
        modelingFee: Math.round(activeC.modelingFee || 0),
        modelingFeeTier: activeC.modelingFee > 0 ? (activeC.modelingFeeTier || "") : "",
        // Additional revisions + on-site travel (drives PDF scope lines).
        revisionRounds: activeC.revisionRounds || 0,
        revisionUnitCost: Math.round(activeC.revisionUnitCost || 0),
        revisionsCost: Math.round(activeC.revisionsCost || 0),
        revisionsIncluded: Number(REVISIONS_CONFIG.includedRounds) || 0,
        revisionsPct: Number(REVISIONS_CONFIG.additionalPct) || 0,
        travelDays: activeC.travelDays || 0,
        travelDayRate: Math.round(activeC.travelDayRate || 0),
        travelCost: Math.round(activeC.travelCost || 0),
        oneTimeAllIn: snapOneTime,
        grandTotal: snapGrandTotal,
      };
      const payload = {
        client_name: clientName.trim(),
        client_entity: clientEntity.trim(),
        client_email: clientEmail.trim(),
        project_type: isSFHActive ? "sfh" : (isCondoActive ? "condo" : "community"),
        // A master plan is a community quote; this flag is what every client
        // surface reads to name it and its included orbits.
        master_plan: !isSFHActive && !isCondoActive && !!c_masterPlan,
        inputs,
        calc_snapshot: calcSnapshot,
        // À La Carte strips the platform setup/monthly out of the milestones
        // (Payment 1 no longer carries a VEXOS setup fee; no monthly row).
        payment_schedule: isAlacarte
          ? { ...paymentSchedule, p1: acP1, p2: acP2, p3: acP3, monthly: 0, term: activeTerm }
          : paymentSchedule,
        contract_term: isSFHActive ? contractTerm : (isCondoActive ? contractTerm : c_contractTerm),
        // The pay-in-full election rides the quote, not just the Stripe
        // session: a client who wires the full amount never creates one, and
        // the project's milestone schedule still has to collapse to one row.
        pay_in_full: payInFull,
        // Drone (project-wide) — captured at top level so the proposal
        // generator can render line items without recomputing math.
        drone: {
          captureAutoApplied:        !!activeC.droneCaptureActive,
          captureFee:                Math.round(activeC.droneCaptureFee || 0),
          interiorDroneShotsActive:  !!activeC.interiorDroneShotsActive,
          interiorDroneShotsPrice:   Math.round(activeC.interiorDroneShotsCost || 0),
          amenityDroneShotsActive:   !!activeC.amenityDroneShotsActive,
          amenityDroneShotsPrice:    Math.round(activeC.amenityDroneShotsCost || 0),
        },
        marketing: marketingActive ? {
          active: true,
          campaignType: marketing.campaignType,
          priceTier: marketing.priceTier,
          units: marketing.units,
          sellOutMonths: marketing.sellOutMonths,
          conversionRate: marketing.conversionRate,
          contentStrategyActive: marketing.contentStrategyActive,
          setupFee: marketingMath.setupFee,
          monthlyServiceFee: marketingMath.monthlyServiceFee,
          // Internal audit trail — which campaign fees the rep set by hand and
          // what the tier would have charged. The client-facing proposal shows
          // only the fee above, with no trace that it was manual.
          setupFeeOverride: marketingMath.setupFeeOverride,
          monthlyServiceFeeOverride: marketingMath.monthlyServiceFeeOverride,
          setupFeeAuto: marketingMath.setupFeeAuto,
          monthlyServiceFeeAuto: marketingMath.monthlyServiceFeeAuto,
          // Tier metadata — drives the small "(Tier N — X units)" label on
          // the marketing output card + PDF Your Investment page.
          campaignFeeTier: marketingMath.campaignFeeTier,
          campaignFeeTierLabel: marketingMath.campaignFeeTierLabel,
          campaignFeeTierUnits: marketingMath.campaignFeeTierUnits,
          monthlyAdSpend: Math.round(marketingMath.monthlyAdSpend),
          monthlyBudget: Math.round(marketingMath.monthlyBudget),
          totalCampaignInvestment: Math.round(marketingMath.totalCampaignInvestment),
          monthlyLeads: Math.round(marketingMath.monthlyLeads),
          totalLeads: Math.round(marketingMath.totalLeads),
        } : { active: false },
        collateral: collateralActive ? {
          active: true,
          ...collateral,
          collateralTotal: Math.round(collateralMath.collateralTotal),
          retainerMonthly: Math.round(collateralMath.retainerMonthly),
          // Freeze the rate card this quote was priced against. The proposal
          // page and the PDF itemize collateral line by line; without a saved
          // snapshot they fall back to hardcoded defaults, so an admin price
          // change makes those lines disagree with the total they sum into.
          // Carries any hand-set line prices (collateralMath.prices is the
          // rate card with overrides laid over it), so the itemized lines
          // still sum to collateralTotal. `priceOverrides` rides along via
          // the spread above for audit + hydrate.
          prices: { ...collateralMath.prices },
          priceOverrides: cleanCollateralOverrides(collateral.priceOverrides),
        } : { active: false },
      };
      let resData;
      if (savedQuoteId) {
        const res = await apiClient.patch(`/vexos-quotes/${savedQuoteId}`, payload);
        resData = res.data;
      } else {
        const res = await apiClient.post("/vexos-quotes", payload);
        resData = res.data;
      }
      setSavedQuoteId(resData.id);
      // The server stamps on create and leaves it alone on update, so its
      // answer is the one that counts — not the state we sent.
      setMilestonePolicy(quoteMilestonePolicy(resData));
      setFinalPaymentPolicy(quoteFinalPaymentPolicy(resData));
      // Refresh the saved-timeline snapshot from the server response so
      // the Production Timeline card matches the just-rendered PDF.
      if (resData?.timeline && typeof resData.timeline === "object") {
        setSavedTimelineSnapshot(resData.timeline);
      }
      setDirty(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
      toast.success(`Quote saved for ${clientName.trim()}.`);
      if (openProposalAfterSave) {
        window.open(`/proposals/${resData.id}`, "_blank", "noopener,noreferrer");
      }
      return resData;
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Failed to save quote.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveQuoteAndOpenProposal = () => persistQuote({ openProposalAfterSave: true });
  const saveQuoteOnly = () => persistQuote({ openProposalAfterSave: false });

  // Sidebar Save Quote button — quiet save with empty-name shake animation
  // and the "✓ Saved" microstate handled by `justSaved`.
  const [nameShake, setNameShake] = useState(false);
  const handleSidebarSave = async () => {
    if (!clientName.trim()) {
      setNameShake(true);
      setTimeout(() => setNameShake(false), 800);
      return;
    }
    await saveQuoteOnly();
  };

  // ── HYDRATE FROM ROUTE ──────────────────────────────────────────────
  // When the calculator is opened at /sales/client-calculator/:quoteId,
  // fetch the saved quote and restore every input + selection. Phase 1–6
  // state is repopulated via the appropriate setters depending on whether
  // the quote is a condo or community build.
  useEffect(() => {
    if (!routeQuoteId) return;
    let cancelled = false;
    setHydrating(true);
    (async () => {
      try {
        const res = await apiClient.get(`/vexos-quotes/${routeQuoteId}`);
        if (cancelled) return;
        const q = res.data;
        setSavedQuoteId(q.id);
        // Before anything else that touches money: an older quote resolves to
        // the milestone it was sold under, not today's.
        setMilestonePolicy(quoteMilestonePolicy(q));
        setFinalPaymentPolicy(quoteFinalPaymentPolicy(q));
        // Capture the saved timeline so the Production Timeline card
        // matches the PDF until the user edits any input.
        if (q.timeline && typeof q.timeline === "object") {
          setSavedTimelineSnapshot(q.timeline);
        }
        setClientName(q.client_name || "");
        setClientEntity(q.client_entity || "");
        setClientEmail(q.client_email || "");
        // Restore closure (Stripe + DocuSign) state if the quote has
        // already been touched by either flow. Drives the status pills
        // + button copy in the Presentation Mode CTA row.
        setClosureState({
          docusignEnvelopeId: q.docusign_envelope_id || null,
          docusignStatus: q.docusign_status || null,
          docusignSentAt: q.docusign_sent_at || null,
          stripeSessionId: q.stripe_session_id || null,
          stripePaymentLink: q.stripe_payment_link || null,
          stripePaymentStatus: q.stripe_payment_status || null,
          stripeAmount: q.stripe_amount || null,
        });
        // A quote already charged (or set up to be charged) in full reopens on
        // that choice — otherwise a rep resending the link would quietly drop
        // back to the deposit.
        setCollectMode(q.pay_in_full ? "full" : "deposit");
        const savedType = q.project_type;
        if (savedType === "sfh") setMode("sfh");
        else if (savedType === "community") setMode("community");
        else setMode("condo");
        const i = q.inputs || {};
        setCMasterPlan(savedType === "community" && !!(q.master_plan || i.c_masterPlan));
        const safe = (v, fallback = "") => (v === undefined || v === null ? fallback : v);
        // Restore manual monthly-rate override if the saved quote has
        // one. The override survives mode-switches because it's a single
        // shared field for whichever project type was active.
        if (i.monthlyRateOverride !== undefined && i.monthlyRateOverride !== null && Number.isFinite(Number(i.monthlyRateOverride))) {
          setMonthlyRateOverride(Number(i.monthlyRateOverride));
        } else {
          setMonthlyRateOverride(null);
        }
        setPostTermOverride(
          i.postTermOverride !== undefined && i.postTermOverride !== null && Number.isFinite(Number(i.postTermOverride))
            ? Number(i.postTermOverride) : null
        );
        // Restore manual per-line price overrides. Opened expanded when the
        // quote carries any, so nobody edits it unaware of a manual price.
        const savedOverrides = cleanPriceOverrides(i.priceOverrides);
        setPriceOverrides(savedOverrides);
        // Quotes saved before the keep/remove choice existed had their
        // discounts removed by any override — reopen them exactly as sent.
        const hasSavedOverrides = Object.keys(savedOverrides).length > 0;
        setOverrideDiscountChoice(
          i.overrideDiscountChoice === "keep" || i.overrideDiscountChoice === "remove"
            ? i.overrideDiscountChoice
            : (hasSavedOverrides ? "remove" : null),
        );
        if (Object.keys(savedOverrides).length > 0) setOverridePanelOpen(true);
        // Restore the bundle choice. Absent = applied: quotes saved before the
        // opt-in were priced with the bundle on and must reopen that way.
        setBundleApplied(savedBundleApplied(i));
        // Restore the explicitly-selected proposal rep (internal users only;
        // empty for quotes created by Julian/Jordan under their own account).
        setSalesRep(typeof i.salesRep === "string" ? i.salesRep : "");
        // Restore VEXOS CRM opt-out state. Default = included.
        setCrmIncluded(i.crmIncluded === false ? false : true);
        // Restore the à la carte payment split. Default = three payments.
        setAlacartePayPlan(i.alacartePayPlan === PLAN_TWO_PART ? PLAN_TWO_PART : ALACARTE_THREE_PART);
        // Restore the rep's plan (100% or 75 / 25). Absent = let the rules
        // decide — every quote saved before 75 / 25 became the default.
        setPayPlanChoice(normalizePayPlanChoice(i.payPlanChoice));
        // Restore the pay-in-full discount switch. Default ON, so quotes saved
        // before the switch existed keep the 10% they were presented with.
        setPifDiscount(i.pifDiscount !== false);
        // Restore the additional proposal discount. Absent = none.
        setProposalDiscount(normalizeProposalDiscount(i.proposalDiscount));
        // Restore the complimentary add-ons as they were given. Absent = none.
        setFreeAddOns(normalizeFreeAddOns(i.freeAddOns));
        // Restore the conceptual design fee. Absent = none.
        setConceptualDesignFee(normalizeConceptualDesignFee(i.conceptualDesignFee));
        setModelingFee(modelingFeeFromInputs(i));
        setVrConfigurator(normalizeVrConfigurator(i.vrConfigurator));
        setTimelineOverride(normalizeTimelineOverride(i.timelineOverride));
        // Restore the AI Motion client-provided-renderings unlock (à la carte).
        setAiMotionClientRenders(!!i.aiMotionClientRenders);
        // Restore VEXOS master toggle. Default = ON (so existing saved
        // quotes that pre-date the toggle continue to include VEXOS).
        // Read from new `vexosEnabled` field; fall back to legacy
        // `vexosActive` for any saved quotes from the wiring-only phase.
        const rawVexosFlag = (i.vexosEnabled !== undefined)
          ? i.vexosEnabled
          : i.vexosActive;
        setVexosEnabled(rawVexosFlag === false ? false : true);
        // Restore additional revision rounds + travel days (default 0 for
        // quotes saved before these features existed).
        setAdditionalRevisions(Math.max(0, Math.round(Number(i.additionalRevisions) || 0)));
        setTravelDays(Math.max(0, Math.round(Number(i.travelDays) || 0)));
        // Restore A La Carte tab state. Quotes saved before A La Carte
        // existed default to the VEXOS Platform tab. Explicit `activeTab`
        // value wins; otherwise fall back to `isAlacarte` flag.
        if (typeof i.activeTab === "string" && ["vexos", "marketing", "alacarte", "lotexplorer"].includes(i.activeTab)) {
          setActiveTab(i.activeTab);
        } else if (i.isAlacarte) {
          setActiveTab("alacarte");
        } else if (i.lotExplorer && i.lotExplorer.enabled) {
          setActiveTab("lotexplorer");
        }
        // Restore Lot Explorer™ scope inputs (mirrors what persistQuote
        // writes under inputs.lotExplorer).
        if (i.lotExplorer && typeof i.lotExplorer === "object") {
          const le = i.lotExplorer;
          if (typeof le.lotCount === "number") setLotExplorerLotCount(le.lotCount);
          if (typeof le.ratePerLot === "number") setLotExplorerRatePerLot(le.ratePerLot);
          if (typeof le.includePoi === "boolean") setLotExplorerIncludePoi(le.includePoi);
          if (typeof le.includeCrm === "boolean") setLotExplorerIncludeCrm(le.includeCrm);
          if (typeof le.includeMedia === "boolean") setLotExplorerIncludeMedia(le.includeMedia);
          if (typeof le.includeBackend === "boolean") setLotExplorerIncludeBackend(le.includeBackend);
          if (typeof le.timelineWeeks === "number") setLotExplorerTimelineWeeks(le.timelineWeeks);
          if (typeof le.hostingMonthly === "number") setLotExplorerHostingMonthly(le.hostingMonthly);
        }
        // Re-baseline the keyframe-pool trackers to what this quote saved.
        // Without this the follow-the-total effect would see the *previous*
        // quote's total, mistake a deliberately-reduced animated or
        // day-to-night count for an un-overridden one, and silently reset it
        // back to "all keyframes" the moment the quote reopened.
        prevTotalKfRef.current = totalExteriorKeyframes(
          i.additionalKeyframesQty, subOrbitsFromSaved(i.subOrbits, i.masterOrbitsQty));
        c_prevTotalKfRef.current = totalExteriorKeyframes(
          i.c_additionalKfQty, subOrbitsFromSaved(i.c_subOrbits, i.c_masterOrbitsQty));

        const isCondoSaved = savedType === "condo";
        if (isCondoSaved) {
          setFloors(safe(i.floors));
          setUnits(safe(i.units));
          setUniqueFloorPlans(safe(i.uniqueFloorPlans));
          setAvgFloorPlanSqFt(safe(i.avgFloorPlanSqFt));
          setAmenitySpaces(safe(i.amenitySpaces));
          setTotalAmenitySqFt(safe(i.totalAmenitySqFt));
          setDayToNight(!!i.dayToNight);
          // Quotes saved before day-to-night went per-keyframe have no count;
          // fall back to the base orbit's keyframes so their price is unchanged.
          setAdditionalKeyframesQty(safe(i.additionalKeyframesQty, 0));
          setAnimatedKeyframesEnabled(!!i.animatedKeyframesEnabled);
          setAnimatedKeyframesQty(safe(i.animatedKeyframesQty, 0));
          setSubOrbits(subOrbitsFromSaved(i.subOrbits, i.masterOrbitsQty));
          setExteriorRendersQty(safe(i.exteriorRendersQty, 0));
          setConceptualRendersQty(safe(i.conceptualRendersQty, 0));
          setExteriorVR(i.exteriorVR ?? null);
          setInteriorRenders(i.interiorRenders || {});
          setInteriorVR(i.interiorVR || {});
          setAmenityRenders(i.amenityRenders || {});
          setAmenityVR(i.amenityVR || {});
          setAiMotionVideos(normalizeVideos(i.aiMotionVideos, i.aiMotionDur));
          setCinematicVideos(normalizeVideos(i.cinematicVideos, i.cinematicDur));
          setVideoProjectSize(i.videoProjectSize || "S");
          setVideoStandaloneSel(hydrateStandaloneSel(i.videoStandaloneSel));
          setFloorPlans2D(i.floorPlans2D || {});
          setFloorPlans3D(i.floorPlans3D || {});
          setFloorPlans3DLevels(i.floorPlans3DLevels || {});
          setFloorPlans3DWorkflow(i.floorPlans3DWorkflow === "traditional" ? "traditional" : "hybrid");
          setInteriorDroneShots(!!i.interiorDroneShots);
          setAmenityDroneShots(!!i.amenityDroneShots);
          setVexosCoverLandingPage(!!i.vexosCoverLandingPage);
        } else {
          setCLots(safe(i.c_lots));
          setCHomeModels(safe(i.c_homeModels));
          setCAvgHomeSqFt(safe(i.c_avgHomeSqFt));
          setCAmenitySpaces(safe(i.c_amenitySpaces));
          setCTotalAmenitySqFt(safe(i.c_totalAmenitySqFt));
          setCDayToNight(!!i.c_dayToNight);
          setCAdditionalKfQty(safe(i.c_additionalKfQty, 0));
          setCAnimatedKfEnabled(!!i.c_animatedKfEnabled);
          setCAnimatedKfQty(safe(i.c_animatedKfQty, 0));
          setCSubOrbits(subOrbitsFromSaved(i.c_subOrbits, i.c_masterOrbitsQty));
          setCExteriorRendersQty(safe(i.c_exteriorRendersQty, 0));
          setCConceptualRendersQty(safe(i.c_conceptualRendersQty, 0));
          setCExteriorVR(i.c_exteriorVR ?? null);
          setCHomeModelRenders(i.c_homeModelRenders || {});
          setCHomeModelVR(i.c_homeModelVR || {});
          setCAmenityRenders(i.c_amenityRenders || {});
          setCAmenityVR(i.c_amenityVR || {});
          setCAiMotionVideos(normalizeVideos(i.c_aiMotionVideos, i.c_aiMotionDur));
          setCCinematicVideos(normalizeVideos(i.c_cinematicVideos, i.c_cinematicDur));
          setCVideoProjectSize(i.c_videoProjectSize || "S");
          setCVideoStandaloneSel(hydrateStandaloneSel(i.c_videoStandaloneSel));
          setCFloorPlans2D(i.c_floorPlans2D || {});
          setCFloorPlans3D(i.c_floorPlans3D || {});
          setCFloorPlans3DLevels(i.c_floorPlans3DLevels || {});
          setCFloorPlans3DWorkflow(i.c_floorPlans3DWorkflow === "traditional" ? "traditional" : "hybrid");
          setCInteriorDroneShots(!!i.c_interiorDroneShots);
          setCAmenityDroneShots(!!i.c_amenityDroneShots);
          setCVexosCoverLandingPage(!!i.c_vexosCoverLandingPage);
        }
        // ── SFH HYDRATION ────────────────────────────────────────
        // SFH quotes persist their viz scope under condo state keys
        // (uniqueFloorPlans, interiorRenders, floorPlans2D, etc.) plus
        // SFH-specific project details (home sq ft, floors, outdoor sq ft).
        if (savedType === "sfh") {
          setSfhHomeSqFt(safe(i.sfh_homeSqFt));
          setFloors(safe(i.sfh_floors));
          setSfhAmenitySqFt(safe(i.sfh_amenitySqFt));
          setUniqueFloorPlans(safe(i.uniqueFloorPlans));
          setDayToNight(!!i.dayToNight);
          // Quotes saved before day-to-night went per-keyframe have no count;
          // fall back to the base orbit's keyframes so their price is unchanged.
          setAdditionalKeyframesQty(safe(i.additionalKeyframesQty, 0));
          setAnimatedKeyframesEnabled(!!i.animatedKeyframesEnabled);
          setAnimatedKeyframesQty(safe(i.animatedKeyframesQty, 0));
          setSubOrbits(subOrbitsFromSaved(i.subOrbits, i.masterOrbitsQty));
          setExteriorRendersQty(safe(i.exteriorRendersQty, 0));
          setConceptualRendersQty(safe(i.conceptualRendersQty, 0));
          setExteriorVR(i.exteriorVR ?? null);
          setInteriorRenders(i.interiorRenders || {});
          setInteriorVR(i.interiorVR || {});
          setAiMotionVideos(normalizeVideos(i.aiMotionVideos, i.aiMotionDur));
          setCinematicVideos(normalizeVideos(i.cinematicVideos, i.cinematicDur));
          setVideoProjectSize(i.videoProjectSize || "S");
          setVideoStandaloneSel(hydrateStandaloneSel(i.videoStandaloneSel));
          setFloorPlans2D(i.floorPlans2D || {});
          setFloorPlans3D(i.floorPlans3D || {});
          setFloorPlans3DLevels(i.floorPlans3DLevels || {});
          setFloorPlans3DWorkflow(i.floorPlans3DWorkflow === "traditional" ? "traditional" : "hybrid");
          setInteriorDroneShots(!!i.interiorDroneShots);
          setVexosCoverLandingPage(!!i.vexosCoverLandingPage);
        }
        if (q.contract_term) {
          if (isCondoSaved || savedType === "sfh") setContractTerm(Number(q.contract_term));
          else setCContractTerm(Number(q.contract_term));
        }
        // Marketing + Collateral state restored from saved JSON (Tab 2).
        if (q.marketing && q.marketing.active) {
          setMarketing((prev) => ({
            ...prev,
            active: true,
            campaignType:          q.marketing.campaignType || prev.campaignType,
            priceTier:             q.marketing.priceTier ?? null,
            units:                 q.marketing.units ?? prev.units,
            sellOutMonths:         q.marketing.sellOutMonths ?? prev.sellOutMonths,
            conversionRate:        q.marketing.conversionRate ?? prev.conversionRate,
            contentStrategyActive: !!q.marketing.contentStrategyActive,
            // Restore hand-set campaign fees so reopening the quote shows
            // exactly the pricing the client was sent.
            setupFeeOverride:          cleanFeeOverride(q.marketing.setupFeeOverride),
            monthlyServiceFeeOverride: cleanFeeOverride(q.marketing.monthlyServiceFeeOverride),
          }));
        }
        if (q.collateral && q.collateral.active) {
          setCollateral((prev) => ({
            ...prev,
            landingPage:           !!q.collateral.landingPage,
            fullWebsite:           !!q.collateral.fullWebsite,
            brandingPackage:       !!q.collateral.brandingPackage,
            investmentPresentation:!!q.collateral.investmentPresentation,
            developmentFlipbook:   !!q.collateral.developmentFlipbook,
            billboardStatic:       !!q.collateral.billboardStatic,
            billboardAnimated:     !!q.collateral.billboardAnimated,
            unitInventorySheet:    !!q.collateral.unitInventorySheet,
            animatedRendersQty:    Number(q.collateral.animatedRendersQty)   || 0,
            renderEnhancementQty:  Number(q.collateral.renderEnhancementQty) || 0,
            socialMediaRetainerPackage: q.collateral.socialMediaRetainerPackage || null,
            priceOverrides: cleanCollateralOverrides(q.collateral.priceOverrides),
          }));
        }
        // If ?view=summary is in the URL, open the relevant overlay on load.
        // For Lot Explorer quotes the VEXOS walkthrough overlay shows no
        // Lot Explorer data (activeCalc has no scope selected), so we open
        // the Lot Explorer overlay instead. Read from raw `i` / `c` because
        // the React state setters above haven't propagated to `isLotExplorer`
        // yet within this batch.
        if (searchParams.get("view") === "summary") {
          const isLEQuote = !!(
            i.activeTab === "lotexplorer" ||
            i.lotExplorer?.enabled ||
            q.calc_snapshot?.lotExplorer?.enabled
          );
          if (isLEQuote) {
            setLotExplorerExpanded(true);
          } else {
            setSummaryExpanded(true);
          }
        }
        setDirty(false);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load quote. It may have been deleted.");
        navigate("/sales/client-calculator", { replace: true });
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeQuoteId]);

  const handleBackToDashboard = () => {
    if (dirty) {
      const ok = window.confirm(
        "You have unsaved changes. Leave without saving?\n\nOK = Leave\nCancel = Stay"
      );
      if (!ok) return;
    }
    navigate("/sales/client-calculator");
  };

  // Fetch the printable proposal HTML (auth required) and open it as a blob
  // URL so the browser's native print-to-PDF can save it locally. The mode
  // parameter (`vexos | marketing | combined`) is appended as a query string
  // when supplied; otherwise the backend auto-detects the right mode.
  // The Marketing & Collateral tab's own download buttons call this instead
  // of openProposalPdf directly — they can be clicked right after toggling
  // marketing/collateral on, with no intervening save. Without persisting
  // first, they'd reopen whatever was last saved (often before marketing
  // was even turned on), silently dropping it from the PDF.
  const saveAndOpenProposal = async (quoteId, modeOverride, view) => {
    const saved = await persistQuote({ openProposalAfterSave: false });
    const idForPdf = saved?.id || quoteId || savedQuoteId;
    return openProposalPdf(idForPdf, modeOverride, view);
  };

  // `view` is "full" for the long reference document — every page, the way
  // the proposal read before it was condensed. Anything else (normally
  // nothing) gets the five-or-six-page client deck, which is what a rep
  // sends and what DocuSign puts in front of a signer.
  const openProposalPdf = async (quoteId, modeOverride, view) => {
    const id = quoteId || savedQuoteId;
    if (!id) {
      toast.error("Save the quote first to generate the proposal.");
      return;
    }
    try {
      const params = new URLSearchParams();
      if (modeOverride) params.set("mode", modeOverride);
      if (view === "full") params.set("view", "full");
      if (displayCurrency === "CAD" && fxRate) params.set("currency", "CAD");
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await apiClient.get(`/vexos-quotes/${id}/proposal-html${qs}`, {
        responseType: "text",
        transformResponse: [(d) => d],
      });
      const blob = new Blob([res.data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      console.error(e);
      toast.error("Failed to open proposal PDF.");
    }
  };

  // ── PRESENTATION PAYMENT SCHEDULE ───────────────────────────────────
  // The Quote Walkthrough is a CLIENT-facing surface, so every milestone it
  // shows has to carry the whole engagement. The marketing campaign setup fee
  // bills in full at signing — it joins no split — so it is added here.
  // Collateral is NOT: it already rides the plan percentages inside
  // `paymentSchedule.p1 / p2 / p3`. `collateralP1 / collateralP2` are non-zero
  // only for a quote with no viz and no platform, where there is no plan to
  // ride and collateral keeps its standalone 50 / 50 signing / delivery split.
  // This is the same math the sidebar Payment Structure block, the web proposal
  // (ProposalPage), and the PDF investment pages run.
  //
  // À La Carte included: it is a hard mode override, not a scope limit, so a
  // viz-only quote can still carry a campaign or collateral built on Tab 2 —
  // and its proposal PDF bills them the same way.
  const presentationSchedule = useMemo(() => {
    const marketingSetup = marketingActive ? Math.round(marketingMath?.setupFee || 0) : 0;
    const collateralOneTime = collateralActive && !paymentSchedule.collateralInSplit
      ? Math.round(collateralMath?.oneTimeTotal || 0)
      : 0;
    // Payment 1 takes the rounded half; Payment 2 takes the remainder, so
    // the two halves always sum EXACTLY to the collateral one-time.
    const collateralP1 = Math.round(collateralOneTime * 0.5);
    const collateralP2 = collateralOneTime - collateralP1;
    return {
      ...paymentSchedule,
      p1: (paymentSchedule.p1 || 0) + marketingSetup + collateralP1,
      p2: (paymentSchedule.p2 || 0) + collateralP2,
      marketingSetup,
      collateralP1,
      collateralP2,
      marketingMonthly: marketingActive ? Math.round(marketingMath?.monthlyServiceFee || 0) : 0,
      marketingMonths: marketingActive ? Number(marketing.sellOutMonths) || 0 : 0,
    };
  }, [paymentSchedule, marketingActive, marketingMath, collateralActive, collateralMath, marketing.sellOutMonths]);

  // The rep's call, made before presenting: does settling up front earn the
  // 5%, or is it simply one payment at full price? It prices the pay-in-full
  // option on a milestone schedule AND Payment 1 on a single-payment one, so it
  // is defined once here and rendered by whichever of the two is on screen.
  const discountSwitch = (
    <div className="mt-2.5 pt-2.5 border-t border-white/[0.08] flex items-center justify-between gap-3"
         data-testid="pif-discount-toggle">
      <p className="text-[9.5px] uppercase tracking-[0.14em] text-white/40">Discount</p>
      <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
        {[[true, `${PIF_DISCOUNT_PCT}% off`], [false, "No discount"]].map(([val, lbl]) => (
          <button
            key={String(val)}
            type="button"
            onClick={() => { markDirty(); setPifDiscount(val); }}
            className={`px-2.5 py-1 text-[10.5px] font-medium transition-colors ${pifDiscount === val ? "bg-[#8FA79B] text-[#000000]" : "bg-transparent text-white/60 hover:text-white/90"}`}
            data-testid={`pif-discount-${val ? "on" : "off"}`}
          >
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );

  // What the à-la-carte sidebar percentages are a percentage OF. That block
  // spells "visualization scope" out in full rather than using the short
  // register, so it needs collateral spelled out the same way.
  const acScopeCopy = paymentSchedule.collateralInSplit
    ? "visualization scope + collateral"
    : "visualization scope";

  // ── Deal Closure (Stripe + DocuSign) — sub-$50k CTAs ────────────────
  // The one-time gate uses the same math the Walkthrough modal renders:
  // p1 + p2 + p3 (all of setup + visualization) plus the marketing campaign
  // setup and any collateral one-time block. Anything ≥ $50k is reserved
  // for the standard invoice / agreement workflow.
  const closureOneTimeTotal = useMemo(() => {
    const p1 = paymentSchedule?.p1 || 0;
    const p2 = paymentSchedule?.p2 || 0;
    const p3 = paymentSchedule?.p3 || 0;
    const marketingSetup = marketingActive ? (marketingMath?.setupFee || 0) : 0;
    // Collateral is inside p1 / p2 / p3 whenever it rides the split — adding it
    // again here would push quotes past the $50k Stripe cap on paper that the
    // proposal bills under it.
    const collateralOneTime = collateralActive && !paymentSchedule.collateralInSplit
      ? (collateralMath?.oneTimeTotal || 0)
      : 0;
    return Math.round(p1 + p2 + p3 + marketingSetup + collateralOneTime);
  }, [paymentSchedule, marketingActive, marketingMath, collateralActive, collateralMath]);

  // ── PAY IN FULL ─────────────────────────────────────────────────────
  // The alternative every proposal offers: settle the whole one-time scope at
  // signing and take 5% off it. Built over `closureOneTimeTotal` — the same
  // "every one-time dollar" figure the Stripe cap uses — so the calculator,
  // the web proposal, and the PDF can never quote three different numbers.
  // On-site travel is a pass-through cost and is billed at cost, so it rides
  // the total without earning the discount.
  // The rep can switch the discount off (see `pifDiscount`): the option stays,
  // the 5% goes, and every surface reads the rate from here.
  const pifPct = pifPctOf(pifDiscount);
  const pif = useMemo(() => {
    // Single-payment terms: the schedule IS the pay-in-full, and Payment 1 is
    // already priced with the discount in it. Rebuilding the offer here would
    // take the 5% off a number that has already had it taken off — and print
    // a second, cheaper price for the payment directly above it.
    if (paymentSchedule.singlePayment) return { eligible: false };
    // Clamped: a travel figure larger than the bill would otherwise make the
    // discountable base negative and turn the discount into a surcharge.
    const travel = Math.max(0, Math.min(paymentSchedule.travelCost || 0, closureOneTimeTotal));
    return pifOffer({
      discountable: closureOneTimeTotal - travel, atCost: travel, pct: pifPct,
    });
  }, [closureOneTimeTotal, paymentSchedule.travelCost, paymentSchedule.singlePayment, pifPct]);
  const pifText = useMemo(
    () => pifCopy({
      atCost: paymentSchedule.travelCost || 0,
      hasRecurring: (presentationSchedule.monthly || 0) > 0 || (presentationSchedule.marketingMonthly || 0) > 0,
      pct: pifPct,
    }),
    [paymentSchedule.travelCost, presentationSchedule.monthly, presentationSchedule.marketingMonthly, pifPct],
  );

  // Lot Explorer runs its own inline plan, so the shared payment-schedule
  // block above never renders for it — but its proposal offers pay in full all
  // the same. Built over the LE milestones, the way LotExplorerProposal builds
  // it, so the sidebar and the proposal can't disagree.
  const lePifSidebar = useMemo(
    () => pifOffer({
      discountable: (lotExplorerCalc?.milestones || []).reduce((sum, m) => sum + (Number(m.amount) || 0), 0),
      pct: pifPct,
    }),
    [lotExplorerCalc, pifPct],
  );

  // Stripe instant-checkout is capped at sub-$50k (card-payment limits).
  const closureEligible = closureOneTimeTotal > 0 && closureOneTimeTotal < 50000;
  // DocuSign e-signature has NO deal-size cap — any quote can be sent.
  const docusignEligible = closureOneTimeTotal > 0;

  // ── Marketing & Collateral closure ──────────────────────────────────
  // Marketing/Collateral has no visualization payment schedule, so its
  // "due today" deposit mirrors the sidebar schedule: the campaign setup
  // fee in full + 50% of collateral one-time (remaining 50% at delivery).
  // The one-time total (for the $50k Stripe cap) is setup + full collateral.
  const marketingOneTimeTotal = useMemo(
    () =>
      Math.round(
        (marketingActive ? marketingMath?.setupFee || 0 : 0) +
          (collateralActive ? collateralMath?.oneTimeTotal || 0 : 0)
      ),
    [marketingActive, marketingMath, collateralActive, collateralMath]
  );
  // The deposit Stripe/wire collects, by proposal type. Marketing = setup +
  // half collateral; every other mode collects the walkthrough's Payment 1,
  // which already folds in the marketing setup and half the collateral — the
  // "Due Today" figure on screen and the amount the button charges have to
  // be the same number.
  const closureDeposit = useMemo(() => {
    if (proposalMode === "marketing") {
      return Math.round(
        (marketingActive ? marketingMath?.setupFee || 0 : 0) +
          (collateralActive ? (collateralMath?.oneTimeTotal || 0) * 0.5 : 0)
      );
    }
    return Math.round(presentationSchedule?.p1 || 0);
  }, [proposalMode, marketingActive, marketingMath, collateralActive, collateralMath, presentationSchedule]);
  // What the closure flow actually collects. Every figure in the card — the
  // Stripe button, the wire panel, "due today" — reads this one value, so the
  // number on screen and the number charged can never disagree. `payInFullTotal`
  // is the same function the backend charges through.
  const payInFull = collectMode === "full" && pif.eligible;
  const closureAmount = payInFull
    ? payInFullTotal({ oneTimeTotal: closureOneTimeTotal, travelCost: paymentSchedule.travelCost || 0, pct: pifPct })
    : closureDeposit;

  // Marketing-only quotes can send DocuSign whenever there's a one-time
  // amount; Stripe instant-checkout stays capped at sub-$50k.
  const marketingClosureEnabled = proposalMode === "marketing" && marketingOneTimeTotal > 0;
  const marketingStripeEligible = closureDeposit > 0 && marketingOneTimeTotal < 50000;

  const persistAndGetId = async () => {
    const saved = await persistQuote({ openProposalAfterSave: false });
    return saved?.id || savedQuoteId;
  };

  const handleSendForEsign = async () => {
    setClosureError("");
    if (!clientName.trim()) {
      setClosureError("Enter a client name in the sidebar first.");
      return;
    }
    const email = clientEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setClosureError("Enter a valid client email in the sidebar to send for e-signature.");
      return;
    }
    setClosureBusy((b) => ({ ...b, envelope: true }));
    try {
      const id = await persistAndGetId();
      if (!id) throw new Error("Quote could not be saved");
      const { data } = await apiClient.post(`/vexos-quotes/${id}/send-envelope`, {
        client_email: email,
        client_name: clientName.trim(),
        mode: proposalMode,
      });
      setClosureState((s) => ({
        ...s,
        docusignEnvelopeId: data.envelope_id,
        docusignStatus: data.status,
        docusignSentAt: data.sent_at,
      }));
      toast.success(`Envelope sent to ${email}.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "DocuSign send failed.";
      setClosureError(msg);
      toast.error(msg);
    } finally {
      setClosureBusy((b) => ({ ...b, envelope: false }));
    }
  };

  const handleStripeCheckout = async () => {
    setClosureError("");
    if (!clientName.trim()) {
      setClosureError("Enter a client name in the sidebar first.");
      return;
    }
    setClosureBusy((b) => ({ ...b, stripe: true }));
    try {
      const id = await persistAndGetId();
      if (!id) throw new Error("Quote could not be saved");
      const origin = window.location.origin;
      const returnPath = `/sales/client-calculator/${id}?view=summary`;
      const { data } = await apiClient.post(`/vexos-quotes/${id}/stripe-checkout`, {
        origin_url: origin,
        return_path: returnPath,
        mode: proposalMode,
        pay_in_full: payInFull,
      });
      setClosureState((s) => ({
        ...s,
        stripeSessionId: data.session_id,
        stripePaymentLink: data.url,
        stripePaymentStatus: "pending",
        stripeAmount: data.amount,
      }));
      window.open(data.url, "_blank", "noopener,noreferrer");
      toast.success(
        data.pay_in_full
          ? "Stripe checkout opened — collecting the full one-time scope."
          : "Stripe checkout opened in a new tab."
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Stripe checkout failed.";
      setClosureError(msg);
      toast.error(msg);
    } finally {
      setClosureBusy((b) => ({ ...b, stripe: false }));
    }
  };

  // After Stripe redirects back with ?stripe_session_id=... poll the
  // status endpoint once so the badge flips to "Paid" without requiring
  // a full hydrate cycle.
  useEffect(() => {
    if (!savedQuoteId) return;
    const sessionId = searchParams.get("stripe_session_id");
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get(
          `/vexos-quotes/${savedQuoteId}/checkout-status/${sessionId}`
        );
        if (cancelled) return;
        if (data?.payment_status) {
          setClosureState((s) => ({ ...s, stripePaymentStatus: data.payment_status }));
          if (data.payment_status === "paid") {
            toast.success("Stripe deposit received — payment confirmed.");
          }
        }
      } catch (e) {
        // Silent: status poll failure is non-fatal.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedQuoteId]);

  // Bundle tier upgrade detector — shows toast for 4s when the quote climbs
  // into a higher tier. Tracks what the quote QUALIFIES for: until the rep
  // applies the bundle it's an "available" nudge pointing at the Apply
  // control (nothing has come off the price); once applied it names the rate
  // the quote now gets. Reads the pre-override calc, so it needs its own
  // manual-pricing guard: a hand-priced quote must never announce a bundle.
  useEffect(() => {
    const activeC = mode === "community" ? calcCommunity : calc;
    const offer = activeC.bundleOffer || {};
    const tier = manualPricingActive ? 0 : (offer.tier || 0);
    if (tier > lastBundleTierRef.current && tier > 0) {
      setBundleToast(activeC.bundleApplied
        ? `${offer.label} applied — ${offer.pct}% off your visualization total.`
        : `${bundleOfferCopy(offer)}. Apply it from the sidebar when you're ready.`);
      const id = setTimeout(() => setBundleToast(null), 4000);
      lastBundleTierRef.current = tier;
      return () => clearTimeout(id);
    }
    lastBundleTierRef.current = tier;
  }, [mode, calc.bundleOffer, calcCommunity.bundleOffer, manualPricingActive]);

  // ── EXPORT HANDLERS ─────────────────────────────────────────────────
  const exportRef = useRef(null);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    const { jsPDF } = await import("jspdf");
    const html2canvas = (await import("html2canvas")).default;
    if (!exportRef.current) return;
    const node = exportRef.current;
    const canvas = await html2canvas(node, {
      backgroundColor: "#000000",
      scale: 2,
      useCORS: true,
    });
    const imgData = canvas.toDataURL("image/jpeg", 0.95);
    const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "letter" });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const ratio = canvas.width / pdfWidth;
    const totalPdfHeight = canvas.height / ratio;
    if (totalPdfHeight <= pdfHeight) {
      pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, totalPdfHeight);
    } else {
      // Multi-page
      let yOffset = 0;
      while (yOffset < canvas.height) {
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.min(pdfHeight * ratio, canvas.height - yOffset);
        const ctx = pageCanvas.getContext("2d");
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(canvas, 0, yOffset, canvas.width, pageCanvas.height, 0, 0, canvas.width, pageCanvas.height);
        const pageImgData = pageCanvas.toDataURL("image/jpeg", 0.95);
        if (yOffset > 0) pdf.addPage();
        pdf.addImage(pageImgData, "JPEG", 0, 0, pdfWidth, pageCanvas.height / ratio);
        yOffset += pageCanvas.height;
      }
    }
    pdf.save(`VEXOS-Quote-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // ── RENDER ──────────────────────────────────────────────────────────
  const isCondo = mode === "condo";
  const isSFH = mode === "sfh";
  // SFH Phase 1 readiness — unlocks the visualization phases (02-06)
  // as soon as the rep enters the home sq ft, floor count, and at least
  // 1 unique home plan. Outdoor amenity sq ft is required but allowed
  // to be 0 (most homes have some outdoor area, but the field can be
  // explicitly 0 for an interior-only engagement).
  const sfh_phase1Ready =
    sfh_homeSqFt !== "" && Number(sfh_homeSqFt) >= 1
    && floors !== "" && Number(floors) >= 1
    && uniqueFloorPlans !== "" && Number(uniqueFloorPlans) >= 1
    && sfh_amenitySqFt !== "";
  // Per-mode Phase 1 readiness check. A La Carte uses the same gates
  // as VEXOS tabs — the rep needs to enter project details before the
  // interior renders / VR / floor plan rows light up, because those
  // sections key off the unit-count and unique-plan inputs.
  const activePhase1Ready = isSFH ? sfh_phase1Ready : (isCondo ? phase1Ready : c_phase1Ready);
  // A La Carte mode strips out VEXOS platform fees (setup + monthly)
  // from the active calc snapshot. The viz subtotals + bundle discounts
  // are preserved so the rep can still build a viz-only quote.
  const baseActiveCalc = activeCalcResolved;
  const activeCalc = isAlacarte
    ? {
        ...baseActiveCalc,
        setupFee: 0,
        monthlyRate: 0,
        recurringContractValue: 0,
        oneTimeAllIn: (Number(baseActiveCalc.vizNet) || 0) + (Number(baseActiveCalc.revisionsCost) || 0) + (Number(baseActiveCalc.travelCost) || 0) - (Number(baseActiveCalc.proposalDiscount) || 0) + (Number(baseActiveCalc.conceptualDesignFee) || 0) + (Number(baseActiveCalc.modelingFee) || 0),
        grandTotal: (Number(baseActiveCalc.vizNet) || 0) + (Number(baseActiveCalc.revisionsCost) || 0) + (Number(baseActiveCalc.travelCost) || 0) - (Number(baseActiveCalc.proposalDiscount) || 0) + (Number(baseActiveCalc.conceptualDesignFee) || 0) + (Number(baseActiveCalc.modelingFee) || 0),
      }
    : baseActiveCalc;
  // Keep the presentation-mode nav in step with what this quote actually shows.
  const sectionOrder = SECTION_ORDER.filter((id) => {
    if (id === "bundle") return (Number(activeCalc.bundleSavings) || 0) > 0;
    if (id === "marketing") return marketingActive;
    return true;
  });
  sectionOrderRef.current = sectionOrder;
  const activeContractTerm = isSFH ? contractTerm : (isCondo ? contractTerm : c_contractTerm);
  const setActiveContractTerm = isSFH ? setContractTerm : (isCondo ? setContractTerm : setCContractTerm);
  // SFH has no primary unit count — fall back to # of unique home plans
  // so downstream UI that uses this for "how many plans to show" works.
  const activePrimaryCount = isSFH ? Number(uniqueFloorPlans) : (isCondo ? Number(units) : Number(c_lots));

  const phaseLocked = (phase) => {
    if (phase >= 2 && phase <= 7) return !activePhase1Ready;
    return false;
  };

  return (
    <div className="min-h-screen bg-[#000000] text-white vexos-zoom-typography" style={{ fontFamily: 'var(--font-sans)' }}>
      {/* Google Fonts */}
      <style>{`
        @media print {
          body { background: white !important; color: black !important; }
          .no-print { display: none !important; }
          .vexos-summary-sticky { position: static !important; }
        }

        /* ─── ZOOM READABILITY OVERHAUL ────────────────────────────────────
           Scope-gated to .vexos-zoom-typography. Pushes muted grays to
           bright whites, bumps small text sizes, and gives inputs more
           presence so the calculator is legible on a compressed Zoom share.
           Pure typography/color — no layout, logic, or pricing changes. */

        /* TEXT SIZE BUMPS — small labels become readable at 60-70% zoom */
        .vexos-zoom-typography .text-\\[10px\\]    { font-size: 12px !important; }
        .vexos-zoom-typography .text-\\[10\\.5px\\] { font-size: 12.5px !important; }
        .vexos-zoom-typography .text-\\[11px\\]    { font-size: 13px !important; }
        .vexos-zoom-typography .text-\\[11\\.5px\\] { font-size: 13.5px !important; }
        .vexos-zoom-typography .text-\\[12px\\]    { font-size: 14px !important; }
        .vexos-zoom-typography .text-\\[12\\.5px\\] { font-size: 14.5px !important; }
        .vexos-zoom-typography .text-\\[13px\\]    { font-size: 15px !important; }
        .vexos-zoom-typography .text-\\[14px\\]    { font-size: 15.5px !important; }
        .vexos-zoom-typography .text-\\[14\\.5px\\] { font-size: 16px !important; }
        .vexos-zoom-typography .text-\\[17px\\]    { font-size: 19px !important; }
        .vexos-zoom-typography .text-\\[20px\\]    { font-size: 22px !important; }

        /* MUTED WHITES → BRIGHT WHITES.
           Keep only true placeholder opacity (40%) on real <input> placeholders;
           every label / description / hint reads as primary or near-primary. */
        .vexos-zoom-typography .text-white\\/20  { color: rgba(255,255,255,0.55) !important; }
        .vexos-zoom-typography .text-white\\/25  { color: rgba(255,255,255,0.6) !important; }
        .vexos-zoom-typography .text-white\\/30  { color: rgba(255,255,255,0.7) !important; }
        .vexos-zoom-typography .text-white\\/35  { color: rgba(255,255,255,0.75) !important; }
        .vexos-zoom-typography .text-white\\/40  { color: rgba(255,255,255,0.8) !important; }
        .vexos-zoom-typography .text-white\\/45  { color: rgba(255,255,255,0.85) !important; }
        .vexos-zoom-typography .text-white\\/50  { color: rgba(255,255,255,0.88) !important; }
        .vexos-zoom-typography .text-white\\/55  { color: rgba(255,255,255,0.9) !important; }
        .vexos-zoom-typography .text-white\\/60  { color: rgba(255,255,255,0.92) !important; }
        .vexos-zoom-typography .text-white\\/65  { color: rgba(255,255,255,0.94) !important; }
        .vexos-zoom-typography .text-white\\/70  { color: rgba(255,255,255,0.95) !important; }

        /* Native placeholder text stays clearly inactive */
        .vexos-zoom-typography input::placeholder,
        .vexos-zoom-typography textarea::placeholder {
          color: rgba(255,255,255,0.4) !important;
          opacity: 1 !important;
        }

        /* INPUT FIELDS — more presence + larger type */
        .vexos-zoom-typography input[type="number"],
        .vexos-zoom-typography input[type="text"],
        .vexos-zoom-typography input[type="email"] {
          font-size: 17px !important;
          color: #ffffff !important;
        }
        /* Bordered/filled inputs (the Project Details numeric fields) */
        .vexos-zoom-typography .border-white\\/10 input[type="number"],
        .vexos-zoom-typography .border-white\\/10 input[type="text"] {
          /* These inputs render inside a styled wrapper — bump the wrapper directly */
        }
        .vexos-zoom-typography input[type="number"].bg-transparent,
        .vexos-zoom-typography input[type="text"].bg-transparent {
          padding-top: 14px !important;
          padding-bottom: 14px !important;
        }
        .vexos-zoom-typography input[type="number"]:focus,
        .vexos-zoom-typography input[type="text"]:focus {
          outline: 1px solid rgba(143,167,155,0.55) !important;
          outline-offset: -1px !important;
        }
        /* Wrapper border bump for the Project Details + Client name fields */
        .vexos-zoom-typography .border-white\\/10 {
          border-color: rgba(255,255,255,0.22) !important;
        }
        .vexos-zoom-typography .border-white\\/\\[0\\.08\\] {
          border-color: rgba(255,255,255,0.2) !important;
        }

        /* PHASE NUMBER BADGES — bigger + bolder gold rim */
        .vexos-zoom-typography [data-testid^="phase-"] > .w-12,
        .vexos-zoom-typography [data-testid="phase-1"] .w-12,
        .vexos-zoom-typography [data-testid="phase-2"] .w-12,
        .vexos-zoom-typography [data-testid="phase-3"] .w-12,
        .vexos-zoom-typography [data-testid="phase-4"] .w-12,
        .vexos-zoom-typography [data-testid="phase-5"] .w-12,
        .vexos-zoom-typography [data-testid="phase-6"] .w-12,
        .vexos-zoom-typography [data-testid="phase-7"] .w-12 {
          width: 56px !important;
          height: 56px !important;
          font-size: 17px !important;
          border-width: 1.5px !important;
        }

        /* PHASE TITLE HEADINGS — bump serif sizes */
        .vexos-zoom-typography .text-\\[34px\\],
        .vexos-zoom-typography .text-\\[36px\\],
        .vexos-zoom-typography .text-\\[38px\\] { font-size: 44px !important; }

        /* "Recommended" hint label — promote to brighter gold */
        .vexos-zoom-typography .text-\\[\\#8FA79B\\]\\/60,
        .vexos-zoom-typography .text-\\[\\#8FA79B\\]\\/75,
        .vexos-zoom-typography .text-\\[\\#8FA79B\\]\\/80,
        .vexos-zoom-typography .text-\\[\\#8FA79B\\]\\/90 {
          color: #A6B8AD !important;
        }

        /* Section eyebrows ("VEXOS PLATFORM · STUDIO VISUALIZATION") */
        .vexos-zoom-typography .text-\\[10\\.5px\\].uppercase,
        .vexos-zoom-typography .text-\\[11px\\].uppercase {
          letter-spacing: 0.14em !important;
        }

        /* RUNNING SUBTOTALS (e.g., "9 renders total · $89,991") */
        .vexos-zoom-typography .text-\\[12px\\].text-white\\/55,
        .vexos-zoom-typography .text-\\[12px\\].text-white\\/45 {
          font-size: 14px !important;
        }

        /* STEPPER BUTTONS + TOGGLE PILLS — guarantee min hit target + visible border */
        .vexos-zoom-typography button.rounded-full.border {
          min-height: 34px !important;
        }

        /* Section vertical breathing room (only the outermost calculator
           sections — guarded by [data-testid^="phase-"] to avoid leaking
           into nested grids). */
        .vexos-zoom-typography section[data-testid="phase-1"],
        .vexos-zoom-typography section[data-testid="phase-2"],
        .vexos-zoom-typography section[data-testid="phase-3"],
        .vexos-zoom-typography section[data-testid="phase-4"],
        .vexos-zoom-typography section[data-testid="phase-5"],
        .vexos-zoom-typography section[data-testid="phase-6"],
        .vexos-zoom-typography section[data-testid="phase-7"] {
          margin-bottom: 28px !important;
        }

        /* FORMFIELD LABELS — promote to spec-bright white at 13px */
        .vexos-zoom-typography label.text-\\[10\\.5px\\].uppercase {
          font-size: 13px !important;
          color: #ffffff !important;
          font-weight: 500 !important;
          letter-spacing: 0.08em !important;
        }

        /* PHASE SUBTITLES (under each numbered header) — readable + brighter */
        .vexos-zoom-typography h2 + p.text-\\[13px\\],
        .vexos-zoom-typography h2 + p[class*="text-white/45"] {
          font-size: 15px !important;
          color: rgba(255,255,255,0.85) !important;
        }

        /* PAGE HEADER SUBTITLE */
        .vexos-zoom-typography header p.text-\\[14px\\] {
          font-size: 16px !important;
          color: rgba(255,255,255,0.85) !important;
        }

        /* "Recommended N renders" / sub-hints below stepper rows */
        .vexos-zoom-typography .text-\\[\\#8FA79B\\] {
          /* base gold stays bright */
        }
      `}</style>

      <div ref={exportRef} className="max-w-[1400px] mx-auto px-8 lg:px-16 py-14">
        {/* Breadcrumb back to Saved Quotes — sits above the page title. */}
        <div className="mb-6 no-print" data-testid="calc-breadcrumb">
          <button
            onClick={handleBackToDashboard}
            className="inline-flex items-center gap-1 text-[12px] text-white/45 hover:text-[#8FA79B] transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Saved Quotes
          </button>
          <span className="mx-2 text-white/20 text-[12px]">|</span>
          <span className="text-[12px] text-white/55">
            {mode === "condo" ? "Condo Building" : mode === "sfh" ? "SFH" : c_masterPlan ? "Master Plan" : "Community"} Calculator
          </span>
          {savedQuoteId && (
            <span
              className="ml-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full"
              style={{
                background: "rgba(143,167,155,0.1)",
                color: "#8FA79B",
                fontSize: "10px",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                border: "1px solid rgba(143,167,155,0.3)",
              }}
              data-testid="editing-pill"
            >
              Editing · {savedQuoteId.slice(0, 8)}
            </span>
          )}
        </div>

        {/* HEADER */}
        <header className="mb-16 no-print flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-[#8FA79B]/80 mb-3">The Render Pros · VEXOS Platform</p>
            <h1
              className="text-[32px] sm:text-[52px] lg:text-[64px] leading-[0.95] tracking-tight"
              style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}
              data-testid="vexos-client-calc-title"
            >
              Client Sales Calculator
            </h1>
            <p className="text-[14px] text-white/50 mt-4 max-w-xl">
              Build your VEXOS Platform investment together — live, while we walk through your project.
            </p>
          </div>
          <div className="shrink-0 sm:mt-2 w-full sm:w-auto flex flex-col sm:flex-row gap-2">
            <a
              href={INTAKE_GUIDE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto justify-center inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-white/15 hover:border-[#8FA79B]/40 text-white/65 hover:text-[#8FA79B] text-[11px] uppercase tracking-[0.14em] transition-colors"
              data-testid="open-intake-guide"
              title="The files we need before the first render. Show it on the call."
            >
              <FileText className="w-3.5 h-3.5" />
              Intake Guide
            </a>
            <button
              onClick={() => setAdminOpen(true)}
              className={`w-full sm:w-auto justify-center inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-white/15 hover:border-[#8FA79B]/40 text-white/65 hover:text-[#8FA79B] text-[11px] uppercase tracking-[0.14em] transition-colors ${canEditPricing ? "" : "hidden"}`}
              data-testid="open-pricing-admin"
              title="Pricing Admin (Admin / COO only)"
              aria-hidden={!canEditPricing}
            >
              <Settings className="w-3.5 h-3.5" />
              Pricing Admin
            </button>
          </div>
        </header>

        {/* BUNDLE TOAST — auto-fades 4s */}
        {bundleToast && (
          <div
            className="fixed top-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full bg-[#8FA79B]/90 text-[#000000] text-[12.5px] font-medium shadow-[0_8px_32px_rgba(143,167,155,0.25)] no-print"
            data-testid="bundle-toast"
            style={{ animation: "fadeIn 250ms ease-out" }}
          >
            <span aria-hidden className="mr-1.5">🎯</span>
            {bundleToast}
            <button
              onClick={() => setBundleToast(null)}
              className="ml-3 opacity-60 hover:opacity-100"
              aria-label="Dismiss"
              data-testid="bundle-toast-close"
            >
              <X className="inline w-3 h-3" />
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-[1fr_360px] gap-12">
          {/* ─── MAIN COLUMN ─── */}
          <div className="space-y-24">
            {/* ─── TAB NAV ─── */}
            <div className="-mt-2 no-print" data-testid="vexos-tab-nav">
              <div className="inline-flex items-center gap-1 bg-white/[0.025] border border-white/10 rounded-full p-1">
                <button
                  onClick={() => setActiveTab("vexos")}
                  className="px-5 py-2 rounded-full text-[12px] transition-all"
                  style={{
                    background: activeTab === "vexos" ? "rgba(143,167,155,0.16)" : "transparent",
                    color: activeTab === "vexos" ? "#8FA79B" : "rgba(255,255,255,0.6)",
                    fontWeight: activeTab === "vexos" ? 500 : 400,
                    letterSpacing: "0.06em",
                  }}
                  data-testid="tab-vexos"
                >
                  VEXOS Platform
                </button>
                <button
                  onClick={() => setActiveTab("marketing")}
                  className="px-5 py-2 rounded-full text-[12px] transition-all"
                  style={{
                    background: activeTab === "marketing" ? "rgba(143,167,155,0.16)" : "transparent",
                    color: activeTab === "marketing" ? "#8FA79B" : "rgba(255,255,255,0.6)",
                    fontWeight: activeTab === "marketing" ? 500 : 400,
                    letterSpacing: "0.06em",
                  }}
                  data-testid="tab-marketing"
                >
                  Marketing &amp; Collateral
                  {marketingNeedsPriceTier && (
                    <span
                      title="Marketing is on but has no price point selected — it will not appear on the proposal."
                      data-testid="tab-marketing-incomplete-dot"
                      style={{
                        display: "inline-block",
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "#F0B429",
                        marginLeft: 7,
                        verticalAlign: "middle",
                      }}
                    />
                  )}
                </button>
                <button
                  onClick={() => {
                    // À La Carte and VEXOS are mutually exclusive — entering
                    // this tab forces the VEXOS platform off so a quote can
                    // never carry both. (Viz UI still renders via
                    // vexosEffectiveEnabled = vexosEnabled || isAlacarte.)
                    setActiveTab("alacarte");
                    setVexosEnabled(false);
                    markDirty();
                  }}
                  className="px-5 py-2 rounded-full text-[12px] transition-all"
                  style={{
                    background: activeTab === "alacarte" ? "rgba(143,167,155,0.16)" : "transparent",
                    color: activeTab === "alacarte" ? "#8FA79B" : "rgba(255,255,255,0.6)",
                    fontWeight: activeTab === "alacarte" ? 500 : 400,
                    letterSpacing: "0.06em",
                  }}
                  data-testid="tab-alacarte"
                  title="Visualization + Collateral, no VEXOS platform"
                >
                  A La Carte
                </button>
                <button
                  onClick={() => setActiveTab("lotexplorer")}
                  className="px-5 py-2 rounded-full text-[12px] transition-all"
                  style={{
                    background: activeTab === "lotexplorer" ? "rgba(103,232,249,0.16)" : "transparent",
                    color: activeTab === "lotexplorer" ? "#8FA79B" : "rgba(255,255,255,0.6)",
                    fontWeight: activeTab === "lotexplorer" ? 500 : 400,
                    letterSpacing: "0.06em",
                  }}
                  data-testid="tab-lotexplorer"
                  title="VEXOS Lot Explorer™ — standalone interactive sitemap product"
                >
                  Lot Explorer
                </button>
              </div>
            </div>

            {activeTab === "lotexplorer" ? (
              <section data-testid="lot-explorer-body" className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div>
                    <p className="uppercase text-cyan-300" style={{ fontSize: "11px", letterSpacing: "0.18em" }}>Standalone Software</p>
                    <h2 className="text-white mt-1 text-[22px] sm:text-[28px]" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>
                      VEXOS Lot Explorer&trade;
                    </h2>
                    <p className="text-[12.5px] text-white/55 mt-2 max-w-2xl">
                      Enterprise-grade interactive sitemap & buyer-engagement platform. Per-lot pricing with a dedicated 50 / 25 / 25 payment structure — completely independent of VEXOS, Marketing, and A La Carte scopes.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 sm:mt-1 no-print">
                    <button
                      onClick={() => setLotExplorerExpanded(true)}
                      disabled={lotExplorerCalc.grandTotal === 0}
                      className="w-full sm:w-auto justify-center inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] tracking-wide border border-cyan-400/35 text-cyan-300 hover:bg-cyan-400/10 hover:border-cyan-400/60 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                      title="Open presentation view · export to PDF"
                      data-testid="lot-explorer-present-btn"
                    >
                      <Presentation className="w-3.5 h-3.5" />
                      Present / PDF
                    </button>
                  </div>
                </div>

                {/* INPUTS */}
                <div className="grid md:grid-cols-2 gap-x-12 gap-y-9">
                  <FormField label="Number of Lots" hint="Total lots to include in the interactive sitemap.">
                    <NumInput value={lotExplorerLotCount} onChange={setLotExplorerLotCount} placeholder="e.g. 120" min={0} dataTestId="lot-explorer-lot-count" />
                  </FormField>
                  <FormField label="Price Per Lot ($)" hint="Per-lot rate. Drives Lot Explorer subtotal.">
                    <NumInput value={lotExplorerRatePerLot} onChange={setLotExplorerRatePerLot} placeholder="250" min={0} dataTestId="lot-explorer-rate-per-lot" />
                  </FormField>
                  <FormField label="Timeline (Weeks)" hint="Estimated delivery window in business weeks.">
                    <NumInput value={lotExplorerTimelineWeeks} onChange={setLotExplorerTimelineWeeks} placeholder="4" min={1} dataTestId="lot-explorer-timeline-weeks" />
                  </FormField>
                  <FormField label="Hosting / Support ($/mo)" hint="Recurring management & hosting fee post-launch.">
                    <NumInput value={lotExplorerHostingMonthly} onChange={setLotExplorerHostingMonthly} placeholder="100" min={0} dataTestId="lot-explorer-hosting-monthly" />
                  </FormField>
                </div>

                {/* FEATURE TOGGLES */}
                <div className="rounded-xl border border-cyan-400/15 bg-cyan-500/[0.03] p-5">
                  <p className="text-[10.5px] uppercase tracking-[0.16em] text-cyan-300/80 mb-4">Included Features</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    {[
                      { state: lotExplorerIncludePoi, setter: setLotExplorerIncludePoi, label: "POI Widget", sub: "Points of interest overlay (schools, parks, transit)", tid: "lot-explorer-poi" },
                      { state: lotExplorerIncludeCrm, setter: setLotExplorerIncludeCrm, label: "CRM Integration", sub: "Inquiry capture → CRM sync", tid: "lot-explorer-crm" },
                      { state: lotExplorerIncludeMedia, setter: setLotExplorerIncludeMedia, label: "Media Pop-up Support", sub: "Per-lot photos / video / virtual tours", tid: "lot-explorer-media" },
                      { state: lotExplorerIncludeBackend, setter: setLotExplorerIncludeBackend, label: "Backend Management", sub: "Client-side lot status & availability editor", tid: "lot-explorer-backend" },
                    ].map(({ state, setter, label, sub, tid }) => (
                      <label key={tid} className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] cursor-pointer hover:border-cyan-400/25 transition-colors">
                        <input type="checkbox" checked={!!state} onChange={(e) => setter(e.target.checked)} className="mt-1 accent-cyan-400" data-testid={tid} />
                        <div className="min-w-0">
                          <p className="text-[13px] text-white">{label}</p>
                          <p className="text-[11px] text-white/45 mt-0.5">{sub}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* SUBTOTAL */}
                <div className="rounded-xl border border-cyan-400/25 bg-cyan-500/[0.06] px-6 py-5 flex items-center justify-between">
                  <div>
                    <p className="text-[10.5px] uppercase tracking-[0.16em] text-cyan-300/80">Lot Explorer Subtotal</p>
                    <p className="text-[11px] text-white/45 mt-0.5">{fmtUSD(lotExplorerCalc.baseFee)} base + {lotExplorerCalc.lotCount.toLocaleString()} lots × {fmtUSD(lotExplorerCalc.ratePerLot)}</p>
                  </div>
                  <p className="text-cyan-200 tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontSize: "30px", fontWeight: 450 }} data-testid="lot-explorer-subtotal">
                    {fmtUSD(lotExplorerCalc.grandTotal)}
                  </p>
                </div>

                {/* PAYMENT PLAN */}
                <div>
                  <p className="text-[10.5px] uppercase tracking-[0.16em] text-cyan-300/80 mb-3">Payment Plan · 50 / 25 / 25</p>
                  <div className="space-y-2">
                    {lotExplorerCalc.milestones.map((m) => (
                      <div key={m.key} className="flex items-start justify-between gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid={`lot-explorer-milestone-${m.key}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] text-white font-medium">{m.label} <span className="text-cyan-300/70 ml-1">· {m.percent}%</span></p>
                          <p className="text-[11px] text-white/45 mt-1 leading-relaxed">{m.note}</p>
                        </div>
                        <p className="text-white tabular-nums shrink-0" style={{ fontFamily: 'var(--font-sans)', fontSize: "20px", fontWeight: 450 }}>
                          {fmtUSD(m.amount)}
                        </p>
                      </div>
                    ))}
                    {lotExplorerCalc.hostingMonthly > 0 && (
                      <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]" data-testid="lot-explorer-hosting-row">
                        <div className="min-w-0 flex-1">
                          <p className="text-[13px] text-white font-medium">Recurring Hosting & Support</p>
                          <p className="text-[11px] text-white/45 mt-1">Begins post-launch · ongoing platform operations.</p>
                        </div>
                        <p className="text-white tabular-nums shrink-0" style={{ fontFamily: 'var(--font-sans)', fontSize: "20px", fontWeight: 450 }}>
                          {fmtUSD(lotExplorerCalc.hostingMonthly)}<span className="text-white/40 text-[14px] ml-1">/mo</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            ) : activeTab === "marketing" ? (
              <>
              <MarketingCollateralTab
                marketing={marketing}
                setMarketing={setMarketing}
                onDirty={markDirty}
                collateral={collateral}
                setCollateral={setCollateral}
                marketingMath={marketingMath}
                collateralMath={collateralMath}
                marketingNeedsPriceTier={marketingNeedsPriceTier}
                cplTiers={cplTiers}
                marketingConfig={marketingConfig}
                collateralConfig={collateralMath.prices}
                termMonths={marketing.sellOutMonths}
                proposalMode={proposalMode}
                savedQuoteId={savedQuoteId}
                onDownloadProposal={saveAndOpenProposal}
              />

              {/* ── CLOSE THE DEAL · Marketing & Collateral ──────────────
                  Self-contained DocuSign + payment-link + wire panel, scoped
                  to marketing-only quotes. Deposit = campaign setup + 50% of
                  collateral one-time (remaining 50% billed at delivery). */}
              {marketingClosureEnabled && (
                <div
                  className="mt-8 rounded-2xl border border-[#8FA79B]/25 bg-[#8FA79B]/[0.04] px-6 py-5 no-print"
                  data-testid="marketing-closure-panel"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80">
                        Close the deal now
                      </p>
                      <p className="text-[12px] text-white/55 mt-1 max-w-[520px]">
                        Send the Marketing &amp; Collateral proposal + agreement as a
                        DocuSign envelope for e-signature, or collect the deposit
                        {marketingStripeEligible ? " via Stripe" : " via wire"}. Both run on real rails.
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[11px] text-white/40 tabular-nums">
                        {fmtUSD(marketingOneTimeTotal)} one-time
                      </p>
                      <p className="text-[12px] text-white/80 tabular-nums mt-0.5">
                        <span className="text-[#8FA79B]">{fmtUSD(closureAmount)}</span> due today
                      </p>
                      {collateralActive && collateralMath.oneTimeTotal > 0 && !payInFull && (
                        <p className="text-[10.5px] text-white/35 mt-0.5">
                          50% of collateral billed at delivery
                        </p>
                      )}
                      {payInFull && (
                        <p className="text-[10.5px] text-white/35 mt-0.5">
                          Settles the engagement in full
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Client identity — bound to the same fields the sidebar uses */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="text-[11px] text-white/50">Client name</label>
                      <input
                        type="text"
                        value={clientName}
                        onChange={(e) => { setClientName(e.target.value); markDirty(); }}
                        placeholder="Client name (required)"
                        className="w-full bg-transparent border-b border-white/10 focus:border-[#8FA79B]/60 outline-none text-[13px] text-white placeholder:text-white/30 py-2 transition-colors"
                        data-testid="marketing-closure-client-name"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-white/50">Client email (signer)</label>
                      <input
                        type="email"
                        value={clientEmail}
                        onChange={(e) => { setClientEmail(e.target.value); markDirty(); }}
                        placeholder="client@company.com"
                        className="w-full bg-transparent border-b border-white/10 focus:border-[#8FA79B]/60 outline-none text-[13px] text-white placeholder:text-white/30 py-2 transition-colors"
                        data-testid="marketing-closure-client-email"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-white/50">Legal entity (optional)</label>
                      <input
                        type="text"
                        value={clientEntity}
                        onChange={(e) => { setClientEntity(e.target.value); markDirty(); }}
                        placeholder="LLC / Entity"
                        className="w-full bg-transparent border-b border-white/10 focus:border-[#8FA79B]/60 outline-none text-[13px] text-white placeholder:text-white/30 py-2 transition-colors"
                        data-testid="marketing-closure-client-entity"
                      />
                    </div>
                  </div>

                  <CollectModeChoice
                    mode={collectMode}
                    onChange={setCollectMode}
                    deposit={closureDeposit}
                    pif={pif}
                    testId="marketing-closure-collect-mode"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* DocuSign — proposal + MSA (no VEXOS platform contract) */}
                    <button
                      type="button"
                      onClick={handleSendForEsign}
                      disabled={closureBusy.envelope || !clientName.trim()}
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[12.5px] tracking-wide border border-[#8FA79B]/40 text-white/90 hover:bg-[#8FA79B]/[0.08] hover:border-[#8FA79B]/70 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="marketing-closure-send-envelope-btn"
                      title="Send the Marketing & Collateral proposal + agreement for e-signature via DocuSign"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      {closureBusy.envelope
                        ? "Sending…"
                        : closureState.docusignEnvelopeId
                          ? "Resend Proposal & Agreement"
                          : "Send Proposal & Agreement"}
                    </button>

                    {/* Stripe — instant-checkout only for sub-$50k deals */}
                    {marketingStripeEligible ? (
                      <button
                        type="button"
                        onClick={handleStripeCheckout}
                        disabled={closureBusy.stripe || !clientName.trim() || !(closureAmount > 0)}
                        className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[12.5px] tracking-wide bg-[#8FA79B] hover:bg-[#A6B8AD] text-[#000000] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="marketing-closure-stripe-checkout-btn"
                        title={payInFull
                          ? `Open a Stripe Checkout for the full one-time scope (${fmtUSD(closureAmount)})`
                          : `Open a Stripe Checkout for the deposit (${fmtUSD(closureAmount)})`}
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        {closureBusy.stripe
                          ? "Opening…"
                          : closureState.stripePaymentStatus === "paid"
                            ? "Deposit Paid ✓"
                            : payInFull
                              ? `Collect ${fmtUSD(closureAmount)} in Full`
                              : `Collect ${fmtUSD(closureAmount)} Deposit`}
                      </button>
                    ) : (
                      <div
                        className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[12px] text-white/45 border border-white/10 bg-white/[0.02] text-center"
                        data-testid="marketing-closure-stripe-invoice-note"
                      >
                        Deposit over $50k — collect via wire / invoice
                      </div>
                    )}
                  </div>

                  {/* Wire transfer alternative — works at any deal size */}
                  {!showWire ? (
                    <button
                      type="button"
                      onClick={() => setShowWire(true)}
                      className="mt-3 w-full text-center text-[12px] text-white/55 hover:text-white/85 transition-colors"
                      data-testid="marketing-closure-wire-toggle"
                    >
                      Want to pay with wire?
                    </button>
                  ) : (
                    <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="marketing-closure-wire-panel">
                      <p className="text-[12px] text-white/60 leading-relaxed mb-3">
                        Send{" "}
                        <span className="text-white/90 font-medium tabular-nums">
                          {fmtUSD(closureAmount)}
                        </span>{" "}
                        to the account below. Email us your transfer reference and we'll
                        confirm receipt.
                      </p>
                      <div className="rounded-lg bg-black/20 border border-white/[0.06] divide-y divide-white/[0.06]">
                        {WIRE_ROWS.map(([label, value]) => (
                          <div key={label} className="flex items-center justify-between gap-3 px-3.5 py-2">
                            <span className="text-[11.5px] text-white/45">{label}</span>
                            <span className="text-[12px] text-white/90 font-mono text-right">{value}</span>
                          </div>
                        ))}
                      </div>
                      <p className="mt-3 text-[11px] text-white/45 leading-relaxed">
                        Email{" "}
                        <a href={`mailto:${WIRE_INSTRUCTIONS.contact}`} className="text-[#8FA79B] hover:text-[#A6B8AD]">
                          {WIRE_INSTRUCTIONS.contact}
                        </a>{" "}
                        with your transaction reference so we can match your payment.
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowWire(false)}
                        className="mt-3 text-[11.5px] text-white/50 hover:text-white/80 transition-colors"
                      >
                        ← Back to card &amp; e-sign
                      </button>
                    </div>
                  )}

                  {/* Status pills */}
                  {(closureState.docusignEnvelopeId || closureState.stripeSessionId) && (
                    <div className="mt-4 flex flex-wrap gap-3 text-[11px]" data-testid="marketing-closure-status-row">
                      {closureState.docusignEnvelopeId && (
                        <span
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/70"
                          data-testid="marketing-closure-docusign-status"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[#8FA79B]" />
                          Envelope · {closureState.docusignStatus || "sent"}
                          {closureState.docusignEnvelopeId
                            ? <span className="ml-1 text-white/40 tabular-nums">{String(closureState.docusignEnvelopeId).slice(0, 8)}</span>
                            : null}
                        </span>
                      )}
                      {closureState.stripePaymentLink && (
                        <a
                          href={closureState.stripePaymentLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/70 hover:text-white hover:border-white/30"
                          data-testid="marketing-closure-stripe-status"
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${closureState.stripePaymentStatus === "paid" ? "bg-emerald-400" : "bg-[#8FA79B]"}`} />
                          Stripe · {closureState.stripePaymentStatus || "pending"} · open link
                        </a>
                      )}
                    </div>
                  )}

                  {closureError && (
                    <p className="mt-3 text-[11.5px] text-red-300" data-testid="marketing-closure-error">
                      {closureError}
                    </p>
                  )}

                  {!clientName.trim() && (
                    <p className="mt-3 text-[11.5px] text-amber-200/70 flex items-center gap-2">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Enter the client&rsquo;s name to send or collect.
                    </p>
                  )}
                </div>
              )}
              </>
            ) : (
            <>
            {/* PROJECT TYPE SELECTOR (shared across modes) */}
            <div data-testid="project-type-selector" className="no-print">
              <div className="flex flex-wrap items-start justify-between gap-6">
                <div>
                  <p className="text-[10.5px] uppercase tracking-[0.14em] text-white/55 font-medium mb-3">Project Type</p>
                  <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.14em]">
                    <button
                      onClick={() => setMode("condo")}
                      className={`px-3 py-1.5 rounded-full border transition-all duration-200 ${
                        mode === "condo"
                          ? "bg-[#8FA79B]/10 text-[#8FA79B] border-[#8FA79B]/30"
                          : "bg-white/[0.02] text-white/55 border-white/10 hover:border-white/25 hover:text-white/80"
                      }`}
                      data-testid="mode-condo"
                    >
                      Condo Building
                    </button>
                    <button
                      onClick={() => selectCommunityType(false)}
                      className={`px-3 py-1.5 rounded-full border transition-all duration-200 ${
                        mode === "community" && !c_masterPlan
                          ? "bg-[#8FA79B]/10 text-[#8FA79B] border-[#8FA79B]/30"
                          : "bg-white/[0.02] text-white/55 border-white/10 hover:border-white/25 hover:text-white/80"
                      }`}
                      data-testid="mode-community"
                    >
                      Community
                    </button>
                    <button
                      onClick={() => selectCommunityType(true)}
                      className={`px-3 py-1.5 rounded-full border transition-all duration-200 ${
                        mode === "community" && c_masterPlan
                          ? "bg-[#8FA79B]/10 text-[#8FA79B] border-[#8FA79B]/30"
                          : "bg-white/[0.02] text-white/55 border-white/10 hover:border-white/25 hover:text-white/80"
                      }`}
                      data-testid="mode-master-plan"
                    >
                      Master Plan
                    </button>
                    <button
                      onClick={() => setMode("sfh")}
                      className={`px-3 py-1.5 rounded-full border transition-all duration-200 ${
                        mode === "sfh"
                          ? "bg-[#8FA79B]/10 text-[#8FA79B] border-[#8FA79B]/30"
                          : "bg-white/[0.02] text-white/55 border-white/10 hover:border-white/25 hover:text-white/80"
                      }`}
                      data-testid="mode-sfh"
                    >
                      SFH
                    </button>
                  </div>
                </div>

                {/* VEXOS Master Toggle — disables the entire VEXOS scope
                    (setup fee, monthly subscription, visualization scope,
                    payment milestones 01–04, production timeline). Off
                    yields a standalone Marketing / Lead-Gen proposal. */}
                {/* VEXOS master toggle hidden in A La Carte tab — that
                    tab is a deliberate "no platform" mode. */}
                {!isAlacarte && (
                <div className="flex items-start gap-3" data-testid="vexos-master-toggle-wrap">
                  <div className="text-right">
                    <p className="text-[10.5px] uppercase tracking-[0.14em] text-white/55 font-medium mb-1">VEXOS Platform</p>
                    <p className="text-[10px] text-white/35 leading-snug max-w-[200px]">
                      {vexosEnabled
                        ? "Visualization + Platform included"
                        : "Excluded — Marketing / Lead-Gen only"}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={vexosEnabled}
                    onClick={() => { setVexosEnabled((v) => !v); markDirty(); }}
                    className="relative inline-block w-11 h-6 rounded-full transition-colors mt-1"
                    style={{ background: vexosEnabled ? "#8FA79B" : "rgba(255,255,255,0.12)" }}
                    data-testid="vexos-master-toggle"
                  >
                    <span
                      className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
                      style={{ left: vexosEnabled ? "22px" : "2px" }}
                    />
                  </button>
                </div>
                )}
              </div>
            </div>

            {/* VEXOS-DISABLED BANNER ── shown when the rep toggles off
                the entire VEXOS platform; the proposal falls back to a
                pure Marketing / Lead-Gen engagement and all VEXOS scope
                phases collapse. Suppressed in A La Carte tab (banner
                would be misleading since A La Carte intentionally
                excludes the platform). */}
            {!vexosEnabled && !isAlacarte && (
              <div
                className="rounded-2xl border border-[#8FA79B]/25 bg-[#8FA79B]/[0.06] px-5 py-4 flex items-start gap-4"
                data-testid="vexos-disabled-banner"
              >
                <div className="text-[#8FA79B] text-lg leading-none mt-0.5">◐</div>
                <div className="flex-1">
                  <p className="text-[12px] uppercase tracking-[0.14em] text-[#8FA79B] mb-1">VEXOS Platform Disabled</p>
                  <p className="text-[11.5px] text-white/60 leading-relaxed">
                    Visualization scope, setup fees, monthly platform subscription, and production timeline are excluded from this proposal. Configure Marketing &amp; Collateral in the next tab to build a standalone engagement.
                  </p>
                </div>
              </div>
            )}

            {/* ════════════════ CONDO BUILDING MODE & SFH ════════════════ */}
            {/* SFH reuses Condo's Phase 2 (Exterior) + Phase 3 (Interior) UI.
                Phase 1 (unit-tier inputs) and Phase 4 (Amenity) are hidden
                for SFH per the SFH spec — see `{isCondo && ...}` wrappers. */}
            {vexosEffectiveEnabled && (isCondo || isSFH) && (
            <>
            {/* PHASE 1 — Condo only (SFH = flat platform pricing, no unit tiers) */}
            {/* Phase 1 (multifamily tier inputs) — shown in all modes
                INCLUDING A La Carte. Even though A La Carte doesn't
                price the platform tier, these inputs drive how many
                interior/VR/floor-plan rows show in phases 03-06. */}
            {isCondo && (
            <section data-testid="phase-1">
              <PhaseHeader number="01" title="Project Details" subtitle="Tell us about your building. These numbers drive every recommendation that follows." />

              <div className="grid md:grid-cols-2 gap-x-12 gap-y-9 pl-[68px]">
                <FormField label="Number of Floors">
                  <NumInput value={floors} onChange={setFloors} placeholder="e.g. 24" min={1} dataTestId="input-floors" />
                </FormField>
                <FormField label="Total Units" hint="Drives setup fee tier & monthly platform installment">
                  <NumInput value={units} onChange={setUnits} placeholder="e.g. 240" min={1} dataTestId="input-units" />
                </FormField>
                <FormField label="Unique Floor Plans">
                  <NumInput value={uniqueFloorPlans} onChange={setUniqueFloorPlans} placeholder="e.g. 8" min={1} dataTestId="input-floor-plans" />
                </FormField>
                <FormField label="Average Sq Ft per Floor Plan">
                  <NumInput value={avgFloorPlanSqFt} onChange={setAvgFloorPlanSqFt} placeholder="e.g. 1,250" min={1} dataTestId="input-avg-sqft" />
                </FormField>
                <FormField label="Number of Amenity Spaces">
                  <NumInput value={amenitySpaces} onChange={setAmenitySpaces} placeholder="e.g. 6" min={0} dataTestId="input-amenity-spaces" />
                </FormField>
                <FormField label="Total Sq Ft of Amenity Areas">
                  <NumInput value={totalAmenitySqFt} onChange={setTotalAmenitySqFt} placeholder="e.g. 8,500" min={0} dataTestId="input-amenity-sqft" />
                </FormField>
              </div>

              {/* Setup-fee + Subscription cards intentionally removed from the
                  main column per UX spec. Both values now live in the
                  sidebar's VEXOS Platform section (with the 12/24/36 term selector). */}
            </section>
            )}

            {/* SFH PROJECT DETAILS — replaces multifamily Phase 1 with
                SFH-relevant fields. These inputs are informational
                (don't drive pricing tiers — SFH is flat-priced per
                Pricing Admin Section 1S) and are surfaced on the PDF
                Scope page to give the client context on the engagement. */}
            {isSFH && (
              <section data-testid="sfh-phase-1">
                <PhaseHeader number="01" title="Project Details" subtitle="Tell us about the home. These details help us size the visualization and VEXOS experience." />

                <div className="grid md:grid-cols-2 gap-x-12 gap-y-9 pl-[68px]">
                  <FormField label="Home Sq Ft" hint="Primary driver for visualization scope.">
                    <NumInput value={sfh_homeSqFt} onChange={setSfhHomeSqFt} placeholder="e.g. 4,500" min={1} dataTestId="sfh-input-home-sqft" />
                  </FormField>
                  <FormField label="Number of Floors">
                    <NumInput value={floors} onChange={setFloors} placeholder="e.g. 2" min={1} dataTestId="sfh-input-floors" />
                  </FormField>
                  <FormField
                    label="Outdoor / Amenity Sq Ft"
                    hint="Includes backyard, pool deck, rooftop terraces, outdoor kitchens, courtyards, etc."
                  >
                    <NumInput value={sfh_amenitySqFt} onChange={setSfhAmenitySqFt} placeholder="e.g. 3,000" min={0} dataTestId="sfh-input-amenity-sqft" />
                  </FormField>
                  <FormField label="Unique Home Plans" hint="Drives interior renders, VR, and floor plan rows below.">
                    <NumInput value={uniqueFloorPlans} onChange={setUniqueFloorPlans} placeholder="e.g. 1" min={1} dataTestId="sfh-input-plans" />
                  </FormField>
                </div>
              </section>
            )}

            {/* PHASE 2 */}
            <section
              data-testid="phase-2"
              className={`transition-all duration-700 ${phaseLocked(2) ? "opacity-30 pointer-events-none blur-[1px]" : "opacity-100"}`}
            >
              <PhaseHeader number="02" title="Exterior Experience" subtitle="The building, brought to life. Base package included — refine with optional enhancements." locked={phaseLocked(2)} />

              {/* Included — hidden in A La Carte (VEXOS-only base package) */}
              {!isAlacarte && (
              <div className="pl-[68px] mb-10">
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-4">Included in Base Package</p>
                <div className="grid md:grid-cols-3 gap-3">
                  {[
                    { icon: Building2, label: "Building Orbit", sub: "Full orbit with 4 keyframes" },
                    { icon: Layers, label: "Overlay Programming & Live Availability", sub: "Connected to live inventory · real-time unit status display" },
                    { icon: Globe, label: "VEXOS Landing Page", sub: "Branded project landing page" },
                  ].map(({ icon: Icon, label, sub }) => (
                    <div key={label} className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                      <Icon className="w-4 h-4 text-[#8FA79B] shrink-0 mt-1" />
                      <div className="min-w-0">
                        <p className="text-[13px] text-white">{label}</p>
                        <p className="text-[11px] text-white/45 mt-0.5">{sub}</p>
                      </div>
                      <Check className="w-3.5 h-3.5 text-[#8FA79B] shrink-0 ml-auto mt-1" />
                    </div>
                  ))}
                </div>
              </div>
              )}

              {/* Exterior Renderings — flat quantity, uses interior render volume tiers */}
              <div className="pl-[68px] mb-10">
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-4">Exterior Renderings</p>
                <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-[14.5px] text-white">Exterior Renderings</p>
                      <p className="text-[12px] text-white/45 mt-1">Photorealistic renders of the building exterior and surroundings.</p>
                      <div className="mt-2 text-[13px]">
                        <PriceTag amount={lookupVolumeRate((calc.totalInteriorRenders || 0) + (Number(exteriorRendersQty) || 0), interiorRenderTiers) || interiorRenderTiers?.[0]?.ratePerUnit || 0} /> <span className="text-white/40 text-[12px]">/ render <span className="text-white/30">(tiered)</span></span><VolumeTierBadge qty={(calc.totalInteriorRenders || 0) + (Number(exteriorRendersQty) || 0)} volumeTiers={interiorRenderTiers} testId="exterior-render-tier-badge" />
                      </div>
                      <div className="mt-2 text-[11.5px] text-white/45">
                        Recommended <span className="text-[#8FA79B]">2</span> renders{Number(exteriorRendersQty) === 2 && <span className="text-[#8FA79B] ml-1">✓</span>}
                      </div>
                    </div>
                    <StepperRow
                      label=""
                      value={exteriorRendersQty}
                      onChange={setExteriorRendersQty}
                      min={0}
                      dataTestId="exterior-renders-qty"
                    />
                  </div>
                </div>
              </div>

              {/* Exterior VR Tour — single per-project point tour (street-view style) */}
              <div className="pl-[68px] mb-10">
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-4">Exterior VR Tour</p>
                <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                  <div className="mb-1">
                    <p className="text-[14.5px] text-white">Building Exterior Tour</p>
                    <p className="text-[12px] text-white/45 mt-1">An immersive tour around the building exterior — street-view style walk-around of the property and grounds.</p>
                    <p className="text-[11.5px] text-white/40 mt-2">
                      Recommended <span className="text-[#8FA79B]">{recExtVR}</span> point{recExtVR === 1 ? "" : "s"} • <PriceTag amount={lookupVolumeRate(calc.exteriorVRPoints, EXTERIOR_VR.volumeTiers) || EXTERIOR_VR.volumeTiers?.[0]?.ratePerUnit || 0} /> <span className="text-white/40">/ point <span className="text-white/30">(tiered)</span></span><VolumeTierBadge qty={calc.exteriorVRPoints} volumeTiers={EXTERIOR_VR.volumeTiers} testId="exterior-vr-tier-badge" />
                    </p>
                  </div>
                  <div className="mt-2 rounded-xl border border-white/[0.05] px-4">
                    <PointSelectorRow
                      label="Exterior tour points"
                      value={exteriorVR}
                      options={EXTERIOR_VR.pointOptions}
                      onChange={setExteriorVR}
                      dataTestId="exterior-vr"
                    />
                  </div>
                  <div className="mt-3 flex justify-between text-[12px] text-white/55">
                    <span>{calc.exteriorVRActive ? `1 tour · ${calc.exteriorVRPoints} points` : "No exterior tour selected"}</span>
                    <span><PriceTag amount={calc.exteriorVRCost} showTbd={false} /></span>
                  </div>
                </div>

                {/* Drone & Reality Capture lives once, in the Interior
                    Experience section — see Phase 03. */}
              </div>

              {/* Orbits & keyframes — hidden in A La Carte (VEXOS-only) */}
              {!isAlacarte && <div className="pl-[68px] mb-10">
                <OrbitKeyframesGroup
                  addons={EXTERIOR_ADDONS}
                  baseOrbitName={isSFH ? "Home orbit" : "Building orbit"}
                  baseExtraKf={additionalKeyframesQty}
                  onBaseExtraKfChange={setAdditionalKeyframesQty}
                  subOrbits={subOrbits}
                  onSubOrbitsChange={setSubOrbits}
                />
              </div>}

              {/* Treatments applied ACROSS the orbits configured above. */}
              {!isAlacarte && <div className="pl-[68px]">
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-white/55 mb-5">Optional Enhancements</p>
                <div className="space-y-3">
                  {/* Day to Night — built into every orbit, priced per keyframe */}
                  <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[14.5px] text-white">{EXTERIOR_ADDONS.dayToNight.label}</p>
                        <p className="text-[12px] text-white/45 mt-1">{EXTERIOR_ADDONS.dayToNight.description}</p>
                        <div className="mt-2 text-[13px]">
                          <PriceTag amount={EXTERIOR_ADDONS.dayToNight.pricePerKeyframe} /> <span className="text-white/40 text-[12px]">/ included keyframe · </span>
                          <PriceTag amount={EXTERIOR_ADDONS.dayToNight.pricePerExtraKeyframe} /> <span className="text-white/40 text-[12px]">/ added keyframe</span>
                        </div>
                      </div>
                      <Switch checked={dayToNight} onCheckedChange={setDayToNight} data-testid="toggle-day-to-night" />
                    </div>
                    {dayToNight && (
                      <div className="pt-3 border-t border-white/[0.04]" data-testid="day-to-night-breakdown">
                        <D2NBreakdown calc={calc} addons={EXTERIOR_ADDONS} />
                      </div>
                    )}
                  </div>

                  {/* Animated Keyframes */}
                  <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[14.5px] text-white">{EXTERIOR_ADDONS.animatedKeyframes.label}</p>
                        <p className="text-[12px] text-white/45 mt-1">{EXTERIOR_ADDONS.animatedKeyframes.description}</p>
                        <div className="mt-2 text-[13px]">
                          <PriceTag amount={EXTERIOR_ADDONS.animatedKeyframes.pricePerKeyframe} /> <span className="text-white/40 text-[12px]">/ keyframe animated</span>
                        </div>
                      </div>
                      <Switch checked={animatedKeyframesEnabled} onCheckedChange={setAnimatedKeyframesEnabled} data-testid="toggle-animated-keyframes" />
                    </div>
                    {animatedKeyframesEnabled && (
                      <div className="pt-3 border-t border-white/[0.04]">
                        <StepperRow
                          label="Keyframes to animate"
                          badge={`of ${exteriorKfTotal} total`}
                          value={animatedKeyframesQty}
                          recommended={exteriorKfTotal}
                          onChange={setAnimatedKeyframesQty}
                          min={0}
                          max={exteriorKfTotal}
                          dataTestId="animated-keyframes-qty"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>}
            </section>

            {/* PHASE 3 — Interior */}
            <section
              data-testid="phase-3"
              className={`transition-all duration-700 ${phaseLocked(3) ? "opacity-30 pointer-events-none blur-[1px]" : "opacity-100"}`}
            >
              <PhaseHeader number="03" title="Interior Experience" subtitle="Floor plan renderings & immersive VR tours, tuned to each plan." locked={phaseLocked(3)} />

              {floorPlanCount > 0 && (
                <div className="pl-[68px] space-y-12">
                  {/* RENDERS */}
                  <div>
                    <div className="h-px bg-[#8FA79B]/40 mb-5"></div>
                    <div className="mb-5">
                      <h3 className="text-[17px] text-white" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>Renderings per Floor Plan</h3>
                      <p className="text-[11.5px] text-white/40 mt-1">
                        Recommended <span className="text-[#8FA79B]">{recRender}</span> renders/plan based on {fmtSqFt(avgFloorPlanSqFt)} avg • <PriceTag amount={lookupVolumeRate(calc.totalInteriorRenders, interiorRenderTiers) || interiorRenderTiers?.[0]?.ratePerUnit || 0} /> <span className="text-white/40">/ render <span className="text-white/30">(tiered)</span></span><VolumeTierBadge qty={calc.totalInteriorRenders + (Number(exteriorRendersQty) || 0)} volumeTiers={interiorRenderTiers} testId="interior-render-tier-badge" />
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/[0.05] divide-y divide-white/[0.05] px-4">
                      {Array.from({ length: floorPlanCount }).map((_, i) => (
                        <React.Fragment key={i}>
                          <StepperRow
                            label={`Floor Plan ${i + 1}`}
                            value={interiorRenders[i] ?? 0}
                            recommended={recRender}
                            onChange={(v) => setInteriorRenders(prev => ({ ...prev, [i]: v }))}
                            min={0}
                            dataTestId={`interior-render-fp-${i + 1}`}
                          />
                          {i === 0 && floorPlanCount > 1 && (
                            <ApplyToAllInline
                              onClick={() => {
                                const v = interiorRenders[0] ?? 0;
                                const next = { ...interiorRenders };
                                for (let j = 1; j < floorPlanCount; j++) next[j] = v;
                                setInteriorRenders(next);
                              }}
                              dataTestId="apply-all-renders"
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-between text-[12px] text-white/55">
                      <span>{calc.totalInteriorRenders} renders total</span>
                      <span><PriceTag amount={calc.interiorRendersCost} showTbd={false} /></span>
                    </div>
                  </div>

                  {/* VR — Point selector per plan (0/2/4/6/8/10) */}
                  <div>
                    <div className="h-px bg-[#8FA79B]/40 mb-5"></div>

                    <div className="mb-5">
                      <h3 className="text-[17px] text-white" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>VR Tour per Floor Plan</h3>
                      <p className="text-[11.5px] text-white/40 mt-1">
                        Recommended <span className="text-[#8FA79B]">{recVR}</span> point{recVR === 1 ? "" : "s"}/plan based on {fmtSqFt(avgFloorPlanSqFt)} avg • <PriceTag amount={lookupVolumeRate(calc.interiorVRPointsSum, INTERIOR_VR.volumeTiers) || INTERIOR_VR.volumeTiers?.[0]?.ratePerUnit || 0} /> <span className="text-white/40">/ point <span className="text-white/30">(tiered)</span></span><VolumeTierBadge qty={calc.interiorVRPointsSum} volumeTiers={INTERIOR_VR.volumeTiers} testId="interior-vr-tier-badge" />
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/[0.05] divide-y divide-white/[0.05] px-4">
                      {Array.from({ length: floorPlanCount }).map((_, i) => (
                        <React.Fragment key={i}>
                          <PointSelectorRow
                            label={`Floor Plan ${i + 1}`}
                            value={interiorVR[i]}
                            options={INTERIOR_VR.pointOptions}
                            onChange={(v) => setInteriorVR(prev => ({ ...prev, [i]: v }))}
                            dataTestId={`interior-vr-fp-${i + 1}`}
                          />
                          {i === 0 && floorPlanCount > 1 && (
                            <ApplyToAllInline
                              disabled={interiorVR[0] == null}
                              onClick={() => {
                                const v = interiorVR[0];
                                if (v == null) return;
                                const next = { ...interiorVR };
                                for (let j = 1; j < floorPlanCount; j++) next[j] = v;
                                setInteriorVR(next);
                              }}
                              dataTestId="apply-all-vr"
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-between text-[12px] text-white/55">
                      <span>{calc.totalInteriorVR} tour{calc.totalInteriorVR === 1 ? "" : "s"} · {calc.interiorVRPointsSum} points total</span>
                      <span><PriceTag amount={calc.interiorVRCost} showTbd={false} /></span>
                    </div>
                    {calc.totalInteriorVR > 0 && (
                      <VrConfiguratorCard
                        sectionKey="residence"
                        value={vrConfigurator.residence}
                        onChange={updateVrConfigurator}
                        costs={calc.residenceConfigurator}
                        noun="floor plan"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Drone & Reality Capture — ONE project-wide $5,000 line, and it
                  lives here in the residence section. Interior, amenity and
                  exterior VR all share the same on-site capture, so the amenity
                  and exterior sections no longer repeat this card — repeating it
                  read as a separate charge per experience on the proposal.
                  Sits outside the floorPlanCount gate so an amenity-only or
                  exterior-only quote still has the toggle, and keys off VR being
                  selected rather than the toggle itself so a rep who switches it
                  off can switch it back on. */}
              {(calc.anyInteriorVRActive || calc.anyAmenityVRActive || calc.anyExteriorVRActive) && (
                <div className="pl-[68px] mt-12">
                  <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]" data-testid="interior-drone-shots-card">
                    <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-3">Drone &amp; Reality Capture</p>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-[14.5px] text-white">Drone &amp; Reality Capture</p>
                        <p className="text-[12px] text-white/45 mt-1">On-site flight, aerial capture &amp; travel — charged once per project and applied to every VR tour, interior and amenity.</p>
                        <div className="mt-2 text-[13px]"><PriceTag amount={DRONE.captureFee} /> <span className="text-white/40 text-[12px]">/ project</span></div>
                      </div>
                      <Switch checked={interiorDroneShots || amenityDroneShots} onCheckedChange={setDroneCaptureEnabled} data-testid="toggle-interior-drone-shots" />
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* PHASE 4 — Amenity (Condo only — SFH skips Amenity per spec) */}
            {isCondo && (
            <section
              data-testid="phase-4"
              className={`transition-all duration-700 ${phaseLocked(4) ? "opacity-30 pointer-events-none blur-[1px]" : "opacity-100"}`}
            >
              <PhaseHeader number="04" title="Amenity Experience" subtitle="Renderings for every amenity space, plus a single immersive VR walk-through." locked={phaseLocked(4)} />

              {amenityCount === 0 ? (
                <div className="pl-[68px] text-[13px] text-white/40 italic">No amenity spaces in this project.</div>
              ) : (
                <div className="pl-[68px] space-y-12">
                  {/* Renders */}
                  <div>
                    <div className="h-px bg-[#8FA79B]/40 mb-5"></div>
                    <div className="mb-5">
                      <h3 className="text-[17px] text-white" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>Renderings per Amenity Space</h3>
                      <p className="text-[11.5px] text-white/40 mt-1">
                        Recommended <span className="text-[#8FA79B]">{recAmenityRender}</span> render{recAmenityRender === 1 ? "" : "s"}/space • <PriceTag amount={lookupVolumeRate(calc.totalAmenityRenders, amenityRenderTiers) || amenityRenderTiers?.[0]?.ratePerUnit || 0} /> <span className="text-white/40">/ render <span className="text-white/30">(tiered)</span></span><VolumeTierBadge qty={calc.totalAmenityRenders} volumeTiers={amenityRenderTiers} testId="amenity-render-tier-badge" />
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/[0.05] divide-y divide-white/[0.05] px-4">
                      {Array.from({ length: amenityCount }).map((_, i) => (
                        <React.Fragment key={i}>
                          <StepperRow
                            label={`Amenity Space ${i + 1}`}
                            value={amenityRenders[i] ?? 0}
                            recommended={recAmenityRender}
                            onChange={(v) => setAmenityRenders(prev => ({ ...prev, [i]: v }))}
                            min={0}
                            dataTestId={`amenity-render-${i + 1}`}
                          />
                          {i === 0 && amenityCount > 1 && (
                            <ApplyToAllInline
                              onClick={() => {
                                const v = amenityRenders[0] ?? 0;
                                const next = { ...amenityRenders };
                                for (let j = 1; j < amenityCount; j++) next[j] = v;
                                setAmenityRenders(next);
                              }}
                              dataTestId="apply-all-amenity-renders"
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-between text-[12px] text-white/55">
                      <span>{calc.totalAmenityRenders} renders total</span>
                      <span><PriceTag amount={calc.amenityRendersCost} showTbd={false} /></span>
                    </div>
                  </div>

                  {/* Amenity VR — per-amenity-space point selector */}
                  <div>
                    <div className="h-px bg-[#8FA79B]/40 mb-5"></div>

                    <div className="mb-5">
                      <h3 className="text-[17px] text-white" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>VR Tour per Amenity Space</h3>
                      <p className="text-[11.5px] text-white/40 mt-1">
                        Recommended <span className="text-[#8FA79B]">{recAmenityVRPoints}</span> point{recAmenityVRPoints === 1 ? "" : "s"}/space based on {fmtSqFt(totalAmenitySqFt)} total amenity area{recAmenityVRPoints > 0 && amenityVRPackagePrice(recAmenityVRPoints) > 0 && (
                          <> • <PriceTag amount={amenityVRPackagePrice(recAmenityVRPoints)} /> <span className="text-white/40">/ {recAmenityVRPoints}-pt package</span></>
                        )}
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/[0.05] divide-y divide-white/[0.05] px-4">
                      {Array.from({ length: amenityCount }).map((_, i) => (
                        <React.Fragment key={i}>
                          <PointSelectorRow
                            label={`Amenity Space ${i + 1}`}
                            value={amenityVR[i]}
                            options={AMENITY_VR.pointOptions}
                            onChange={(v) => setAmenityVR(prev => ({ ...prev, [i]: v }))}
                            dataTestId={`amenity-vr-${i + 1}`}
                          />
                          {i === 0 && amenityCount > 1 && (
                            <ApplyToAllInline
                              disabled={amenityVR[0] == null}
                              onClick={() => {
                                const v = amenityVR[0];
                                if (v == null) return;
                                const next = { ...amenityVR };
                                for (let j = 1; j < amenityCount; j++) next[j] = v;
                                setAmenityVR(next);
                              }}
                              dataTestId="apply-all-amenity-vr"
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-between text-[12px] text-white/55">
                      <span>{calc.totalAmenityVR} tour{calc.totalAmenityVR === 1 ? "" : "s"} · {calc.amenityVRPointsSum} points total</span>
                      <span><PriceTag amount={calc.amenityVRCost} showTbd={false} /></span>
                    </div>
                    {calc.totalAmenityVR > 0 && (
                      <VrConfiguratorCard
                        sectionKey="amenity"
                        value={vrConfigurator.amenity}
                        onChange={updateVrConfigurator}
                        costs={calc.amenityConfigurator}
                        noun="amenity"
                      />
                    )}

                    {/* Drone & Reality Capture is charged once for the whole
                        project and covers amenity VR too — the card lives in
                        the Interior Experience section (Phase 03). */}
                  </div>
                </div>
              )}
            </section>
            )}
            </>
            )}

            {/* ════════════════ COMMUNITY MODE ════════════════ */}
            {vexosEffectiveEnabled && mode === "community" && (
            <>
            {/* PHASE 1 — Community (shown in all modes including A La Carte) */}
            <section data-testid="c-phase-1">
              <PhaseHeader number="01" title={c_masterPlan ? "Master Plan Details" : "Community Details"} subtitle={`Tell us about your ${c_masterPlan ? "master plan" : "community"}. These numbers drive every recommendation that follows.`} />

              <div className="grid md:grid-cols-2 gap-x-12 gap-y-9 pl-[68px]">
                <FormField
                  label={c_masterPlan ? "Number of Units" : "Number of Lots"}
                  hint={c_masterPlan
                    ? `Drives the monthly platform installment · setup fee is a flat ${fmt(getMasterPlanSetupFee())}`
                    : "Drives setup fee tier & monthly platform installment"}
                >
                  <NumInput value={c_lots} onChange={setCLots} placeholder="e.g. 180" min={1} dataTestId="c-input-lots" />
                </FormField>
                <FormField label="Unique Home Models" hint="Optional — leave at 0 if only lots are being sold">
                  <NumInput value={c_homeModels} onChange={setCHomeModels} placeholder="e.g. 6" min={0} dataTestId="c-input-home-models" />
                </FormField>
                <FormField label="Average Sq Ft per Home Model" hint={Number(c_homeModels) > 0 ? undefined : "Not needed without home models"}>
                  <NumInput value={c_avgHomeSqFt} onChange={setCAvgHomeSqFt} placeholder="e.g. 2,400" min={0} dataTestId="c-input-avg-home-sqft" />
                </FormField>
                <FormField label="Number of Amenity Buildings / Spaces">
                  <NumInput value={c_amenitySpaces} onChange={setCAmenitySpaces} placeholder="e.g. 3" min={0} dataTestId="c-input-amenity-spaces" />
                </FormField>
                <FormField label="Total Sq Ft of Amenity Areas">
                  <NumInput value={c_totalAmenitySqFt} onChange={setCTotalAmenitySqFt} placeholder="e.g. 6,000" min={0} dataTestId="c-input-amenity-sqft" />
                </FormField>
              </div>
            </section>

            {/* PHASE 2 — Community Exterior */}
            <section
              data-testid="c-phase-2"
              className={`transition-all duration-700 ${phaseLocked(2) ? "opacity-30 pointer-events-none blur-[1px]" : "opacity-100"}`}
            >
              <PhaseHeader number="02" title={c_masterPlan ? "Master Plan Exterior Experience" : "Community Exterior Experience"} subtitle={`The ${c_masterPlan ? "master plan" : "community"}, brought to life. Base package included — refine with optional enhancements.`} locked={phaseLocked(2)} />

              {/* Included — hidden in A La Carte (VEXOS-only base package) */}
              {!isAlacarte && (
              <div className="pl-[68px] mb-10">
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-4">Included in Base Package</p>
                <div className={`grid gap-3 ${c_masterPlan ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
                  {[
                    c_masterPlan
                      ? { icon: Building2, label: "Master Orbit", sub: `Full orbit with ${BASE_ORBIT_KEYFRAMES} keyframes` }
                      : { icon: Building2, label: "Community Orbit", sub: "Full orbit with 4 keyframes" },
                    ...(c_masterPlan
                      ? [{ icon: Building2, label: `${MASTER_PLAN_INCLUDED_SUBORBITS} Suborbits`, sub: `${MASTER_PLAN_SUBORBIT_KEYFRAMES} keyframes each` }]
                      : []),
                    { icon: Layers, label: "Overlay Programming & Live Availability", sub: "Connected to live inventory · real-time lot status display" },
                    { icon: Globe, label: "VEXOS Landing Page", sub: "Branded community landing page" },
                  ].map(({ icon: Icon, label, sub }) => (
                    <div key={label} className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                      <Icon className="w-4 h-4 text-[#8FA79B] shrink-0 mt-1" />
                      <div className="min-w-0">
                        <p className="text-[13px] text-white">{label}</p>
                        <p className="text-[11px] text-white/45 mt-0.5">{sub}</p>
                      </div>
                      <Check className="w-3.5 h-3.5 text-[#8FA79B] shrink-0 ml-auto mt-1" />
                    </div>
                  ))}
                </div>
              </div>
              )}

              {/* Exterior Renderings — flat quantity, uses home-model render volume tiers */}
              <div className="pl-[68px] mb-10">
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-4">Exterior Renderings</p>
                <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-[14.5px] text-white">Exterior Renderings</p>
                      <p className="text-[12px] text-white/45 mt-1">Photorealistic renders of the community exterior and surroundings.</p>
                      <div className="mt-2 text-[13px]">
                        <PriceTag amount={lookupVolumeRate((calcCommunity.totalHomeRenders || 0) + (Number(c_exteriorRendersQty) || 0), homeRenderTiers) || homeRenderTiers?.[0]?.ratePerUnit || 0} /> <span className="text-white/40 text-[12px]">/ render <span className="text-white/30">(tiered)</span></span><VolumeTierBadge qty={(calcCommunity.totalHomeRenders || 0) + (Number(c_exteriorRendersQty) || 0)} volumeTiers={homeRenderTiers} testId="c-exterior-render-tier-badge" />
                      </div>
                      <div className="mt-2 text-[11.5px] text-white/45">
                        Recommended <span className="text-[#8FA79B]">2</span> renders{Number(c_exteriorRendersQty) === 2 && <span className="text-[#8FA79B] ml-1">✓</span>}
                      </div>
                    </div>
                    <StepperRow
                      label=""
                      value={c_exteriorRendersQty}
                      onChange={setCExteriorRendersQty}
                      min={0}
                      dataTestId="c-exterior-renders-qty"
                    />
                  </div>
                </div>
              </div>

              {/* Exterior VR Tour — single per-project point tour (street-view style) */}
              <div className="pl-[68px] mb-10">
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-4">Exterior VR Tour</p>
                <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                  <div className="mb-1">
                    <p className="text-[14.5px] text-white">Community Exterior Tour</p>
                    <p className="text-[12px] text-white/45 mt-1">An immersive tour around the community exterior — street-view style walk-around of the streetscapes, entry &amp; common frontage.</p>
                    <p className="text-[11.5px] text-white/40 mt-2">
                      Recommended <span className="text-[#8FA79B]">{recExtVR}</span> point{recExtVR === 1 ? "" : "s"} • <PriceTag amount={lookupVolumeRate(calcCommunity.exteriorVRPoints, EXTERIOR_VR.volumeTiers) || EXTERIOR_VR.volumeTiers?.[0]?.ratePerUnit || 0} /> <span className="text-white/40">/ point <span className="text-white/30">(tiered)</span></span><VolumeTierBadge qty={calcCommunity.exteriorVRPoints} volumeTiers={EXTERIOR_VR.volumeTiers} testId="c-exterior-vr-tier-badge" />
                    </p>
                  </div>
                  <div className="mt-2 rounded-xl border border-white/[0.05] px-4">
                    <PointSelectorRow
                      label="Exterior tour points"
                      value={c_exteriorVR}
                      options={EXTERIOR_VR.pointOptions}
                      onChange={setCExteriorVR}
                      dataTestId="c-exterior-vr"
                    />
                  </div>
                  <div className="mt-3 flex justify-between text-[12px] text-white/55">
                    <span>{calcCommunity.exteriorVRActive ? `1 tour · ${calcCommunity.exteriorVRPoints} points` : "No exterior tour selected"}</span>
                    <span><PriceTag amount={calcCommunity.exteriorVRCost} showTbd={false} /></span>
                  </div>
                </div>

                {/* Drone & Reality Capture lives once, in the Home Model
                    Experience section — see Phase 03. */}
              </div>

              {/* Orbits & keyframes — hidden in A La Carte (VEXOS-only) */}
              {!isAlacarte && <div className="pl-[68px] mb-10">
                <OrbitKeyframesGroup
                  addons={COMMUNITY_EXTERIOR_ADDONS}
                  baseOrbitName={c_masterPlan ? "Master orbit" : "Community orbit"}
                  baseExtraKf={c_additionalKfQty}
                  onBaseExtraKfChange={setCAdditionalKfQty}
                  subOrbits={c_subOrbits}
                  onSubOrbitsChange={setCSubOrbits}
                  includedSubOrbits={c_masterPlan ? MASTER_PLAN_INCLUDED_SUBORBITS : 0}
                  testPrefix="c-"
                />
              </div>}

              {/* Treatments applied ACROSS the orbits configured above. */}
              {!isAlacarte && <div className="pl-[68px]">
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-white/55 mb-5">Optional Enhancements</p>
                <div className="space-y-3">
                  {/* Day to Night — built into every orbit, priced per keyframe */}
                  <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[14.5px] text-white">{COMMUNITY_EXTERIOR_ADDONS.dayToNight.label}</p>
                        <p className="text-[12px] text-white/45 mt-1">{COMMUNITY_EXTERIOR_ADDONS.dayToNight.description}</p>
                        <div className="mt-2 text-[13px]">
                          <PriceTag amount={COMMUNITY_EXTERIOR_ADDONS.dayToNight.pricePerKeyframe} /> <span className="text-white/40 text-[12px]">/ included keyframe · </span>
                          <PriceTag amount={COMMUNITY_EXTERIOR_ADDONS.dayToNight.pricePerExtraKeyframe} /> <span className="text-white/40 text-[12px]">/ added keyframe</span>
                        </div>
                      </div>
                      <Switch checked={c_dayToNight} onCheckedChange={setCDayToNight} data-testid="c-toggle-day-to-night" />
                    </div>
                    {c_dayToNight && (
                      <div className="pt-3 border-t border-white/[0.04]" data-testid="c-day-to-night-breakdown">
                        <D2NBreakdown calc={calcCommunity} addons={COMMUNITY_EXTERIOR_ADDONS} />
                      </div>
                    )}
                  </div>

                  {/* Animated Keyframes */}
                  <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[14.5px] text-white">{COMMUNITY_EXTERIOR_ADDONS.animatedKeyframes.label}</p>
                        <p className="text-[12px] text-white/45 mt-1">{COMMUNITY_EXTERIOR_ADDONS.animatedKeyframes.description}</p>
                        <div className="mt-2 text-[13px]">
                          <PriceTag amount={COMMUNITY_EXTERIOR_ADDONS.animatedKeyframes.pricePerKeyframe} /> <span className="text-white/40 text-[12px]">/ keyframe animated</span>
                        </div>
                      </div>
                      <Switch checked={c_animatedKfEnabled} onCheckedChange={setCAnimatedKfEnabled} data-testid="c-toggle-animated-keyframes" />
                    </div>
                    {c_animatedKfEnabled && (
                      <div className="pt-3 border-t border-white/[0.04]">
                        <StepperRow
                          label="Keyframes to animate"
                          badge={`of ${c_exteriorKfTotal} total`}
                          value={c_animatedKfQty}
                          recommended={c_exteriorKfTotal}
                          onChange={setCAnimatedKfQty}
                          min={0}
                          max={c_exteriorKfTotal}
                          dataTestId="c-animated-keyframes-qty"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>}
            </section>

            {/* PHASE 3 — Community Home Models */}
            <section
              data-testid="c-phase-3"
              className={`transition-all duration-700 ${phaseLocked(3) ? "opacity-30 pointer-events-none blur-[1px]" : "opacity-100"}`}
            >
              <PhaseHeader number="03" title="Home Model Experience" subtitle="Renderings & immersive VR tours, tuned to each home model." locked={phaseLocked(3)} />

              {c_homeModelCount > 0 && (
                <div className="pl-[68px] space-y-12">
                  {/* RENDERS */}
                  <div>
                    <div className="h-px bg-[#8FA79B]/40 mb-5"></div>
                    <div className="mb-5">
                      <h3 className="text-[17px] text-white" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>Renderings per Home Model</h3>
                      <p className="text-[11.5px] text-white/40 mt-1">
                        Recommended <span className="text-[#8FA79B]">{c_recHomeRender}</span> renders/model based on {fmtSqFt(c_avgHomeSqFt)} avg • <PriceTag amount={lookupVolumeRate(calcCommunity.totalHomeRenders, homeRenderTiers) || homeRenderTiers?.[0]?.ratePerUnit || 0} /> <span className="text-white/40">/ render <span className="text-white/30">(tiered)</span></span><VolumeTierBadge qty={calcCommunity.totalHomeRenders + (Number(c_exteriorRendersQty) || 0)} volumeTiers={homeRenderTiers} testId="home-render-tier-badge" />
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/[0.05] divide-y divide-white/[0.05] px-4">
                      {Array.from({ length: c_homeModelCount }).map((_, i) => (
                        <React.Fragment key={i}>
                          <StepperRow
                            label={`Home Model ${i + 1}`}
                            value={c_homeModelRenders[i] ?? 0}
                            recommended={c_recHomeRender}
                            onChange={(v) => setCHomeModelRenders(prev => ({ ...prev, [i]: v }))}
                            min={0}
                            dataTestId={`c-home-render-${i + 1}`}
                          />
                          {i === 0 && c_homeModelCount > 1 && (
                            <ApplyToAllInline
                              onClick={() => {
                                const v = c_homeModelRenders[0] ?? 0;
                                const next = { ...c_homeModelRenders };
                                for (let j = 1; j < c_homeModelCount; j++) next[j] = v;
                                setCHomeModelRenders(next);
                              }}
                              dataTestId="c-apply-all-renders"
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-between text-[12px] text-white/55">
                      <span>{calcCommunity.totalHomeRenders} renders total</span>
                      <span><PriceTag amount={calcCommunity.homeRendersCost} showTbd={false} /></span>
                    </div>
                  </div>

                  {/* VR — Point selector per home model (0/2/4/6/8/10) */}
                  <div>
                    <div className="h-px bg-[#8FA79B]/40 mb-5"></div>

                    <div className="mb-5">
                      <h3 className="text-[17px] text-white" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>VR Tour per Home Model</h3>
                      <p className="text-[11.5px] text-white/40 mt-1">
                        Recommended <span className="text-[#8FA79B]">{c_recHomeVR}</span> point{c_recHomeVR === 1 ? "" : "s"}/model based on {fmtSqFt(c_avgHomeSqFt)} avg • <PriceTag amount={lookupVolumeRate(calcCommunity.homeVRPointsSum, HOME_MODEL_VR.volumeTiers) || HOME_MODEL_VR.volumeTiers?.[0]?.ratePerUnit || 0} /> <span className="text-white/40">/ point <span className="text-white/30">(tiered)</span></span><VolumeTierBadge qty={calcCommunity.homeVRPointsSum} volumeTiers={HOME_MODEL_VR.volumeTiers} testId="home-vr-tier-badge" />
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/[0.05] divide-y divide-white/[0.05] px-4">
                      {Array.from({ length: c_homeModelCount }).map((_, i) => (
                        <React.Fragment key={i}>
                          <PointSelectorRow
                            label={`Home Model ${i + 1}`}
                            value={c_homeModelVR[i]}
                            options={HOME_MODEL_VR.pointOptions}
                            onChange={(v) => setCHomeModelVR(prev => ({ ...prev, [i]: v }))}
                            dataTestId={`c-home-vr-${i + 1}`}
                          />
                          {i === 0 && c_homeModelCount > 1 && (
                            <ApplyToAllInline
                              disabled={c_homeModelVR[0] == null}
                              onClick={() => {
                                const v = c_homeModelVR[0];
                                if (v == null) return;
                                const next = { ...c_homeModelVR };
                                for (let j = 1; j < c_homeModelCount; j++) next[j] = v;
                                setCHomeModelVR(next);
                              }}
                              dataTestId="c-apply-all-vr"
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-between text-[12px] text-white/55">
                      <span>{calcCommunity.totalHomeVR} tour{calcCommunity.totalHomeVR === 1 ? "" : "s"} · {calcCommunity.homeVRPointsSum} points total</span>
                      <span><PriceTag amount={calcCommunity.homeVRCost} showTbd={false} /></span>
                    </div>
                    {calcCommunity.totalHomeVR > 0 && (
                      <VrConfiguratorCard
                        sectionKey="residence"
                        value={vrConfigurator.residence}
                        onChange={updateVrConfigurator}
                        costs={calcCommunity.residenceConfigurator}
                        noun="home model"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Drone & Reality Capture — ONE project-wide $5,000 line, kept
                  in the residence (home model) section. Mirrors the condo side:
                  amenity and exterior VR share this same capture, so those
                  sections no longer repeat the card. Sits outside the
                  homeModelCount gate and keys off VR selection rather than the
                  toggle, so it can never be stranded or switched off for good. */}
              {(calcCommunity.anyInteriorVRActive || calcCommunity.anyAmenityVRActive || calcCommunity.anyExteriorVRActive) && (
                <div className="pl-[68px] mt-12">
                  <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.05]" data-testid="c-interior-drone-shots-card">
                    <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-3">Drone &amp; Reality Capture</p>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-[14.5px] text-white">Drone &amp; Reality Capture</p>
                        <p className="text-[12px] text-white/45 mt-1">On-site flight, aerial capture &amp; travel — charged once per project and applied to every VR tour, home model and amenity.</p>
                        <div className="mt-2 text-[13px]"><PriceTag amount={DRONE.captureFee} /> <span className="text-white/40 text-[12px]">/ project</span></div>
                      </div>
                      <Switch checked={c_interiorDroneShots || c_amenityDroneShots} onCheckedChange={setCDroneCaptureEnabled} data-testid="c-toggle-interior-drone-shots" />
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* PHASE 4 — Community Amenity */}
            <section
              data-testid="c-phase-4"
              className={`transition-all duration-700 ${phaseLocked(4) ? "opacity-30 pointer-events-none blur-[1px]" : "opacity-100"}`}
            >
              <PhaseHeader number="04" title="Amenity Experience" subtitle="Renderings for every amenity building or space, plus a single immersive VR walk-through." locked={phaseLocked(4)} />

              {c_amenityCount === 0 ? (
                <div className="pl-[68px] text-[13px] text-white/40 italic">No amenity buildings or spaces in this community.</div>
              ) : (
                <div className="pl-[68px] space-y-12">
                  {/* Renders */}
                  <div>
                    <div className="h-px bg-[#8FA79B]/40 mb-5"></div>
                    <div className="mb-5">
                      <h3 className="text-[17px] text-white" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>Renderings per Amenity Building / Space</h3>
                      <p className="text-[11.5px] text-white/40 mt-1">
                        Recommended <span className="text-[#8FA79B]">{c_recAmenityRender}</span> render{c_recAmenityRender === 1 ? "" : "s"}/space • <PriceTag amount={lookupVolumeRate(calcCommunity.totalAmenityRenders, cAmenityRenderTiers) || cAmenityRenderTiers?.[0]?.ratePerUnit || 0} /> <span className="text-white/40">/ render <span className="text-white/30">(tiered)</span></span><VolumeTierBadge qty={calcCommunity.totalAmenityRenders} volumeTiers={cAmenityRenderTiers} testId="c-amenity-render-tier-badge" />
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/[0.05] divide-y divide-white/[0.05] px-4">
                      {Array.from({ length: c_amenityCount }).map((_, i) => (
                        <React.Fragment key={i}>
                          <StepperRow
                            label={`Amenity ${i + 1}`}
                            value={c_amenityRenders[i] ?? 0}
                            recommended={c_recAmenityRender}
                            onChange={(v) => setCAmenityRenders(prev => ({ ...prev, [i]: v }))}
                            min={0}
                            dataTestId={`c-amenity-render-${i + 1}`}
                          />
                          {i === 0 && c_amenityCount > 1 && (
                            <ApplyToAllInline
                              onClick={() => {
                                const v = c_amenityRenders[0] ?? 0;
                                const next = { ...c_amenityRenders };
                                for (let j = 1; j < c_amenityCount; j++) next[j] = v;
                                setCAmenityRenders(next);
                              }}
                              dataTestId="c-apply-all-amenity-renders"
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-between text-[12px] text-white/55">
                      <span>{calcCommunity.totalAmenityRenders} renders total</span>
                      <span><PriceTag amount={calcCommunity.amenityRendersCost} showTbd={false} /></span>
                    </div>
                  </div>

                  {/* Amenity VR — per-amenity-space point selector */}
                  <div>
                    <div className="h-px bg-[#8FA79B]/40 mb-5"></div>

                    <div className="mb-5">
                      <h3 className="text-[17px] text-white" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>VR Tour per Amenity Building / Space</h3>
                      <p className="text-[11.5px] text-white/40 mt-1">
                        Recommended <span className="text-[#8FA79B]">{c_recAmenityVRPoints}</span> point{c_recAmenityVRPoints === 1 ? "" : "s"}/space based on {fmtSqFt(c_totalAmenitySqFt)} total amenity area{c_recAmenityVRPoints > 0 && amenityVRPackagePrice(c_recAmenityVRPoints) > 0 && (
                          <> • <PriceTag amount={amenityVRPackagePrice(c_recAmenityVRPoints)} /> <span className="text-white/40">/ {c_recAmenityVRPoints}-pt package</span></>
                        )}
                      </p>
                    </div>
                    <div className="rounded-xl border border-white/[0.05] divide-y divide-white/[0.05] px-4">
                      {Array.from({ length: c_amenityCount }).map((_, i) => (
                        <React.Fragment key={i}>
                          <PointSelectorRow
                            label={`Amenity ${i + 1}`}
                            value={c_amenityVR[i]}
                            options={COMMUNITY_AMENITY_VR.pointOptions}
                            onChange={(v) => setCAmenityVR(prev => ({ ...prev, [i]: v }))}
                            dataTestId={`c-amenity-vr-${i + 1}`}
                          />
                          {i === 0 && c_amenityCount > 1 && (
                            <ApplyToAllInline
                              disabled={c_amenityVR[0] == null}
                              onClick={() => {
                                const v = c_amenityVR[0];
                                if (v == null) return;
                                const next = { ...c_amenityVR };
                                for (let j = 1; j < c_amenityCount; j++) next[j] = v;
                                setCAmenityVR(next);
                              }}
                              dataTestId="c-apply-all-amenity-vr"
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="mt-3 flex justify-between text-[12px] text-white/55">
                      <span>{calcCommunity.totalAmenityVR} tour{calcCommunity.totalAmenityVR === 1 ? "" : "s"} · {calcCommunity.amenityVRPointsSum} points total</span>
                      <span><PriceTag amount={calcCommunity.amenityVRCost} showTbd={false} /></span>
                    </div>
                    {calcCommunity.totalAmenityVR > 0 && (
                      <VrConfiguratorCard
                        sectionKey="amenity"
                        value={vrConfigurator.amenity}
                        onChange={updateVrConfigurator}
                        costs={calcCommunity.amenityConfigurator}
                        noun="amenity"
                      />
                    )}

                    {/* Drone & Reality Capture is charged once for the whole
                        project and covers amenity VR too — the card lives in
                        the Home Model Experience section (Phase 03). */}
                  </div>
                </div>
              )}
            </section>
            </>
            )}

            {/* ════════════════ PHASE 05 — VIDEO ANIMATION (shared) ════════════════ */}
            <section
              data-testid="phase-5"
              className={`transition-all duration-700 ${phaseLocked(5) ? "opacity-30 pointer-events-none blur-[1px]" : "opacity-100"}`}
            >
              <PhaseHeader number="05" title="Video Animation" subtitle="Bring the project to life in motion. Choose your production style and duration." locked={phaseLocked(5)} />

              {(() => {
                // Standalone = À La Carte tab with no other visualization
                // products (video sold on its own). Anywhere else the video
                // is an add-on to a Vexos package.
                const standaloneMode = isAlacarte && !activeCalc.hasOtherScope;
                const forCondo = isCondo || isSFH;
                const activeSize   = forCondo ? videoProjectSize : c_videoProjectSize;
                const setActiveSize = forCondo ? setVideoProjectSize : setCVideoProjectSize;
                const activeSel    = forCondo ? videoStandaloneSel : c_videoStandaloneSel;
                const setActiveSel = forCondo ? setVideoStandaloneSel : setCVideoStandaloneSel;
                const activeCinVideos = forCondo ? cinematicVideos : c_cinematicVideos;
                const setActiveCinVideos = forCondo ? setCinematicVideos : setCCinematicVideos;
                const activeAiVideos  = forCondo ? aiMotionVideos : c_aiMotionVideos;
                const setActiveAiVideos = forCondo ? setAiMotionVideos : setCAiMotionVideos;

                // À La Carte: AI Motion normally needs renders in the quote.
                // When they aren't there, the rep can unlock it by attesting
                // the client is supplying the source renderings.
                const showClientRendersToggle = isAlacarte && !activeCalc.aiMotionRenderGateMet;

                if (standaloneMode) {
                  return (
                    <>
                      <StandaloneVideoFlow
                        size={activeSize}
                        onSize={setActiveSize}
                        sel={activeSel}
                        onSel={setActiveSel}
                        result={activeCalc.standaloneVideo}
                      />
                      <div className="pl-[68px] mt-7 space-y-3" data-testid="standalone-ai-motion">
                        <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80">AI Motion · client-provided renderings</p>
                        <AiMotionClientRendersToggle
                          checked={aiMotionClientRenders}
                          onToggle={setAiMotionClientRenders}
                        />
                        <div className="grid md:grid-cols-2 gap-6">
                          <VideoAnimationCard
                            label={VIDEO_ANIMATION.aiMotion.label}
                            description={VIDEO_ANIMATION.aiMotion.description}
                            durations={VIDEO_ANIMATION.aiMotion.durations}
                            videos={activeAiVideos}
                            onChange={setActiveAiVideos}
                            productLabel="AI motion video"
                            testIdRoot="ai-motion"
                            disabled={!activeCalc.aiMotionEligible}
                            disabledHint="Check the box above to confirm the client is providing a minimum of 5 to 6 renderings."
                          />
                        </div>
                      </div>
                    </>
                  );
                }

                return (
                  <>
                    <div className="pl-[68px] grid md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <VideoAnimationCard
                          label={VIDEO_ANIMATION.aiMotion.label}
                          description={VIDEO_ANIMATION.aiMotion.description}
                          durations={VIDEO_ANIMATION.aiMotion.durations}
                          videos={activeAiVideos}
                          onChange={setActiveAiVideos}
                          productLabel="AI motion video"
                          testIdRoot="ai-motion"
                          disabled={!activeCalc.aiMotionEligible}
                          disabledHint={showClientRendersToggle
                            ? "Requires at least 1 exterior + 3 interior renders — or check the box below if the client is providing renderings."
                            : "Requires at least 1 exterior + 3 interior renders."}
                        />
                        {showClientRendersToggle && (
                          <AiMotionClientRendersToggle
                            checked={aiMotionClientRenders}
                            onToggle={setAiMotionClientRenders}
                          />
                        )}
                      </div>
                      <VideoAnimationCard
                        label={VIDEO_ANIMATION.cinematic.label}
                        description={VIDEO_ANIMATION.cinematic.description}
                        durations={VIDEO_ANIMATION.cinematic.durations}
                        videos={activeCinVideos}
                        onChange={setActiveCinVideos}
                        productLabel="cinematic animation"
                        testIdRoot="cinematic"
                        durationNotes={{
                          30: "Covers up to 5 interior spaces",
                          60: "Covers up to 8 interior spaces",
                          90: "Covers up to 10 interior spaces",
                        }}
                      />
                    </div>

                    <div className="pl-[68px] mt-5"><VideoSubtext /></div>

                    {/* Motion Package — auto-activates when both products are selected */}
                    {activeCalc.motionPackageActive && (
                      <div className="pl-[68px] mt-6" data-testid="motion-package-callout">
                        <div
                          className="rounded-xl px-5 py-4 flex items-start gap-4"
                          style={{
                            background: "rgba(143,167,155,0.06)",
                            border: "1px solid rgba(143,167,155,0.22)",
                          }}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline justify-between gap-3 mb-1">
                              <span
                                className="text-[#8FA79B]"
                                style={{ fontFamily: 'var(--font-sans)', fontWeight: 400, fontSize: "14px" }}
                              >
                                Motion Package
                              </span>
                              <span className="text-[#8FA79B] text-[12px] font-medium tabular-nums">
                                −{activeCalc.motionPackagePct}%
                              </span>
                            </div>
                            <p className="text-[12px] text-white/55 leading-snug">
                              Both AI Motion + Cinematic selected.
                            </p>
                            <p className="text-[12.5px] mt-1.5">
                              <span className="text-white/55">Saving </span>
                              <span className="text-emerald-300/90 tabular-nums">{fmt(activeCalc.motionPackageSavings)}</span>
                              <span className="text-white/55"> on your video investment.</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </section>

            {/* ════════════════ PHASE 06 — FLOOR PLANS (shared) ════════════════ */}
            <section
              data-testid="phase-6"
              className={`transition-all duration-700 ${phaseLocked(6) ? "opacity-30 pointer-events-none blur-[1px]" : "opacity-100"}`}
            >
              <PhaseHeader number="06" title="Floor Plans" subtitle="Marketing-ready floor plan drawings, synced to your unique plan count." locked={phaseLocked(6)} />

              <div className="pl-[68px]">
                <FloorPlansSection
                  title="2D Floor Plans"
                  sublabel="CAD marketing-ready · vectorized · print and digital ready"
                  pricePerPlan={FLOOR_PLANS.price2D}
                  planCount={(isCondo || isSFH) ? Number(uniqueFloorPlans) || 0 : Number(c_homeModels) || 0}
                  planLabel={isSFH ? "Home Plan" : (isCondo ? "Floor Plan" : "Home Model")}
                  values={(isCondo || isSFH) ? floorPlans2D : c_floorPlans2D}
                  onChange={(isCondo || isSFH) ? setFloorPlans2D : setCFloorPlans2D}
                  testIdRoot="fp-2d"
                  manualPricing={activeCalc.manualPricing}
                />
                <div className="mt-10">
                  <FloorPlansSection
                    title="3D Floor Plans"
                    sublabel="Dimensioned 3D perspective view · spatially communicates the layout"
                    pricePerPlan={floorPlan3DRate((isCondo || isSFH) ? floorPlans3DWorkflow : c_floorPlans3DWorkflow)}
                    planCount={(isCondo || isSFH) ? Number(uniqueFloorPlans) || 0 : Number(c_homeModels) || 0}
                    planLabel={isSFH ? "Home Plan" : (isCondo ? "Floor Plan" : "Home Model")}
                    values={(isCondo || isSFH) ? floorPlans3D : c_floorPlans3D}
                    onChange={(isCondo || isSFH) ? setFloorPlans3D : setCFloorPlans3D}
                    levels={(isCondo || isSFH) ? floorPlans3DLevels : c_floorPlans3DLevels}
                    onLevelsChange={(isCondo || isSFH) ? setFloorPlans3DLevels : setCFloorPlans3DLevels}
                    workflow={(isCondo || isSFH) ? floorPlans3DWorkflow : c_floorPlans3DWorkflow}
                    onWorkflowChange={(isCondo || isSFH) ? setFloorPlans3DWorkflow : setCFloorPlans3DWorkflow}
                    workflowOptions={FP3D_WORKFLOWS.map((w) => ({ ...w, price: floorPlan3DRate(w.key) }))}
                    testIdRoot="fp-3d"
                    manualPricing={activeCalc.manualPricing}
                  />
                </div>
              </div>
            </section>

            {/* ════════════ PHASE 07 — CONCEPTUAL RENDERING (shared) ════════════
                VEXOS + À La Carte. Reads the active project type's quantity,
                like the other shared phases. */}
            {conceptualAvailable && (
              <section
                data-testid="phase-7"
                className={`transition-all duration-700 ${phaseLocked(7) ? "opacity-30 pointer-events-none blur-[1px]" : "opacity-100"}`}
              >
                <PhaseHeader number="07" title="Conceptual Rendering" subtitle="Hybrid workflow: fully modeled, with AI assisting the design, materials and finishes." locked={phaseLocked(7)} />
                {(() => {
                  const forCondo = isCondo || isSFH;
                  return (
                    <ConceptualModelingSection
                      qty={forCondo ? conceptualRendersQty : c_conceptualRendersQty}
                      onChange={forCondo ? setConceptualRendersQty : setCConceptualRendersQty}
                      rate={activeCalc.conceptualRenderRate}
                      cost={activeCalc.conceptualRendersCost}
                      testIdRoot={forCondo ? "conceptual" : "c-conceptual"}
                    />
                  );
                })()}
              </section>
            )}
            {/* Interior design sits OUTSIDE Phase 07's lock: it doesn't depend
                on the Phase 1 project facts, and a greyed-out card at the
                bottom of the page is why reps couldn't find the old fee. */}
            {conceptualAvailable && (
              <InteriorDesignFeeCard fee={conceptualDesignFee} onChange={updateConceptualDesignFee} />
            )}

            {/* A LA CARTE — inline Marketing & Collateral panel so the
                rep can configure both viz scope (above) and marketing /
                collateral deliverables in a single, platform-free tab. */}
            {isAlacarte && (
              <div className="mt-10 pt-8 border-t border-white/[0.06]" data-testid="alacarte-marketing-inline">
                <MarketingCollateralTab
                  marketing={marketing}
                  setMarketing={setMarketing}
                  onDirty={markDirty}
                  collateral={collateral}
                  setCollateral={setCollateral}
                  marketingMath={marketingMath}
                  collateralMath={collateralMath}
                  marketingNeedsPriceTier={marketingNeedsPriceTier}
                  cplTiers={cplTiers}
                  marketingConfig={marketingConfig}
                  collateralConfig={collateralMath.prices}
                  termMonths={marketing.sellOutMonths}
                  proposalMode={proposalMode}
                  savedQuoteId={savedQuoteId}
                  onDownloadProposal={saveAndOpenProposal}
                />
              </div>
            )}

            {/* EXPORT BAR — Print Quote / Download PDF removed per user request */}
            </>
            )}

          </div>

          {/* ─── STICKY QUOTE SUMMARY ─── */}
          <aside className="lg:sticky lg:top-8 self-start vexos-summary-sticky">
            <div className="rounded-2xl border border-[#8FA79B]/15 bg-[#000000] p-7 shadow-[0_0_40px_rgba(0,0,0,0.4)]">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-[10.5px] uppercase tracking-[0.18em] text-[#8FA79B]/80 mb-1">Live Quote</p>
                  <h3 className="text-[26px] tracking-tight" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>Project Summary</h3>
                </div>
                {isLotExplorer ? (
                  <button
                    onClick={() => setLotExplorerExpanded(true)}
                    disabled={lotExplorerCalc.grandTotal === 0}
                    className="shrink-0 mt-1 p-1.5 rounded-md text-cyan-300/50 hover:text-cyan-300 hover:bg-cyan-400/[0.06] disabled:opacity-30 disabled:cursor-not-allowed transition-colors no-print"
                    title="Open Lot Explorer presentation"
                    data-testid="expand-summary"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => setSummaryExpanded(true)}
                    disabled={!activePhase1Ready}
                    className="shrink-0 mt-1 p-1.5 rounded-md text-white/40 hover:text-[#8FA79B] hover:bg-white/[0.03] disabled:opacity-30 disabled:cursor-not-allowed transition-colors no-print"
                    title="Expand full breakdown"
                    data-testid="expand-summary"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* ─── CLIENT IDENTITY ─── */}
              <div className="mb-6 pb-5 border-b border-white/[0.06]">
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-3">Client</p>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => { setClientName(e.target.value); markDirty(); }}
                  placeholder="Client name (required to save)"
                  className={`w-full bg-transparent border-b focus:border-[#8FA79B]/60 outline-none text-[13px] text-white placeholder:text-white/30 py-2 mb-2 transition-colors ${
                    nameShake ? "border-red-400 animate-pulse" : "border-white/10"
                  }`}
                  data-testid="client-name-input"
                />
                <input
                  type="text"
                  value={clientEntity}
                  onChange={(e) => { setClientEntity(e.target.value); markDirty(); }}
                  placeholder="LLC / Entity (optional)"
                  className="w-full bg-transparent border-b border-white/10 focus:border-[#8FA79B]/60 outline-none text-[13px] text-white placeholder:text-white/30 py-2 transition-colors"
                  data-testid="client-entity-input"
                />
                <input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => { setClientEmail(e.target.value); markDirty(); }}
                  placeholder="Client email (for e-signature)"
                  className="w-full bg-transparent border-b border-white/10 focus:border-[#8FA79B]/60 outline-none text-[13px] text-white placeholder:text-white/30 py-2 mt-2 transition-colors"
                  data-testid="client-email-input"
                />
                {nameShake && (
                  <p className="mt-2 text-[11px] text-red-300" data-testid="client-name-error">
                    Please enter a client name to save
                  </p>
                )}
                {/* Proposal rep selector — only for internal accounts
                    (Bernard/Cole). Julian/Jordan generating their own
                    proposals resolve automatically from their login. */}
                {!currentUserIsKnownRep && (
                  <select
                    value={salesRep}
                    onChange={(e) => { setSalesRep(e.target.value); markDirty(); }}
                    className="w-full bg-transparent border-b border-white/10 focus:border-[#8FA79B]/60 outline-none text-[13px] text-white py-2 mt-2 transition-colors [&>option]:bg-[#000000] [&>option]:text-white"
                    data-testid="sales-rep-select"
                    title="Which rep should appear on the proposal's contact page"
                  >
                    <option value="">Proposal rep (contact on PDF)…</option>
                    {REP_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                )}
                <button
                  onClick={handleSidebarSave}
                  disabled={saving || hydrating}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-[12px] tracking-wide transition-all disabled:opacity-60"
                  style={{
                    background: justSaved ? "rgba(60,201,122,0.18)" : "#8FA79B",
                    color: justSaved ? "#3ECF83" : "#000000",
                    border: justSaved ? "1px solid rgba(60,201,122,0.5)" : "1px solid transparent",
                    fontWeight: 400,
                  }}
                  data-testid="sidebar-save-quote-btn"
                >
                  {justSaved
                    ? "✓ Saved"
                    : saving
                      ? "Saving…"
                      : savedQuoteId
                        ? "Update Quote"
                        : "Save Quote"}
                </button>
              </div>

              {/* ─── LOT EXPLORER (standalone, replaces all other sections) ─── */}
              {isLotExplorer && (
                <div className="mb-6" data-testid="sidebar-lot-explorer">
                  <p className="text-[10.5px] uppercase tracking-[0.16em] text-cyan-300/80 mb-3">VEXOS Lot Explorer&trade;</p>
                  <div className="flex items-baseline justify-between text-[12px] mb-2" data-testid="sidebar-lot-explorer-count">
                    <span className="text-white/55">Lots</span>
                    <span className="text-white tabular-nums">{lotExplorerCalc.lotCount.toLocaleString()}</span>
                  </div>
                  <SummaryRow label="Base Platform Fee" value={lotExplorerCalc.baseFee} />
                  <SummaryRow label="Rate Per Lot" value={lotExplorerCalc.ratePerLot} />
                  <SummaryRow label="Subtotal" value={lotExplorerCalc.grandTotal} sublabel={`${fmtUSD(lotExplorerCalc.baseFee)} base + ${lotExplorerCalc.lotCount.toLocaleString()} lots × ${fmtUSD(lotExplorerCalc.ratePerLot)}`} />
                  {lotExplorerCalc.features.length > 0 && (
                    <p className="text-[10.5px] text-white/45 mt-3 leading-relaxed">
                      <span className="text-cyan-300/70">Features:</span> {lotExplorerCalc.features.join(" · ")}
                    </p>
                  )}
                  <p className="text-[10.5px] text-white/45 mt-2">Timeline: <span className="text-white/70">{lotExplorerCalc.timelineWeeks} weeks</span></p>
                  {lotExplorerCalc.hostingMonthly > 0 && (
                    <p className="text-[10.5px] text-white/45 mt-1">Hosting / Support: <span className="text-white/70">{fmtUSD(lotExplorerCalc.hostingMonthly)} / mo</span></p>
                  )}
                  <div className="mt-4 pt-3 border-t border-white/[0.06]">
                    <p className="text-[10.5px] uppercase tracking-[0.16em] text-cyan-300/80 mb-2">Payment Plan</p>
                    {lotExplorerCalc.milestones.map((m) => (
                      <SummaryRow key={m.key} label={`${m.percent}% · ${m.label.replace(/ \(Due at Signing\)/, "").replace(/ Delivery$/, "")}`} value={m.amount} />
                    ))}
                    {/* Pay in full — the Lot Explorer proposal offers it too,
                        so the rep has to see the same number, and the same
                        discount switch, before presenting. Built over the
                        milestones above, exactly as the proposal builds it. */}
                    {lePifSidebar.eligible && (
                      <div className="mt-3 rounded-lg px-3 py-2.5"
                           style={{ background: "rgba(103,232,249,0.06)", border: "1px solid rgba(103,232,249,0.22)" }}
                           data-testid="sidebar-le-pif-option">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-[11.5px] text-white/80">Pay in Full — Due at Signing</p>
                            <p className="text-[10.5px] text-white/45 mt-0.5 leading-relaxed">
                              Replaces the milestones above · {lePifSidebar.pct > 0 ? `${lePifSidebar.pct}% off` : "no discount"}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-[12.5px] tabular-nums text-white/80">{fmt(lePifSidebar.total)}</p>
                            {lePifSidebar.savings > 0 && (
                              <p className="text-[10.5px] tabular-nums text-cyan-300/90 mt-0.5">−{fmt(lePifSidebar.savings)}</p>
                            )}
                          </div>
                        </div>
                        <div className="mt-2.5 pt-2.5 border-t border-white/[0.08] flex items-center justify-between gap-3"
                             data-testid="le-pif-discount-toggle">
                          <p className="text-[9.5px] uppercase tracking-[0.14em] text-white/40">Discount</p>
                          <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
                            {[[true, `${PIF_DISCOUNT_PCT}% off`], [false, "No discount"]].map(([val, lbl]) => (
                              <button
                                key={String(val)}
                                type="button"
                                onClick={() => setPifDiscount(val)}
                                className={`px-2.5 py-1 text-[10.5px] font-medium transition-colors ${pifDiscount === val ? "bg-cyan-400/80 text-[#000000]" : "bg-transparent text-white/60 hover:text-white/90"}`}
                                data-testid={`le-pif-discount-${val ? "on" : "off"}`}
                              >
                                {lbl}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setLotExplorerExpanded(true)}
                    disabled={lotExplorerCalc.grandTotal === 0}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-[12px] tracking-wide border border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/[0.08] hover:border-cyan-400/55 disabled:opacity-30 disabled:cursor-not-allowed transition-all no-print"
                    data-testid="sidebar-lot-explorer-present-btn"
                  >
                    <Presentation className="w-3.5 h-3.5" />
                    Present / Export PDF
                  </button>
                </div>
              )}

              {/* ─── SECTION A · VEXOS Platform ─── */}
              {!isLotExplorer && vexosEnabled && !isAlacarte && (
              <div className="mb-6" data-testid="sidebar-vexos-platform">
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-3">VEXOS Platform</p>
                <SetupFeeRow
                  value={activeCalc.setupFee}
                  auto={Number(activeCalcUnpriced.setupFee) || 0}
                  overridden={cleanPriceOverrides(priceOverrides).setupFee !== undefined}
                  onOverrideChange={(v) => setPriceOverride("setupFee", v)}
                />
                <GuardrailBadge verdict={guardrailByLine.setupFee} testId="guardrail-setupFee-inline" />
                <MonthlySubscriptionRow
                  value={paymentSchedule.monthly}
                  base={paymentSchedule.monthlyBase}
                  auto={paymentSchedule.monthlyAuto}
                  overridden={paymentSchedule.monthlyOverridden}
                  crmDeduction={paymentSchedule.crmDeduction}
                  sublabel={activePrimaryCount >= 1
                    ? `${activeContractTerm}-month agreement`
                    : (isCondo ? "Enter units to calculate" : "Enter lot count to calculate")}
                  usdNote={displayCurrency !== "USD"}
                  onOverrideChange={(v) => { setMonthlyRateOverride(v); markDirty(); }}
                />
                <GuardrailBadge verdict={guardrailByLine.monthlyRate} testId="guardrail-monthlyRate-inline" />
                <PostTermFeeRow
                  value={postTermMonthly}
                  defaultValue={defaultPostTermMonthly(mode)}
                  overridden={postTermOverride !== null}
                  onOverrideChange={(v) => { setPostTermOverride(v); markDirty(); }}
                />

                {/* Contract term selector. 12 months is the only term sold; it
                    only renders when a reopened quote sits on a retired term,
                    so the rep can move it back to 12. */}
                {activePrimaryCount >= 1 && !CONTRACT_TERM_OPTIONS.includes(activeContractTerm) && (
                  <div className="mt-3 flex gap-1.5">
                    {[activeContractTerm, ...CONTRACT_TERM_OPTIONS].map(term => (
                      <button
                        key={term}
                        onClick={() => setActiveContractTerm(term)}
                        className={`flex-1 py-1.5 rounded-md text-[11px] font-medium transition-all duration-200 border ${
                          activeContractTerm === term
                            ? "bg-[#8FA79B]/15 text-[#8FA79B] border-[#8FA79B]/40"
                            : "bg-transparent text-white/55 border-white/10 hover:border-white/25"
                        }`}
                        data-testid={`term-${term}`}
                      >
                        {term} mo
                      </button>
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* ─── SECTION B · Studio Visualization ─── */}
              {!isLotExplorer && vexosEffectiveEnabled && (
              <div className="mb-6 pt-5 border-t border-white/[0.08]" data-testid="sidebar-studio-visualization">
                <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-3">Studio Visualization</p>
                {/* Exterior Add-Ons rolls up Day/Night, keyframes, additional
                    orbits — these line items are exposed individually in
                    the main column; the sidebar summary row is hidden
                    in A La Carte per spec (VEXOS-only line item). */}
                {!isAlacarte && (
                  <SummaryRow label="Exterior Add-Ons" value={activeCalc.exteriorAddonsSubtotal} sublabel={activeCalc.exteriorAddonsSubtotal > 0 ? "Day/Night, keyframes, additional orbits" : null} />
                )}
                {activeCalc.vexosCoverLandingPageActive && (
                  <SummaryRow label="VEXOS Cover Landing Page" value={activeCalc.vexosCoverLandingPageCost} sublabel="Branded project landing page" />
                )}                <SummaryRow
                  label="Exterior Renderings"
                  value={activeCalc.exteriorRendersCost}
                  sublabel={(activeCalc.exteriorRendersQty || 0) > 0 ? `${activeCalc.exteriorRendersQty} render${activeCalc.exteriorRendersQty === 1 ? "" : "s"}` : null}
                />
                {(activeCalc.conceptualRendersQty || 0) > 0 && (
                  <SummaryRow
                    label={CONCEPTUAL_MODELING.label}
                    value={activeCalc.conceptualRendersCost}
                    sublabel={`${activeCalc.conceptualRendersQty} image${activeCalc.conceptualRendersQty === 1 ? "" : "s"} · hybrid 3D + AI`}
                  />
                )}
                {conceptualAvailable && (
                  <ModelingFeeRow
                    fee={modelingFee}
                    resolvedTier={activeCalc.modelingFeeTier}
                    autoTier={activeCalc.modelingFeeTierAuto}
                    amount={activeCalc.modelingFee}
                    onChange={updateModelingFee}
                  />
                )}
                {conceptualAvailable && (
                  <InteriorDesignRow fee={conceptualDesignFee} onChange={updateConceptualDesignFee} />
                )}
                {activeCalc.exteriorVRActive && (
                  <SummaryRow
                    label="Exterior VR Tour"
                    value={activeCalc.exteriorVRCost}
                    sublabel={`1 tour · ${activeCalc.exteriorVRPoints} points`}
                  />
                )}
                {/* Drone & Reality Capture — auto-applied when any VR tour active. */}
                {activeCalc.droneCaptureActive && (
                  <SummaryRow
                    label="Drone & Reality Capture"
                    value={activeCalc.droneCaptureFee}
                    sublabel="On-site capture + travel"
                  />
                )}
                {isCondo ? (
                  <>
                    <SummaryRow label="Interior Renderings" value={activeCalc.interiorRendersCost} sublabel={`${activeCalc.totalInteriorRenders} renders`} />
                    <SummaryRow label="Interior VR Tours" value={activeCalc.interiorVRCost} sublabel={activeCalc.totalInteriorVR > 0 ? `${activeCalc.totalInteriorVR} tour${activeCalc.totalInteriorVR === 1 ? "" : "s"} · ${activeCalc.interiorVRPointsSum} points` : "No tours selected"} />
                    {activeCalc.residenceConfiguratorCost > 0 && (
                      <SummaryRow label={VR_CONFIGURATOR_LABEL} value={activeCalc.residenceConfiguratorCost} sublabel={configuratorSub(activeCalc.residenceConfigurator)} />
                    )}
                  </>
                ) : (
                  <>
                    <SummaryRow label="Home Model Renderings" value={activeCalc.homeRendersCost} sublabel={`${activeCalc.totalHomeRenders} renders`} />
                    <SummaryRow label="Home Model VR Tours" value={activeCalc.homeVRCost} sublabel={activeCalc.totalHomeVR > 0 ? `${activeCalc.totalHomeVR} tour${activeCalc.totalHomeVR === 1 ? "" : "s"} · ${activeCalc.homeVRPointsSum} points` : "No tours selected"} />
                    {activeCalc.residenceConfiguratorCost > 0 && (
                      <SummaryRow label={VR_CONFIGURATOR_LABEL} value={activeCalc.residenceConfiguratorCost} sublabel={configuratorSub(activeCalc.residenceConfigurator)} />
                    )}
                  </>
                )}
                <SummaryRow label="Amenity Renderings" value={activeCalc.amenityRendersCost} sublabel={`${activeCalc.totalAmenityRenders} renders`} />
                <SummaryRow label="Amenity VR Tour" value={activeCalc.amenityVRCost} sublabel={activeCalc.totalAmenityVR > 0 ? `${activeCalc.totalAmenityVR} tour${activeCalc.totalAmenityVR === 1 ? "" : "s"} · ${activeCalc.amenityVRPointsSum} points` : "No tours selected"} />
                {activeCalc.amenityConfiguratorCost > 0 && (
                  <SummaryRow label={`${VR_CONFIGURATOR_LABEL} · Amenity`} value={activeCalc.amenityConfiguratorCost} sublabel={configuratorSub(activeCalc.amenityConfigurator)} />
                )}
                {/* ── Phase 05 · Video Animation (conditional) ── */}
                {activeCalc.aiMotionDur != null && (
                  <SummaryRow
                    label={`AI Motion (${shortVideos(normalizeVideos(activeCalc.aiMotionVideos, activeCalc.aiMotionDur))})`}
                    value={activeCalc.aiMotionCost}
                  />
                )}
                {activeCalc.cinematicDur != null && (
                  <SummaryRow
                    label={`Cinematic Animation (${shortVideos(normalizeVideos(activeCalc.cinematicVideos, activeCalc.cinematicDur))})`}
                    value={activeCalc.cinematicCost}
                  />
                )}
                {activeCalc.motionPackageActive && (
                  <div className="flex justify-between items-baseline text-[12px] py-1.5" data-testid="sidebar-motion-package">
                    <span className="text-[#8FA79B]">Motion Package (−{activeCalc.motionPackagePct}%)</span>
                    <span className="text-emerald-300/85 tabular-nums">−{fmt(activeCalc.motionPackageSavings)}</span>
                  </div>
                )}
                {/* ── Phase 06 · Floor Plans (conditional, post-volume-discount net) ── */}
                {activeCalc.total2DPlans > 0 && (
                  <SummaryRow
                    label={
                      activeCalc.floorPlans2DPct > 0
                        ? <>2D Floor Plans <span className="text-[#8FA79B] text-[10.5px] ml-1">(−{activeCalc.floorPlans2DPct}%)</span></>
                        : "2D Floor Plans"
                    }
                    value={activeCalc.floorPlans2DCost}
                    sublabel={`${activeCalc.total2DPlans} plan${activeCalc.total2DPlans === 1 ? "" : "s"}`}
                  />
                )}
                {activeCalc.total3DPlans > 0 && (
                  <SummaryRow
                    label={
                      activeCalc.floorPlans3DPct > 0
                        ? <>3D Floor Plans <span className="text-[#8FA79B] text-[10.5px] ml-1">(−{activeCalc.floorPlans3DPct}%)</span></>
                        : "3D Floor Plans"
                    }
                    value={activeCalc.floorPlans3DCost}
                    sublabel={`${floorPlan3DScopeLabel(activeCalc.total3DUnits, activeCalc.total3DLevels, activeCalc.total3DPlans)} · ${activeCalc.floorPlans3DWorkflow === "traditional" ? "Traditional 3D pipeline" : "AI Hybrid"}`}
                  />
                )}

                {/* Additional Revision Rounds — priced as a % of net viz
                    scope. Standard package includes REVISIONS_CONFIG.includedRounds
                    rounds; each extra round adds revisionUnitCost. */}
                {(Number(activeCalc.vizNet) || 0) > 0 && (
                  <div className="pt-3 mt-1 border-t border-white/[0.06]" data-testid="sidebar-revisions">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[12.5px] text-white/75">Additional Revision Rounds</p>
                        <p className="text-[10.5px] text-white/35 mt-0.5">
                          {REVISIONS_CONFIG.includedRounds} included · {fmt(activeCalc.revisionUnitCost)}/round ({REVISIONS_CONFIG.additionalPct}% of viz scope)
                        </p>
                      </div>
                      <CompactStepper
                        value={additionalRevisions}
                        onChange={(v) => { setAdditionalRevisions(v); markDirty(); }}
                        min={0}
                        dataTestId="revisions-stepper"
                      />
                    </div>
                    {(activeCalc.revisionsCost || 0) > 0 && (
                      <SummaryRow
                        label={`Additional Revisions (${activeCalc.revisionRounds})`}
                        value={activeCalc.revisionsCost}
                        sublabel={`${activeCalc.revisionRounds} round${activeCalc.revisionRounds === 1 ? "" : "s"} beyond the ${REVISIONS_CONFIG.includedRounds} included`}
                      />
                    )}
                  </div>
                )}
              </div>
              )}

              {/* ─── ON-SITE TRAVEL ─── (easy sidebar toggle; mostly VEXOS) */}
              {!isLotExplorer && vexosEffectiveEnabled && (
              <div className="mb-6 pt-5 border-t border-white/[0.08]" data-testid="sidebar-travel">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80">On-Site Travel</p>
                  <span className="text-[10.5px] text-white/40 tabular-nums">{fmt(activeCalc.travelDayRate)} / day</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[12.5px] text-white/75">Travel Days</p>
                    <p className="text-[10.5px] text-white/35 mt-0.5">Our team on-site · up to {TRAVEL_CONFIG.maxDays} days</p>
                  </div>
                  <CompactStepper
                    value={travelDays}
                    onChange={(v) => { setTravelDays(v); markDirty(); }}
                    min={0}
                    max={TRAVEL_CONFIG.maxDays}
                    dataTestId="travel-stepper"
                  />
                </div>
                {(activeCalc.travelCost || 0) > 0 && (
                  <SummaryRow
                    label={`Travel (${activeCalc.travelDays} day${activeCalc.travelDays === 1 ? "" : "s"})`}
                    value={activeCalc.travelCost}
                    sublabel="On-site team travel · billed at signing"
                  />
                )}
              </div>
              )}

              {/* ─── BUNDLE INCENTIVE BLOCK ─── */}
              {vexosEnabled && activePhase1Ready && activeCalc.vizGross > 0 && (
                <BundleBlock
                  calc={activeCalc}
                  onApply={(on) => { setBundleApplied(!!on); markDirty(); }}
                />
              )}

              {/* ─── ADDITIONAL DISCOUNT ─── rep-negotiated $ or % off the
                  whole proposal; printed to the client as its own line. */}
              {!isLotExplorer && activePhase1Ready
                && ((Number(activeCalc.vizNet) || 0) + (Number(activeCalc.revisionsCost) || 0)) > 0 && (
                <ProposalDiscountPanel
                  discount={proposalDiscount}
                  amount={activeCalc.proposalDiscount || 0}
                  oneTimeTotal={activeCalc.oneTimeAllIn || 0}
                  guardrail={proposalDiscountGuardrail}
                  onChange={(next) => { setProposalDiscount(normalizeProposalDiscount(next)); markDirty(); }}
                />
              )}

              {/* ─── COMPLIMENTARY ADD-ONS ─── given at no charge, capped at a
                  % of the one-time contract value. Printed as "Included". */}
              {!isLotExplorer && activePhase1Ready
                && ((paymentSchedule.oneTimeScope || 0) > 0 || freeAddOns.length > 0)
                && (FREE_ADD_ONS.items.length > 0 || freeAddOns.length > 0) && (
                <FreeAddOnsPanel
                  options={FREE_ADD_ONS.items}
                  selected={freeAddOns}
                  status={freeAddOnStatus}
                  capPct={freeAddOnCapPct}
                  onChange={changeFreeAddOns}
                />
              )}

              {/* ─── VIDEO UPGRADE NUDGE — Triple/Full Stack but no video selected ─── */}
              {vexosEnabled && activePhase1Ready
                && activeCalc.bundle
                && activeCalc.bundle.tier >= 2
                && activeCalc.aiMotionDur == null
                && activeCalc.cinematicDur == null && (
                <p
                  className="text-[10.5px] text-white/45 mt-3 leading-snug"
                  data-testid="video-upgrade-nudge"
                >
                  + Add Video Animation to unlock the Motion Package upgrade.
                </p>
              )}

              {/* ─── MARKETING & COLLATERAL (Tab 2 contributions) ─── */}
              {!isLotExplorer && (marketingActive || collateralActive) && (
                <div className="mt-6 pt-5 border-t border-white/[0.08] space-y-4" data-testid="sidebar-marketing-collateral">
                  {marketingActive && (
                    <div>
                      <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-3">Marketing &amp; Lead Generation</p>
                      <SummaryRow label="Campaign Setup Fee"        value={marketingMath.setupFee} />
                      <SummaryRow label="Monthly Service Fee"       value={marketingMath.monthlyServiceFee} sublabel="/mo" />
                      <SummaryRow label="Monthly Ad Spend"          value={Math.round(marketingMath.monthlyAdSpend)} sublabel={`/mo · client-controlled`} />
                      <SummaryRow label="Recommended Monthly Budget" value={Math.round(marketingMath.monthlyBudget)} sublabel="/mo" />
                    </div>
                  )}
                  {collateralActive && (
                    <div>
                      <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-3">Collateral Suite</p>
                      <SummaryRow label="One-Time Collateral" value={Math.round(collateralMath.oneTimeTotal)} />
                      {collateralMath.retainerMonthly > 0 && (
                        <SummaryRow
                          label="AI Social Media Retainer"
                          value={Math.round(collateralMath.retainerMonthly)}
                          sublabel="/mo"
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ─── PAYMENT STRUCTURE ─── (hidden in Lot Explorer; that
                  tab uses its own 50/25/25 plan rendered inline above) */}
              {!isLotExplorer && (activePhase1Ready || (!vexosEnabled && (marketingActive || collateralActive))) && (
                <div className="mt-6 pt-5 border-t border-white/[0.08]" data-testid="sidebar-payment-structure">
                  <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80 mb-3">Payment Structure</p>
                  {/* Which side of the $100k size rule this quote landed on,
                      and what it did to the setup fee. Internal only. */}
                  {planNote(paymentSchedule) && (
                    <p className="text-[10.5px] text-[#8FA79B]/85 mb-3 leading-relaxed" data-testid="large-quote-plan-note">
                      {planNote(paymentSchedule)}
                    </p>
                  )}
                  {/* Payment plan chooser — set this before presenting.
                      The milestone option is 50/25/25 up to $100k and 25/50/25
                      over it, so the button relabels itself; the stored value
                      is the same either way. 100% is available on every quote,
                      not just à la carte: "they're paying it all at signing" is
                      a term a rep negotiates on any engagement, and it used to
                      be reachable only by the client electing pay-in-full
                      after the proposal had gone out. */}
                  {/* Only where there IS a plan to choose. A marketing &
                      collateral quote divides nothing through the milestones —
                      its collateral keeps a standalone 50 / 50 the surface owns
                      — so a 100% button there would be a control that silently
                      does nothing. */}
                  {((paymentSchedule.p1 || 0) + (paymentSchedule.p2 || 0) + (paymentSchedule.p3 || 0)) > 0 && (
                  <div className="mb-3" data-testid="payplan-chooser">
                    <p className="text-[9.5px] uppercase tracking-[0.14em] text-white/40 mb-1.5">Payment Plan</p>
                    <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
                      {/* The order reps sell in (Cole, 2026-11-02): pay in
                          full first, then 75 / 25, and three payments only
                          when the client pushes back hard. The 50 / 50 is
                          retired — shown only on an old à-la-carte quote that
                          already carries it, so its terms stay visible. */}
                      {[
                        [PLAN_FULL, "Pay in full"],
                        [PLAN_75_25, "75 / 25"],
                        [PAY_PLAN_AUTO, paymentSchedule.largeQuote ? "25 / 50 / 25" : "50 / 25 / 25"],
                        ...(isAlacarte && alacartePayPlan === PLAN_TWO_PART ? [[PLAN_TWO_PART, "50 / 50"]] : []),
                      ].map(([val, lbl]) => {
                        // 100% and 75 / 25 live on `payPlanChoice`; the
                        // à-la-carte 50/50 is stored on its own legacy key, so
                        // "which button is lit" reads both pieces of state.
                        const repChoice = normalizePayPlanChoice(val) !== PAY_PLAN_AUTO;
                        const active = repChoice
                          ? payPlanChoice === val
                          : normalizePayPlanChoice(payPlanChoice) === PAY_PLAN_AUTO
                            && (val === PLAN_TWO_PART
                              ? alacartePayPlan === PLAN_TWO_PART
                              : alacartePayPlan !== PLAN_TWO_PART);
                        return (
                          <button
                            key={val || "auto"}
                            type="button"
                            onClick={() => {
                              markDirty();
                              setPayPlanChoice(normalizePayPlanChoice(val));
                              if (!repChoice) setAlacartePayPlan(val === PLAN_TWO_PART ? PLAN_TWO_PART : ALACARTE_THREE_PART);
                            }}
                            className={`px-3 py-1.5 text-[11px] font-medium transition-colors ${active ? "bg-[#8FA79B] text-[#000000]" : "bg-transparent text-white/60 hover:text-white/90"}`}
                            data-testid={`payplan-${val || "auto"}`}
                          >
                            {lbl}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  )}
                  {/* A La Carte uses the non-VEXOS branch — no platform
                      setup or monthly subscription rows, just viz +
                      collateral milestones. */}
                  {paymentSchedule.singlePayment ? (
                    <>
                    {/* One payment, so one row. The scope line names every
                        component the amount actually contains, and the saving —
                        when the rep left the discount on — is stated on the row
                        rather than in a separate box underneath, because there
                        is no second option to compare it against. */}
                    <PaymentMilestone
                      label="Payment 1 — Due at Signing · 100%"
                      sub={`The entire one-time scope${(paymentSchedule.travelCost || 0) > 0 ? ", travel included" : ""}${marketingActive ? ` + Marketing Setup (${fmt(marketingMath.setupFee)})` : ""}`}
                      details={paymentSchedule.singlePaymentSavings > 0
                        ? [`${fmt(paymentSchedule.singlePaymentGross)} less the ${paymentSchedule.pifPct}% pay-in-full discount — client saves ${fmt(paymentSchedule.singlePaymentSavings)}.`]
                        : ["Full price — the pay-in-full discount is switched off for this quote."]}
                      value={presentationSchedule.p1}
                    />
                    {/* The discount normally lives on the pay-in-full block,
                        which a single-payment schedule suppresses — but it is
                        what prices THIS row, so it has to come with it. */}
                    {discountSwitch}
                    </>
                  ) : vexosEnabled && !isAlacarte ? (
                    <>
                      <PaymentMilestone
                        label="Payment 1 — Due at Signing"
                        sub={`${paymentSchedule.setupInSplit ? `${paymentSchedule.pct.p1}% of ${paymentSchedule.scopeLabelShort}` : `VEXOS Setup + ${paymentSchedule.pct.p1}% of viz scope`}${(paymentSchedule.travelCost || 0) > 0 ? " + travel" : ""}${marketingActive ? ` + Marketing Setup (${fmt(marketingMath.setupFee)})` : ""}${presentationSchedule.collateralP1 > 0 ? " + 50% of collateral" : ""}`}
                        value={presentationSchedule.p1}
                      />
                      {paymentSchedule.twoPart ? (
                      /* 75 / 25: the second payment IS the final one — no V1
                         payment in between. */
                      <PaymentMilestone
                        label="Payment 2 — Due on Finalization"
                        sub={`Remaining ${paymentSchedule.pct.p2}% of ${paymentSchedule.scopeLabelShort}${presentationSchedule.collateralP2 > 0 ? " + 50% of collateral" : ""} — ${paymentSchedule.finalAssets}${finalTiming(paymentSchedule.finalPaymentPolicy)}`}
                        value={presentationSchedule.p2}
                      />
                      ) : (
                      <>
                      <PaymentMilestone
                        label={`Payment 2 — Due at ${paymentSchedule.v1.dueLabel}`}
                        sub={`${paymentSchedule.pct.p2}% of ${paymentSchedule.scopeLabelShort}${presentationSchedule.collateralP2 > 0 ? " + 50% of collateral" : ""}`}
                        details={paymentSchedule.v1.bullets}
                        value={presentationSchedule.p2}
                      />
                      <PaymentMilestone
                        label="Payment 3 — Due on Finalization"
                        sub={`Remaining ${paymentSchedule.pct.p3}% of ${paymentSchedule.scopeLabelShort} — ${paymentSchedule.finalAssets}${finalTiming(paymentSchedule.finalPaymentPolicy)}`}
                        value={paymentSchedule.p3}
                      />
                      </>
                      )}
                    </>
                  ) : (
                    <>
                      {/* A La Carte / Marketing-only — viz milestones
                          prepend so the rep can still see the
                          visualization payment schedule when the
                          tab is configured for viz scope. */}
                      {isAlacarte && ((Number(activeCalc.vizNet) || 0) + (Number(activeCalc.revisionsCost) || 0) + (Number(activeCalc.travelCost) || 0) + (Number(activeCalc.conceptualDesignFee) || 0) + (Number(activeCalc.modelingFee) || 0)) > 0 && (
                        <>
                          {paymentSchedule.twoPart ? (
                            <>
                              <PaymentMilestone
                                label="Payment 1 — Due at Signing"
                                sub={`${paymentSchedule.pct.p1}% of ${acScopeCopy} — project initiation${(activeCalc.travelCost || 0) > 0 ? " + travel" : ""}`}
                                value={paymentSchedule.p1}
                              />
                              <PaymentMilestone
                                label="Payment 2 — Due on Finalization"
                                sub={`${paymentSchedule.pct.p2}% of ${acScopeCopy} — ${paymentSchedule.finalAssets}${finalTiming(paymentSchedule.finalPaymentPolicy)}`}
                                value={paymentSchedule.p2}
                              />
                            </>
                          ) : (
                            <>
                              <PaymentMilestone
                                label="Payment 1 — Due at Signing"
                                sub={`${paymentSchedule.pct.p1}% of ${acScopeCopy} — project initiation${(activeCalc.travelCost || 0) > 0 ? " + travel" : ""}`}
                                value={paymentSchedule.p1}
                              />
                              <PaymentMilestone
                                label={`Payment 2 — Due at ${paymentSchedule.v1.dueLabel}`}
                                sub={`${paymentSchedule.pct.p2}% of ${acScopeCopy}`}
                                details={[paymentSchedule.v1.note]}
                                value={paymentSchedule.p2}
                              />
                              <PaymentMilestone
                                label="Payment 3 — Due on Finalization"
                                sub={`Remaining ${paymentSchedule.pct.p3}% of ${acScopeCopy} — ${paymentSchedule.finalAssets}${finalTiming(paymentSchedule.finalPaymentPolicy)}`}
                                value={paymentSchedule.p3}
                              />
                            </>
                          )}
                        </>
                      )}
                      {/* Only the money the viz milestones above do NOT already
                          carry: the campaign setup fee (it joins no split) and,
                          on a marketing & collateral quote with no viz scope for
                          it to ride, the collateral 50 / 50. */}
                      {(marketingActive || presentationSchedule.collateralP1 > 0) && (
                        <PaymentMilestone
                          label="Payment 1 — Due at Signing"
                          sub={`${marketingActive ? `Marketing Setup (${fmt(marketingMath.setupFee)})` : ""}${marketingActive && presentationSchedule.collateralP1 > 0 ? " + " : ""}${presentationSchedule.collateralP1 > 0 ? "50% of collateral" : ""}`}
                          value={
                            (marketingActive ? marketingMath.setupFee : 0)
                            + presentationSchedule.collateralP1
                          }
                        />
                      )}
                      {presentationSchedule.collateralP2 > 0 && (
                        <PaymentMilestone
                          label="Payment 2 — Due at Delivery"
                          sub="50% of collateral"
                          value={presentationSchedule.collateralP2}
                        />
                      )}
                    </>
                  )}
                  {/* Pay in full — the alternative the proposal offers the
                      client, shown here so the rep sees the same number before
                      presenting. Sits after the one-time milestones and before
                      the recurring block, where the proposal puts it. */}
                  {pif.eligible && (
                    <div className="mt-3 rounded-lg px-3 py-2.5"
                         style={{ background: "rgba(143,167,155,0.07)", border: "1px solid rgba(143,167,155,0.25)" }}
                         data-testid="sidebar-pif-option">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-[11.5px] text-white/80">Pay in Full — Due at Signing</p>
                          <p className="text-[10.5px] text-white/45 mt-0.5 leading-relaxed">
                            Replaces the milestones above · {pif.pct > 0 ? `${pif.pct}% off` : "no discount"}{pif.pct > 0 && (paymentSchedule.travelCost || 0) > 0 ? " (travel at cost)" : ""}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-[12.5px] tabular-nums text-white/80">{fmt(pif.total)}</p>
                          {pif.savings > 0 && (
                            <p className="text-[10.5px] tabular-nums text-[#8FA79B] mt-0.5">−{fmt(pif.savings)}</p>
                          )}
                        </div>
                      </div>
                      {discountSwitch}
                    </div>
                  )}
                  <div className="mt-4 pt-4 border-t border-white/[0.06]">
                    {vexosEnabled && !isAlacarte && (
                      <PaymentMilestone
                        label="Monthly Platform Installment"
                        sub={`Begins post-launch · ${paymentSchedule.term}-month agreement`}
                        value={paymentSchedule.monthly}
                        isMonthly
                      />
                    )}
                    {marketingActive && (
                      <PaymentMilestone
                        label="Marketing Service Fee"
                        sub={`Begins post-launch · ${marketing.sellOutMonths}-month campaign`}
                        value={marketingMath.monthlyServiceFee}
                        isMonthly
                      />
                    )}
                    {collateralActive && collateralMath.retainerMonthly > 0 && (
                      <PaymentMilestone
                        label="AI Social Media Retainer"
                        sub={`Begins post-launch · ${collateral.socialMediaRetainerPackage}/mo plan`}
                        value={collateralMath.retainerMonthly}
                        isMonthly
                      />
                    )}
                    {marketingActive && marketingMath.monthlyAdSpend > 0 && (
                      <p className="text-[10.5px] text-white/45 mt-3 italic" data-testid="ad-spend-disclaimer">
                        + ~{fmt(Math.round(marketingMath.monthlyAdSpend))}/mo client-controlled ad spend
                        deployed to Meta &amp; Google. Not retained by The Render Pros.
                      </p>
                    )}
                  </div>
                  {vexosEnabled && paymentSchedule.bundleSavings > 0 && (
                    <p className="text-[10.5px] text-[#8FA79B]/75 mt-3 italic">
                      Bundle savings of {fmt(paymentSchedule.bundleSavings)} applied to visualization scope.
                    </p>
                  )}
                  {(paymentSchedule.proposalDiscount || 0) > 0 && (
                    <p className="text-[10.5px] text-[#8FA79B]/75 mt-2 italic" data-testid="payment-proposal-discount-note">
                      Additional discount of {fmt(paymentSchedule.proposalDiscount)} applied across the visualization milestones.
                    </p>
                  )}
                </div>
              )}

              {/* ─── MANUAL PRICE OVERRIDE ─── (internal; never shown to the client) */}
              {!isLotExplorer && overridePanelRows.length > 0 && (
                <PriceOverridePanel
                  open={overridePanelOpen}
                  onToggle={() => setOverridePanelOpen((v) => !v)}
                  rows={overridePanelRows}
                  guardrails={guardrailByLine}
                  count={overridePanelCount}
                  manualPricing={manualPricingActive}
                  hasLineOverrides={Object.keys(cleanPriceOverrides(priceOverrides)).length > 0}
                  discountAmount={overrideDiscountAmount}
                  discountChoice={overrideDiscountChoice}
                  bundleApplied={!!activeCalcUnpriced.bundle?.tier}
                  onDiscountChoice={(choice) => { setOverrideDiscountChoice(choice); markDirty(); }}
                  onChange={setPriceOverride}
                  onClearAll={clearAllPriceOverrides}
                />
              )}

              {/* ─── TIMELINE OVERRIDE ─── (internal control; the client sees
                  the result on the timeline card, PDF and intake) */}
              {!isLotExplorer && activePhase1Ready && displayTimeline && (
                <TimelineOverridePanel
                  timeline={displayTimeline}
                  override={timelineOverride}
                  showVexos={!isAlacarte && vexosActive}
                  onChange={(next) => { setTimelineOverride(normalizeTimelineOverride(next)); markDirty(); }}
                />
              )}

              {/* TBD warning */}
              {activePhase1Ready && (
                isCondo
                  ? ((activeCalc.exteriorAddonsSubtotal || activeCalc.interiorRendersCost || activeCalc.interiorVRCost || activeCalc.amenitySubtotal) > 0 &&
                      [EXTERIOR_ADDONS.dayToNight.pricePerKeyframe, EXTERIOR_ADDONS.animatedKeyframes.pricePerKeyframe,
                       EXTERIOR_ADDONS.additionalKeyframes.pricePerKeyframe, EXTERIOR_ADDONS.masterOrbit.pricePerOrbit,
                       ...(INTERIOR_RENDERING.volumeTiers || []).map(t => t.ratePerUnit),
                       ...(INTERIOR_VR.volumeTiers || []).map(t => t.ratePerUnit),
                       ...(AMENITY_RENDERING.volumeTiers || []).map(t => t.ratePerUnit),
                       ...(AMENITY_VR.volumeTiers || []).map(t => t.ratePerUnit)].some(isTBD))
                  : ((activeCalc.exteriorAddonsSubtotal || activeCalc.homeRendersCost || activeCalc.homeVRCost || activeCalc.amenitySubtotal) > 0 &&
                      [COMMUNITY_EXTERIOR_ADDONS.dayToNight.pricePerKeyframe, COMMUNITY_EXTERIOR_ADDONS.animatedKeyframes.pricePerKeyframe,
                       COMMUNITY_EXTERIOR_ADDONS.additionalKeyframes.pricePerKeyframe, COMMUNITY_EXTERIOR_ADDONS.masterOrbit.pricePerOrbit,
                       ...(HOME_MODEL_RENDERING.volumeTiers || []).map(t => t.ratePerUnit),
                       ...(HOME_MODEL_VR.volumeTiers || []).map(t => t.ratePerUnit),
                       ...(COMMUNITY_AMENITY_RENDERING.volumeTiers || []).map(t => t.ratePerUnit),
                       ...(COMMUNITY_AMENITY_VR.volumeTiers || []).map(t => t.ratePerUnit)].some(isTBD))
              ) && (
                <div className="mt-5 p-3 rounded-lg bg-amber-400/[0.06] border border-amber-400/15 text-[10.5px] text-amber-200/80 flex items-start gap-2 leading-relaxed">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>Some line-item rates are placeholders. Open <strong>Pricing Admin</strong> to set real values before going live.</span>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

        /* ─── PRESENTATION MODE — Zoom-optimized layout ─────────────────
           Activated via the Present button or P key. Applies only when the
           walkthrough modal is open (the body class is toggled by an effect
           inside VexosClientCalculator). Zero impact when OFF. All gated
           against the walkthrough overlay so other surfaces are untouched. */
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] {
          cursor: default;
        }
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] [data-vexos-toolkit] {
          cursor: default;
        }

        /* ─── PRESENTATION MODE — Click-to-Expand hover affordance ───────
           When Presentation Mode is on, every section tagged with
           the vexos-clickable-section class gets a subtle gold outline on
           hover plus a tiny expand glyph top-right. Zero visual change
           when Presentation Mode is OFF. */
        body.vexos-presentation-mode .vexos-clickable-section {
          position: relative;
          cursor: pointer;
          transition: outline-color 0.18s ease, background-color 0.18s ease;
          outline: 1px solid transparent;
          outline-offset: 4px;
          border-radius: 6px;
        }
        body.vexos-presentation-mode .vexos-clickable-section:hover {
          outline: 1px solid rgba(143,167,155, 0.42);
          background-color: rgba(143,167,155, 0.025);
        }
        body.vexos-presentation-mode .vexos-clickable-section::after {
          content: "\\2922";
          position: absolute;
          top: 6px;
          right: 10px;
          font-size: 14px;
          line-height: 1;
          color: rgba(143,167,155, 0);
          transition: color 0.18s ease;
          pointer-events: none;
        }
        body.vexos-presentation-mode .vexos-clickable-section:hover::after {
          color: rgba(143,167,155, 0.62);
        }
        /* Bump key typography ~25–30%. Targets the screen-mode classes so
           the live walkthrough scales without any layout recalc cost. */
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] header h1 {
          font-size: 56px !important;
        }
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] header p {
          font-size: 17px !important;
        }
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] .text-\\[12px\\] {
          font-size: 14px !important;
        }
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] .text-\\[13px\\] {
          font-size: 16px !important;
        }
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] .text-\\[11px\\] {
          font-size: 13px !important;
        }
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] .text-\\[10\\.5px\\] {
          font-size: 13px !important;
        }
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] .text-\\[10px\\] {
          font-size: 12.5px !important;
        }
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] .text-\\[14px\\] {
          font-size: 17px !important;
        }
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] .text-\\[20px\\] {
          font-size: 26px !important;
        }
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] [data-testid="scope-grid"] {
          padding-left: 4px !important;
          padding-right: 4px !important;
        }
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] > div:nth-of-type(2) {
          padding-left: 3% !important;
          padding-right: 3% !important;
        }
        body.vexos-presentation-mode [data-testid="summary-expanded-overlay"] > div:nth-of-type(2) > div {
          max-width: 100% !important;
        }
        /* Hide the Save Quote row in Presentation Mode so the rep's screen
           stays focused on numbers only. Pricing Admin / Save flow still
           reachable by exiting Presentation Mode. */
        body.vexos-presentation-mode [data-testid="save-quote-row"] {
          display: none !important;
        }

        /* ─── CLICK-TO-SPOTLIGHT — gold pulsing ring on clicked element ──
           Uses outline + box-shadow so no layout shift. One ring at a time
           is enforced by the React handler (it removes the class on the
           previous target before applying to the new one). */
        @keyframes vexosSpotlightPulse {
          0%   { outline-offset: 0px; box-shadow: 0 0 0 0 rgba(143,167,155,0.55); }
          40%  { outline-offset: 4px; box-shadow: 0 0 0 6px rgba(143,167,155,0.18); }
          100% { outline-offset: 2px; box-shadow: 0 0 0 0 rgba(143,167,155,0); }
        }
        @keyframes vexosSpotlightFade {
          from { outline-color: rgba(143,167,155,0.95); }
          to   { outline-color: rgba(143,167,155,0); }
        }
        .vexos-spotlight-pulse {
          outline: 2px solid rgba(143,167,155,0.95);
          outline-offset: 2px;
          border-radius: 8px;
          animation:
            vexosSpotlightPulse 0.4s ease-in-out 1 forwards,
            vexosSpotlightFade 0.3s ease-in 2.5s 1 forwards;
        }

        /* ─── PRINT — VEXOS Quote Walkthrough (Cmd+P from the overlay) ───
           Surgical print rules scoped to the expanded walkthrough overlay.
           Every selector is gated by [data-testid="summary-expanded-overlay"]
           so screen styles are untouched and other surfaces don't leak in. */
        @media print {
          /* Force background colors / images to render in PDF */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Presentation Mode toolkit chrome never prints. */
          [data-vexos-toolkit],
          [data-testid="presentation-controlbar"],
          [data-testid="presentation-expand-modal"] {
            display: none !important;
          }

          @page { size: A4 portrait; margin: 14mm 16mm; }

          /* The overlay is portaled to document.body (createPortal) — so
             when it's open we can cleanly hide every other direct body child
             (top nav, sidebar, calculator chrome, toaster). :has() is
             supported in all modern browsers (Chrome 105+, Safari 15.4+,
             Firefox 121+). */
          html:has([data-testid="summary-expanded-overlay"]),
          body:has([data-testid="summary-expanded-overlay"]) {
            margin: 0 !important;
            padding: 0 !important;
            background: #000000 !important;
            height: auto !important;
            overflow: visible !important;
          }
          body:has([data-testid="summary-expanded-overlay"]) > *:not([data-testid="summary-expanded-overlay"]) {
            display: none !important;
          }

          /* Overlay container — switch from fixed to static so it paginates
             cleanly across PDF pages instead of fixed-redrawing on every
             page. Full bleed, dark bg, no backdrop-filter. Browsers re-lay
             out content against the @page CSS dimensions when printing, so
             we just give the overlay clean static positioning and let the
             contained Tailwind grid/flex utilities flow at print width. */
          [data-testid="summary-expanded-overlay"] {
            position: static !important;
            inset: auto !important;
            background: #000000 !important;
            color: #ffffff !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
            overflow: visible !important;
            padding: 0 !important;
            margin: 0 !important;
            left: 0 !important;
            transform: none !important;
            width: 100% !important;
            z-index: auto !important;
            /* Dynamic viewport-based zoom set by useEffect on beforeprint —
               scales the wide screen layout to fit A4 printable width. */
            zoom: var(--vexos-print-scale, 1);
          }
          [data-testid="summary-expanded-overlay"] > div {
            min-height: 0 !important;
            padding: 6mm 0 !important;
            display: block !important;
          }
          [data-testid="summary-expanded-overlay"] > div > div {
            max-width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          /* Hide screen-only chrome inside the overlay */
          [data-testid="summary-expanded-close"],
          [data-testid="save-quote-row"] {
            display: none !important;
          }

          /* Page break control — keep discrete blocks intact, never orphan
             section headings. */
          [data-testid="summary-expanded-overlay"] section,
          [data-testid="summary-expanded-overlay"] header,
          [data-testid="summary-expanded-overlay"] aside,
          [data-testid="summary-expanded-overlay"] [data-testid="scope-grid"],
          [data-testid="summary-expanded-overlay"] [class*="rounded-"],
          [data-testid="summary-expanded-overlay"] [class*="border"] {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          [data-testid="summary-expanded-overlay"] h1,
          [data-testid="summary-expanded-overlay"] h2,
          [data-testid="summary-expanded-overlay"] h3,
          [data-testid="summary-expanded-overlay"] header > p {
            break-after: avoid;
            page-break-after: avoid;
          }

          /* Preserve the 6-column scope grid in print — Tailwind's
             grid-cols-6 already does this, but force it against any
             responsive collapse, use minmax(0,1fr) so columns can shrink
             below their min-content (long labels truncate), and tighten
             typography for the print pt scale. */
          [data-testid="summary-expanded-overlay"] [data-testid="scope-grid"] > div {
            display: grid !important;
            grid-template-columns: repeat(6, minmax(0, 1fr)) !important;
            width: 100% !important;
            gap: 0 !important;
          }
          [data-testid="summary-expanded-overlay"] [data-testid="scope-grid"] > div > * {
            min-width: 0 !important;
          }
          [data-testid="summary-expanded-overlay"] [data-testid="scope-grid"] {
            font-size: 9pt !important;
          }

          /* Two-column payment schedule (Investment Snapshot left, Payment
             Schedule right). Lock layout against any print collapse. */
          [data-testid="summary-expanded-overlay"] .grid.grid-cols-1.lg\\:grid-cols-2,
          [data-testid="summary-expanded-overlay"] [class*="lg:grid-cols-2"] {
            display: grid !important;
            grid-template-columns: 52% 44% !important;
            gap: 4% !important;
            width: 100% !important;
          }

          /* One family in print too — Helvetica Neue, weight preserved. */
          [data-testid="summary-expanded-overlay"] * {
            font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
          }

          /* "Due Today" hero number stays the visual anchor on page 3 of the
             print run — Tailwind's text-[64px] etc. translates to ~48pt. */
          [data-testid="summary-expanded-overlay"] [class*="text-[64px]"],
          [data-testid="summary-expanded-overlay"] [class*="text-[56px]"],
          [data-testid="summary-expanded-overlay"] [class*="text-[44px]"] {
            font-size: 36pt !important;
            line-height: 1.05 !important;
          }
        }

        /* ─── LOT EXPLORER PRESENTATION MODE — Typography scaling ──────────
           Activated via the Present button inside the Lot Explorer overlay.
           Bumps text ~25% so the content reads cleanly on a shared screen. */
        body.lot-explorer-presentation-mode [data-testid="lot-explorer-overlay"] .text-\\[14px\\] {
          font-size: 18px !important;
        }
        body.lot-explorer-presentation-mode [data-testid="lot-explorer-overlay"] .text-\\[15px\\] {
          font-size: 19px !important;
        }
        body.lot-explorer-presentation-mode [data-testid="lot-explorer-overlay"] .text-\\[12px\\] {
          font-size: 15px !important;
        }
        body.lot-explorer-presentation-mode [data-testid="lot-explorer-overlay"] .text-\\[13px\\] {
          font-size: 16px !important;
        }
        body.lot-explorer-presentation-mode [data-testid="lot-explorer-overlay"] .text-\\[11px\\] {
          font-size: 14px !important;
        }
        body.lot-explorer-presentation-mode [data-testid="lot-explorer-overlay"] .text-\\[10px\\] {
          font-size: 13px !important;
        }

        /* ─── PRINT — LOT EXPLORER OVERLAY (Cmd+P / Export PDF button) ────
           Mirrors the pattern from the main walkthrough overlay. Hides all
           page chrome except the overlay itself, forces static layout, and
           preserves dark-theme background colors for a clean PDF output. */
        @media print {
          html:has([data-testid="lot-explorer-overlay"]),
          body:has([data-testid="lot-explorer-overlay"]) {
            margin: 0 !important;
            padding: 0 !important;
            background: #000000 !important;
            height: auto !important;
            overflow: visible !important;
          }
          body:has([data-testid="lot-explorer-overlay"]) > *:not([data-testid="lot-explorer-overlay"]) {
            display: none !important;
          }
          [data-lot-explorer-toolkit] {
            display: none !important;
          }
          [data-testid="lot-explorer-overlay"] {
            position: static !important;
            inset: auto !important;
            background: #000000 !important;
            color: #ffffff !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
            overflow: visible !important;
            padding: 0 !important;
            margin: 0 !important;
            width: 100% !important;
            z-index: auto !important;
          }
          [data-testid="lot-explorer-overlay"] > div {
            min-height: 0 !important;
            padding: 10mm 14mm !important;
            display: block !important;
            align-items: initial !important;
            justify-content: initial !important;
          }
          [data-testid="lot-explorer-overlay"] > div > div {
            max-width: 100% !important;
          }
          [data-testid="lot-explorer-overlay"] header,
          [data-testid="lot-explorer-overlay"] section,
          [data-testid="lot-explorer-overlay"] footer,
          [data-testid="lot-explorer-overlay"] [class*="rounded-"] {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          [data-testid="lot-explorer-overlay"] h1,
          [data-testid="lot-explorer-overlay"] h2,
          [data-testid="lot-explorer-overlay"] p[class*="uppercase"] {
            break-after: avoid;
            page-break-after: avoid;
          }
          [data-testid="lot-explorer-overlay"] * {
            font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
          }
          @page { size: A4 portrait; margin: 14mm 16mm; }
        }
      `}</style>

      {/* Price override → keep or remove the bundle discount (internal) */}
      {discountPrompt && createPortal(
        <OverrideDiscountPrompt
          amount={discountPrompt.amount}
          bundle={activeCalcUnpriced.bundle}
          motionPackagePct={activeCalcUnpriced.motionPackageActive ? activeCalcUnpriced.motionPackagePct : 0}
          onChoose={(choice) => {
            setOverrideDiscountChoice(choice);
            setDiscountPrompt(null);
            markDirty();
          }}
        />,
        document.body,
      )}

      {/* Expanded Project Summary — presentation mode (never shown on Lot Explorer tab) */}
      {summaryExpanded && !isLotExplorer && createPortal(
        <div
          className="fixed inset-0 z-[70] bg-[#000000] overflow-y-auto animate-fadeIn"
          style={{ fontFamily: 'var(--font-sans)' }}
          data-testid="summary-expanded-overlay"
          onClick={handlePresentationClick}
        >
          {/* Top-right control cluster — Currency + Present + Export PDF + Close. */}
          <div
            className="fixed top-6 right-6 z-[71] flex items-center gap-2 no-print"
            data-vexos-toolkit="topbar"
          >
            <CurrencyToggle
              displayCurrency={displayCurrency}
              onChange={setDisplayCurrencyState}
              fxLoading={fxLoading}
            />
            <button
              onClick={() => setPresentationMode((v) => !v)}
              title={presentationMode ? "Exit Presentation (press P)" : "Enter Presentation (press P)"}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] tracking-wide transition-all ${
                presentationMode
                  ? "bg-[#8FA79B] text-[#000000] border border-[#8FA79B] hover:bg-[#A6B8AD]"
                  : "bg-white/[0.03] text-[#8FA79B] border border-[#8FA79B]/45 hover:border-[#8FA79B] hover:bg-white/[0.06]"
              }`}
              data-testid="presentation-toggle-btn"
            >
              <Presentation className="w-3.5 h-3.5" />
              {presentationMode ? "Exit Presentation" : "Present"}
            </button>
            <button
              onClick={() => window.print()}
              title="Export as PDF — uses browser print dialog"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] tracking-wide bg-white/[0.03] text-white/65 border border-white/15 hover:border-white/35 hover:text-white transition-all"
              data-testid="walkthrough-export-pdf-btn"
            >
              <Download className="w-3.5 h-3.5" />
              Export PDF
            </button>
            <button
              onClick={() => setSummaryExpanded(false)}
              className="p-2.5 rounded-full bg-white/[0.04] border border-white/10 text-white/55 hover:text-white hover:border-white/30 transition-colors"
              data-testid="summary-expanded-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="min-h-screen w-full flex items-center justify-center px-8 lg:px-12 py-10">
            <div className="w-full max-w-[1400px]">
            {/* 1 · HEADER ─────────────────────────────────────────────── */}
            <header className="mb-10">
              <p className="text-[12px] uppercase tracking-[0.26em] text-[#8FA79B] mb-3">Your VEXOS Investment</p>
              <h1
                className="leading-[1.05] tracking-tight text-white mb-3"
                style={{ fontFamily: 'var(--font-sans)', fontWeight: 450, fontSize: "44px" }}
              >
                {clientName.trim() ? `Prepared for ${clientName.trim()}` : "Here\u2019s what your project includes."}
              </h1>
              <p className="text-[14px] text-white/50 tabular-nums">
                {clientEntity.trim() && (
                  <>
                    <span className="text-white/70">{clientEntity.trim()}</span>
                    <span className="mx-2 text-white/25">·</span>
                  </>
                )}
                {isCondo ? "Condo Building" : c_masterPlan ? "Master Plan" : "Community"}
                <span className="mx-2 text-white/25">·</span>
                {(isCondo ? Number(units) || 0 : Number(c_lots) || 0)} {isCondo || c_masterPlan ? "units" : "lots"}
                <span className="mx-2 text-white/25">·</span>
                {(isCondo || Number(c_homeModels) > 0) && (
                  <>
                    {(isCondo ? Number(uniqueFloorPlans) || 0 : Number(c_homeModels) || 0)} {isCondo ? "floor plans" : "home models"}
                    <span className="mx-2 text-white/25">·</span>
                  </>
                )}
                {activeContractTerm}-month agreement
              </p>
            </header>

            {/* 2 · HERO · DUE TODAY ───────────────────────────────────── */}
            <section
              className="text-center py-12 mb-10 vexos-clickable-section"
              style={{
                borderTop: "1px solid rgba(255,255,255,0.07)",
                borderBottom: "1px solid rgba(255,255,255,0.07)",
              }}
              data-testid="hero-due-today"
              data-vexos-section="hero"
            >
              <p className="text-[12px] uppercase tracking-[0.3em] text-white/45 mb-6">Due Today to Get Started</p>
              <p
                className="text-white tabular-nums leading-none"
                style={{ fontFamily: 'var(--font-sans)', fontWeight: 450, fontSize: "104px" }}
                data-testid="hero-due-today-amount"
              >
                {fmt(presentationSchedule.p1)}
              </p>
              <p className="text-[14px] text-white/45 mt-6">{dueTodayCopy(presentationSchedule)}</p>
              {displayCurrency === "CAD" && fxRate && (
                <p className="text-[11px] text-white/30 mt-2" data-testid="fx-disclosure">
                  Converted from USD at a live rate of 1 USD = {fxRate.toFixed(4)} CAD
                  {fxFetchedAt && ` as of ${new Date(fxFetchedAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`}
                </p>
              )}
            </section>

            {/* 3 · SCOPE GRID ─────────────────────────────────────────── */}
            <ScopeGrid
              isCondo={isCondo}
              isAlacarte={isAlacarte}
              activeCalc={paymentSchedule.monthlyOverridden
                ? { ...activeCalc, monthlyRate: paymentSchedule.monthly }
                : activeCalc}
              activeContractTerm={activeContractTerm}
              exteriorItems={(() => {
                const items = [];
                const dn = isCondo ? dayToNight : c_dayToNight;
                if (dn) items.push({ label: `Day-to-night · ${Number(activeCalc.dayToNightKfQty) || 0} keyframes`, value: activeCalc.dayToNightCost });
                const addKf = Number(activeCalc.additionalKfQty) || 0;
                if (addKf > 0) items.push({ label: `Additional keyframes · ${addKf}`, value: activeCalc.additionalKfCost });
                const animKf = isCondo ? animatedKeyframesEnabled : c_animatedKfEnabled;
                if (animKf) {
                  const q = Number(isCondo ? animatedKeyframesQty : c_animatedKfQty) || 0;
                  items.push({ label: `Animated keyframes · ${q}`, value: activeCalc.animatedKfCost });
                }
                const mo = Number(activeCalc.billableSubOrbits ?? activeCalc.masterOrbitsQty) || 0;
                if (mo > 0) items.push({ label: `${ADDITIONAL_ORBIT_LABEL} · ${mo} suborbit${mo === 1 ? "" : "s"}`, value: activeCalc.masterOrbitCost });
                const exR = Number(activeCalc.exteriorRendersQty) || 0;
                if (exR > 0) items.push({ label: `Exterior renders · ${exR}`, value: activeCalc.exteriorRendersCost });
                const conR = Number(activeCalc.conceptualRendersQty) || 0;
                if (conR > 0) items.push({ label: `Conceptual design · ${conR}`, value: activeCalc.conceptualRendersCost });
                if ((activeCalc.conceptualDesignFee || 0) > 0) items.push({ label: conceptualDesignFeeLabel(activeCalc.conceptualDesignFeeSpaces), value: activeCalc.conceptualDesignFee });
                if ((activeCalc.modelingFee || 0) > 0) items.push({ label: MODELING_FEE_LABEL, value: activeCalc.modelingFee });
                if (activeCalc.exteriorVRActive) items.push({ label: `Exterior VR tour · ${Number(activeCalc.exteriorVRPoints) || 0} points`, value: activeCalc.exteriorVRCost });
                return items;
              })()}
              interior={{
                rendersCost: isCondo ? activeCalc.interiorRendersCost : activeCalc.homeRendersCost,
                rendersActive: (isCondo ? activeCalc.totalInteriorRenders : activeCalc.totalHomeRenders) > 0,
                vrCost: isCondo ? activeCalc.interiorVRCost : activeCalc.homeVRCost,
                vrActive: (isCondo ? activeCalc.totalInteriorVR : activeCalc.totalHomeVR) > 0,
                vrUnitsCount: isCondo ? activeCalc.totalInteriorVR : activeCalc.totalHomeVR,
                vrUnitLabel: isCondo ? "plans" : "models",
                configuratorCost: activeCalc.residenceConfiguratorCost || 0,
              }}
              amenity={{
                rendersCost: activeCalc.amenityRendersCost,
                rendersActive: activeCalc.totalAmenityRenders > 0,
                vrCost: activeCalc.amenityVRCost,
                vrActive: activeCalc.totalAmenityVR > 0,
                vrUnitsCount: activeCalc.totalAmenityVR,
                configuratorCost: activeCalc.amenityConfiguratorCost || 0,
              }}
              video={{
                aiMotionActive: activeCalc.aiMotionDur != null,
                aiMotionDur: activeCalc.aiMotionDur,
                aiMotionVideos: normalizeVideos(activeCalc.aiMotionVideos, activeCalc.aiMotionDur),
                aiMotionCost: activeCalc.aiMotionCost,
                cinematicActive: activeCalc.cinematicDur != null,
                cinematicDur: activeCalc.cinematicDur,
                cinematicVideos: normalizeVideos(activeCalc.cinematicVideos, activeCalc.cinematicDur),
                cinematicCost: activeCalc.cinematicCost,
                motionPackageActive: !!activeCalc.motionPackageActive,
                motionPackagePct: activeCalc.motionPackagePct || 0,
                videoNet: activeCalc.videoAnimationCost || 0,
              }}
              floorPlans={{
                plans2DActive: (activeCalc.total2DPlans || 0) > 0,
                total2D: activeCalc.total2DPlans || 0,
                cost2D: activeCalc.floorPlans2DCost || 0,
                pct2D: activeCalc.floorPlans2DPct || 0,
                plans3DActive: (activeCalc.total3DPlans || 0) > 0,
                total3D: activeCalc.total3DPlans || 0,
                cost3D: activeCalc.floorPlans3DCost || 0,
                pct3D: activeCalc.floorPlans3DPct || 0,
                workflow3D: activeCalc.floorPlans3DWorkflow || "hybrid",
              }}
              marketing={{
                active: marketingActive,
                setupFee: marketingMath.setupFee,
                monthlyServiceFee: marketingMath.monthlyServiceFee,
                monthlyAdSpend: Math.round(marketingMath.monthlyAdSpend || 0),
                sellOutMonths: Number(marketing.sellOutMonths) || 0,
              }}
            />

            {/* 4 · BUNDLE STRIP / SIGNATURE PACKAGE (conditional) ────── */}
            {(() => {
              const isFullStack = activeCalc.bundle && activeCalc.bundle.tier === 3;
              const hasBoth2D3D = (activeCalc.total2DPlans || 0) > 0 && (activeCalc.total3DPlans || 0) > 0;
              const isSignature = isFullStack && activeCalc.motionPackageActive && hasBoth2D3D;
              if (isSignature) {
                const combinedSavings = (activeCalc.bundleSavings || 0) + (activeCalc.motionPackageSavings || 0);
                return (
                  <div className="flex justify-center mb-10" data-testid="signature-package">
                    <div
                      className="w-full max-w-[760px] text-center px-10 py-8 rounded-2xl"
                      style={{
                        background: "rgba(143,167,155,0.10)",
                        border: "1px solid rgba(143,167,155,0.32)",
                      }}
                    >
                      <p
                        className="uppercase mb-3"
                        style={{ fontSize: "12px", letterSpacing: "0.32em", color: "#8FA79B" }}
                      >
                        Signature Package
                      </p>
                      <p
                        className="mx-auto max-w-[520px] text-white/80 mb-5"
                        style={{ fontFamily: 'var(--font-sans)', fontWeight: 400, fontSize: "16px", lineHeight: 1.45 }}
                      >
                        The complete VEXOS experience — platform, visualization, motion, and floor plans.
                      </p>
                      <p className="text-[13px] text-white/55 mb-1">
                        Full Stack Bundle ({activeCalc.bundle.pct}%) + Motion Package ({activeCalc.motionPackagePct}%)
                      </p>
                      <p className="text-[14px]">
                        <span className="text-white/55">Total savings: </span>
                        <span
                          className="text-emerald-300/95 tabular-nums"
                          style={{ fontFamily: 'var(--font-sans)', fontWeight: 400, fontSize: "20px" }}
                        >
                          {fmt(combinedSavings)}
                        </span>
                      </p>
                    </div>
                  </div>
                );
              }
              if (activeCalc.bundle && activeCalc.bundle.tier > 0) {
                const videoActive = activeCalc.aiMotionDur != null || activeCalc.cinematicDur != null;
                return (
                  <div className="flex flex-col items-center gap-2 mb-10 vexos-clickable-section" data-testid="bundle-strip" data-vexos-section="bundle">
                    <div
                      className="inline-flex items-center gap-3 px-6 py-2.5 rounded-full text-[14px]"
                      style={{
                        background: "rgba(143,167,155,0.07)",
                        border: "1px solid rgba(143,167,155,0.18)",
                        color: "#8FA79B",
                      }}
                    >
                      <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 400 }}>
                        {activeCalc.bundle.label}
                      </span>
                      <span className="text-white/30">·</span>
                      <span>{activeCalc.bundle.pct}% off visualization</span>
                      <span className="text-white/30">·</span>
                      <span className="text-emerald-300/90 tabular-nums">saving {fmt(activeCalc.bundleSavings)}</span>
                    </div>
                    {isFullStack && videoActive && (
                      <p className="text-[12px] text-white/55" data-testid="bundle-video-upgrade-line">
                        <span className="text-[#8FA79B]">+ Video Upgrade included</span>
                        <span className="mx-2 text-white/25">—</span>
                        your project includes motion content at Full Stack rates.
                      </p>
                    )}
                  </div>
                );
              }
              return null;
            })()}

            {/* 4b · ADDITIONAL DISCOUNT (conditional) — the rep's negotiated
                reduction, stated once, right above the payments it lowers. */}
            {(activeCalc.proposalDiscount || 0) > 0 && (
              <div className="flex justify-center mb-10" data-testid="walkthrough-proposal-discount">
                <div
                  className="inline-flex items-center gap-3 px-6 py-2.5 rounded-full text-[14px]"
                  style={{ background: "rgba(143,167,155,0.07)", border: "1px solid rgba(143,167,155,0.18)", color: "#8FA79B" }}
                >
                  <span>{proposalDiscountLabel(activeCalc.proposalDiscountPct)}</span>
                  <span className="text-white/30">·</span>
                  <span className="text-emerald-300/90 tabular-nums">−{fmt(activeCalc.proposalDiscount)}</span>
                </div>
              </div>
            )}

            {/* 5 · PAYMENT SCHEDULE ───────────────────────────────────
                One tile per payment: three normally, two on the à-la-carte
                50/50, and ONE when the quote is written on single-payment
                terms. A lone tile in a three-column grid reads as two missing
                payments, so the grid follows the plan. */}
            {paymentSchedule.singlePayment ? (
              <div className="grid grid-cols-1 gap-4 mb-4 vexos-clickable-section" data-testid="payment-tiles" data-vexos-section="payments">
                <PaymentTile
                  label="Due at Signing · 100%"
                  amount={presentationSchedule.p1}
                  description={paymentSchedule.singlePaymentSavings > 0
                    ? `The entire one-time scope, less the ${paymentSchedule.pifPct}% pay-in-full discount — you save ${fmt(paymentSchedule.singlePaymentSavings)}`
                    : "The entire one-time scope in a single payment"}
                  weight="full"
                  testid="payment-tile-signing"
                />
              </div>
            ) : (
            <div className={`grid ${paymentSchedule.twoPart ? "grid-cols-2" : "grid-cols-3"} gap-4 mb-4 vexos-clickable-section`} data-testid="payment-tiles" data-vexos-section="payments">
              <PaymentTile
                label="Due at Signing"
                amount={presentationSchedule.p1}
                description={dueTodayCopy(presentationSchedule, { short: true })}
                weight="full"
                testid="payment-tile-signing"
              />
              <PaymentTile
                label={paymentSchedule.twoPart ? "Due on Finalization" : `Due at ${paymentSchedule.v1.dueLabel}`}
                amount={presentationSchedule.p2}
                description={`${paymentSchedule.twoPart ? `${paymentSchedule.pct.p2}% of scope · approved, prior to final delivery` : `${paymentSchedule.pct.p2}% of scope`}${presentationSchedule.collateralP2 > 0 ? " + 50% of collateral" : ""}`}
                weight="dim"
                testid="payment-tile-delivery"
              />
              {!paymentSchedule.twoPart && (
              <PaymentTile
                label="Due on Finalization"
                amount={paymentSchedule.p3}
                description={`${paymentSchedule.pct.p3}% of scope · approved, prior to final delivery`}
                weight="dim"
                testid="payment-tile-launch"
              />
              )}
            </div>
            )}

            {/* 5·PIF · PAY IN FULL ─────────────────────────────────────
                An ALTERNATIVE to the tiles above, not a fourth tile: one
                payment for the whole one-time scope, 5% off — or at full
                price, when the rep switched the discount off in the sidebar.
                Deliberately un-numbered so it can't be read as another amount
                owed. */}
            {pif.eligible && (
              <div
                className="mb-8 rounded-xl px-7 py-5 flex items-start justify-between gap-8"
                style={{ background: "rgba(143,167,155,0.06)", border: "1px solid rgba(143,167,155,0.28)" }}
                data-testid="pif-option"
              >
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#8FA79B]/80 mb-2">Alternative to the schedule above</p>
                  <p className="text-[16px] text-white/85">{pifText.label}</p>
                  <p className="text-[13px] text-white/45 leading-relaxed mt-1">{pifText.sub}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[26px] tabular-nums text-white/85" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300 }}>{fmt(pif.total)}</p>
                  {pif.savings > 0 && (
                    <p className="text-[13px] tabular-nums text-[#8FA79B] mt-1">You save {fmt(pif.savings)}</p>
                  )}
                </div>
              </div>
            )}

            {/* 5A · V1 CLAY CLARIFICATION ─────────────────────────────
                Client-facing definition of the Payment 2 trigger. Copy comes
                from the shared resolver so it always lists what THIS quote's
                clay package actually holds — orbit and keyframes, stills, 360
                tours — and what it does not: materials and color (next stage),
                the VEXOS platform build, animation. Never say "V1 Platform"
                here; that phrasing is what clients read as the whole build. */}
            {/* Only where a payment is actually gated on V1: a 75 / 25 or a
                single payment has no V1 payment, and "the final 0%" is noise. */}
            {!isAlacarte && !paymentSchedule.twoPart && !paymentSchedule.singlePayment && (
              <div
                className="mb-8 rounded-xl px-7 py-5"
                style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
                data-testid="v1-platform-note"
              >
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#8FA79B]/80 mb-3">
                  {paymentSchedule.v1.title}
                </p>
                <ul className="text-[13px] text-white/55 leading-relaxed list-disc pl-5 space-y-1.5">
                  {paymentSchedule.v1.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  <li>
                    {finalDueSentence({ pct: paymentSchedule.pct.p3, assets: paymentSchedule.finalAssets, policy: paymentSchedule.finalPaymentPolicy })}
                  </li>
                </ul>
              </div>
            )}

            {/* 5B · PRODUCTION TIMELINE ────────────────────────────────── */}
            <ProductionTimelineCards timeline={displayTimeline} isAlacarte={isAlacarte} />

            {/* 6 · RECURRING ROW ──────────────────────────────────────── */}
            {/* À La Carte is visualization-only — no platform subscription. */}
            {!isAlacarte && (
            <div className="flex justify-center vexos-clickable-section" data-testid="recurring-row" data-vexos-section="monthly">
              <button
                type="button"
                onClick={() => setActiveSection("monthly")}
                className="px-4 py-2 rounded-full hover:bg-white/[0.03] transition-colors"
                style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer" }}
                data-testid="recurring-row-open-monthly"
                aria-label="Open monthly installment breakdown"
              >
                <p className="text-[18px] text-white/55">
                  Then monthly, post-launch
                  <span className="mx-3 text-white/25">·</span>
                  <span className="text-[#8FA79B] tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300, fontSize: "24px" }}>
                    {fmt(paymentSchedule.monthly)}
                  </span>
                  <span className="text-white/55"> / mo</span>
                </p>
              </button>
            </div>
            )}

            {/* 6A · RECURRING MARKETING MANAGEMENT ─────────────────────
                Kept on its own line rather than folded into the platform
                subscription above: it runs on the campaign term, not the
                platform contract term, so a single blended number would be
                wrong for both. Clicking opens the Marketing section. */}
            {marketingActive && presentationSchedule.marketingMonthly > 0 && (
            <div className="flex justify-center vexos-clickable-section mt-2" data-testid="recurring-marketing-row" data-vexos-section="marketing">
              <button
                type="button"
                onClick={() => setActiveSection("marketing")}
                className="px-4 py-2 rounded-full hover:bg-white/[0.03] transition-colors"
                style={{ background: "transparent", border: "none", color: "inherit", cursor: "pointer" }}
                data-testid="recurring-row-open-marketing"
                aria-label="Open marketing breakdown"
              >
                <p className="text-[15px] text-white/45">
                  Marketing management
                  {presentationSchedule.marketingMonths > 0 && (
                    <span className="text-white/30"> · {presentationSchedule.marketingMonths}-month campaign</span>
                  )}
                  <span className="mx-3 text-white/25">·</span>
                  <span className="text-[#8FA79B] tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontWeight: 300, fontSize: "20px" }}>
                    {fmt(presentationSchedule.marketingMonthly)}
                  </span>
                  <span className="text-white/45"> / mo</span>
                </p>
              </button>
            </div>
            )}

            {/* 6B · DEAL CLOSURE CTAs · DocuSign (any size) + Stripe (sub-$50k) ── */}
            {savedQuoteId && docusignEligible && (
              <div
                className="mt-10 mx-auto max-w-[720px] rounded-2xl border border-[#8FA79B]/25 bg-[#8FA79B]/[0.04] px-6 py-5 no-print"
                data-testid="closure-cta-card"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80">
                      Close the deal now
                    </p>
                    <p className="text-[12px] text-white/55 mt-1">
                      {closureEligible
                        ? "Send the proposal + Master Services Agreement as a single DocuSign envelope (client signs once), or collect the initial deposit via Stripe — both run on real rails."
                        : "Send the proposal + Master Services Agreement as a single DocuSign envelope for e-signature. On $50k+ deals, collect the deposit via invoice rather than Stripe instant-checkout."}
                    </p>
                  </div>
                  <p className="text-[11px] text-white/40 tabular-nums">
                    {fmtUSD(closureOneTimeTotal)} one-time
                  </p>
                </div>

                  <CollectModeChoice
                    mode={collectMode}
                    onChange={setCollectMode}
                    deposit={closureDeposit}
                    pif={pif}
                    testId="closure-collect-mode"
                  />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* DocuSign */}
                  <button
                    type="button"
                    onClick={handleSendForEsign}
                    disabled={closureBusy.envelope || !clientName.trim()}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[12.5px] tracking-wide border border-[#8FA79B]/40 text-white/90 hover:bg-[#8FA79B]/[0.08] hover:border-[#8FA79B]/70 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="closure-send-envelope-btn"
                    title="Send the proposal + agreement to the client for e-signature via DocuSign"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    {closureBusy.envelope
                      ? "Sending\u2026"
                      : closureState.docusignEnvelopeId
                        ? "Resend Agreement Bundle"
                        : "Send Agreement Bundle (3 docs)"}
                  </button>

                  {/* Stripe — instant-checkout only for sub-$50k deals. */}
                  {closureEligible ? (
                  <button
                    type="button"
                    onClick={handleStripeCheckout}
                    disabled={closureBusy.stripe || !clientName.trim() || !(closureAmount > 0)}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[12.5px] tracking-wide bg-[#8FA79B] hover:bg-[#A6B8AD] text-[#000000] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="closure-stripe-checkout-btn"
                    title={payInFull
                      ? `Open a Stripe Checkout for the full one-time scope (${fmtUSD(closureAmount)})`
                      : `Open a Stripe Checkout for the Initial Deposit (${fmtUSD(closureAmount)})`}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {closureBusy.stripe
                      ? "Opening\u2026"
                      : closureState.stripePaymentStatus === "paid"
                        ? (payInFull ? "Paid in Full ✓" : "Deposit Paid ✓")
                        : payInFull
                          ? `Collect ${fmtUSD(closureAmount)} in Full`
                          : `Collect ${fmtUSD(closureAmount)} Deposit`}
                  </button>
                  ) : (
                    <div
                      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[12px] text-white/45 border border-white/10 bg-white/[0.02] text-center"
                      data-testid="closure-stripe-invoice-note"
                    >
                      Deposit over $50k — collect via invoice
                    </div>
                  )}
                </div>

                {/* Wire transfer alternative — works at any deal size, and is
                    the natural path on $50k+ deals where Stripe instant-checkout
                    is disabled. */}
                {!showWire ? (
                  <button
                    type="button"
                    onClick={() => setShowWire(true)}
                    className="mt-3 w-full text-center text-[12px] text-white/55 hover:text-white/85 transition-colors"
                    data-testid="closure-wire-toggle"
                  >
                    Want to pay with wire?
                  </button>
                ) : (
                  <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="closure-wire-panel">
                    <p className="text-[12px] text-white/60 leading-relaxed mb-3">
                      Send{" "}
                      <span className="text-white/90 font-medium tabular-nums">
                        {fmtUSD(closureAmount)}
                      </span>{" "}
                      to the account below. Email us your transfer reference and we'll
                      confirm receipt.
                    </p>
                    <div className="rounded-lg bg-black/20 border border-white/[0.06] divide-y divide-white/[0.06]">
                      {WIRE_ROWS.map(([label, value]) => (
                        <div key={label} className="flex items-center justify-between gap-3 px-3.5 py-2">
                          <span className="text-[11.5px] text-white/45">{label}</span>
                          <span className="text-[12px] text-white/90 font-mono text-right">{value}</span>
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 text-[11px] text-white/45 leading-relaxed">
                      Email{" "}
                      <a href={`mailto:${WIRE_INSTRUCTIONS.contact}`} className="text-[#8FA79B] hover:text-[#A6B8AD]">
                        {WIRE_INSTRUCTIONS.contact}
                      </a>{" "}
                      with your transaction reference so we can match your payment.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowWire(false)}
                      className="mt-3 text-[11.5px] text-white/50 hover:text-white/80 transition-colors"
                    >
                      ← Back to card &amp; e-sign
                    </button>
                  </div>
                )}

                {/* Status pills */}
                {(closureState.docusignEnvelopeId || closureState.stripeSessionId) && (
                  <div className="mt-4 flex flex-wrap gap-3 text-[11px]" data-testid="closure-status-row">
                    {closureState.docusignEnvelopeId && (
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/70"
                        data-testid="closure-docusign-status"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#8FA79B]" />
                        Envelope · {closureState.docusignStatus || "sent"}
                        {closureState.docusignEnvelopeId
                          ? <span className="ml-1 text-white/40 tabular-nums">{String(closureState.docusignEnvelopeId).slice(0, 8)}</span>
                          : null}
                      </span>
                    )}
                    {closureState.stripePaymentLink && (
                      <a
                        href={closureState.stripePaymentLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/70 hover:text-white hover:border-white/30"
                        data-testid="closure-stripe-status"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${closureState.stripePaymentStatus === "paid" ? "bg-emerald-400" : "bg-[#8FA79B]"}`} />
                        Stripe · {closureState.stripePaymentStatus || "pending"} · open link
                      </a>
                    )}
                  </div>
                )}

                {closureError && (
                  <p className="mt-3 text-[11.5px] text-red-300" data-testid="closure-error">
                    {closureError}
                  </p>
                )}
              </div>
            )}

            {/* 7 · SAVE + GENERATE PROPOSAL ───────────────────────────── */}
            <div className="mt-10 flex flex-col items-center gap-3" data-testid="save-quote-row">
              {!clientName.trim() && (
                <p className="text-[12px] text-amber-200/70 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Enter the client&rsquo;s name in the sidebar to save this quote.
                </p>
              )}
              {!savedQuoteId && (
                <button
                  onClick={saveQuoteAndOpenProposal}
                  disabled={saving || !clientName.trim()}
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-[13px] tracking-wide transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: clientName.trim() ? "#8FA79B" : "rgba(143,167,155,0.25)",
                    color: clientName.trim() ? "#000000" : "rgba(255,255,255,0.5)",
                    fontWeight: 400,
                  }}
                  data-testid="save-quote-btn"
                >
                  <FileText className="w-4 h-4" />
                  {saving ? "Saving\u2026" : "Save Quote & Generate Proposal"}
                </button>
              )}
              {savedQuoteId && (
                <button
                  onClick={async () => {
                    // Always persist the latest in-memory state (manual
                    // monthly override, CRM opt-out toggle, scope edits)
                    // BEFORE rendering the PDF so the proposal reflects
                    // what the rep just changed in Presentation Mode.
                    // Without this, the PDF would read the previous
                    // saved snapshot from the DB and show stale numbers.
                    const saved = await persistQuote({ openProposalAfterSave: false });
                    const idForPdf = saved?.id || savedQuoteId;
                    openProposalPdf(idForPdf, proposalMode);
                  }}
                  disabled={saving || !clientName.trim()}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-[12px] tracking-wide border border-white/15 text-white/85 hover:border-white/35 hover:bg-white/[0.04] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="download-proposal-pdf-btn"
                  title={`Mode: ${proposalMode} · Saves current state before download`}
                >
                  <Download className="w-3.5 h-3.5" />
                  {proposalButtonLabel}
                </button>
              )}
              {savedQuoteId && (
                <button
                  onClick={async () => {
                    const saved = await persistQuote({ openProposalAfterSave: false });
                    openProposalPdf(saved?.id || savedQuoteId, proposalMode, "full");
                  }}
                  disabled={saving || !clientName.trim()}
                  className="text-[11px] tracking-wide text-white/45 hover:text-white/80 underline underline-offset-4 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="download-full-proposal-btn"
                  title="Every page — platform overview, case studies, and the full terms"
                >
                  Full version
                </button>
              )}
              {savedQuoteId && (
                <p className="text-[11px] text-white/40">
                  Saved. Quote ID: <span className="tabular-nums text-white/60">{savedQuoteId.slice(0, 8)}</span>
                </p>
              )}
            </div>
            </div>
          </div>

          {/* Click-to-Expand section modal — opens in BOTH the regular
              walkthrough ("Your VEXOS Investment") and Presentation Mode
              whenever a section is clicked. Shows a single section's
              content at large, Zoom-readable scale on a solid dark
              background so the client's eye anchors to it. */}
          {activeSection && (() => {
            const idx = sectionOrder.indexOf(activeSection);
            const prevId = idx > 0 ? sectionOrder[idx - 1] : null;
            const nextId = idx >= 0 && idx < sectionOrder.length - 1 ? sectionOrder[idx + 1] : null;
            return (
              <div
                className="no-print animate-fadeIn"
                data-vexos-toolkit="expand-modal"
                data-testid="presentation-expand-modal"
                onClick={(e) => {
                  // Close on click outside the content area.
                  if (e.target === e.currentTarget) setActiveSection(null);
                }}
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 9999,
                  background: "#000000",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: 'var(--font-sans)',
                  color: "#ffffff",
                }}
              >
                {/* Close */}
                <button
                  onClick={() => setActiveSection(null)}
                  data-vexos-toolkit="expand-close"
                  data-testid="expand-modal-close"
                  aria-label="Close"
                  style={{
                    position: "absolute", top: "28px", right: "32px",
                    background: "transparent", border: "none",
                    color: "#8FA79B", cursor: "pointer", padding: "8px",
                    fontSize: "20px", lineHeight: 1,
                  }}
                >
                  ✕
                </button>

                {/* Prev arrow */}
                {prevId && (
                  <button
                    onClick={() => setActiveSection(prevId)}
                    data-vexos-toolkit="expand-prev"
                    data-testid="expand-modal-prev"
                    aria-label={`Previous: ${SECTION_TITLES[prevId]}`}
                    style={{
                      position: "absolute", left: "32px", top: "50%", transform: "translateY(-50%)",
                      width: "44px", height: "44px", borderRadius: "999px",
                      background: "rgba(143,167,155,0.08)",
                      border: "1px solid rgba(143,167,155,0.35)",
                      color: "#8FA79B", fontSize: "22px", lineHeight: 1,
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    ‹
                  </button>
                )}

                {/* Next arrow */}
                {nextId && (
                  <button
                    onClick={() => setActiveSection(nextId)}
                    data-vexos-toolkit="expand-next"
                    data-testid="expand-modal-next"
                    aria-label={`Next: ${SECTION_TITLES[nextId]}`}
                    style={{
                      position: "absolute", right: "32px", top: "50%", transform: "translateY(-50%)",
                      width: "44px", height: "44px", borderRadius: "999px",
                      background: "rgba(143,167,155,0.08)",
                      border: "1px solid rgba(143,167,155,0.35)",
                      color: "#8FA79B", fontSize: "22px", lineHeight: 1,
                      cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    ›
                  </button>
                )}

                {/* Content */}
                <div
                  style={{ width: "100%", maxWidth: "800px", padding: "40px 48px" }}
                  data-testid={`expand-modal-content-${activeSection}`}
                >
                  {renderExpandSection(activeSection, {
                    c: paymentSchedule.monthlyOverridden
                      ? { ...activeCalc, monthlyRate: paymentSchedule.monthly, recurringContractValue: paymentSchedule.monthly * activeContractTerm }
                      : activeCalc,
                    // The modals are client-facing, so they read the same
                    // marketing-inclusive milestones the walkthrough shows.
                    paymentSchedule: presentationSchedule,
                    marketing: {
                      active: marketingActive,
                      setupFee: marketingMath.setupFee,
                      monthlyServiceFee: marketingMath.monthlyServiceFee,
                      monthlyAdSpend: marketingMath.monthlyAdSpend,
                      sellOutMonths: Number(marketing.sellOutMonths) || 0,
                    },
                    isCondo,
                    contractTerm: activeContractTerm,
                    crmIncluded,
                    onCrmToggle: (v) => { setCrmIncluded(v); markDirty(); },
                    monthlyRateOverride,
                    onOverrideChange: (v) => { setMonthlyRateOverride(v); markDirty(); },
                    postTerm: {
                      value: postTermMonthly,
                      defaultValue: defaultPostTermMonthly(mode),
                      overridden: postTermOverride !== null,
                      onOverrideChange: (v) => { setPostTermOverride(v); markDirty(); },
                    },
                  })}
                </div>
              </div>
            );
          })()}

          {/* Bottom-center control bar — visible only in Presentation Mode. */}
          {presentationMode && (
            <div
              className="no-print"
              data-vexos-toolkit="controlbar"
              data-testid="presentation-controlbar"
              style={{
                position: "fixed",
                left: "50%",
                bottom: "20px",
                transform: "translateX(-50%)",
                zIndex: 9997,
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "10px 22px",
                borderRadius: "999px",
                background: "rgba(15, 16, 19, 0.86)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                color: "rgba(255,255,255,0.78)",
                fontSize: "12px",
                fontFamily: 'var(--font-sans)',
                letterSpacing: "0.02em",
                boxShadow: "0 10px 32px rgba(0,0,0,0.45)",
              }}
            >
              <span style={{ color: "rgba(255,255,255,0.78)" }}>
                Click any section to expand
              </span>
              <span style={{ width: "1px", height: "16px", background: "rgba(255,255,255,0.1)" }} />
              <span style={{ color: "rgba(255,255,255,0.45)", letterSpacing: "0.08em", fontSize: "10.5px" }}>
                P = exit · ← / → = nav · Esc = close
              </span>
            </div>
          )}
        </div>,
        document.body
      )}

      {/* ── LOT EXPLORER PRESENTATION OVERLAY ─────────────────────────────
          Standalone portal — completely independent of the main walkthrough.
          Opens when the rep clicks "Present / PDF" on the Lot Explorer tab.
          Scoped @media print CSS (below) hides everything else when printing
          from this overlay, matching the main walkthrough print behaviour. */}
      {lotExplorerExpanded && createPortal(
        <div
          className="fixed inset-0 z-[70] bg-[#000000] overflow-y-auto"
          data-testid="lot-explorer-overlay"
          style={{ fontFamily: 'var(--font-sans)' }}
        >
          {/* ─── CONTROL CLUSTER ─── */}
          <div
            className="fixed top-6 right-6 z-[71] flex items-center gap-2 no-print"
            data-lot-explorer-toolkit="topbar"
          >
            <CurrencyToggle
              displayCurrency={displayCurrency}
              onChange={setDisplayCurrencyState}
              fxLoading={fxLoading}
            />
            <button
              onClick={() => setLotExplorerPresentMode((v) => !v)}
              title={lotExplorerPresentMode ? "Exit Presentation" : "Enter Presentation"}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] tracking-wide transition-all ${
                lotExplorerPresentMode
                  ? "bg-cyan-300 text-[#000000] border border-cyan-300 hover:bg-cyan-200"
                  : "bg-white/[0.03] text-cyan-300 border border-cyan-400/40 hover:border-cyan-300 hover:bg-white/[0.06]"
              }`}
              data-testid="lot-explorer-present-mode-btn"
            >
              <Presentation className="w-3.5 h-3.5" />
              {lotExplorerPresentMode ? "Exit Presentation" : "Present"}
            </button>
            <button
              onClick={async () => {
                let id = savedQuoteId;
                if (!id) id = await persistAndGetId();
                if (id) openProposalPdf(id, "lotexplorer");
              }}
              title="Export branded Lot Explorer proposal as PDF"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12px] tracking-wide bg-white/[0.03] text-white/65 border border-white/15 hover:border-white/35 hover:text-white transition-all"
              data-testid="lot-explorer-export-pdf-btn"
            >
              <Download className="w-3.5 h-3.5" />
              Export PDF
            </button>
            <button
              onClick={() => setLotExplorerExpanded(false)}
              className="p-2.5 rounded-full bg-white/[0.04] border border-white/10 text-white/55 hover:text-white hover:border-white/30 transition-colors"
              data-testid="lot-explorer-overlay-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* ─── CONTENT ─── */}
          <div className="min-h-screen w-full flex items-center justify-center px-8 lg:px-12 py-20">
            <div className="w-full max-w-[920px]">

              {/* 1 · HEADER ──────────────────────────────────────────── */}
              <header className="mb-12">
                <p className="text-[11px] uppercase tracking-[0.26em] text-cyan-300 mb-3">
                  The Render Pros &middot; VEXOS Lot Explorer&trade;
                </p>
                <h1
                  className="leading-[1.05] tracking-tight text-white mb-3"
                  style={{ fontFamily: 'var(--font-sans)', fontWeight: 400, fontSize: "44px" }}
                >
                  {clientName.trim()
                    ? `Prepared for ${clientName.trim()}`
                    : "Your Lot Explorer Investment"}
                </h1>
                <p className="text-[14px] text-white/50">
                  {clientEntity.trim() && (
                    <>
                      <span className="text-white/70">{clientEntity.trim()}</span>
                      <span className="mx-2 text-white/25">&middot;</span>
                    </>
                  )}
                  <span>{lotExplorerCalc.lotCount.toLocaleString()} lots</span>
                  <span className="mx-2 text-white/25">&middot;</span>
                  <span>Interactive Sitemap Platform</span>
                  {lotExplorerCalc.timelineWeeks > 0 && (
                    <>
                      <span className="mx-2 text-white/25">&middot;</span>
                      <span>~{lotExplorerCalc.timelineWeeks}-week delivery</span>
                    </>
                  )}
                </p>
              </header>

              {/* 2 · HERO — TOTAL INVESTMENT ────────────────────────── */}
              <section
                className="text-center py-12 mb-12"
                style={{ borderTop: "1px solid rgba(255,255,255,0.07)", borderBottom: "1px solid rgba(255,255,255,0.07)" }}
                data-testid="lot-explorer-hero"
              >
                <p className="text-[12px] uppercase tracking-[0.3em] text-white/45 mb-6">Total Investment</p>
                <p
                  className="text-white tabular-nums leading-none"
                  style={{ fontFamily: 'var(--font-sans)', fontWeight: 450, fontSize: "96px" }}
                  data-testid="lot-explorer-hero-total"
                >
                  {fmtUSD(lotExplorerCalc.grandTotal)}
                </p>
                <p className="text-[14px] text-white/45 mt-6">
                  {fmtUSD(lotExplorerCalc.baseFee)} base platform fee &middot; {lotExplorerCalc.lotCount.toLocaleString()} lots &times; {fmtUSD(lotExplorerCalc.ratePerLot)} per lot
                </p>
              </section>

              {/* 3 · PLATFORM FEATURES ──────────────────────────────── */}
              {lotExplorerCalc.features.length > 0 && (
                <section className="mb-12">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-cyan-300/80 mb-5">Platform Features</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { key: "poi", label: "POI Widget", sub: "Points of interest overlay — schools, parks, transit", active: lotExplorerIncludePoi },
                      { key: "crm", label: "CRM Integration", sub: "Inquiry capture synced to your CRM", active: lotExplorerIncludeCrm },
                      { key: "media", label: "Media Pop-up Support", sub: "Per-lot photos, video, and virtual tours", active: lotExplorerIncludeMedia },
                      { key: "backend", label: "Backend Management", sub: "Client-side lot status & availability editor", active: lotExplorerIncludeBackend },
                    ].filter(f => f.active).map(f => (
                      <div
                        key={f.key}
                        className="flex items-start gap-3 p-4 rounded-xl border border-cyan-400/15 bg-cyan-500/[0.04]"
                      >
                        <Check className="w-4 h-4 mt-0.5 shrink-0 text-cyan-300" />
                        <div>
                          <p className="text-[14px] text-white font-medium">{f.label}</p>
                          <p className="text-[12px] text-white/45 mt-0.5">{f.sub}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* 4 · PAYMENT PLAN ───────────────────────────────────── */}
              <section className="mb-12">
                <p className="text-[11px] uppercase tracking-[0.2em] text-cyan-300/80 mb-5">
                  Payment Plan &middot; 50 / 25 / 25
                </p>
                <div className="space-y-3">
                  {lotExplorerCalc.milestones.map((m, i) => (
                    <div
                      key={m.key}
                      className="flex items-start justify-between gap-6 p-5 rounded-xl border border-white/[0.07] bg-white/[0.02]"
                      data-testid={`lot-explorer-overlay-milestone-${m.key}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border"
                            style={{
                              color: "#8FA79B",
                              borderColor: "rgba(103,232,249,0.3)",
                              background: "rgba(103,232,249,0.06)",
                            }}
                          >
                            {i === 0 ? "01" : i === 1 ? "02" : "03"} &middot; {m.percent}%
                          </span>
                          <p className="text-[15px] text-white font-medium">{m.label}</p>
                        </div>
                        <p className="text-[12px] text-white/45 leading-relaxed mt-1">{m.note}</p>
                      </div>
                      <p
                        className="text-white tabular-nums shrink-0"
                        style={{ fontFamily: 'var(--font-sans)', fontSize: "28px", fontWeight: 400 }}
                      >
                        {fmtUSD(m.amount)}
                      </p>
                    </div>
                  ))}
                  {lotExplorerCalc.hostingMonthly > 0 && (
                    <div
                      className="flex items-start justify-between gap-6 p-5 rounded-xl border border-white/[0.07] bg-white/[0.02]"
                      data-testid="lot-explorer-overlay-hosting"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-[15px] text-white font-medium">Recurring Hosting &amp; Support</p>
                        <p className="text-[12px] text-white/45 mt-1">Begins post-launch &middot; ongoing platform operations, updates, and support.</p>
                      </div>
                      <p
                        className="text-white tabular-nums shrink-0"
                        style={{ fontFamily: 'var(--font-sans)', fontSize: "28px", fontWeight: 400 }}
                      >
                        {fmtUSD(lotExplorerCalc.hostingMonthly)}
                        <span className="text-white/35 text-[16px] ml-1">/mo</span>
                      </p>
                    </div>
                  )}
                </div>
              </section>

              {/* 5 · TIMELINE & TOTALS SUMMARY ─────────────────────── */}
              <section
                className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 pt-8"
                style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}
              >
                <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-5">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-cyan-300/80 mb-3">Estimated Timeline</p>
                  <p
                    className="text-white tabular-nums"
                    style={{ fontFamily: 'var(--font-sans)', fontSize: "40px", fontWeight: 450 }}
                  >
                    ~{lotExplorerCalc.timelineWeeks}
                    <span className="text-white/40 text-[20px] ml-2">weeks</span>
                  </p>
                  <p className="text-[12px] text-white/40 mt-2">from project kick-off to launch</p>
                </div>
                <div className="rounded-xl border border-cyan-400/20 bg-cyan-500/[0.05] p-5">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-cyan-300/80 mb-3">Investment Summary</p>
                  <div className="space-y-2 text-[13px]">
                    <div className="flex justify-between">
                      <span className="text-white/55">Base platform fee</span>
                      <span className="text-white tabular-nums">{fmtUSD(lotExplorerCalc.baseFee)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/55">{lotExplorerCalc.lotCount.toLocaleString()} lots × {fmtUSD(lotExplorerCalc.ratePerLot)}</span>
                      <span className="text-white tabular-nums">{fmtUSD(lotExplorerCalc.lotSubtotal)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-white/[0.06]">
                      <span className="text-white font-medium">Total</span>
                      <span
                        className="text-cyan-200 tabular-nums font-medium"
                        style={{ fontFamily: 'var(--font-sans)', fontSize: "18px" }}
                      >
                        {fmtUSD(lotExplorerCalc.grandTotal)}
                      </span>
                    </div>
                    {lotExplorerCalc.hostingMonthly > 0 && (
                      <div className="flex justify-between pt-1">
                        <span className="text-white/55">Hosting / Support</span>
                        <span className="text-white/80 tabular-nums">{fmtUSD(lotExplorerCalc.hostingMonthly)}/mo</span>
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* 6 · FOOTER ─────────────────────────────────────────── */}
              <footer className="pt-6 border-t border-white/[0.06] flex items-center justify-between text-[11px] text-white/30">
                <span>The Render Pros &middot; VEXOS Lot Explorer&trade;</span>
                <span>Prepared {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
              </footer>

            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Pricing Admin overlay */}
      {adminOpen && (
        <VexosPricingAdmin
          onClose={() => setAdminOpen(false)}
          onSaved={({ meta }) => {
            setPricingVersion((v) => v + 1);
            if (meta) setPricingMeta(meta);
          }}
          initialMeta={pricingMeta}
          initialAudienceCount={pricingAudience}
        />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 *  ScopeGrid — scope band for the Quote Walkthrough modal. Column count is
 *  scope-driven: Platform (VEXOS only) + the four viz families + Floor Plans,
 *  plus Marketing when the quote carries a lead-gen campaign.
 *  Compact, 12px row text, hairline dividers between columns, no card.
 * ─────────────────────────────────────────────────────────────────────── */
function ScopeGrid({ isCondo, isAlacarte, activeCalc, activeContractTerm, exteriorItems, interior, amenity, video, floorPlans, marketing }) {
  const monthlyTotal = (Number(activeCalc.monthlyRate) || 0) * (Number(activeContractTerm) || 0);

  // Column totals — render as a hairline "Total" row beneath each
  // column so the client sees the per-section subtotal at a glance.
  // Numbers match the live calculator math, so toggling scope updates
  // each column total in real time.
  const platformTotal = (Number(activeCalc.setupFee) || 0) + monthlyTotal;
  const exteriorTotal = (exteriorItems || []).reduce(
    (sum, it) => sum + (Number(it?.value) || 0),
    0,
  );
  const interiorTotal =
    (interior.rendersActive ? Number(interior.rendersCost) || 0 : 0) +
    (interior.vrActive ? Number(interior.vrCost) || 0 : 0) +
    (Number(interior.configuratorCost) || 0);
  const amenityTotal =
    (amenity.rendersActive ? Number(amenity.rendersCost) || 0 : 0) +
    (amenity.vrActive ? Number(amenity.vrCost) || 0 : 0) +
    (Number(amenity.configuratorCost) || 0);
  const videoTotal =
    (video.aiMotionActive ? Number(video.aiMotionCost) || 0 : 0) +
    (video.cinematicActive ? Number(video.cinematicCost) || 0 : 0);
  const floorPlansTotal =
    (floorPlans.plans2DActive ? Number(floorPlans.cost2D) || 0 : 0) +
    (floorPlans.plans3DActive ? Number(floorPlans.cost3D) || 0 : 0);

  // MARKETING — its own column, only when the rep built a campaign on Tab 2.
  // The total mirrors the Platform column's shape (one-time fee + the
  // recurring fee across its term). Ad spend is deliberately excluded: it is
  // client-controlled money paid straight to Meta/Google, not a Render Pros
  // fee, so folding it into a column total would overstate the engagement.
  const marketingActive = !!marketing?.active;
  const marketingMonths = Number(marketing?.sellOutMonths) || 0;
  const marketingMgmtTotal = (Number(marketing?.monthlyServiceFee) || 0) * marketingMonths;
  const marketingTotal = (Number(marketing?.setupFee) || 0) + marketingMgmtTotal;

  // Column count has to be a literal class string for Tailwind to emit it.
  const columnCount = (isAlacarte ? 5 : 6) + (marketingActive ? 1 : 0);
  const gridColsClass =
    columnCount === 7 ? "grid-cols-7" : columnCount === 6 ? "grid-cols-6" : "grid-cols-5";

  return (
    <section className="mb-10" data-testid="scope-grid">
      <div className={`grid ${gridColsClass}`}>
        {/* PLATFORM — VEXOS only; omitted for À La Carte (viz-only). */}
        {!isAlacarte && (
        <ScopeColumn label="Platform" first total={platformTotal}>
          <ScopeRow label="Setup fee" valueAmount={activeCalc.setupFee} />
          <ScopeRow
            label={`${fmt(activeCalc.monthlyRate)}/mo · ${activeContractTerm} mo`}
            valueAmount={monthlyTotal}
          />
        </ScopeColumn>
        )}

        {/* EXTERIOR */}
        <ScopeColumn label="Exterior" first={isAlacarte} total={exteriorTotal}>
          <ScopeRow label={isCondo ? "Building orbit" : activeCalc.masterPlan ? "Master orbit" : "Community orbit"} valueKind="included" />
          {Number(activeCalc.masterPlanIncludedSubOrbits) > 0 && (
            <ScopeRow label={`${activeCalc.masterPlanIncludedSubOrbits} suborbits · ${MASTER_PLAN_SUBORBIT_KEYFRAMES} kf each`} valueKind="included" />
          )}
          {exteriorItems.map((it, i) => (
            <ScopeRow key={i} label={it.label} valueAmount={it.value} />
          ))}
        </ScopeColumn>

        {/* INTERIOR */}
        <ScopeColumn label="Interior" total={interiorTotal}>
          <ScopeRow
            label="Renderings"
            valueAmount={interior.rendersActive ? interior.rendersCost : null}
          />
          <ScopeRow
            label={interior.vrActive ? `VR tours · ${interior.vrUnitsCount} ${interior.vrUnitLabel}` : "VR tours"}
            valueAmount={interior.vrActive ? interior.vrCost : null}
          />
          {interior.configuratorCost > 0 && (
            <ScopeRow label="Configurator" valueAmount={interior.configuratorCost} />
          )}
        </ScopeColumn>

        {/* AMENITIES */}
        <ScopeColumn label="Amenities" total={amenityTotal}>
          <ScopeRow
            label="Renderings"
            valueAmount={amenity.rendersActive ? amenity.rendersCost : null}
          />
          <ScopeRow
            label={amenity.vrActive ? `VR tours · ${amenity.vrUnitsCount} space${amenity.vrUnitsCount === 1 ? "" : "s"}` : "VR tours"}
            valueAmount={amenity.vrActive ? amenity.vrCost : null}
          />
          {amenity.configuratorCost > 0 && (
            <ScopeRow label="Configurator" valueAmount={amenity.configuratorCost} />
          )}
        </ScopeColumn>

        {/* VIDEO */}
        <ScopeColumn label="Video" total={videoTotal}>
          <ScopeRow
            label={video.aiMotionActive ? `AI Motion · ${shortVideos(video.aiMotionVideos)}` : "AI Motion"}
            valueAmount={video.aiMotionActive ? video.aiMotionCost : null}
          />
          <ScopeRow
            label={video.cinematicActive ? `Cinematic · ${shortVideos(video.cinematicVideos)}` : "Cinematic"}
            valueAmount={video.cinematicActive ? video.cinematicCost : null}
            subBadge={video.motionPackageActive ? `Motion Package · −${video.motionPackagePct}%` : null}
          />
        </ScopeColumn>

        {/* FLOOR PLANS */}
        <ScopeColumn label="Floor Plans" total={floorPlansTotal}>
          <ScopeRow
            label={floorPlans.plans2DActive ? `2D plans · ${floorPlans.total2D}` : "2D plans"}
            valueAmount={floorPlans.plans2DActive ? floorPlans.cost2D : null}
            subBadge={floorPlans.plans2DActive && floorPlans.pct2D > 0 ? `Volume discount · −${floorPlans.pct2D}%` : null}
          />
          <ScopeRow
            label={
              floorPlans.plans3DActive
                ? `3D plans · ${floorPlans.total3D} · ${floorPlans.workflow3D === "traditional" ? "Traditional 3D" : "AI Hybrid"}`
                : "3D plans"
            }
            valueAmount={floorPlans.plans3DActive ? floorPlans.cost3D : null}
            subBadge={floorPlans.plans3DActive && floorPlans.pct3D > 0 ? `Volume discount · −${floorPlans.pct3D}%` : null}
          />
        </ScopeColumn>

        {/* MARKETING — Tab 2 lead-gen campaign. Sits alongside Floor Plans so
            the client sees the campaign as part of the same engagement. */}
        {marketingActive && (
        <ScopeColumn label="Marketing" total={marketingTotal}>
          <ScopeRow
            label="Campaign setup"
            valueAmount={marketing.setupFee}
            subBadge="One-time · due at signing"
          />
          <ScopeRow
            label={`${fmt(marketing.monthlyServiceFee)}/mo · ${marketingMonths} mo`}
            valueAmount={marketingMgmtTotal}
            subBadge={
              (Number(marketing.monthlyAdSpend) || 0) > 0
                ? `+ ~${fmt(marketing.monthlyAdSpend)}/mo ad spend · client-controlled`
                : null
            }
          />
        </ScopeColumn>
        )}
      </div>
    </section>
  );
}

function ScopeColumn({ label, children, first = false, total = null }) {
  // Map column label to its expand-modal section id (Presentation Mode).
  const sectionId = (label || "").toLowerCase().replace(/\s+/g, "");
  const mapped = sectionId === "floorplans" ? "floorplans" : sectionId;
  const totalTestId = `scope-total-${mapped}`;
  return (
    <div
      className="flex flex-col vexos-clickable-section"
      data-vexos-section={mapped}
      style={{
        padding: "0 14px",
        borderLeft: first ? "none" : "1px solid rgba(255,255,255,0.07)",
      }}
    >
      <p
        className="uppercase text-[#8FA79B] mb-5"
        style={{ fontSize: "11px", letterSpacing: "0.22em" }}
      >
        {label}
      </p>
      <div className="flex flex-col flex-1">{children}</div>

      {/* Per-column total — hairline rule above, tabular-nums right-align */}
      {total != null && (
        <div
          className="mt-3 pt-2.5 flex items-baseline justify-between gap-2"
          style={{ borderTop: "1px solid rgba(255,255,255,0.10)" }}
          data-testid={totalTestId}
        >
          <span
            className="uppercase text-[#8FA79B]/85 tabular-nums"
            style={{ fontSize: "9.5px", letterSpacing: "0.22em" }}
          >
            Total
          </span>
          <span
            className="text-white tabular-nums"
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 400,
              fontSize: "14.5px",
            }}
          >
            {fmt(total)}
          </span>
        </div>
      )}
    </div>
  );
}

function ScopeRow({ label, valueAmount = null, valueKind = null, subBadge = null }) {
  const renderValue = () => {
    if (valueKind === "included") {
      return (
        <span
          className="uppercase text-white/35 tabular-nums"
          style={{ fontSize: "10.5px", letterSpacing: "0.18em" }}
          data-testid="scope-value-included"
        >
          Included
        </span>
      );
    }
    if (valueAmount == null) {
      return (
        <span className="text-white/30 tabular-nums" style={{ fontSize: "15px" }} data-testid="scope-value-empty">
          —
        </span>
      );
    }
    return (
      <span
        className="text-white tabular-nums"
        style={{ fontSize: "13px", fontWeight: 400 }}
        data-testid="scope-value-amount"
      >
        {fmt(valueAmount)}
      </span>
    );
  };

  return (
    <div
      className="flex flex-col gap-0.5"
      style={{ paddingTop: "6px", paddingBottom: "6px" }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-white/60 leading-snug" style={{ fontSize: "11px" }}>
          {label}
        </span>
        {renderValue()}
      </div>
      {subBadge && (
        <p className="text-[10px] text-[#8FA79B]/80 leading-snug">{subBadge}</p>
      )}
    </div>
  );
}

/* PaymentTile — used in the Quote Walkthrough modal payment schedule row */
function PaymentTile({ label, amount, description, weight = "full", testid }) {
  const isFull = weight === "full";
  return (
    <div
      className="rounded-xl px-7 py-6"
      style={{
        background: isFull ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.02)",
      }}
      data-testid={testid}
    >
      <p
        className="uppercase mb-3"
        style={{
          fontSize: "12px",
          letterSpacing: "0.24em",
          color: isFull ? "#8FA79B" : "rgba(255,255,255,0.35)",
        }}
      >
        {label}
      </p>
      <p
        className="tabular-nums leading-none"
        style={{
          fontFamily: 'var(--font-sans)',
          fontWeight: 400,
          fontSize: "34px",
          color: isFull ? "#ffffff" : "rgba(255,255,255,0.4)",
        }}
      >
        {fmt(amount)}
      </p>
      <p
        className="mt-3"
        style={{
          fontSize: "13px",
          color: isFull ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.3)",
        }}
      >
        {description}
      </p>
    </div>
  );
}

function SummaryRow({ label, value, sublabel, isMonthly = false }) {
  return (
    <div className="flex items-start justify-between py-2.5 border-b border-white/[0.04] last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] text-white/75">{label}</p>
        {sublabel && <p className="text-[10.5px] text-white/35 mt-0.5">{sublabel}</p>}
      </div>
      <span className="text-[13px] text-white tabular-nums shrink-0">
        {isTBD(value)
          ? <TbdBadge />
          : (<>{fmt(value)}{isMonthly && <span className="text-[10px] text-white/40 ml-1">/ mo</span>}</>)}
      </span>
    </div>
  );
}

/* VR selections are dicts keyed by row index ({0: 8, 1: null, 2: 6}).
   Tours = rows with points > 0, in row order, numbered as the rep saw them. */
function vrTourList(dict) {
  return Object.keys(dict || {})
    .map(Number)
    .sort((a, b) => a - b)
    .map(i => ({ n: i + 1, points: Number(dict[i]) || 0 }))
    .filter(t => t.points > 0);
}

function vrPointsBound(dict, pick) {
  const nums = Object.values(dict || {}).map(Number).filter(n => n > 0);
  return nums.length ? pick(...nums) : 0;
}

/* Sidebar "One-Time Setup Fee" row — click the amount to hand-set it, the
   same way as the monthly below it. Writes the existing `setupFee` price
   override, so the Price Override panel, payment milestones, saved quote,
   web proposal and PDF all read the same number. The setup fee sits outside
   the visualization scope, so overriding it never asks keep-or-remove and
   never touches the bundle (see overridesPutDiscountsAtStake). */
function SetupFeeRow({ value, auto, overridden, onOverrideChange }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const startEditing = () => { setDraft(String(Math.round(Number(value) || 0))); setEditing(true); };
  const commit = (raw) => {
    setEditing(false);
    const cleaned = String(raw == null ? "" : raw).replace(/[^0-9.]/g, "");
    const n = Number(cleaned);
    // Empty / unusable / back to the rate card → no override.
    if (cleaned === "" || !Number.isFinite(n) || n < 0 || Math.round(n) === auto) { onOverrideChange(null); return; }
    onOverrideChange(Math.round(n));
  };
  return (
    <div className="py-2.5 border-b border-white/[0.04] last:border-b-0" data-testid="sidebar-setup-row">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] text-white/75 flex items-center gap-1.5">
            One-Time Setup Fee
            {overridden && (
              <span className="px-1.5 py-[1px] rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-200 text-[9px] uppercase tracking-[0.12em]">
                Manual
              </span>
            )}
          </p>
          {overridden && <p className="text-[10.5px] text-white/35 mt-0.5">Rate card {fmtOverrideUSD(auto)}</p>}
        </div>
        {editing ? (
          <div className="flex items-center rounded-md border border-amber-400/45 bg-amber-400/[0.07] px-2 shrink-0">
            <span className="text-[11px] text-white/40 mr-0.5">$</span>
            <input
              type="text"
              inputMode="decimal"
              autoFocus
              value={draft}
              placeholder={String(auto)}
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => commit(draft)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit(draft);
                if (e.key === "Escape") setEditing(false);
              }}
              className="w-[70px] bg-transparent outline-none py-1 text-[13px] text-white tabular-nums placeholder:text-white/25"
              data-testid="sidebar-setup-input"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={startEditing}
            title="Click to set a custom setup fee"
            className={`shrink-0 text-[13px] tabular-nums rounded-md px-1.5 py-1 -mr-1.5 border border-transparent transition-colors hover:border-white/20 hover:bg-white/[0.04] ${
              overridden ? "text-amber-200" : "text-white"
            }`}
            data-testid="sidebar-setup-value"
          >
            {fmt(value)}
          </button>
        )}
      </div>
    </div>
  );
}

/* "After month 12" maintenance fee — click the amount to hand-set it.
   Defaults to $750/mo ($250 for SFH); a rep can trim it to close a deal. The
   figure is saved on the quote (calc_snapshot.postTermMonthly) and is what the
   proposal, its terms, and therefore the signed agreement state. Two looks:
   the sidebar row, and the dark presentation-view row in the monthly panel. */
function PostTermFeeRow({ value, defaultValue, overridden, onOverrideChange, variant = "sidebar" }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const startEditing = () => { setDraft(String(Math.round(Number(value) || 0))); setEditing(true); };
  const commit = (raw) => {
    setEditing(false);
    const cleaned = String(raw == null ? "" : raw).replace(/[^0-9.]/g, "");
    const n = Number(cleaned);
    // Empty / unusable / back to the default → no override.
    if (cleaned === "" || !Number.isFinite(n) || n < 0 || Math.round(n) === defaultValue) { onOverrideChange(null); return; }
    onOverrideChange(Math.round(n));
  };
  const present = variant === "present";
  const input = (
    <input
      type="text"
      inputMode="decimal"
      autoFocus
      value={draft}
      placeholder={String(defaultValue)}
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => commit(draft)}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit(draft);
        if (e.key === "Escape") setEditing(false);
      }}
      className={present
        ? "w-[90px] bg-transparent outline-none text-[22px] text-white tabular-nums border-b-2 border-[#8FA79B]/60"
        : "w-[60px] bg-transparent outline-none py-1 text-[13px] text-white tabular-nums placeholder:text-white/25"}
      data-testid={`${variant}-postterm-input`}
    />
  );

  if (present) {
    return (
      <div className="flex items-center justify-between gap-4 py-4 border-t border-white/[0.08] mt-2" data-testid="present-postterm-row">
        <div>
          <div className="text-[16px] text-white/90">After month 12 &mdash; keep the platform live</div>
          <div className="text-[13px] text-white/45 mt-1">Month to month &middot; cancel anytime</div>
        </div>
        <div className="flex items-baseline gap-1 shrink-0">
          {editing ? (<><span className="text-[18px] text-white/50">$</span>{input}</>) : (
            <button
              type="button"
              onClick={startEditing}
              title="Click to set a custom after-month-12 fee"
              className={`text-[22px] tabular-nums bg-transparent border-none p-0 cursor-pointer ${overridden ? "text-amber-200" : "text-white"}`}
              data-testid="present-postterm-value"
            >
              {fmtUSD(value)}
            </button>
          )}
          <span className="text-[14px] text-white/50">/mo</span>
        </div>
      </div>
    );
  }

  return (
    <div className="py-2.5 border-b border-white/[0.04] last:border-b-0" data-testid="sidebar-postterm-row">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] text-white/75 flex items-center gap-1.5">
            After Month 12
            {overridden && (
              <span className="px-1.5 py-[1px] rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-200 text-[9px] uppercase tracking-[0.12em]">
                Manual
              </span>
            )}
          </p>
          <p className="text-[10.5px] text-white/35 mt-0.5">
            Maintenance &middot; month to month, cancel anytime
            {overridden && <> &middot; default {fmtUSD(defaultValue)}</>}
          </p>
        </div>
        {editing ? (
          <div className="flex items-center rounded-md border border-amber-400/45 bg-amber-400/[0.07] px-2 shrink-0">
            <span className="text-[11px] text-white/40 mr-0.5">$</span>
            {input}
            <span className="text-[10px] text-white/40 ml-0.5">/ mo</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={startEditing}
            title="Click to set a custom after-month-12 fee"
            className={`shrink-0 text-[13px] tabular-nums rounded-md px-1.5 py-1 -mr-1.5 border border-transparent transition-colors hover:border-white/20 hover:bg-white/[0.04] ${
              overridden ? "text-amber-200" : "text-white"
            }`}
            data-testid="sidebar-postterm-value"
          >
            {fmtUSD(value)}<span className="text-[10px] text-white/40 ml-1">/ mo</span>
          </button>
        )}
      </div>
    </div>
  );
}

/* Sidebar "Monthly Subscription" row — click the amount to hand-set the
   VEXOS monthly. The monthly is the number a rep negotiates live, so it is
   editable where it is read instead of only from the Price Override panel
   at the bottom of the sidebar and the walkthrough hero. All three write
   the SAME `monthlyRateOverride` seam, so the sidebar, payment milestones,
   saved quote, web proposal and PDF can never disagree.

   The amount DISPLAYED is the effective monthly (after any CRM opt-out
   deduction); the amount EDITED is the base rate that deduction comes off,
   because that is what the override actually replaces. When the two differ
   the row says so rather than seeding the input with a number the rep is
   not looking at. Overriding the monthly deliberately does NOT flip the
   quote to manual pricing — see MARKETING_OVERRIDE_LINES for why. */
function MonthlySubscriptionRow({
  value,
  base,
  auto,
  overridden,
  crmDeduction = 0,
  sublabel,
  usdNote = false,
  onOverrideChange,
}) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");

  const baseN = Math.round(Number(base) || 0);
  const autoN = Math.round(Number(auto) || 0);
  const deduction = Number(crmDeduction) || 0;

  const startEditing = () => { setDraft(String(baseN)); setEditing(true); };

  const commit = (raw) => {
    setEditing(false);
    const cleaned = String(raw == null ? "" : raw).replace(/[^0-9.]/g, "");
    const n = Number(cleaned);
    // Empty / unusable → back to the volume-tiered rate-card price.
    if (cleaned === "" || !Number.isFinite(n) || n < 0) { onOverrideChange(null); return; }
    onOverrideChange(Math.round(n));
  };

  return (
    <div className="py-2.5 border-b border-white/[0.04] last:border-b-0" data-testid="sidebar-monthly-row">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[12.5px] text-white/75 flex items-center gap-1.5">
            Monthly Platform Installment
            {overridden && (
              <span
                className="px-1.5 py-[1px] rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-200 text-[9px] uppercase tracking-[0.12em]"
                data-testid="sidebar-monthly-manual-badge"
              >
                Manual
              </span>
            )}
          </p>
          {sublabel && <p className="text-[10.5px] text-white/35 mt-0.5">{sublabel}</p>}
        </div>

        {editing ? (
          <div
            className="flex items-center rounded-md border border-amber-400/45 bg-amber-400/[0.07] px-2 shrink-0"
            data-testid="sidebar-monthly-editor"
          >
            <span className="text-[11px] text-white/40 mr-0.5">$</span>
            <input
              type="text"
              inputMode="decimal"
              autoFocus
              value={draft}
              placeholder={String(autoN)}
              // The field opens pre-filled with the current rate, so select it:
              // typing a new rate replaces it instead of appending to it.
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => commit(draft)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit(draft);
                if (e.key === "Escape") setEditing(false);
              }}
              className="w-[74px] bg-transparent outline-none py-1 text-[13px] text-white tabular-nums placeholder:text-white/25"
              data-testid="sidebar-monthly-input"
            />
            <span className="text-[10px] text-white/40 ml-0.5">/ mo</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={startEditing}
            title="Click to set a custom monthly rate"
            className={`shrink-0 text-[13px] tabular-nums rounded-md px-1.5 py-1 -mr-1.5 border border-transparent transition-colors hover:border-white/20 hover:bg-white/[0.04] ${
              overridden ? "text-amber-200" : "text-white"
            }`}
            data-testid="sidebar-monthly-value"
          >
            {isTBD(value) ? <TbdBadge /> : (<>{fmt(value)}<span className="text-[10px] text-white/40 ml-1">/ mo</span></>)}
          </button>
        )}
      </div>

      {/* Footnotes: what the rate card says, what the CRM opt-out took off,
          and the reminder that a typed rate is always raw USD even when the
          quote is being displayed in CAD. */}
      {(editing || overridden || deduction > 0) && (
        <div className="mt-1.5 flex items-center justify-between gap-2" data-testid="sidebar-monthly-note">
          <p className="text-[10px] text-white/40 leading-snug min-w-0">
            {overridden && autoN > 0 && (
              <span className="text-amber-300/80">Calculated {fmtOverrideUSD(autoN)} / mo</span>
            )}
            {overridden && autoN > 0 && (deduction > 0 || (editing && usdNote)) && <span className="text-white/20"> · </span>}
            {deduction > 0 && (
              <span>CRM opt-out −{fmtOverrideUSD(deduction)} off {fmtOverrideUSD(baseN)}</span>
            )}
            {editing && usdNote && (
              <>
                {deduction > 0 && <span className="text-white/20"> · </span>}
                <span>Enter USD</span>
              </>
            )}
          </p>
          {overridden && !editing && (
            <button
              type="button"
              onClick={() => onOverrideChange(null)}
              className="shrink-0 text-[10px] text-white/40 hover:text-white/80 underline underline-offset-2 transition-colors"
              data-testid="sidebar-monthly-reset"
            >
              Reset
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── MANUAL PRICE OVERRIDE — internal sidebar panel ──────────────────
   Collapsed by default; auto-expands when a loaded quote already carries
   overrides. Amounts here are always raw USD rate-card dollars (never the
   CAD display conversion) because that's what the calculator math and the
   saved snapshot store. */
const fmtOverrideUSD = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

/* Sidebar "Additional Discount" — a $ amount or a % off the whole proposal.
   Commits on blur / Enter like the price-override inputs, so a half-typed
   number never re-prices the milestones mid-keystroke. The kind toggle keeps
   the typed value and just reinterprets it. */
function ProposalDiscountPanel({ discount, amount, oneTimeTotal, guardrail, onChange }) {
  const kind = discount?.kind === DISCOUNT_PCT ? DISCOUNT_PCT : DISCOUNT_AMOUNT;
  const [draftKind, setDraftKind] = React.useState(kind);
  const [draft, setDraft] = React.useState(discount ? String(discount.value) : "");
  const [focused, setFocused] = React.useState(false);

  React.useEffect(() => {
    if (focused) return;
    setDraft(discount ? String(discount.value) : "");
    if (discount) setDraftKind(discount.kind);
  }, [discount, focused]);

  const commit = (k = draftKind, raw = draft) => {
    const n = Number(String(raw).replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(n) || n <= 0) {
      setDraft("");
      onChange(null);
      return;
    }
    onChange({ kind: k, value: n });
  };

  const pctMode = draftKind === DISCOUNT_PCT;
  // A $ figure above the visualization split is clamped; say so rather than
  // letting the rep think the full number went through.
  const typed = Number(String(draft).replace(/[^0-9.]/g, "")) || 0;
  const capped = !pctMode && discount && typed > 0 && amount > 0 && Math.round(typed) > amount;

  return (
    <div className="mt-5 pt-5 border-t border-white/[0.08]" data-testid="sidebar-proposal-discount">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80">Additional Discount</p>
        {amount > 0 && (
          <button
            type="button"
            onClick={() => { setDraft(""); onChange(null); }}
            className="text-[10.5px] text-white/40 hover:text-white/80"
            data-testid="proposal-discount-clear"
          >
            Remove
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <div className="inline-flex rounded-lg border border-white/10 overflow-hidden shrink-0">
          {[[DISCOUNT_AMOUNT, "$ off"], [DISCOUNT_PCT, "% off"]].map(([k, lbl]) => (
            <button
              key={k}
              type="button"
              onClick={() => { setDraftKind(k); if (draft !== "") commit(k, draft); }}
              className={`px-2.5 py-1.5 text-[11px] font-medium transition-colors ${draftKind === k ? "bg-[#8FA79B] text-[#000000]" : "bg-transparent text-white/60 hover:text-white/90"}`}
              data-testid={`proposal-discount-kind-${k}`}
            >
              {lbl}
            </button>
          ))}
        </div>
        <div className={`flex flex-1 items-center rounded-md border px-2 transition-colors ${
          amount > 0 ? "border-[#8FA79B]/45 bg-[#8FA79B]/[0.07]" : "border-white/12 bg-white/[0.02]"
        }`}>
          {!pctMode && <span className="text-[11px] text-white/40 mr-0.5">$</span>}
          <input
            type="text"
            inputMode="decimal"
            value={draft}
            placeholder="0"
            onFocus={() => setFocused(true)}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => { setFocused(false); commit(); }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") {
                setDraft(discount ? String(discount.value) : "");
                e.currentTarget.blur();
              }
            }}
            className="w-full bg-transparent outline-none py-1.5 text-[12px] text-white tabular-nums placeholder:text-white/25"
            data-testid="proposal-discount-input"
          />
          {pctMode && <span className="text-[11px] text-white/40 ml-0.5">%</span>}
        </div>
      </div>
      {amount > 0 ? (
        <>
          <div className="flex items-start justify-between py-2.5 mt-2 border-b border-white/[0.04]" data-testid="proposal-discount-line">
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] text-white/75">{proposalDiscountLabel(discount?.kind === DISCOUNT_PCT ? discount.value : null)}</p>
              <p className="text-[10.5px] text-white/35 mt-0.5">Printed on the proposal · comes out of the visualization milestones</p>
            </div>
            <span className="text-[13px] text-emerald-300/90 tabular-nums shrink-0">−{fmt(amount)}</span>
          </div>
          <div className="flex justify-between items-baseline text-[12px] mt-2">
            <span className="text-white/55">One-time total after discount</span>
            <span className="text-white tabular-nums">{fmt(oneTimeTotal)}</span>
          </div>
          <GuardrailBadge verdict={guardrail} testId="guardrail-proposalDiscount" />
          {capped && (
            <p className="text-[10.5px] text-amber-200/80 mt-2 leading-snug">
              Capped at {fmt(amount)} — the discount can't exceed the visualization scope it comes out of.
            </p>
          )}
        </>
      ) : (
        <p className="text-[10.5px] text-white/35 mt-2 leading-snug">
          Take a fixed amount or a % off the one-time scope. Travel and monthly fees are never discounted.
        </p>
      )}
    </div>
  );
}

/* Internal discount-guardrail badge (Pricing Admin Section 8C). Green while a
   hand-set price is inside the first step, amber between the step and the
   floor, red below the floor with the commission rule. Advisory only — it
   never blocks, and it lives only in the rep's sidebar (never the client's
   walkthrough, proposal or PDF). */
function GuardrailBadge({ verdict, testId }) {
  if (!verdict || !(verdict.pct > 0)) return null;
  const pct = Number.isInteger(verdict.pct) ? verdict.pct : Math.round(verdict.pct * 10) / 10;
  const tone = verdict.level === GUARDRAIL_BELOW_FLOOR
    ? "border-red-400/45 bg-red-500/[0.08] text-red-200"
    : verdict.level === GUARDRAIL_APPROVAL
      ? "border-amber-400/45 bg-amber-400/[0.08] text-amber-200"
      : "border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-200/90";
  const text = verdict.message || `Within the ${verdict.firstStepPct}% first step`;
  return (
    <p
      className={`mt-1.5 rounded-md border px-2 py-1 text-[10px] leading-snug no-print ${tone}`}
      data-testid={testId}
      data-level={verdict.level}
    >
      <span className="tabular-nums">{pct}% off list</span> · {text}
    </p>
  );
}

/* Sidebar "Complimentary Add-Ons" (Pricing Admin Section 8D). The rep ticks
   add-ons from the admin list; each prints on the proposal as "Included" and
   moves no money. The running value is held to a % of the one-time contract
   value — `onChange` returns a message when it refuses a change. */
function FreeAddOnsPanel({ options, selected, status, capPct, onChange }) {
  const [error, setError] = React.useState(null);
  const byId = new Map(selected.map((a) => [a.id, a]));
  // Admin list first; anything already given that has since left the list
  // stays visible so it can still be removed.
  const rows = [
    ...options.map((o) => ({ ...o, picked: byId.get(o.id) || null })),
    ...selected.filter((a) => !options.some((o) => o.id === a.id)).map((a) => ({ ...a, picked: a })),
  ];
  const apply = (next) => setError(onChange(next) || null);
  const toggle = (row) => apply(row.picked
    ? selected.filter((a) => a.id !== row.id)
    : [...selected, { id: row.id, label: row.label, value: row.value, qty: 1 }]);
  const setQty = (row, qty) => apply(selected.map((a) => (a.id === row.id ? { ...a, qty: Math.max(1, qty) } : a)));
  const pctLabel = Number.isInteger(capPct) ? capPct : Math.round(capPct * 10) / 10;

  return (
    <div className="mt-5 pt-5 border-t border-white/[0.08] no-print" data-testid="sidebar-free-addons">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/80">Complimentary Add-Ons</p>
        <span
          className={`text-[10.5px] tabular-nums ${status.over ? "text-red-300" : "text-white/50"}`}
          data-testid="free-addons-meter"
        >
          {fmtOverrideUSD(status.total)} of {fmtOverrideUSD(status.cap)} cap
        </span>
      </div>
      <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden mb-3">
        <div
          className={`h-full ${status.over ? "bg-red-400/80" : "bg-[#8FA79B]/80"}`}
          style={{ width: `${status.cap > 0 ? Math.min(100, (status.total / status.cap) * 100) : (status.total > 0 ? 100 : 0)}%` }}
        />
      </div>
      <div className="divide-y divide-white/[0.04]">
        {rows.map((row) => (
          <div key={row.id} className="flex items-center justify-between gap-2 py-1.5" data-testid={`free-addon-${row.id}`}>
            <label className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer">
              <input
                type="checkbox"
                checked={!!row.picked}
                onChange={() => toggle(row)}
                className="accent-[#8FA79B]"
                data-testid={`free-addon-toggle-${row.id}`}
              />
              <span className="text-[12px] text-white/75 truncate">{row.label}</span>
              <span className="text-[10px] text-white/35 tabular-nums shrink-0">{fmtOverrideUSD(row.value)}</span>
            </label>
            {row.picked && (
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => setQty(row, row.picked.qty - 1)}
                  disabled={row.picked.qty <= 1}
                  className="p-1 rounded text-white/40 hover:text-white/80 disabled:opacity-30"
                  data-testid={`free-addon-dec-${row.id}`}
                >
                  <Minus className="w-3 h-3" />
                </button>
                <span className="w-5 text-center text-[11.5px] text-white tabular-nums" data-testid={`free-addon-qty-${row.id}`}>{row.picked.qty}</span>
                <button
                  type="button"
                  onClick={() => setQty(row, row.picked.qty + 1)}
                  className="p-1 rounded text-white/40 hover:text-white/80"
                  data-testid={`free-addon-inc-${row.id}`}
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      {error ? (
        <p className="text-[10.5px] text-red-300 mt-2 leading-snug" data-testid="free-addons-error">{error}</p>
      ) : status.over ? (
        <p className="text-[10.5px] text-red-300/90 mt-2 leading-snug" data-testid="free-addons-over">
          Over the {pctLabel}% cap ({fmtOverrideUSD(status.cap)}) — needs admin approval.
        </p>
      ) : (
        <p className="text-[10.5px] text-white/35 mt-2 leading-snug">
          Printed on the proposal as "Included" — no charge, no change to totals or payments. Capped at {pctLabel}% of the one-time contract value.
        </p>
      )}
    </div>
  );
}

function PriceOverrideRow({ row, guardrail, onChange }) {
  const [draft, setDraft] = React.useState(row.override === null ? "" : String(row.override));
  const [focused, setFocused] = React.useState(false);

  // Track external changes (reset-all, quote hydration) without stomping on
  // whatever the rep is mid-way through typing.
  React.useEffect(() => {
    if (!focused) setDraft(row.override === null ? "" : String(row.override));
  }, [row.override, focused]);

  const commit = () => {
    const raw = draft.replace(/[^0-9.]/g, "");
    const n = Number(raw);
    if (raw === "" || !Number.isFinite(n) || n < 0) {
      setDraft("");
      onChange(row.id, null);
      return;
    }
    onChange(row.id, n);
  };

  const overridden = row.override !== null;

  return (
    <div className="py-2" data-testid={`override-row-${row.id}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0 flex-1">
        <p className="text-[12px] text-white/75 truncate">{row.label}</p>
        <p className="text-[10px] mt-0.5 tabular-nums">
          {overridden ? (
            <span className="text-amber-300/80">
              Calculated <span className="line-through text-white/35">{fmtOverrideUSD(row.auto)}</span>
            </span>
          ) : (
            <span className="text-white/35">Calculated {fmtOverrideUSD(row.auto)}{row.monthly ? " / mo" : ""}</span>
          )}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <div className={`flex items-center rounded-md border px-2 transition-colors ${
          overridden ? "border-amber-400/45 bg-amber-400/[0.07]" : "border-white/12 bg-white/[0.02]"
        }`}>
          <span className="text-[11px] text-white/40 mr-0.5">$</span>
          <input
            type="text"
            inputMode="decimal"
            value={draft}
            placeholder={String(Math.round(row.auto))}
            onFocus={() => setFocused(true)}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => { setFocused(false); commit(); }}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.currentTarget.blur(); }
              if (e.key === "Escape") {
                setDraft(row.override === null ? "" : String(row.override));
                e.currentTarget.blur();
              }
            }}
            className="w-[74px] bg-transparent outline-none py-1.5 text-[12px] text-white tabular-nums placeholder:text-white/25"
            data-testid={`override-input-${row.id}`}
          />
        </div>
        <button
          type="button"
          onClick={() => { setDraft(""); onChange(row.id, null); }}
          disabled={!overridden}
          title="Restore calculated price"
          className="p-1 rounded text-white/30 hover:text-white/70 disabled:opacity-0 disabled:pointer-events-none transition-colors"
          data-testid={`override-reset-${row.id}`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
    <GuardrailBadge verdict={guardrail} testId={`guardrail-${row.id}`} />
    </div>
  );
}

// Popup shown when a rep's first price override would cost the quote its
// bundle / motion-package discount. States exactly what removing it adds to
// the client's total and makes the rep pick. Escape / clicking outside keeps
// the discount, which is also what happens if nobody answers.
function OverrideDiscountPrompt({ amount, bundle, motionPackagePct, onChoose }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onChoose("keep"); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onChoose]);

  const parts = [];
  // The bundle is opt-in, so it is only at stake once the rep applied it; a
  // quote without it can still have the Motion Package riding on the prices.
  const bundleOn = !!(bundle && bundle.tier > 0 && bundle.label);
  if (bundleOn) parts.push(`the ${bundle.label} (${bundle.pct}% off visualization)`);
  if (motionPackagePct > 0) parts.push(`the Motion Package (${motionPackagePct}% off video)`);
  const what = parts.length ? parts.join(" and ") : "its discount";
  const noun = parts.length > 1 ? "these discounts" : "this discount";
  const headline = bundleOn ? "the bundle discount" : (motionPackagePct > 0 ? "the Motion Package discount" : "the discount");

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center px-4 no-print"
      onClick={() => onChoose("keep")}
      data-testid="override-discount-prompt"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="override-discount-title"
        className="w-full max-w-[440px] rounded-2xl border border-white/12 bg-[#0b0b0b] p-6 shadow-2xl"
        style={{ fontFamily: "var(--font-sans)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[10.5px] uppercase tracking-[0.16em] text-amber-300/90">Price Override</p>
        <h2 id="override-discount-title" className="mt-2 text-[19px] leading-snug text-white">
          Overriding a price removes {headline}
        </h2>
        <p className="mt-3 text-[13px] leading-relaxed text-white/65">
          This quote has {what}. Should it stay on the quote with your new price?
        </p>
        <div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-400/[0.06] px-4 py-3">
          <p className="text-[12px] text-white/60">Removing {noun} adds back</p>
          <p className="mt-0.5 text-[26px] text-amber-200 tabular-nums" data-testid="override-discount-amount">
            +{fmtOverrideUSD(amount)}
          </p>
          <p className="text-[11px] text-white/45">to the client's one-time total.</p>
        </div>
        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            autoFocus
            onClick={() => onChoose("keep")}
            className="w-full py-2.5 rounded-full text-[13px] bg-[#8FA79B] text-black hover:bg-[#A6B8AD] transition-colors"
            data-testid="override-discount-keep"
          >
            Keep {headline}
          </button>
          <button
            type="button"
            onClick={() => onChoose("remove")}
            className="w-full py-2.5 rounded-full text-[13px] border border-white/15 text-white/70 hover:text-white hover:border-white/35 transition-colors"
            data-testid="override-discount-remove"
          >
            Remove it (+{fmtOverrideUSD(amount)})
          </button>
        </div>
      </div>
    </div>
  );
}

// Rep-set production timeline. Each track takes a committed week range
// ("7 to 8 weeks"); one number is fine too. Empty = the calculated estimate.
// The override replaces the estimate everywhere the timeline is shown —
// see apply_timeline_override in backend/routers/vexos_timeline.py.
function TimelineOverridePanel({ timeline, override, showVexos, onChange }) {
  const tracks = [
    ...(showVexos ? [{ key: "vexos", label: "VEXOS Platform" }] : []),
    { key: "viz", label: "Studio Visualization" },
  ];
  const active = tracks.filter((t) => override?.[t.key]).length;
  const [open, setOpen] = React.useState(active > 0);
  React.useEffect(() => { if (active > 0) setOpen(true); }, [active]);

  const setTrack = (key, value) => onChange({ ...(override || {}), [key]: value });

  return (
    <div className="mt-6 pt-5 border-t border-white/[0.08] no-print" data-testid="sidebar-timeline-override">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid="timeline-override-toggle"
      >
        <span className="flex items-center gap-2">
          <span className={`text-[10.5px] uppercase tracking-[0.16em] ${active > 0 ? "text-amber-300/90" : "text-white/45"}`}>
            Timeline Override
          </span>
          {active > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-200 text-[9.5px] tabular-nums">
              {active} active
            </span>
          )}
        </span>
        {open ? <Minus className="w-3 h-3 text-white/40" /> : <Plus className="w-3 h-3 text-white/40" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-[10px] text-white/45 leading-relaxed">
            Replaces the calculated delivery estimate. The walkthrough timeline, the
            proposal PDF, the launch date and the project intake all follow it.
            Leave empty to keep the calculated estimate.
          </p>
          {tracks.map((t) => (
            <TimelineOverrideRow
              key={t.key}
              trackKey={t.key}
              label={t.label}
              calculated={timeline[`${t.key}CalculatedWeeksDisplay`] || timeline[`${t.key}WeeksDisplay`]}
              value={override?.[t.key] || null}
              onChange={(v) => setTrack(t.key, v)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineOverrideRow({ trackKey, label, calculated, value, onChange }) {
  const [lo, setLo] = React.useState(value ? String(value.minWeeks) : "");
  const [hi, setHi] = React.useState(value ? String(value.maxWeeks) : "");
  const focused = React.useRef(false);
  const boxRef = React.useRef(null);
  React.useEffect(() => {
    if (focused.current) return;
    setLo(value ? String(value.minWeeks) : "");
    setHi(value ? String(value.maxWeeks) : "");
  }, [value]);

  const commit = () => {
    focused.current = false;
    const a = lo.trim() === "" ? null : Number(lo);
    const b = hi.trim() === "" ? null : Number(hi);
    onChange(a === null && b === null ? null : { minWeeks: a, maxWeeks: b });
  };
  const field = (val, set, testId, placeholder) => (
    <input
      type="text"
      inputMode="numeric"
      value={val}
      placeholder={placeholder}
      onFocus={() => { focused.current = true; }}
      onChange={(e) => set(e.target.value.replace(/[^0-9]/g, ""))}
      // Tabbing from "7" to "8" is one edit — commit when focus leaves the pair.
      onBlur={(e) => { if (!boxRef.current?.contains(e.relatedTarget)) commit(); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      className={`w-11 rounded-md border px-2 py-1.5 bg-transparent outline-none text-center text-[12px] text-white tabular-nums placeholder:text-white/25 ${
        value ? "border-amber-400/45 bg-amber-400/[0.06]" : "border-white/12"
      }`}
      data-testid={testId}
    />
  );

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2.5" data-testid={`timeline-override-${trackKey}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[12px] text-white/75">{label}</p>
        {value && (
          <button
            type="button"
            onClick={() => { setLo(""); setHi(""); onChange(null); }}
            className="text-[10.5px] text-white/40 hover:text-white/80"
            data-testid={`timeline-override-${trackKey}-clear`}
          >
            Reset
          </button>
        )}
      </div>
      <p className={`text-[10px] tabular-nums mb-2 ${value ? "text-white/35 line-through" : "text-white/40"}`}>
        Calculated {calculated || "—"}
      </p>
      <div ref={boxRef} className="flex items-center gap-2 text-[11px] text-white/45">
        {field(lo, setLo, `timeline-override-${trackKey}-min`, "7")}
        <span>to</span>
        {field(hi, setHi, `timeline-override-${trackKey}-max`, "8")}
        <span>weeks</span>
      </div>
    </div>
  );
}

function PriceOverridePanel({
  open, onToggle, rows, count, manualPricing, onChange, onClearAll,
  hasLineOverrides, discountAmount, discountChoice, onDiscountChoice,
  guardrails = {}, bundleApplied = true,
}) {
  const discountRemoved = discountChoice === "remove";
  // With the bundle not applied (it's opt-in) only the Motion Package can be
  // at stake, so the copy names that instead of a bundle the quote lacks.
  const discountName = bundleApplied ? "bundle discount" : "Motion Package discount";
  // Worth showing whenever a hand-set price has discounts at stake, or the
  // rep already removed them (so they can put them back).
  const showDiscountChoice = hasLineOverrides && (discountAmount > 0 || discountRemoved);
  const sections = PRICE_OVERRIDE_SECTIONS
    .map((name) => ({ name, items: rows.filter((r) => r.section === name) }))
    .filter((s) => s.items.length > 0);

  return (
    <div className="mt-6 pt-5 border-t border-white/[0.08] no-print" data-testid="sidebar-price-override">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid="override-panel-toggle"
      >
        <span className="flex items-center gap-2">
          <span className={`text-[10.5px] uppercase tracking-[0.16em] ${count > 0 ? "text-amber-300/90" : "text-white/45"}`}>
            Price Override
          </span>
          {count > 0 && (
            <span
              className="px-1.5 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-200 text-[9.5px] tabular-nums"
              data-testid="override-active-count"
            >
              {count} active
            </span>
          )}
        </span>
        {open ? <Minus className="w-3 h-3 text-white/40" /> : <Plus className="w-3 h-3 text-white/40" />}
      </button>

      {count > 0 && !open && (
        <p className="mt-2 text-[10px] text-amber-200/70 leading-snug">
          {manualPricing
            ? "Manual pricing is applied to this quote and its proposal PDF. Bundle and motion-package discounts are off."
            : hasLineOverrides && discountAmount > 0
              ? `Manual pricing is applied to this quote and its proposal PDF. The ${discountName} is kept.`
              : "Manual pricing is applied to this quote and its proposal PDF."}
        </p>
      )}

      {showDiscountChoice && (
        <div
          className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2"
          data-testid="override-discount-choice"
        >
          <div className="min-w-0">
            <p className="text-[12px] text-white/75">Keep {discountName}</p>
            <p className="text-[10px] text-white/40 tabular-nums leading-snug">
              {discountRemoved
                ? `Removed — client pays ${fmtOverrideUSD(discountAmount)} more`
                : `Kept — removing it adds ${fmtOverrideUSD(discountAmount)}`}
            </p>
          </div>
          <Switch
            checked={!discountRemoved}
            onCheckedChange={(on) => onDiscountChoice(on ? "keep" : "remove")}
            data-testid="override-discount-switch"
          />
        </div>
      )}

      {open && (
        <div className="mt-3">
          <p className="text-[10px] text-white/45 leading-relaxed mb-3">
            Replaces the calculated amount on any line. Totals, payment milestones,
            and the proposal PDF all follow the override. Leave a field empty to keep
            calculated pricing. When a <span className="text-white/60">visualization or platform</span>{" "}
            price is set by hand you choose whether the bundle and motion-package
            discounts stay. Removing them takes the quote off the rate card and no
            discount is shown anywhere — quote, proposal, or PDF. Campaign fees,
            collateral and the monthly subscription price independently and never
            affect the bundle.
          </p>
          {sections.map((s) => (
            <div key={s.name} className="mb-3">
              <p className="text-[9.5px] uppercase tracking-[0.14em] text-white/30 mb-0.5">{s.name}</p>
              <div className="divide-y divide-white/[0.04]">
                {s.items.map((r) => (
                  <PriceOverrideRow key={r.id} row={r} guardrail={guardrails[r.id]} onChange={onChange} />
                ))}
              </div>
            </div>
          ))}
          {count > 0 && (
            <button
              type="button"
              onClick={onClearAll}
              className="mt-1 w-full py-2 rounded-full text-[11px] tracking-wide border border-white/15 text-white/65 hover:text-white hover:border-white/35 transition-colors"
              data-testid="override-reset-all"
            >
              Reset all to calculated pricing
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Walkthrough/Presentation Mode "Then Monthly" expand panel — opens
// when the rep (or client) clicks the recurring price pill. Shows the
// itemised inclusions and the VEXOS CRM opt-out checkbox so the rep can
// handle live monthly-price objections without leaving the deck. The
// hero number stays click-to-edit so the rep can honour a negotiated
// rate inline.
function MonthlyExpandPanel({
  c,
  contractTerm,
  crmIncluded,
  onCrmToggle,
  monthlyRateOverride,
  onOverrideChange,
  paymentSchedule,
  postTerm,
}) {
  const isOverridden = monthlyRateOverride !== null && Number.isFinite(Number(monthlyRateOverride));
  const deduction = Number(paymentSchedule?.crmDeduction || 0);
  const baseMonthly = Number(paymentSchedule?.monthlyBase || c.monthlyRate || 0);
  const effective = Number(paymentSchedule?.monthly || c.monthlyRate || 0);
  const autoMonthly = Number(c.monthlyRateAuto || paymentSchedule?.monthlyAuto || 0);

  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");

  const startEditing = () => {
    setDraft(String(baseMonthly || 0));
    setEditing(true);
  };
  const commit = (raw) => {
    setEditing(false);
    if (raw === "" || raw == null) { onOverrideChange(null); return; }
    const n = Number(String(raw).replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(n) || n < 0) { onOverrideChange(null); return; }
    onOverrideChange(Math.round(n));
  };

  const inclusions = [
    { id: "hosting",   label: "Platform hosting & infrastructure" },
    { id: "support",   label: "Dedicated support team" },
    { id: "content",   label: "Content & asset management" },
    { id: "updates",   label: "Software updates & feature releases" },
  ];
  // The VEXOS CRM is kept apart from "feature releases" — its own product,
  // due end of December 2026 — and the pre-sale velocity dashboard is no
  // longer promised (Cole / Bernard, 2026-11-02). The CRM toggle itself, and
  // the $1,500 it takes off the monthly, are unchanged.
  const crmRow = { id: "crm", label: "VEXOS CRM · its own product, releasing end of December 2026", isToggle: true };

  return (
    <div>
      <div style={expandStyles.eyebrow}>Then Monthly, Post-Launch</div>

      {/* Hero monthly amount — click to edit */}
      <div data-testid="expand-monthly-amount" style={{ margin: "16px 0 18px" }}>
        {editing ? (
          <div style={{ display: "inline-flex", alignItems: "baseline" }}>
            <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 400, fontSize: "72px", color: "rgba(255,255,255,0.55)", marginRight: "10px" }}>$</span>
            <input
              type="number"
              inputMode="decimal"
              autoFocus
              value={draft}
              min={0}
              step={1}
              onFocus={(e) => e.currentTarget.select()}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => commit(draft)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit(draft);
                if (e.key === "Escape") setEditing(false);
              }}
              style={{
                fontFamily: 'var(--font-sans)',
                fontWeight: 400,
                fontSize: "96px",
                lineHeight: 1,
                color: "#ffffff",
                background: "transparent",
                border: "none",
                borderBottom: "2px solid rgba(143,167,155,0.6)",
                outline: "none",
                width: "300px",
                fontVariantNumeric: "tabular-nums",
                textAlign: "left",
              }}
              data-testid="expand-monthly-input"
            />
            <span style={{ fontSize: "40px", color: "rgba(255,255,255,0.5)", marginLeft: "12px" }}>/mo</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={startEditing}
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 400,
              fontSize: "96px",
              lineHeight: 1,
              color: "#ffffff",
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontVariantNumeric: "tabular-nums",
              display: "inline-flex",
              alignItems: "baseline",
            }}
            title="Click to set a custom monthly rate"
            data-testid="expand-monthly-value"
          >
            {fmtUSD(effective)}
            <span style={{ fontSize: "40px", color: "rgba(255,255,255,0.5)", marginLeft: "12px" }}>/mo</span>
          </button>
        )}
      </div>

      {/* Status row — agreement term + manual / CRM-removed badges */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "8px" }}>
        <span style={{ fontSize: "22px", color: "rgba(255,255,255,0.7)" }}>{contractTerm}-month agreement</span>
        {isOverridden && (
          <span
            data-testid="expand-monthly-manual-badge"
            style={{
              fontSize: "11px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              padding: "4px 10px",
              borderRadius: "4px",
              background: "rgba(143,167,155,0.15)",
              color: "#8FA79B",
              border: "1px solid rgba(143,167,155,0.4)",
            }}
          >
            Manual rate
          </span>
        )}
        {deduction > 0 && (
          <span
            data-testid="expand-monthly-crm-removed-badge"
            style={{
              fontSize: "11px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              padding: "4px 10px",
              borderRadius: "4px",
              background: "rgba(194,74,46,0.13)",
              color: "#D9A385",
              border: "1px solid rgba(194,74,46,0.32)",
            }}
            title={`Auto-rate ${fmtUSD(baseMonthly)} − ${fmtUSD(1500)} CRM`}
          >
            CRM opt-out · −{fmtUSD(deduction)}/mo
          </span>
        )}
      </div>
      <div style={{ fontSize: "16px", color: "rgba(255,255,255,0.45)", marginBottom: "24px" }}>
        Begins post-launch of VEXOS platform
      </div>

      {postTerm && <PostTermFeeRow variant="present" {...postTerm} />}

      {/* What's included panel */}
      <div
        style={{
          marginTop: "8px",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          paddingTop: "20px",
        }}
        data-testid="expand-monthly-breakdown"
      >
        <div style={{ fontSize: "11px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(143,167,155,0.85)", marginBottom: "14px" }}>
          What&rsquo;s included in the monthly
        </div>
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {[...inclusions, crmRow].map((it) => {
            const rowStyle = {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              padding: "12px 0",
              borderBottom: "1px solid rgba(255,255,255,0.04)",
            };
            if (it.isToggle) {
              return (
                <li key={it.id} style={{ ...rowStyle, marginTop: "14px", borderTop: "1px solid rgba(255,255,255,0.08)" }} data-testid={`expand-inclusion-${it.id}`}>
                  <label style={{ display: "flex", alignItems: "center", gap: "12px", cursor: "pointer", flex: 1, color: "rgba(255,255,255,0.92)", fontSize: "16px" }}>
                    <input
                      type="checkbox"
                      checked={!!crmIncluded}
                      onChange={(e) => onCrmToggle(e.target.checked)}
                      style={{ width: "16px", height: "16px", accentColor: "#8FA79B", cursor: "pointer" }}
                      data-testid="expand-monthly-crm-toggle"
                    />
                    {it.label}
                  </label>
                  <span style={{ fontSize: "12px", letterSpacing: "0.12em", textTransform: "uppercase", whiteSpace: "nowrap", color: crmIncluded ? "#8FA79B" : "#D9A385" }}>
                    {crmIncluded ? "Included" : `−${fmtUSD(1500)}/mo`}
                  </span>
                </li>
              );
            }
            return (
              <li key={it.id} style={rowStyle} data-testid={`expand-inclusion-${it.id}`}>
                <span style={{ display: "flex", alignItems: "center", gap: "12px", color: "rgba(255,255,255,0.92)", fontSize: "16px" }}>
                  <Check className="w-4 h-4" style={{ color: "#8FA79B" }} />
                  {it.label}
                </span>
                <span style={{ fontSize: "12px", letterSpacing: "0.12em", textTransform: "uppercase", color: "#8FA79B" }}>Included</span>
              </li>
            );
          })}
        </ul>

        {!crmIncluded && (
          <div
            style={{ marginTop: "14px", padding: "12px 16px", borderRadius: "6px", background: "rgba(194,74,46,0.08)", border: "1px solid rgba(194,74,46,0.22)", fontSize: "13px", color: "rgba(255,255,255,0.78)" }}
            data-testid="expand-monthly-crm-removed-note"
          >
            CRM opt-out applied — monthly reduced by {fmtUSD(1500)} (minimum {fmtUSD(1000)}/mo floor).
          </div>
        )}

        {isOverridden && autoMonthly > 0 && (
          <div style={{ marginTop: "14px", padding: "12px 16px", borderRadius: "6px", background: "rgba(143,167,155,0.06)", border: "1px solid rgba(143,167,155,0.22)", fontSize: "13px", color: "rgba(255,255,255,0.72)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
            <span>Custom rate active — auto-calculated was {fmtUSD(autoMonthly)}/mo</span>
            <button
              type="button"
              onClick={() => onOverrideChange(null)}
              style={{ background: "transparent", border: "none", color: "#8FA79B", fontSize: "12px", letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer", padding: 0 }}
              data-testid="expand-monthly-reset-override"
            >
              Reset to auto
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ExpRow({ label, sub, value, isMonthly = false, muted = false }) {
  return (
    <div className="flex items-start justify-between py-3.5 border-b border-white/[0.05] last:border-b-0">
      <div className="min-w-0 flex-1 pr-4">
        <p className={`text-[15px] ${muted ? "text-white/55" : "text-white/85"}`}>{label}</p>
        {sub && <p className="text-[11.5px] text-white/40 mt-1 leading-snug">{sub}</p>}
      </div>
      <span className={`text-[16px] tabular-nums shrink-0 ${muted ? "text-white/40" : "text-white"}`}>
        {muted && value === 0 ? <span className="text-[11.5px] uppercase tracking-[0.14em] text-[#8FA79B]/70">Included</span>
          : <>{fmt(value)}{isMonthly && <span className="text-[11px] text-white/40 ml-1">/ mo</span>}</>}
      </span>
    </div>
  );
}

function ExpSubtotal({ label, value }) {
  return (
    <div className="flex items-baseline justify-between mt-4 pt-4 border-t border-[#8FA79B]/15">
      <span className="text-[12px] uppercase tracking-[0.14em] text-white/55">{label}</span>
      <span className="text-[20px] text-white tabular-nums">{fmt(value)}</span>
    </div>
  );
}

function PaymentMilestone({ label, sub, details, value, isMonthly = false }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-white/[0.04] last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] text-white/85">{label}</p>
        {sub && <p className="text-[10.5px] text-white/40 mt-0.5 leading-snug">{sub}</p>}
        {details && details.length > 0 && (
          <ul className="text-[10px] text-white/35 mt-1.5 leading-snug list-disc pl-3.5 space-y-1">
            {details.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        )}
      </div>
      <span className="text-[14px] font-medium text-white tabular-nums shrink-0">
        {fmt(value)}{isMonthly && <span className="text-[10px] font-normal text-white/40 ml-1">/ mo</span>}
      </span>
    </div>
  );
}

function BundleBlock({ calc, onApply }) {
  const { bundle, bundleOffer, vizGross, vizNet, bundleSavings, moduleActive, manualPricing } = calc;

  // A hand-priced quote has no bundle and gets no "unlock more savings" nudge
  // either — just the visualization total, which is the sum of its own lines.
  if (manualPricing) {
    return (
      <div className="mt-5 pt-5 border-t border-white/[0.08]">
        <div className="flex justify-between items-baseline text-[13px]" data-testid="bundle-block-manual">
          <span className="text-white/85">Visualization Total</span>
          <span className="text-white font-medium tabular-nums">{fmt(vizGross)}</span>
        </div>
      </div>
    );
  }

  const modules = [
    { key: "exterior", label: "Exterior" },
    { key: "interiorRender", label: "Renderings" },
    { key: "interiorVR", label: "VR Tours" },
    { key: "amenity", label: "Amenities" },
    { key: "video", label: "Video" },
    { key: "floorPlans", label: "Floor Plans" },
  ];
  // `bundle` is what the rep applied; `offer` is what the modules qualify
  // for. The bundle is never added on its own — see ./bundleOptIn.
  const offer = bundleOffer || bundle || {};
  const active = bundle && bundle.tier > 0;
  const available = !active && offer.tier > 0;
  const NEXT_TIER_PCTS = { 1: 10, 2: 15 };
  const inactiveLabels = modules.filter(m => !moduleActive[m.key]).map(m => m.label);
  const nextPct = NEXT_TIER_PCTS[offer.tier] || null;
  const moduleDots = (
    <div className="flex flex-wrap gap-x-2.5 gap-y-1 text-[10px] uppercase tracking-[0.08em]">
      {modules.map(m => (
        <span key={m.key} className={moduleActive[m.key] ? "text-[#8FA79B]" : "text-white/25"}>
          {moduleActive[m.key] ? "●" : "○"} {m.label}
        </span>
      ))}
    </div>
  );
  const nextTierNudge = offer.tier > 0 && offer.tier < 3 && inactiveLabels.length > 0 && (
    <p className="text-[10.5px] text-white/45 mt-2.5 leading-snug">
      Add {inactiveLabels.join(" + ")} → unlock {offer.tier === 1 ? "Triple Experience Bundle" : "Full Stack Bundle"} ({nextPct}%)
    </p>
  );

  return (
    <div className="mt-5 pt-5 border-t border-white/[0.08]">
      <div className="flex justify-between items-baseline text-[12px] mb-2">
        <span className="text-white/55">Visualization Subtotal</span>
        <span className="text-white tabular-nums">{fmt(vizGross)}</span>
      </div>

      {active ? (
        <>
          <div className={`mt-3 rounded-lg p-3.5 border ${
            bundle.tier === 3 ? "border-[#8FA79B]/40 bg-[#8FA79B]/[0.10]"
            : bundle.tier === 2 ? "border-[#8FA79B]/25 bg-[#8FA79B]/[0.05]"
            : "border-[#8FA79B]/15 bg-[#8FA79B]/[0.025]"
          }`} data-testid="bundle-block-active">
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-[12.5px] text-[#8FA79B]" style={{ fontFamily: 'var(--font-sans)', fontWeight: 450 }}>
                {bundle.label}
              </span>
              <span className="text-[12px] text-[#8FA79B] font-medium tabular-nums">{bundle.pct}% off</span>
            </div>
            <div className="flex justify-between items-baseline mb-2">
              <span className="text-[10.5px] uppercase tracking-[0.12em] text-white/45">You save</span>
              <span className="text-[14px] text-emerald-300/90 font-medium tabular-nums">−{fmt(bundleSavings)}</span>
            </div>
            {moduleDots}
            {nextTierNudge}
            {onApply && (
              <button
                type="button"
                onClick={() => onApply(false)}
                className="mt-2.5 text-[10.5px] text-white/40 hover:text-white/75 underline-offset-2 hover:underline transition-colors"
                data-testid="bundle-remove"
              >
                Remove bundle discount
              </button>
            )}
          </div>
          <div className="flex justify-between items-baseline text-[13px] mt-3 pt-3 border-t border-white/[0.06]">
            <span className="text-white/85">Net Visualization Total</span>
            <span className="text-white font-medium tabular-nums">{fmt(vizNet)}</span>
          </div>
        </>
      ) : available ? (
        <div className="mt-3 rounded-lg p-3.5 border border-dashed border-[#8FA79B]/35" data-testid="bundle-block-available">
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0">
              <p className="text-[12.5px] text-[#8FA79B] leading-snug" style={{ fontFamily: 'var(--font-sans)', fontWeight: 450 }}>
                {bundleOfferCopy(offer)}
              </p>
              <p className="text-[10.5px] text-white/45 mt-0.5 tabular-nums">
                Not on the quote · would save {fmt(bundleOfferSavings(offer, vizGross))}
              </p>
            </div>
            {onApply && (
              <button
                type="button"
                onClick={() => onApply(true)}
                className="shrink-0 px-3 py-1 rounded-full text-[11px] font-medium bg-[#8FA79B] text-black hover:bg-[#A6B8AD] transition-colors"
                data-testid="bundle-apply"
              >
                Apply
              </button>
            )}
          </div>
          {moduleDots}
          {nextTierNudge}
        </div>
      ) : (
        <div className="mt-2 rounded-lg p-3 bg-white/[0.02] border border-white/[0.05]" data-testid="bundle-block-prompt">
          <div className="mb-2">{moduleDots}</div>
          <p className="text-[10.5px] text-white/45 leading-snug">
            Add more experience modules to unlock bundle savings.
          </p>
        </div>
      )}
    </div>
  );
}


